import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE from '../api';
import FOOTBALL_CLUBS from '../footballClubs';
import { tierOfClub, loadTierOverrides } from '../clubTiers';

const TOKEN_KEY = 'ustar_admin_token';
axios.interceptors.request.use(config => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers['x-admin-token'] = token;
  return config;
});

// כשהטוקן פג או הוחלף בשרת, כל בקשה מוגנת חוזרת עם "גישה אסורה" (401).
// בלי הטיפול הזה השגיאה נבלעת וכל טאב יוצא ריק — מה שנראה כאילו המידע
// נמחק, למרות שהוא שלם. במקום זה: מנקים את הטוקן הישן ומחזירים למסך
// ההתחברות, כדי שהמשתמש יקבל טוקן חדש והמידע יחזור מיד.
// חריג: כשל בהתחברות עצמה (/auth/login) — שם 401 אומר "סיסמה שגויה",
// ואסור לזרוק ממנו למסך ההתחברות בלולאה.
let sessionExpiryHandled = false;
axios.interceptors.response.use(
  res => res,
  err => {
    const status = err.response && err.response.status;
    const url = (err.config && err.config.url) || '';
    const isLoginCall = url.includes('/auth/login');
    if (status === 401 && !isLoginCall && !sessionExpiryHandled) {
      sessionExpiryHandled = true;
      try {
        localStorage.removeItem(TOKEN_KEY);
        sessionStorage.setItem('ustar_session_expired', '1');
      } catch (e) { /* ignore */ }
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

const API = API_BASE;
const SITE_URL = process.env.NODE_ENV === 'production' ? 'https://www.ustarclub.co.il' : 'http://localhost:3000';

// ─── שדה חיפוש קבוצה (עם יצירת קבוצה חדשה) ───────────────────────────────────
function TeamSearchInput({ value, onChange, options, placeholder }) {
  const [query, setQuery] = React.useState(value || '');
  const [open, setOpen] = React.useState(false);
  const boxRef = React.useRef(null);
  React.useEffect(() => { setQuery(value || ''); }, [value]);
  React.useEffect(() => {
    const onDoc = e => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const q = (query || '').trim();
  const matches = q ? options.filter(t => t.includes(q)).slice(0, 8) : options.slice(0, 8);
  const exact = options.some(t => t === q);
  const pick = val => { onChange(val); setQuery(val); setOpen(false); };
  return (
    <div ref={boxRef} style={{ position:'relative' }}>
      <div style={{ position:'relative' }}>
        <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:'#d4af37', fontSize:'0.85rem' }}>🔍</span>
        <input value={query}
          onChange={e => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder || 'הקלד שם קבוצה'}
          autoComplete="off"
          style={{ width:'100%', background:'#0a1a3e', border:`1px solid ${open?'#d4af37':'#2a3a6a'}`, borderRadius:10, padding:'10px 32px 10px 12px', color:'#fff', fontSize:'0.9rem', boxSizing:'border-box' }} />
        {query && <button type="button" onClick={()=>{ setQuery(''); onChange(''); setOpen(true); }}
          style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', color:'#8a9aba', cursor:'pointer', fontSize:'1rem' }}>×</button>}
      </div>
      {open && (
        <div style={{ position:'absolute', top:'calc(100% + 4px)', right:0, left:0, zIndex:40, background:'#0d1a35', border:'1px solid #2a3a6a', borderRadius:10, overflow:'hidden', boxShadow:'0 12px 40px rgba(0,0,0,0.6)', maxHeight:260, overflowY:'auto' }}>
          {matches.map(t => (
            <div key={t} onMouseDown={e=>{ e.preventDefault(); pick(t); }}
              style={{ padding:'9px 13px', cursor:'pointer', fontSize:'0.9rem', color:'#fff' }}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(212,175,55,0.12)'}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>{t}</div>
          ))}
          {q && !exact && (
            <div onMouseDown={e=>{ e.preventDefault(); pick(q); }}
              style={{ padding:'10px 13px', cursor:'pointer', fontSize:'0.88rem', color:'#25d366', fontWeight:700, borderTop: matches.length?'1px solid #1e2e5a':'none', background:'rgba(37,211,102,0.06)' }}>
              ➕ צור קבוצה חדשה: "{q}"
            </div>
          )}
          {matches.length === 0 && !q && <div style={{ padding:'10px 13px', color:'#4a5a7a', fontSize:'0.85rem' }}>הקלד כדי לחפש...</div>}
        </div>
      )}
    </div>
  );
}

// ─── helpers ───────────────────────────────────────────────────────────────
function pad4(n) { return n ? String(n).padStart(4, '0') : '----'; }

function currentSeason() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  if (month >= 8) return `${year}/${String(year + 1).slice(-2)}`;
  return `${year - 1}/${String(year).slice(-2)}`;
}

const SEASONS = (() => {
  const base = 2022;
  const result = [];
  for (let y = base; y <= new Date().getFullYear() + 1; y++) {
    result.push(`${y}/${String(y + 1).slice(-2)}`);
  }
  return result.reverse();
})();

function calcAge(dob) {
  if (!dob) return null;
  const b = new Date(dob), now = new Date();
  let a = now.getFullYear() - b.getFullYear();
  if (now.getMonth() < b.getMonth() || (now.getMonth() === b.getMonth() && now.getDate() < b.getDate())) a--;
  return a;
}

function daysUntilBirthday(dob) {
  if (!dob) return null;
  const b = new Date(dob), now = new Date();
  let next = new Date(now.getFullYear(), b.getMonth(), b.getDate());
  if (next < now) next.setFullYear(next.getFullYear() + 1);
  return Math.ceil((next - now) / (1000 * 60 * 60 * 24));
}

function monthsTo13(dob) {
  if (!dob) return null;
  const b = new Date(dob);
  const bar = new Date(b.getFullYear() + 13, b.getMonth(), b.getDate());
  return (bar - new Date()) / (1000 * 60 * 60 * 24 * 30.44);
}

function barBadge(dob) {
  const m = monthsTo13(dob);
  if (m === null) return null;
  if (m <= 0) return { type: 'done', label: '✅ בר מצווה' };
  if (m <= 6) return { type: 'soon', label: `🚨 ${Math.round(m)} חודשים` };
  if (m <= 12) return { type: 'near', label: `⚠️ ${Math.round(m)} חודשים` };
  return null;
}

function fmtDate(d) { return d ? d.substring(0, 10) : '—'; }
function fmtPrice(p) { return p != null ? '₪' + Number(p).toFixed(0) : '—'; }

// Smart segment definitions
function getSegments(members, orders = []) {
  const memberHasOrder = id => orders.some(o => o.member_id === id);
  const now = new Date();
  const thisMonth = now.getMonth();
  const nextMonth = (thisMonth + 1) % 12;

  return [
    {
      id: 'birthday_month',
      icon: '🎂',
      label: 'יום הולדת החודש',
      color: '#ff9966',
      borderColor: '#cc5500',
      product: 'קלף יוסטאר',
      tip: 'שלח ברכת יום הולדת + הצע קלף יוסטאר מרהיב כמתנה',
      members: members.filter(m => {
        if (!m.child_dob) return false;
        const b = new Date(m.child_dob);
        return b.getMonth() === thisMonth;
      }),
      template: (m) => `🎂 יום הולדת שמח לשחקן/ית שלך!\n\nשלום ${m.first_name},\n\nמועדון יוסטאר מאחל יום הולדת שמח לילדך! ⚽🎉\n\nלרגל יום ההולדת – קבל/י 15% הנחה על קלף הכוכבים של יוסטאר:\nקלף כדורגל מרהיב בעיצוב אישי – מתנה שתישאר לנצח! 🌟\n\nלפרטים ולהזמנה: 054-6318704\n\nמועדון יוסטאר ⭐`,
    },
    {
      id: 'birthday_next_month',
      icon: '🎁',
      label: 'יום הולדת חודש הבא',
      color: '#ffcc44',
      borderColor: '#997700',
      product: 'קלף יוסטאר',
      tip: 'הכן מראש — שלח עכשיו כדי שיספיקו להזמין לפני יום ההולדת',
      members: members.filter(m => {
        if (!m.child_dob) return false;
        return new Date(m.child_dob).getMonth() === nextMonth;
      }),
      template: (m) => `🎁 יום הולדת מתקרב! הכן מתנה מיוחדת\n\nשלום ${m.first_name},\n\nחודש הבא הילד/ה שלך חוגג/ת יום הולדת! 🎂\n\nמה אם תפתיע אותו/ה בקלף כוכבים של יוסטאר?\nקלף כדורגל מרהיב בעיצוב אישי – מתנה ייחודית שכל ילד חולם עליה! ⭐\n\nהזמן/י עכשיו ועוד יגיע בזמן: 054-6318704\n\nמועדון יוסטאר ⚽`,
    },
    {
      id: 'bar_mitzvah_soon',
      icon: '🚨',
      label: 'בר/בת מצווה — עד 6 חודשים',
      color: '#ff6b00',
      borderColor: '#cc4400',
      product: 'קליפ יוסטאר פרימיום',
      tip: 'הצע קליפ יוסטאר לאירוע — מוצר פרימיום שלא יחזור!',
      members: members.filter(m => { const mt = monthsTo13(m.child_dob); return mt !== null && mt > 0 && mt <= 6; }),
      template: (m) => `🎊 בר/בת מצווה מתקרב/ת – נציח את הרגע!\n\nשלום ${m.first_name},\n\nשמענו שהשחקן/ית שלך מתקרב/ת לגיל בר/בת מצווה! 🎉\n\n*קליפ יוסטאר לבר מצווה* – מתנה שתישאר לנצח:\n🎬 קליפ כדורגל מקצועי ומרגש\n⭐ מחיר מיוחד לחברי מועדון: 441₪ (10% הנחה)\n\nראה דוגמה: https://www.youtube.com/watch?v=akg97C2Ru8M\n\nמהר – המקומות מוגבלים!\nחייגו עכשיו: 054-6318704\n\nמועדון יוסטאר ⚽`,
    },
    {
      id: 'bar_mitzvah_near',
      icon: '⚠️',
      label: 'בר/בת מצווה — 6-12 חודשים',
      color: '#ffd700',
      borderColor: '#997700',
      product: 'קליפ יוסטאר פרימיום',
      tip: 'זמן מושלם לתכנן — שלח עכשיו כדי שיהיה להם זמן להחליט',
      members: members.filter(m => { const mt = monthsTo13(m.child_dob); return mt !== null && mt > 6 && mt <= 12; }),
      template: (m) => `⭐ קליפ יוסטאר לבר/בת מצווה – כדאי לדעת!\n\nשלום ${m.first_name},\n\nשנה הקרובה מתוכנן אירוע בר/בת מצווה משפחתי? 🎊\n\nיוסטאר מציעה קליפ כדורגל מרגש ומקצועי לאירוע –\nמתנת הבר מצווה הייחודית ביותר שיכולה להיות לשחקן/ית צעיר/ה!\n\n🎬 תראה דוגמה: https://www.youtube.com/watch?v=akg97C2Ru8M\n📞 לפרטים: 054-6318704\n\nמועדון יוסטאר ⚽`,
    },
    {
      id: 'young_players',
      icon: '⚽',
      label: 'שחקנים צעירים (גיל 5-8)',
      color: '#44aaff',
      borderColor: '#0055aa',
      product: 'קלף יוסטאר',
      tip: 'הורים לשחקנים צעירים — קלף יוסטאר הוא המתנה המושלמת להתחלה',
      members: members.filter(m => { const a = calcAge(m.child_dob); return a !== null && a >= 5 && a <= 8; }),
      template: (m) => `⚽ הילד/ה שלך מתחיל/ה את המסע בכדורגל!\n\nשלום ${m.first_name},\n\nהצטרפת למועדון ההטבות של יוסטאר – ואנחנו שמחים שאתה כאן! 🌟\n\nלשחקנים בתחילת הדרך, יש לנו משהו מיוחד:\n🃏 *קלף הכוכבים של יוסטאר* – קלף כדורגל בעיצוב אישי עם תמונת הילד!\nמחיר מיוחד לחברי מועדון: 58₪ (במקום 65₪)\n\nלהזמנה: 054-6318704\n\nמועדון יוסטאר ⚽`,
    },
    {
      id: 'older_players',
      icon: '🏆',
      label: 'שחקנים מבוגרים (גיל 9-12)',
      color: '#aa66ff',
      borderColor: '#6622cc',
      product: 'קליפ יוסטאר',
      tip: 'שחקנים בגיל זה כבר משחקים ברצינות — קליפ יוסטאר יתפוס אותם בשיא',
      members: members.filter(m => { const a = calcAge(m.child_dob); return a !== null && a >= 9 && a <= 12; }),
      template: (m) => `🎬 תנציח את הכישרון של הילד/ה שלך!\n\nשלום ${m.first_name},\n\nהשחקן/ית שלך כבר משחק/ת ברצינות – הגיע הזמן לתעד!\n\n🎬 *קליפ יוסטאר* – קליפ כדורגל מקצועי ומלהיב\nמראה את הכישרון האמיתי שלו/ה על המגרש!\n\n⭐ מחיר לחברי מועדון: 441₪ (10% הנחה)\n🎥 דוגמה: https://www.youtube.com/watch?v=akg97C2Ru8M\n\nלפרטים: 054-6318704\nמועדון יוסטאר ⚽`,
    },
    {
      id: 'active_members',
      icon: '⚡',
      label: 'חברים פעילים (הזמינו מוצר)',
      color: '#25d366',
      borderColor: '#1a7a44',
      product: 'כל המוצרים',
      tip: 'חברים שכבר הזמינו — שלח הצעה לרכישה חוזרת או מוצר חדש',
      members: members.filter(m => memberHasOrder(m.id)),
      template: (m) => `שלום ${m.first_name}! 👋\n\nמליגת הכוכבים של יוסטאר ⭐\n\nתודה שאתה לקוח שלנו! 🙏\n\n[עדכן את חבר המועדון על מבצע / מוצר חדש]\n\nמועדון יוסטאר ⚽\n054-6318704`,
    },
    {
      id: 'prospects',
      icon: '🎯',
      label: 'חברים פוטנציאלים (טרם הזמינו)',
      color: '#7a9aff',
      borderColor: '#3a5aaa',
      product: 'קלף / קליפ יוסטאר',
      tip: 'חברים שעדיין לא קנו — תזכורת על ההנחה לחברי מועדון',
      members: members.filter(m => !memberHasOrder(m.id)),
      template: (m) => `שלום ${m.first_name}! 👋\n\nמליגת הכוכבים של יוסטאר ⭐\n\nרצינו להזכיר שכחבר המועדון מגיעה לך הנחה של *10%* על כל מוצרי יוסטאר:\n\n🎬 קליפ ביצועים אישי\n⭐ קליפ הכוכבים של יוסטאר\n🎊 קליפ בר מצווה\n🃏 קלף הכוכבים\n\nלפרטים: 054-6318704\n\nמועדון יוסטאר ⚽`,
    },
    {
      id: 'wa_only',
      icon: '📱',
      label: 'וואטסאפ בלבד (ללא אימייל)',
      color: '#25d366',
      borderColor: '#1a7a44',
      product: 'כל המוצרים',
      tip: 'חברים ללא כתובת אימייל — ניתן לתקשר איתם רק בוואטסאפ',
      members: members.filter(m => !m.email),
      template: (m) => `שלום ${m.first_name}! 👋\n\n*עדכון מליגת הכוכבים של יוסטאר* ⭐\n\n[כתוב כאן את ההודעה שלך]\n\nמועדון יוסטאר ⚽\n054-6318704`,
    },
    {
      id: 'all',
      icon: '📣',
      label: 'כל חברי המועדון',
      color: '#d4af37',
      borderColor: '#997722',
      product: 'כל המוצרים',
      tip: 'שלח עדכון כללי לכל חברי המועדון',
      members: members,
      template: (m) => `שלום ${m.first_name}! 👋\n\n*עדכון מליגת הכוכבים של יוסטאר* ⭐\n\n[כתוב כאן את ההודעה שלך]\n\nמועדון יוסטאר ⚽\n054-6318704`,
    },
  ];
}

// ── טלפון: נרמול לכל פורמט (+972, 972, מקפים, רווחים) ──
// צבעי חולצה — לתצוגת נקודת צבע בטאב הפתיח
const SHIRT_HEX = { 'אדום':'#d63030', 'כתום':'#e8842a', 'צהוב':'#e8c832', 'ירוק':'#2fa84f', 'כחול':'#2b6fd6', 'סגול':'#8a4fd0' };

function normPhone(phone) {          // → 0528505822 (תצוגה / חיפוש)
  let d = String(phone || '').replace(/\D/g, '');
  if (d.startsWith('972')) d = '0' + d.slice(3);
  else if (d && !d.startsWith('0')) d = '0' + d;
  return d;
}
function waNum(phone) {              // → 972528505822 (wa.me)
  const d = normPhone(phone);
  return d ? '972' + d.slice(1) : '';
}

// שיוך מלא: מועדון + שכבת גיל + שנתון → "הפועל באר שבע נערים א 2010"
function fullTeamLabel(o) {
  if (!o) return '';
  const year = o.birth_year || (o.child_dob ? new Date(o.child_dob).getFullYear() : null);
  return [o.team, o.age_group, year].filter(Boolean).join(' ');
}

// תאריך משחק קריא: "יום ראשון, 9 באוגוסט 2026"
const HEB_DAYS_FULL = ['ראשון','שני','שלישי','רביעי','חמישי','שישי','שבת'];
const HEB_MONTHS_FULL = ['ינואר','פברואר','מרץ','אפריל','מאי','יוני','יולי','אוגוסט','ספטמבר','אוקטובר','נובמבר','דצמבר'];
function niceDate(d) {
  if (!d) return '';
  const dt = new Date(String(d).slice(0,10) + 'T00:00:00');
  if (isNaN(dt)) return String(d);
  return `יום ${HEB_DAYS_FULL[dt.getDay()]}, ${dt.getDate()} ב${HEB_MONTHS_FULL[dt.getMonth()]} ${dt.getFullYear()}`;
}

function waLink(phone, text) {
  return `https://wa.me/${waNum(phone)}?text=${encodeURIComponent(text)}`;
}

// eslint-disable-next-line no-unused-vars
function welcomeWAText(m) {
  return `שלום ${m.first_name}! 🎉\n\nברוך הבא למועדון ההטבות של יוסטאר! ⭐\n\nמספר החבר שלך: *${pad4(m.member_number)}*\n\nהכרטיס הדיגיטלי שלך:\n${SITE_URL}/card/${m.id}\n\n🎁 *מתנת הצטרפות:* קלף הכוכבים של יוסטאר\n(שווי 65₪ – תשלמו על משלוח בלבד)\n\nבברכה,\n*מועדון יוסטאר* ⚽`;
}

function welcomeEmailHtml(m) {
  const cardUrl = `${SITE_URL}/card/${m.id}`;
  return `<div dir="rtl" style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;background:#0a1120;color:#fff;border-radius:16px;overflow:hidden;">
  <div style="background:linear-gradient(135deg,#0a1a3e,#1a3a7e);padding:32px;text-align:center;">
    <h1 style="color:#d4af37;margin:0;">ברוכים הבאים למועדון ⭐</h1>
  </div>
  <div style="padding:32px;">
    <p>שלום <strong style="color:#d4af37;">${m.first_name}</strong>! 🎉</p>
    <div style="background:#1a2a4a;border:2px solid #d4af37;border-radius:12px;padding:20px;margin:20px 0;text-align:center;">
      <div style="color:#888;">מספר חבר</div>
      <div style="color:#d4af37;font-size:2.5rem;font-weight:bold;font-family:monospace;">${pad4(m.member_number)}</div>
    </div>
    <div style="text-align:center;margin:20px 0;">
      <a href="${cardUrl}" style="background:#d4af37;color:#0a1120;padding:14px 32px;border-radius:50px;text-decoration:none;font-weight:bold;display:inline-block;">הכרטיס הדיגיטלי שלך 🎴</a>
    </div>
    <div style="background:#0a2a1a;border:2px solid #25d366;border-radius:12px;padding:20px;">
      <h3 style="color:#25d366;margin-top:0;">🎁 מתנת הצטרפות!</h3>
      <p style="color:#ccc;">קלף הכוכבים של יוסטאר – תשלמו על משלוח בלבד.</p>
      <p style="color:#d4af37;">054-6318704</p>
    </div>
  </div>
</div>`;
}

const STATUS_C = { pending: '#d4af37', completed: '#25d366', cancelled: '#ff6b6b' };

const PARTNER_CATEGORIES = ['ספורט והנעלה','ציוד כדורגל','ביגוד ספורט','תזונה ובריאות','ביטוח ספורט','צילום ווידאו','אביזרים','בידור ופנאי','חינוך','אחר'];
const EMOJI_OPTIONS = ['🏢','⚽','👟','👕','🏃','🍎','📸','🎬','🏋️','🎽','🛡️','🎁','💪','🏆','🌟'];

function dealLabel(deal) {
  if (!deal) return '';
  if (deal.discount_type === 'percent') return `${deal.discount_value}% הנחה`;
  if (deal.discount_type === 'fixed') return `${deal.discount_value}₪ הנחה`;
  if (deal.discount_type === 'free') return 'חינם';
  return deal.discount_value ? `${deal.discount_value}` : '';
}

// ─── Customer Work Order Card ────────────────────────────────────────────────
function CustomerWorkOrder({ o, products, API, onUpdate, onComplete }) {
  const [editing, setEditing] = React.useState(false);
  const [productName, setProductName] = React.useState(o.product_name || 'קליפ ביצועים');
  const [price, setPrice] = React.useState(o.total || 0);

  const save = async () => {
    const { data } = await axios.put(API + '/orders/' + o.id, {
      ...o, product_name: productName, unit_price: parseFloat(price)||0,
      total: parseFloat(price)||0, discount_pct: 0, quantity: 1
    });
    onUpdate(data);
    setEditing(false);
  };

  const inputS = { background:'#0a1a3e', border:'1px solid #2a3a6a', borderRadius:8, padding:'7px 12px', color:'#fff', fontSize:'0.85rem' };

  return (
    <div style={{ background:'#0d1b3e', border:'1px solid #2a4080', borderRadius:12, padding:'14px 16px' }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
        <div style={{ flex:1 }}>
          <div style={{ color:'#fff', fontWeight:800, marginBottom:4 }}>
            {o.c_first_name} {o.c_last_name}
            {o.c_phone && <a href={`https://wa.me/${waNum(o.c_phone)}`} target="_blank" rel="noopener noreferrer"
              style={{ marginRight:10, color:'#25d366', fontSize:'0.8rem', textDecoration:'none' }}>💬 {o.c_phone}</a>}
          </div>
          <div style={{ color:'#8a9aba', fontSize:'0.83rem', lineHeight:1.7 }}>
            שחקן: <strong style={{ color:'#c0cce6' }}>{o.c_child_name} {o.c_child_last_name||''}</strong>
            {(() => { const lbl = fullTeamLabel({ team:o.c_team, age_group:o.c_age_group, birth_year:o.c_birth_year, child_dob:o.c_child_dob }); return lbl ? <> · <span style={{ color:'#d4af37', fontWeight:700 }}>{lbl}</span></> : null; })()}
            {o.jersey_number && <> · חולצה: #{o.jersey_number}</>}
            {o.opponent_team && <> · יריבה: {o.opponent_team}</>}
            {o.c_positions && <> · עמדה: {o.c_positions}</>}
          </div>
          {o.notes && <div style={{ color:'#4a5a7a', fontSize:'0.78rem', marginTop:4 }}>📝 {o.notes}</div>}
        </div>

        {/* מוצר + מחיר */}
        <div style={{ display:'flex', flexDirection:'column', gap:8, minWidth:220 }}>
          {editing ? (
            <>
              <select value={productName} onChange={e=>setProductName(e.target.value)} style={inputS}>
                <option value="קליפ ביצועים">קליפ ביצועים מצילומים קיימים</option>
                <option value="קליפ הכוכבים">קליפ הכוכבים — צילום מקצועי</option>
                <option value="קליפ בר מצווה">קליפ בר מצווה</option>
                <option value="חבילה עונתית">חבילה עונתית 5 קליפים</option>
                {products.filter(p=>p.is_active).map(p=><option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
              <input type="number" value={price} onChange={e=>setPrice(e.target.value)} placeholder="מחיר ₪" style={inputS} />
              <div style={{ display:'flex', gap:6 }}>
                <button onClick={save} style={{ flex:1, background:'#d4af37', color:'#000', border:'none', borderRadius:8, padding:'7px', cursor:'pointer', fontWeight:800, fontSize:'0.82rem' }}>💾 שמור</button>
                <button onClick={()=>setEditing(false)} style={{ background:'transparent', color:'#aaa', border:'1px solid #2a3a6a', borderRadius:8, padding:'7px 10px', cursor:'pointer', fontSize:'0.82rem' }}>ביטול</button>
              </div>
            </>
          ) : (
            <>
              <div style={{ marginBottom:6 }}>
                <div style={{ color:'#c0cce6', fontSize:'0.85rem', marginBottom:4 }}>📦 {o.product_name}</div>
                {o.total > 0 && (
                  <div style={{ color:'#d4af37', fontWeight:900, fontSize:'1.5rem', lineHeight:1 }}>₪{(o.total).toLocaleString('he-IL')}</div>
                )}
              </div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                <button onClick={()=>setEditing(true)} style={{ background:'rgba(212,175,55,0.1)', color:'#d4af37', border:'1px solid rgba(212,175,55,0.3)', borderRadius:8, padding:'6px 10px', cursor:'pointer', fontSize:'0.78rem', fontWeight:700 }}>✏️ עדכן</button>
                <button onClick={async () => {
                  await axios.put(API+'/orders/'+o.id+'/status', { status:'edited' });
                  onUpdate({ ...o, status:'edited' });
                }} style={{ background: o.status==='edited' ? 'rgba(90,155,245,0.2)' : 'rgba(90,155,245,0.08)', color:'#5a9bf5', border:`1px solid ${o.status==='edited'?'#5a9bf5':'rgba(90,155,245,0.35)'}`, borderRadius:8, padding:'6px 10px', cursor:'pointer', fontSize:'0.78rem', fontWeight:800 }}>
                  {o.status==='edited' ? '✂️ נערך' : '✂️ סיימתי עריכה'}
                </button>
                <button onClick={onComplete} style={{ background:'rgba(37,211,102,0.12)', color:'#25d366', border:'1px solid rgba(37,211,102,0.35)', borderRadius:8, padding:'6px 10px', cursor:'pointer', fontSize:'0.78rem', fontWeight:800 }}>💰 קיבלתי תשלום</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────
export default function Admin({ onLogout }) {
  const [tab, setTab] = useState('dashboard');
  const [members, setMembers]   = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders]     = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [partners, setPartners] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [incomeLog, setIncomeLog] = useState([]);
  const [clipPrice, setClipPrice] = useState('');
  const [clipPriceSaving, setClipPriceSaving] = useState(false);
  const [formPriceMode, setFormPriceMode] = useState('manual'); // 'manual' | 'product'
  const [formProductId, setFormProductId] = useState('');
  const [formTemplates, setFormTemplates] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [quickOrderSetup, setQuickOrderSetup] = useState(null); // { customerId, productName, price, productId }
  const [quickOrderCopied, setQuickOrderCopied] = useState(false);
  const [expandedBenefits, setExpandedBenefits] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editCustomerData, setEditCustomerData] = useState({});
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    full_name:'', phone:'', product_name:'', price:'', productId:'',
    customer_type:'player', team:'', age_group:'', birth_year:'',
    child_name:'', child_last_name:'', child_dob:'', positions:'', jersey_number:'', email:'',
  });
  const [editingOrderPrice, setEditingOrderPrice] = useState(null); // { id, value }
  // שיטת בבל — tax rates (editable, persisted in settings)
  const [babelVat,        setBabelVat]        = useState('18');   // מע״מ 18% (מ-1.1.2025)
  const [babelIncomeTax,  setBabelIncomeTax]  = useState('10');   // מדרגות — ניתן לעריכה
  const [babelNatIns,     setBabelNatIns]     = useState('5.97'); // ב״ל+בריאות, מדרגה מופחתת
  const [babelShowTax,    setBabelShowTax]    = useState(false);
  const [babelMonth,      setBabelMonth]      = useState(() => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; });
  const [babelSaving,     setBabelSaving]     = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name:'', product_name:'קליפ ביצועים', price:'' });
  const [templatePriceMode, setTemplatePriceMode] = useState('manual'); // 'manual' | 'product'
  const [templateProductId, setTemplateProductId] = useState('');
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('success');

  // campaign wizard
  const [wizardStep, setWizardStep] = useState(1); // 1=segment, 2=compose, 3=send
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [campMsg, setCampMsg] = useState('');
  const [campTitle, setCampTitle] = useState('');
  const [campChannel, setCampChannel] = useState('whatsapp');
  const [waIndex, setWaIndex] = useState(0);
  const [broadcastActive, setBroadcastActive] = useState(false);
  const [copiedNums, setCopiedNums] = useState(false);
  const [copiedMsg, setCopiedMsg] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  // orders sub-tab
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberOrders, setMemberOrders] = useState([]);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [orderForm, setOrderForm] = useState({ product_id:'', product_name:'', quantity:'1', unit_price:'', discount_pct:'10', notes:'', status:'pending', season: currentSeason() });

  // partner forms
  const [showPartnerForm, setShowPartnerForm] = useState(false);
  const [newPartner, setNewPartner] = useState({ name:'', category:'ספורט והנעלה', website:'', contact_name:'', contact_phone:'', contact_email:'', logo_emoji:'🏢', notes:'' });
  const [expandedPartner, setExpandedPartner] = useState(null);
  const [newDeal, setNewDeal] = useState({ title:'', description:'', coupon_code:'', discount_type:'percent', discount_value:'', terms:'', valid_until:'' });
  const [showDealForm, setShowDealForm] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [dealCampaignPartner, setDealCampaignPartner] = useState(null);
  const [quickSend, setQuickSend] = useState(null); // { partner, deal, msg } — inline send panel
  const [qsWaIndex, setQsWaIndex] = useState(0);
  const [qsSendingEmail, setQsSendingEmail] = useState(false);
  const [qsEmailResult, setQsEmailResult] = useState(null);

  // forms
  const [newBenefit, setNewBenefit] = useState({ title:'', description:'', category:'', discount:'', partner_name:'', coupon_code:'', website:'' });
  const [showBenefitForm, setShowBenefitForm] = useState(false);
  const [benefitFilter, setBenefitFilter] = useState('all');
  const [newProduct, setNewProduct] = useState({ name:'', description:'', category:'', price:'', member_discount_pct:'0', product_type:'sale', media_type:'video', member_discount_enabled:false });
  const [showProductForm, setShowProductForm] = useState(false);
  const [productCampaignId, setProductCampaignId] = useState(null);
  const [productCampaignAudience, setProductCampaignAudience] = useState('all');
  const [productCampaignSending, setProductCampaignSending] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressDraft, setAddressDraft] = useState('');
  const [benefitNewName, setBenefitNewName] = useState('');
  const [benefitNewDesc, setBenefitNewDesc] = useState('');
  const [benefitNewVariants, setBenefitNewVariants] = useState([]);
  const [benefitNewVariantInput, setBenefitNewVariantInput] = useState('');
  const [benefitShowForm, setBenefitShowForm] = useState(false);
  const [benefitShareId, setBenefitShareId] = useState(null);
  const [benefitShareAudience, setBenefitShareAudience] = useState('all');
  const [benefitSending, setBenefitSending] = useState(false);
  const [benefitEditVariantsId, setBenefitEditVariantsId] = useState(null);
  const [benefitEditVariantInput, setBenefitEditVariantInput] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [editProductData, setEditProductData] = useState(null);

  // member benefits history
  const [memberBenefits, setMemberBenefits] = useState([]);
  const [showBenefitHistForm, setShowBenefitHistForm] = useState(false);
  const [newBenefitHist, setNewBenefitHist] = useState({ title:'', description:'', notes:'' });

  // members tab filters
  const [memberTeamFilter, setMemberTeamFilter] = useState('all');
  const [memberBirthYearFilter, setMemberBirthYearFilter] = useState('all');
  const [memberSearch, setMemberSearch] = useState('');
  const [memberStatusFilter, setMemberStatusFilter] = useState('all'); // 'all' | 'prospect' | 'customer'
  const [memberViewMode, setMemberViewMode] = useState('table'); // 'table' | 'cohorts'
  const [memberSort, setMemberSort] = useState({ key:'member_number', dir:'asc' });
  const [expandedMemberCard, setExpandedMemberCard] = useState(null);
  const [addrDraft, setAddrDraft] = useState({});

  // orders tab filters
  const [orderSeasonFilter, setOrderSeasonFilter] = useState('all');
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [orderView, setOrderView] = useState('active'); // active = טרם שולם | archive = הושלם ושולם
  const [orderMonth, setOrderMonth] = useState(() => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; });
  const [editingWorkOrder, setEditingWorkOrder] = useState(null);
  const [editWorkData, setEditWorkData] = useState({});
  const [orderSearch, setOrderSearch] = useState('');
  const [orderSort, setOrderSort] = useState('newest'); // newest | oldest | amount | status
  const [customerSort, setCustomerSort] = useState('newest'); // newest | name | status
  const [customerStatusFilter, setCustomerStatusFilter] = useState('all'); // all | active | new | contacted | closed
  const [gameShoots, setGameShoots] = useState([]);
  const [newShoot, setNewShoot] = useState({ team_a:'', team_b:'', birth_year:'', game_date:'', notes:'', shoot_type:'video', age_group:'', match_category:'', squad_size:'', active_players:'' });
  const [expandedShoot, setExpandedShoot] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [shootOrders, setShootOrders] = useState([]);
  const [expandedShootOrders, setExpandedShootOrders] = useState(null);
  const [newShootOrder, setNewShootOrder] = useState({ customer_name:'', team:'', birth_year:'', phone:'', product_name:'', price:'', productId:'' });
  const [shootTallies, setShootTallies] = useState([]);
  const [shootSchedule, setShootSchedule] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [showRules, setShowRules] = useState(true);
  const [showManualShoot, setShowManualShoot] = useState(false);
  const [introSearch, setIntroSearch] = useState('');
  const [introFilter, setIntroFilter] = useState('all');
  const [expandedIntro, setExpandedIntro] = useState(null);
  const [tierOverrides] = useState(loadTierOverrides);
  const [showNewSlot, setShowNewSlot] = useState(null); // תאריך היום שנבחר
  const [newSlot, setNewSlot] = useState({ shoot_time:'', team_a:'', team_b:'', age_group:'', birth_year:'', shoot_type:'video', match_category:'', location:'', notes:'' });
  const [clipPackages, setClipPackages] = useState([]);
  const [showNewPackage, setShowNewPackage] = useState(false);
  const [newPackage, setNewPackage] = useState({ customer_id:'', customer_name:'', phone:'', team:'', birth_year:'', product_name:'חבילת 5 קליפ ביצועים', total_clips:'5', price:'', notes:'' });
  const [expandedShootTally, setExpandedShootTally] = useState(null);
  const [expandedShootLeads, setExpandedShootLeads] = useState(null);
  const [newTally, setNewTally] = useState({ product_name:'', unit_price:'', quantity:'', team_side:'home' });
  const [showNewOrderForm, setShowNewOrderForm] = useState(false);
  const [newOrderForm, setNewOrderForm] = useState({ product_id:'', product_name:'', unit_price:'', discount_pct:'10', quantity:'1', notes:'', season:'', status:'pending' });
  const [newOrderMemberSearch, setNewOrderMemberSearch] = useState('');
  const [newOrderMember, setNewOrderMember] = useState(null);

  // post-create campaign prompt
  const [postCreateCampaign, setPostCreateCampaign] = useState(null); // { title, msg }

  // ── ניהול פרופיל שחקן ──
  const [playerProfiles, setPlayerProfiles] = useState([]);
  const [expandedPlayerProfile, setExpandedPlayerProfile] = useState(null);
  const [showNewPlayerProfile, setShowNewPlayerProfile] = useState(false);
  const emptyPlayerForm = { slug:'', name:'', first_name:'', position:'', team:'', age_group:'', birth_year:'', shirt:'', season:'26/27', language:'he', graphic_base64:'' };
  const [newPlayerForm, setNewPlayerForm] = useState(emptyPlayerForm);
  const [editPlayerId, setEditPlayerId] = useState(null);
  const [editPlayerForm, setEditPlayerForm] = useState(emptyPlayerForm);
  const [playerClips, setPlayerClips] = useState({}); // { [playerId]: [clips] }
  const [playerPhotos, setPlayerPhotos] = useState({}); // { [playerId]: [photos] }
  const [showNewClipFor, setShowNewClipFor] = useState(null); // playerId
  const emptyClipForm = { youtube_url:'', opponent:'', age_group:'', season:'26/27', match_date:'', match_category:'' };
  const [newClipForm, setNewClipForm] = useState(emptyClipForm);
  const [editClipId, setEditClipId] = useState(null);
  const [editClipForm, setEditClipForm] = useState(emptyClipForm);

  const loadAll = useCallback(() => {
    axios.get(API + '/members').then(r => setMembers(r.data));
    axios.get(API + '/benefits').then(r => setBenefits(r.data));
    axios.get(API + '/products').then(r => setProducts(r.data));
    axios.get(API + '/orders').then(r => setOrders(r.data));
    axios.get(API + '/campaigns').then(r => setCampaigns(r.data));
    axios.get(API + '/partners').then(r => setPartners(r.data));
    axios.get(API + '/customers').then(r => setCustomers(r.data));
    axios.get(API + '/income').then(r => setIncomeLog(r.data));
    axios.get(API + '/form-templates').then(r => setFormTemplates(r.data));
    axios.get(API + '/settings/clip_price').then(r => setClipPrice(r.data.value || ''));
    axios.get(API + '/settings/form_product_id').then(r => {
      const v = r.data.value;
      if (v && parseInt(v) > 0) { setFormProductId(v); setFormPriceMode('product'); }
      else { setFormPriceMode('manual'); setFormProductId(''); }
    });
    axios.get(API + '/settings/babel_vat').then(r => { if (r.data.value) setBabelVat(r.data.value); });
    axios.get(API + '/settings/babel_income_tax').then(r => { if (r.data.value) setBabelIncomeTax(r.data.value); });
    axios.get(API + '/settings/babel_nat_ins').then(r => { if (r.data.value) setBabelNatIns(r.data.value); });
    axios.get(API + '/game-shoots').then(r => setGameShoots(r.data)).catch(() => {});
    axios.get(API + '/shoot-orders').then(r => setShootOrders(r.data)).catch(() => {});
    axios.get(API + '/shoot-tallies').then(r => setShootTallies(r.data)).catch(() => {});
    axios.get(API + '/clip-packages').then(r => setClipPackages(r.data)).catch(() => {});
    axios.get(API + '/shoot-schedule').then(r => setShootSchedule(r.data)).catch(() => {});
    axios.get(API + '/player-profiles').then(r => setPlayerProfiles(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // רענון הכנסות בכל כניסה לטאב קופת יוסטאר / שיטת בבל — כדי שהנתונים תמיד יהיו טריים
  useEffect(() => {
    if (tab === 'treasury' || tab === 'babel') {
      axios.get(API + '/income').then(r => setIncomeLog(r.data)).catch(() => {});
    }
  }, [tab]);

  const flash = (text, type = 'success') => {
    setMsg(text); setMsgType(type); setTimeout(() => setMsg(''), 4000);
  };

  const segments = useMemo(() => getSegments(members, orders), [members, orders]);

  // ── campaign wizard ──
  const pickSegment = (seg) => {
    setSelectedSegment(seg);
    setCampTitle(seg.label);
    setCampMsg(seg.members.length > 0 ? seg.template(seg.members[0]) : seg.template({ first_name: '{{שם}}', member_number: 1 }));
    setWizardStep(2);
    setWaIndex(0);
    setBroadcastActive(false);
    setEmailResult(null);
  };

  const startBroadcast = async () => {
    if (!campMsg || !campTitle) { flash('❌ כותרת והודעה חובה', 'error'); return; }
    const { data: camp } = await axios.post(API + '/campaigns', { title: campTitle, channel: campChannel, message: campMsg });
    setCampaigns(prev => [camp, ...prev]);
    setBroadcastActive(true);
    setWizardStep(3);
    setWaIndex(0);
  };

  const sendCampaignEmail = async () => {
    if (!campMsg || !campTitle) { flash('❌ כותרת והודעה חובה', 'error'); return; }
    setSendingEmail(true); setEmailResult(null);
    try {
      const { data: camp } = await axios.post(API + '/campaigns', { title: campTitle, channel: 'email', message: campMsg });
      const targetIds = selectedSegment?.id !== 'all' ? selectedSegment.members.map(m => m.id) : [];
      const r = await axios.post(API + '/email/bulk', { member_ids: targetIds, subject: campTitle, html: `<div dir="rtl" style="font-family:Arial;max-width:600px;margin:auto;background:#0a1120;color:#fff;padding:32px;border-radius:16px;">${campMsg.replace(/\n/g,'<br/>')}</div>` });
      await axios.put(API + '/campaigns/' + camp.id + '/sent', { sent_count: r.data.sent?.length || 0 });
      setEmailResult(r.data);
      setCampaigns(prev => prev.map(c => c.id === camp.id ? { ...c, status:'sent', sent_count: r.data.sent?.length||0 } : c));
      flash(`✅ אימייל נשלח ל-${r.data.sent?.length||0} חברים`);
    } catch(e) { flash(`❌ ${e.response?.data?.error || 'שגיאה'}`, 'error'); }
    finally { setSendingEmail(false); }
  };

  // ── email welcome ──
  const sendWelcomeEmail = async (m) => {
    try {
      const r = await axios.post(API + '/email/welcome', { member_id: m.id, subject: 'ברוך הבא למועדון יוסטאר ⭐', html: welcomeEmailHtml(m) });
      flash(`✅ אימייל נשלח ל-${r.data.sent_to}`);
    } catch(e) { flash(`❌ ${e.response?.data?.error || 'שגיאה'}`, 'error'); }
  };

  // ── orders ──
  const openMemberOrders = async (m) => {
    setSelectedMember(m);
    const [ordersRes, benefitsRes] = await Promise.all([
      axios.get(API + '/orders/member/' + m.id),
      axios.get(API + '/member-benefits/' + m.id),
    ]);
    setMemberOrders(ordersRes.data);
    setMemberBenefits(benefitsRes.data);
    setShowBenefitHistForm(false);
    setTab('member-orders');
  };

  const handleProductSelect = (e) => {
    const pid = e.target.value;
    const p = products.find(x => String(x.id) === String(pid));
    if (p) setOrderForm({ ...orderForm, product_id: pid, product_name: p.name, unit_price: String(p.price), discount_pct: String(p.member_discount_pct) });
    else setOrderForm({ ...orderForm, product_id:'', product_name:'', unit_price:'', discount_pct:'10' });
  };

  const addOrder = async e => {
    e.preventDefault();
    const qty = parseInt(orderForm.quantity)||1, disc = parseFloat(orderForm.discount_pct)||0;
    const total = parseFloat(orderForm.unit_price)*qty*(1-disc/100);
    const { data } = await axios.post(API + '/orders', { ...orderForm, member_id: selectedMember.id, total });
    setMemberOrders([data, ...memberOrders]);
    setOrders([{ ...data, first_name: selectedMember.first_name, member_number: selectedMember.member_number }, ...orders]);
    setShowOrderForm(false);
    setOrderForm({ product_id:'', product_name:'', quantity:'1', unit_price:'', discount_pct:'10', notes:'', status:'pending', season: currentSeason() });
    flash('הזמנה נוספה!');
  };

  const updateOrderStatus = async (oid, status) => {
    await axios.put(API + '/orders/' + oid + '/status', { status });
    setMemberOrders(memberOrders.map(o => o.id===oid?{...o,status}:o));
    setOrders(orders.map(o => o.id===oid?{...o,status}:o));
  };

  const deleteOrder = async id => {
    if (!window.confirm('למחוק?')) return;
    await axios.delete(API + '/orders/' + id);
    setMemberOrders(memberOrders.filter(o=>o.id!==id));
    setOrders(orders.filter(o=>o.id!==id));
  };

  const deleteMember = async id => {
    if (!window.confirm('למחוק חבר?')) return;
    await axios.delete(API + '/members/' + id);
    setMembers(members.filter(m => m.id !== id));
  };

  // eslint-disable-next-line no-unused-vars
  const addBenefit = async e => {
    e.preventDefault();
    const { data } = await axios.post(API + '/benefits', newBenefit);
    setBenefits([...benefits, data]);
    setNewBenefit({ title:'', description:'', category:'', discount:'' });
    flash('הטבה נוספה!');
  };

  // ── partners ──
  const addPartner = async e => {
    e.preventDefault();
    const { data } = await axios.post(API + '/partners', newPartner);
    setPartners([data, ...partners]);
    setNewPartner({ name:'', category:'ספורט והנעלה', website:'', contact_name:'', contact_phone:'', contact_email:'', logo_emoji:'🏢', notes:'' });
    setShowPartnerForm(false);
    flash('שותף נוסף בהצלחה! 🤝');
  };

  const deletePartner = async id => {
    if (!window.confirm('למחוק שותף?')) return;
    await axios.delete(API + '/partners/' + id);
    setPartners(partners.filter(p => p.id !== id));
  };

  const addDeal = async (e, partnerId) => {
    e.preventDefault();
    const { data } = await axios.post(API + '/partner-deals', { ...newDeal, partner_id: partnerId });
    setPartners(partners.map(p => p.id === partnerId ? { ...p, deals: [data, ...(p.deals||[])] } : p));
    const partner = partners.find(p => p.id === partnerId);
    const discLabel = dealLabel(data);
    const msg = `🎉 הטבה בלעדית לחברי מועדון יוסטאר!\n\n*${partner?.logo_emoji || ''} ${partner?.name || ''}*\n\n${data.title}\n${data.description || ''}\n\n${discLabel ? `💰 ${discLabel}` : ''}${data.coupon_code ? `\nקוד קופון: *${data.coupon_code}*` : ''}${partner?.website ? `\n\n🔗 ${partner.website}` : ''}${data.valid_until ? `\n⏰ בתוקף עד: ${data.valid_until}` : ''}\n\nמועדון ההטבות של יוסטאר ⭐`;
    setPostCreateCampaign({ title: `הטבה חדשה — ${partner?.name} — ${data.title}`, msg });
    setNewDeal({ title:'', description:'', coupon_code:'', discount_type:'percent', discount_value:'', terms:'', valid_until:'' });
    setShowDealForm(null);
    flash('✅ הטבה נוספה! 📣 רוצה להודיע לחברים?');
  };

  const deleteDeal = async (dealId, partnerId) => {
    if (!window.confirm('למחוק הטבה?')) return;
    await axios.delete(API + '/partner-deals/' + dealId);
    setPartners(partners.map(p => p.id === partnerId ? { ...p, deals: (p.deals||[]).filter(d => d.id !== dealId) } : p));
  };

  const openDealCampaign = (partner, deal) => {
    const msg = `🎉 הטבה בלעדית לחברי מועדון יוסטאר!\n\n*${partner.logo_emoji} ${partner.name}*\n\n${deal.title}\n${deal.description || ''}\n\n${dealLabel(deal)}${deal.coupon_code ? `\nקוד קופון: *${deal.coupon_code}*` : ''}${partner.website ? `\n\n🔗 ${partner.website}` : ''}${deal.valid_until ? `\n⏰ בתוקף עד: ${deal.valid_until}` : ''}\n\nמועדון ההטבות של יוסטאר ⭐`;
    setQuickSend({ partner, deal, msg });
    setQsWaIndex(0); setQsEmailResult(null);
  };

  const addProduct = async e => {
    e.preventDefault();
    const { data } = await axios.post(API + '/products', newProduct);
    setProducts([...products, data]);
    // הנחת חברי מועדון מוצגת רק אם הופעלה על המוצר
    const discOn = data.member_discount_enabled && (data.member_discount_pct || 0) > 0;
    const memberPrice = data.price * (1 - (data.member_discount_pct || 0) / 100);
    const msg = discOn
      ? `⭐ מוצר חדש של יוסטאר!\n\n*${data.name}*\n${data.description || ''}\n\n💰 מחיר לחברי המועדון: *${fmtPrice(memberPrice)}* (במקום ${fmtPrice(data.price)})\n\nלהזמנה: 054-6318704\nמועדון יוסטאר ⭐`
      : `⭐ מוצר חדש של יוסטאר!\n\n*${data.name}*\n${data.description || ''}\n\n💰 מחיר: *${fmtPrice(data.price)}*\n\nלהזמנה: 054-6318704\nמועדון יוסטאר ⭐`;
    setPostCreateCampaign({ title: `מוצר חדש — ${data.name}`, msg });
    setNewProduct({ name:'', description:'', category:'', price:'', member_discount_pct:'0', product_type:'sale', media_type:'video', member_discount_enabled:false });
    setShowProductForm(false);
    flash('✅ מוצר נוסף! 📣 רוצה להודיע לחברים?');
  };

  // computed
  const barAlerts = members.filter(m => { const b = barBadge(m.child_dob); return b?.type==='soon'; });
  const bdayToday = members.filter(m => daysUntilBirthday(m.child_dob) === 0);
  const bdaySoon = members.filter(m => { const d=daysUntilBirthday(m.child_dob); return d!==null && d>0 && d<=14; });
  const totalRevenue = orders.filter(o=>o.status==='completed').reduce((s,o)=>s+(o.total||0),0);
  const membersWithEmail = members.filter(m => m.email).length;
  const membersWAOnly = members.length - membersWithEmail;
  // חברי ליגת הכוכבים = נרשמו דרך אתר המועדון בלבד (לא לקוחות שנגזרו מהזמנה)
  const clubMembers = members.filter(m => (m.source || 'club') === 'club');
  const activeMembers = members.filter(m => orders.some(o=>o.member_id===m.id || o.customer_id===m.id)).length;
  const prospectMembers = members.length - activeMembers;

  // ── דשבורד: לקוחות מול חברי מועדון + הכנסות + מוצרים לפי חודש/שנה ──
  const HEB_MONTHS = ['ינואר','פברואר','מרץ','אפריל','מאי','יוני','יולי','אוגוסט','ספטמבר','אוקטובר','נובמבר','דצמבר'];
  const _now = new Date();
  const curMonthKey = `${_now.getFullYear()}-${String(_now.getMonth()+1).padStart(2,'0')}`;
  const curYearKey = String(_now.getFullYear());
  // כל לקוח הוא חבר מועדון. ההפרדה: חבר פעיל (הזמין מוצר בתשלום) מול חבר לא פעיל (קלף בלבד)
  const activeMembersCountDash = activeMembers;
  const inactiveMembersCountDash = prospectMembers;
  // הכנסות — מקופת יוסטאר (income_log), תואם לקופה ולשיטת בבל
  const incomeMonth = incomeLog.filter(e => (e.created_at||'').slice(0,7) === curMonthKey).reduce((s,e)=>s+(e.amount||0),0);
  const monthlyIncomeCount = incomeLog.filter(e => (e.created_at||'').slice(0,7) === curMonthKey).length;
  const incomeYear  = incomeLog.filter(e => (e.created_at||'').slice(0,4) === curYearKey).reduce((s,e)=>s+(e.amount||0),0);
  const incomeByMonth = {}; const incomeByYear = {};
  incomeLog.forEach(e => {
    const mk = (e.created_at||'').slice(0,7); const yk = (e.created_at||'').slice(0,4);
    if (mk) incomeByMonth[mk] = (incomeByMonth[mk]||0) + (e.amount||0);
    if (yk) incomeByYear[yk] = (incomeByYear[yk]||0) + (e.amount||0);
  });
  const incomeMonthsSorted = Object.keys(incomeByMonth).sort().reverse();
  const incomeYearsSorted = Object.keys(incomeByYear).sort().reverse();
  // מוצרים שנמכרו — רק מכירות בפועל (הזמנות ששולמו + לקוחות צילום). ספירת צילום היא תצוגה בלבד ולא נספרת כאן.
  const _sales = [];
  orders.filter(o => o.status === 'completed').forEach(o => _sales.push({ date:o.created_at, product:o.product_name||'מוצר', qty:o.quantity||1 }));
  shootOrders.forEach(o => _sales.push({ date:o.created_at, product:o.product_name||'מוצר', qty:1 }));
  const prodMonth = _sales.filter(s => (s.date||'').slice(0,7) === curMonthKey).reduce((sum,s)=>sum+s.qty,0);
  const prodYear  = _sales.filter(s => (s.date||'').slice(0,4) === curYearKey).reduce((sum,s)=>sum+s.qty,0);
  const prodBreakdownMonth = {};
  _sales.filter(s => (s.date||'').slice(0,7) === curMonthKey).forEach(s => { prodBreakdownMonth[s.product] = (prodBreakdownMonth[s.product]||0) + s.qty; });
  const prodBreakdownList = Object.entries(prodBreakdownMonth).sort((a,b)=>b[1]-a[1]);
  const monthLabelHe = k => { const [y,m] = k.split('-'); return `${HEB_MONTHS[parseInt(m)-1]} ${y}`; };

  // ── לוח בקרה: הזמנות · חברי מועדון · לקוחות משלמים ──
  // הזמנות שנוצרו החודש (כל הסטטוסים) מול הזמנות ששולמו
  const ordersMonthAll  = orders.filter(o => (o.created_at||'').slice(0,7) === curMonthKey);
  const ordersMonthPaid = ordersMonthAll.filter(o => o.status === 'completed');
  // חברי ליגת הכוכבים — נרשמו דרך אתר המועדון בלבד
  const membersJoinedMonth = clubMembers.filter(m => (m.joined_at||'').slice(0,7) === curMonthKey).length;
  const clubMembersTotal   = clubMembers.length;
  // לקוחות יוסטאר — הזמינו לפחות מוצר אחד
  const customersNewMonth  = customers.filter(c => (c.created_at||'').slice(0,7) === curMonthKey).length;
  const customersTotal     = customers.length;
  // לקוחות שהזמינו לפחות מוצר אחד — כללי מול החודש הנוכחי
  const payingCustomerIdsAll = new Set(orders.map(o => o.customer_id).filter(Boolean));
  const payingCustomerIdsMonth = new Set(ordersMonthAll.map(o => o.customer_id).filter(Boolean));
  const payingCustomersAll   = payingCustomerIdsAll.size;
  const payingCustomersMonth = payingCustomerIdsMonth.size;

  const segTargets = selectedSegment ? selectedSegment.members : [];
  const allPhones = segTargets.map(m => m.phone).join('\n');

  // ── nav tabs — סדר הגיוני: דשבורד ← חברים ← כרטיסיית חבר ← הזמנות ← מוצרים ← שותפים ← הטבות ← קמפיינים
  const navTabs = [
    { id:'dashboard',      icon:'📊', label:'לוח בקרה',        sub:'תמונת החודש' },
    { id:'customers',      icon:'👨‍👩‍👦', label:'לקוחות יוסטאר',   sub:'הזמינו מוצר יוסטאר' },
    { id:'members',        icon:'⭐', label:'חברי ליגת הכוכבים', sub:'נרשמו באתר המועדון' },
    { id:'order-forms',    icon:'📋', label:'טופס הזמנת מוצרי יוסטאר', sub:'ניהול טפסים' },
    { id:'orders',         icon:'📦', label:'לוח עבודה',         sub:'הזמנות עבודה' },
    { id:'clip-packages',  icon:'🎬', label:'חבילת קליפ ביצועים', sub:'מעקב 5 כוכבים' },
    { id:'schedule',       icon:'🗓', label:'לוז צילומים',       sub:'תכנון שבועי' },
    { id:'filming',        icon:'🎥', label:'יוסטאר מצלמים',    sub:'צילומי משחקים' },
    { id:'intro-profiles', icon:'🎞', label:'פתיח פרופיל מקצועי', sub:'נרשמו לפתיח יוסטאר' },
    { id:'player-profiles', icon:'🏅', label:'ניהול פרופיל שחקן', sub:'אתרים אישיים לשחקנים' },
    { id:'birthdays',      icon:'🎂', label:'ימי הולדת',        sub:'ברכות ומתנות' },
    { id:'ustar-products', icon:'⭐', label:'מוצרי יוסטאר',    sub:'קטלוג מוצרים' },
    { id:'club-benefits',  icon:'🎁', label:'הטבות יוסטאר',    sub:'הטבות לחברים' },
    { id:'treasury',       icon:'💰', label:'קופת יוסטאר',      sub:'הכנסות ותיעוד' },
    { id:'babel',          icon:'🏛', label:'שיטת בבל',          sub:'חלוקת הכנסות חודשית' },
  ];

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'#05080f', direction:'rtl' }}>

      {/* ══ SIDEBAR — ימין ══ */}
      <div style={{ width:230, background:'linear-gradient(180deg,#070e1e,#050b18)', borderLeft:'1px solid #1a2a4a', display:'flex', flexDirection:'column', position:'fixed', top:0, bottom:0, right:0, zIndex:100, overflowY:'auto' }}>

        {/* לוגו + שם המועדון */}
        <div style={{ padding:'22px 16px 18px', borderBottom:'1px solid #1a2a4a', textAlign:'center' }}>
          <Link to="/"><img src="/ustar-logo.png" alt="USTAR" style={{ height:54, filter:'drop-shadow(0 0 14px rgba(212,175,55,0.6))' }} /></Link>
          <div style={{ color:'#d4af37', fontWeight:900, fontSize:'0.88rem', marginTop:10, lineHeight:1.4 }}>ליגת הכוכבים</div>
          <div style={{ color:'#d4af37', fontWeight:900, fontSize:'0.88rem', lineHeight:1.4 }}>של יוסטאר</div>
          <div style={{ color:'#3a4a6a', fontSize:'0.65rem', marginTop:6, letterSpacing:1 }}>מרכז ניהול פנימי</div>
        </div>

        {/* פריטי ניווט */}
        <nav style={{ flex:1, padding:'10px 0' }}>
          {navTabs.map(t => (
            <button key={t.id} onClick={()=>setTab(t.id)} style={{
              width:'100%', textAlign:'right',
              background: tab===t.id ? 'rgba(212,175,55,0.1)' : 'transparent',
              color: tab===t.id ? '#d4af37' : '#7a8aaa',
              border:'none',
              borderRight: tab===t.id ? '3px solid #d4af37' : '3px solid transparent',
              padding:'12px 18px 12px 12px',
              cursor:'pointer', transition:'all 0.15s',
              display:'flex', alignItems:'center', gap:10
            }}>
              <span style={{ fontSize:'1.1rem' }}>{t.icon}</span>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontWeight: tab===t.id?700:500, fontSize:'0.88rem', lineHeight:1.2 }}>{t.label}</div>
                <div style={{ fontSize:'0.68rem', color: tab===t.id?'rgba(212,175,55,0.6)':'#3a4a6a', marginTop:1 }}>{t.sub}</div>
              </div>
            </button>
          ))}
        </nav>

        {/* יציאה */}
        <div style={{ padding:'14px 12px', borderTop:'1px solid #1a2a4a' }}>
          <button onClick={onLogout} style={{ width:'100%', background:'transparent', color:'#ff6b6b', border:'1px solid rgba(255,107,107,0.25)', borderRadius:8, padding:'9px', cursor:'pointer', fontWeight:600, fontSize:'0.85rem' }}>
            🔓 יציאה
          </button>
        </div>
      </div>

      {/* ══ תוכן ראשי ══ */}
      <div style={{ flex:1, marginRight:230, padding:'28px 32px', minHeight:'100vh' }}>

        {msg && (
          <div style={{ background:msgType==='error'?'#3a0a0a':'#0a2a1a', border:`1px solid ${msgType==='error'?'#ff6b6b':'#25d366'}`, color:msgType==='error'?'#ff9999':'#90f0b0', borderRadius:10, padding:'12px 20px', marginBottom:16 }}>
            {msg}
          </div>
        )}

        {/* ══════ DASHBOARD ══════ */}
        {tab === 'dashboard' && (
          <div>
            {/* ── כותרת ── */}
            <div style={{ marginBottom:20 }}>
              <h2 style={{ color:'#d4af37', margin:0, fontSize:'1.5rem' }}>📊 לוח בקרה</h2>
              <p style={{ color:'#8a9aba', margin:'5px 0 0', fontSize:'0.9rem' }}>
                תמונת המצב של <strong style={{ color:'#d4af37' }}>{monthLabelHe(curMonthKey)}</strong>
              </p>
            </div>

            {/* ══ שורת החודש הנוכחי ══ */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(190px,1fr))', gap:14, marginBottom:14 }}>
              {[
                { icon:'💰', label:'הכנסות החודש', val:fmtPrice(incomeMonth), color:'#25d366',
                  sub:`${monthlyIncomeCount} עסקאות`, onClick:()=>setTab('treasury') },
                { icon:'📦', label:'הזמנות החודש', val:ordersMonthAll.length, color:'#d4af37',
                  sub:`${ordersMonthPaid.length} שולמו · ${ordersMonthAll.length-ordersMonthPaid.length} בעבודה`, onClick:()=>setTab('orders') },
                { icon:'🎬', label:'מוצרים שנמכרו החודש', val:prodMonth, color:'#b478ff',
                  sub:'הזמנות ששולמו', onClick:()=>setTab('orders') },
                { icon:'🛒', label:'לקוחות יוסטאר חדשים', val:customersNewMonth, color:'#f0cc60',
                  sub:`${payingCustomersMonth} הזמינו החודש`, onClick:()=>setTab('customers') },
                { icon:'⭐', label:'חברי מועדון חדשים', val:membersJoinedMonth, color:'#7ab0ff',
                  sub:'נרשמו באתר ליגת הכוכבים', onClick:()=>setTab('members') },
              ].map(k => (
                <div key={k.label} onClick={k.onClick}
                  style={{ background:'#0d1b3e', border:`2px solid ${k.color}55`, borderRadius:14, padding:'16px 18px', cursor:'pointer' }}>
                  <div style={{ color:'#8a9aba', fontSize:'0.78rem', marginBottom:6 }}>{k.icon} {k.label}</div>
                  <div style={{ color:k.color, fontWeight:900, fontSize:'2rem', lineHeight:1 }}>{k.val}</div>
                  <div style={{ color:'#4a5a7a', fontSize:'0.74rem', marginTop:6 }}>{k.sub}</div>
                </div>
              ))}
            </div>

            {/* ══ סיכומים כלליים ══ */}
            <div style={{ background:'#0a1525', border:'1px solid #1e3060', borderRadius:14, padding:'14px 18px', marginBottom:22 }}>
              <div style={{ color:'#8a9aba', fontWeight:800, fontSize:'0.82rem', marginBottom:10 }}>📈 סך הכל (מצטבר)</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12 }}>
                {[
                  { label:`הכנסות ${curYearKey}`, val:fmtPrice(incomeYear), color:'#d4af37' },
                  { label:'סה״כ מוצרים השנה', val:prodYear, color:'#b478ff' },
                  { label:'סה״כ לקוחות יוסטאר', val:customersTotal, color:'#f0cc60' },
                  { label:'סה״כ חברי ליגת הכוכבים', val:clubMembersTotal, color:'#7ab0ff' },
                ].map(k => (
                  <div key={k.label}>
                    <div style={{ color:'#4a5a7a', fontSize:'0.74rem', marginBottom:3 }}>{k.label}</div>
                    <div style={{ color:k.color, fontWeight:900, fontSize:'1.3rem' }}>{k.val}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* ══ אילו מוצרים הוזמנו החודש ══ */}
            <div style={{ background:'#0d1b3e', border:'1px solid #1e3060', borderRadius:14, padding:'18px 20px', marginBottom:22 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8, marginBottom:14 }}>
                <div style={{ color:'#c0cce6', fontWeight:900, fontSize:'0.95rem' }}>🎬 מוצרים שהוזמנו — {monthLabelHe(curMonthKey)}</div>
                <div style={{ color:'#d4af37', fontWeight:900, fontSize:'1rem' }}>סה״כ {prodMonth} מוצרים</div>
              </div>
              {prodBreakdownList.length === 0 ? (
                <div style={{ color:'#4a5a7a', fontSize:'0.86rem', textAlign:'center', padding:'18px 0' }}>עדיין לא נמכרו מוצרים החודש</div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {prodBreakdownList.map(([name, qty]) => {
                    const pc = prodMonth > 0 ? Math.round(qty / prodMonth * 100) : 0;
                    return (
                      <div key={name}>
                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.86rem', marginBottom:4 }}>
                          <span style={{ color:'#c0cce6', fontWeight:600 }}>{name}</span>
                          <span style={{ color:'#d4af37', fontWeight:800 }}>{qty} <span style={{ color:'#4a5a7a', fontWeight:400, fontSize:'0.78rem' }}>({pc}%)</span></span>
                        </div>
                        <div style={{ background:'rgba(255,255,255,0.06)', borderRadius:6, height:9, overflow:'hidden' }}>
                          <div style={{ width:`${pc}%`, height:'100%', background:'linear-gradient(90deg,#d4af37,#f0cc60)', borderRadius:6 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ══ התראות דחופות ══ */}
            {(bdayToday.length > 0 || barAlerts.length > 0) && (
              <div style={{ display:'grid', gap:14, gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', marginBottom:22 }}>
                {bdayToday.length > 0 && (
                  <div style={{ background:'linear-gradient(135deg,#3a1a0a,#6a2a0a)', border:'2px solid #ff9966', borderRadius:12, padding:16 }}>
                    <div style={{ color:'#ff9966', fontWeight:800, fontSize:'0.95rem', marginBottom:8 }}>🎂 יום הולדת היום!</div>
                    {bdayToday.map(m => (
                      <div key={m.id} style={{ color:'#ffd', marginBottom:6, fontSize:'0.88rem' }}>
                        <strong>{m.child_name}</strong> (הורה: {m.first_name})
                        <a href={waLink(m.phone, `🎂 יום הולדת שמח ל${m.child_name}!\n\nשלום ${m.first_name}, מועדון יוסטאר מאחל יום הולדת שמח לשחקן/ית שלך! ⚽🎉`)} target="_blank" rel="noopener noreferrer"
                          style={{ marginRight:8, background:'#25d366', color:'#fff', padding:'2px 10px', borderRadius:6, textDecoration:'none', fontSize:'0.78rem' }}>📱 ברכה</a>
                      </div>
                    ))}
                  </div>
                )}
                {barAlerts.length > 0 && (
                  <div style={{ background:'linear-gradient(135deg,#1a0a3a,#3a1a6a)', border:'2px solid #aa66ff', borderRadius:12, padding:16 }}>
                    <div style={{ color:'#aa66ff', fontWeight:800, marginBottom:8, fontSize:'0.95rem' }}>🎊 בר מצווה — חצי שנה הקרובה</div>
                    {barAlerts.slice(0,5).map(m => (
                      <div key={m.id} style={{ color:'#ddd', marginBottom:6, fontSize:'0.86rem' }}>
                        <strong>{m.child_name}</strong> — {barBadge(m.child_dob)?.label}
                      </div>
                    ))}
                    <button onClick={()=>setTab('birthdays')} style={{ marginTop:6, background:'#6622cc', color:'#fff', border:'none', borderRadius:6, padding:'4px 14px', cursor:'pointer', fontSize:'0.8rem' }}>לטאב ימי הולדת →</button>
                  </div>
                )}
              </div>
            )}

            {/* ══ ארכיון חודשי מקוצר ══ */}
            {incomeMonthsSorted.length > 1 && (
              <div style={{ background:'#0d1b3e', border:'1px solid #1e3060', borderRadius:14, padding:'16px 20px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                  <div style={{ color:'#c0cce6', fontWeight:800, fontSize:'0.9rem' }}>🗄 חודשים קודמים</div>
                  <button onClick={()=>setTab('treasury')} style={{ background:'none', color:'#d4af37', border:'1px solid rgba(212,175,55,0.4)', borderRadius:8, padding:'4px 14px', cursor:'pointer', fontSize:'0.78rem' }}>לקופת יוסטאר →</button>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:200, overflowY:'auto' }}>
                  {incomeMonthsSorted.slice(0,12).map(mk => {
                    const cnt = _sales.filter(x => (x.date||'').slice(0,7) === mk).reduce((sum,x)=>sum+x.qty,0);
                    return (
                      <div key={mk} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', borderBottom:'1px solid #16233f' }}>
                        <span style={{ color: mk===curMonthKey?'#d4af37':'#8a9aba', fontWeight: mk===curMonthKey?800:400, fontSize:'0.85rem' }}>
                          {monthLabelHe(mk)}{mk===curMonthKey?' •':''}
                        </span>
                        <span style={{ display:'flex', gap:14, alignItems:'center' }}>
                          <span style={{ color:'#4a5a7a', fontSize:'0.78rem' }}>🎬 {cnt}</span>
                          <span style={{ color:'#25d366', fontWeight:800, fontSize:'0.88rem' }}>{fmtPrice(incomeByMonth[mk])}</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══════ MEMBERS TAB ══════ */}
        {tab === 'members' && (() => {
          const currentYear = new Date().getFullYear();

          // VIP: סך הזמנות > 1000
          const vipMembers = clubMembers.filter(m => {
            const total = orders.filter(o=>o.member_id===m.id&&o.status==='completed').reduce((s,o)=>s+(o.total||0),0);
            return total >= 1000;
          });

          // בר מצווה בחצי שנה הקרובה
          const barMitzvahSoon = clubMembers.filter(m => {
            const months = monthsTo13(m.child_dob);
            return months !== null && months >= 0 && months <= 6;
          });

          // חברי מועדון מועדפים = VIP + בר מצווה (ללא כפילות)
          const preferredIds = new Set([...vipMembers.map(m=>m.id), ...barMitzvahSoon.map(m=>m.id)]);
          const preferredMembers = clubMembers.filter(m => preferredIds.has(m.id));

          // ── המרה: חבר מועדון שהפך ללקוח יוסטאר (לפי טלפון או הזמנה על שמו) ──
          const custPhoneSet = new Set(customers.map(c => normPhone(c.phone)).filter(Boolean));
          const isConverted = m => custPhoneSet.has(normPhone(m.phone)) || orders.some(o => o.member_id === m.id);
          const convertedMembers   = clubMembers.filter(isConverted);
          const toConvertMembers   = clubMembers.filter(m => !isConverted(m));
          const conversionRate     = clubMembers.length > 0 ? Math.round(convertedMembers.length / clubMembers.length * 100) : 0;

          const allTeams = [...new Set(clubMembers.map(m=>m.team).filter(Boolean))].sort();
          const allBirthYears = [...new Set(clubMembers.map(m => m.birth_year || (m.child_dob ? new Date(m.child_dob).getFullYear() : null)).filter(Boolean))].sort((a,b)=>b-a);
          const filteredMembers = clubMembers.filter(m => {
            const by = m.birth_year || (m.child_dob ? new Date(m.child_dob).getFullYear() : null);
            const memberOrderCount = orders.filter(o=>o.member_id===m.id || o.customer_id===m.id).length;
            if (memberTeamFilter !== 'all' && m.team !== memberTeamFilter) return false;
            if (memberBirthYearFilter !== 'all' && String(by) !== memberBirthYearFilter) return false;
            if (memberStatusFilter === 'customer' && !isConverted(m)) return false;
            if (memberStatusFilter === 'prospect' && isConverted(m)) return false;
            if (memberStatusFilter === 'preferred' && !preferredIds.has(m.id)) return false;
            if (memberStatusFilter === 'intro' && m.lead_source !== 'intro') return false;
            if (memberSearch.trim()) {
              const q = memberSearch.trim().toLowerCase();
              const num = String(m.member_number||'');
              const phone = normPhone(m.phone);
              const qPhone = normPhone(q);
              const nameMatch = `${m.first_name||''} ${m.child_name||''} ${m.child_last_name||''}`.toLowerCase().includes(q);
              const phoneMatch = qPhone && phone.includes(qPhone);
              const numMatch = num.includes(q);
              if (!nameMatch && !phoneMatch && !numMatch) return false;
            }
            return true;
          });
          const introMembers = clubMembers.filter(m => m.lead_source === 'intro');
          const customerCount = convertedMembers.length;
          const prospectCount = toConvertMembers.length;

          // ── השלמת פרטי שחקן מרשומת הלקוח המקושרת (אותו id או אותו טלפון) ──
          const digits = s => (s||'').replace(/\D/g,'');
          const custByKey = {};
          customers.forEach(c => {
            if (c.id) custByKey[c.id] = c;
            const p = digits(c.phone);
            if (p && !custByKey['p'+p]) custByKey['p'+p] = c;
          });
          const enrich = (m) => {
            const c = custByKey[m.id] || custByKey['p'+digits(m.phone)];
            if (!c) return m;
            return {
              ...m,
              child_name:      m.child_name      || c.child_name,
              child_last_name: m.child_last_name || c.child_last_name,
              child_dob:       m.child_dob       || c.child_dob,
              birth_year:      m.birth_year      || c.birth_year,
              team:            m.team            || c.team,
              positions:       m.positions       || c.positions,
              jersey_number:   m.jersey_number   || c.jersey_number,
            };
          };

          // ── מיון עמודות ──
          const memberSortVal = (m) => {
            const by = m.birth_year || (m.child_dob ? new Date(m.child_dob).getFullYear() : null);
            switch (memberSort.key) {
              case 'member_number': return m.member_number || 0;
              case 'name':          return (m.first_name || '').toLowerCase();
              case 'child':         return (m.child_name || '').toLowerCase();
              case 'team':          return (m.team || '~').toLowerCase();
              case 'birth_year':    return by || 0;
              case 'age':           return calcAge(m.child_dob) ?? -1;
              case 'birthday':      { const d = daysUntilBirthday(m.child_dob); return d == null ? 99999 : d; }
              case 'joined':        return m.joined_at ? new Date(m.joined_at).getTime() : 0;
              case 'orders':        return orders.filter(o=>o.member_id===m.id || o.customer_id===m.id).length;
              default:              return 0;
            }
          };
          const sortedMembers = filteredMembers.map(enrich).sort((a,b) => {
            const dir = memberSort.dir === 'asc' ? 1 : -1;
            const va = memberSortVal(a), vb = memberSortVal(b);
            if (typeof va === 'string' || typeof vb === 'string') return String(va).localeCompare(String(vb), 'he') * dir;
            return (va - vb) * dir;
          });
          const Th = ({ k, children, style }) => {
            const active = memberSort.key === k;
            return (
              <th className={k ? `th-sort${active ? ' active' : ''}` : undefined} style={style}
                onClick={k ? () => setMemberSort(s => ({ key:k, dir: s.key===k && s.dir==='asc' ? 'desc' : 'asc' })) : undefined}>
                {children}{k && active && <span className="th-arrow">{memberSort.dir==='asc' ? '▲' : '▼'}</span>}
              </th>
            );
          };

          const MemberRow = ({ m }) => {
            const bm = barBadge(m.child_dob);
            const days = daysUntilBirthday(m.child_dob);
            const memberOrdersList = orders.filter(o=>o.member_id===m.id || o.customer_id===m.id);
            const cnt = memberOrdersList.length;
            const fullName = ((m.first_name||'').trim().endsWith((m.last_name||'').trim()) || !m.last_name)
              ? (m.first_name||'').trim()
              : `${(m.first_name||'').trim()} ${(m.last_name||'').trim()}`;
            const childFullName = [m.child_name, m.child_last_name].map(s=>(s||'').trim()).filter(Boolean).join(' ');
            const totalSpend = memberOrdersList.filter(o=>o.status==='completed').reduce((s,o)=>s+(o.total||0),0);
            const isCustomer = isConverted(m);   // הומר ללקוח יוסטאר
            const by = m.birth_year || (m.child_dob ? new Date(m.child_dob).getFullYear() : null);
            // חבר שהומר → הודעת שימור. חבר שטרם הומר → הודעת המרה שמותאמת לשחקן ולקבוצה שלו
            const _childFirst = (m.child_name||'').trim().split(' ')[0] || 'הילד/ה';
            const personalWA = isCustomer
              ? `שלום ${m.first_name}! 👋\n\nמליגת הכוכבים של יוסטאר 🌟\n\nתודה שאתה לקוח שלנו — רצינו לעדכן אותך על החידושים והמוצרים האחרונים.\n\nלכל שאלה אנחנו כאן: 054-6318704\n\nמועדון יוסטאר ⚽`
              : `שלום ${m.first_name}! 👋\n\nמליגת הכוכבים של יוסטאר ⭐\n\nראינו ש${_childFirst} רשום/ה במועדון${m.team ? ` ומשחק/ת ב${m.team}` : ''} — ורצינו להציע משהו מיוחד 🎬\n\n*קליפ ביצועים אישי* — סרטון ערוך שמציג את היכולות של ${_childFirst} על המגרש.\nכרטיס הביקור המושלם מול מאמנים, וגם מזכרת שנשארת למשפחה.\n\nרוצה שאספר לך עוד? 📞 054-6318704\n\nליגת הכוכבים של יוסטאר ⚽`;
            const cardOpen = expandedMemberCard === m.id;
            const saveFulfillment = async (patch) => {
              const { data } = await axios.put(API+'/members/'+m.id+'/fulfillment', patch);
              setMembers(prev => prev.map(x => x.id===m.id ? { ...x, ...data } : x));
              return data;
            };
            return (
              <React.Fragment>
              <tr style={bm?.type==='soon'?{background:'rgba(255,107,0,0.07)'}:days<=7&&days!==null?{background:'rgba(255,153,0,0.05)'}:{}}>
                <td style={{ color:'#d4af37', fontFamily:'monospace', fontWeight:700 }}>{pad4(m.member_number)}</td>
                <td>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{ fontWeight:600 }}>{fullName}</span>
                    <span title={isCustomer ? 'הומר ללקוח יוסטאר — הזמין מוצר' : 'טרם הזמין מוצר — יעד להמרה'} style={{ background:isCustomer?'#0a3a1a':'#3a2410', color:isCustomer?'#25d366':'#ff9966', borderRadius:10, padding:'1px 7px', fontSize:'0.7rem', fontWeight:700, whiteSpace:'nowrap' }}>
                      {isCustomer ? '✅ לקוח' : '🎯 יעד להמרה'}
                    </span>
                  </div>
                </td>
                <td style={{ fontSize:'0.82rem' }}>{m.phone}</td>
                <td style={{ fontWeight:600 }}>{childFullName||'—'}</td>
                <td style={{ color:'#aaa', fontSize:'0.85rem' }}>{m.team||'—'}</td>
                <td style={{ color:'#aaa', fontSize:'0.85rem', fontFamily:'monospace' }}>{by||'—'}</td>
                <td>{calcAge(m.child_dob)??'—'}</td>
                <td style={{ fontSize:'0.8rem' }}>
                  {days!==null ? (days===0?<span style={{color:'#ff9966'}}>🎂 היום!</span>:days<=7?<span style={{color:'#ffcc44'}}>🎂 {days} ימים</span>:`${days} ימים`) : '—'}
                </td>
                <td style={{ fontSize:'0.82rem' }}>
                  {bm ? <span style={{ color:bm.type==='soon'?'#ff6b00':bm.type==='near'?'#ffd700':'#25d366', fontWeight:bm.type==='soon'?700:400 }}>{bm.label}</span> : '—'}
                </td>
                <td style={{ fontSize:'0.82rem' }}>{fmtDate(m.joined_at)}</td>
                <td>
                  <a href={waLink(m.phone, personalWA)} target="_blank" rel="noopener noreferrer"
                    style={{ background:'#25d366', color:'#fff', padding:'4px 10px', borderRadius:8, textDecoration:'none', fontWeight:600, fontSize:'0.8rem', display:'inline-block' }}>📱</a>
                </td>
                <td>
                  {m.email
                    ? <button onClick={()=>sendWelcomeEmail(m)} style={{ background:'#1a3a8e', color:'#fff', border:'none', borderRadius:8, padding:'4px 10px', cursor:'pointer', fontSize:'0.8rem' }}>📧</button>
                    : <span style={{ background:'#0a2a1a', color:'#25d366', borderRadius:8, padding:'2px 7px', fontSize:'0.72rem', fontWeight:600 }}>📱 WA בלבד</span>}
                </td>
                <td>
                  <button onClick={()=>openMemberOrders(m)} style={{ background:isCustomer?'#1a3a8e':'#222', color:'#fff', border:'none', borderRadius:8, padding:'4px 10px', cursor:'pointer', fontSize:'0.8rem' }}>
                    {isCustomer ? <><span>📦 {cnt}</span>{totalSpend>0&&<span style={{color:'#25d366',marginRight:4}}> {fmtPrice(totalSpend)}</span>}</> : '+ הוסף'}
                  </button>
                </td>
                <td>
                  <button onClick={()=>{ setExpandedMemberCard(cardOpen?null:m.id); setAddrDraft({ ship_city:m.ship_city||'', ship_street:m.ship_street||'', ship_building:m.ship_building||'', ship_apartment:m.ship_apartment||'', ship_zip:m.ship_zip||'' }); }}
                    title="קלף וכתובת משלוח"
                    style={{ background: m.card_delivered ? '#0a3a1a' : m.card_sent ? '#1a2a5a' : '#222', color: m.card_delivered ? '#25d366' : m.card_sent ? '#7ab0ff' : '#8a9aba', border:'none', borderRadius:8, padding:'4px 9px', cursor:'pointer', fontSize:'0.8rem', whiteSpace:'nowrap' }}>
                    {m.card_delivered ? '✅ הגיע' : m.card_sent ? '📮 נשלח' : '🎴 קלף'} {cardOpen?'▲':'▼'}
                  </button>
                  {m.lead_source === 'intro' && (
                    <button onClick={()=>saveFulfillment({ intro_sent: !m.intro_sent })}
                      title={m.intro_sent ? 'הפתיח נשלח להורה בוואטסאפ' : 'סמן שהפתיח נשלח להורה'}
                      style={{ marginRight:5, background: m.intro_sent ? '#0a3a1a' : '#222',
                        color: m.intro_sent ? '#25d366' : '#8a9aba', border:'none', borderRadius:8,
                        padding:'4px 9px', cursor:'pointer', fontSize:'0.8rem', whiteSpace:'nowrap' }}>
                      {m.intro_sent ? '✅ פתיח' : '🎬 פתיח'}
                    </button>
                  )}
                </td>
                <td><Link to={'/card/'+m.id} className="btn btn-gold btn-sm">כרטיס</Link></td>
                <td><button onClick={()=>deleteMember(m.id)} className="btn btn-danger btn-sm">מחק</button></td>
              </tr>
              {cardOpen && (
                <tr>
                  <td colSpan={16} style={{ background:'rgba(212,175,55,0.04)', padding:'16px 20px' }}>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:20, alignItems:'flex-start' }}>
                      {/* סטטוס קלף */}
                      <div style={{ minWidth:220 }}>
                        <div style={{ color:'#d4af37', fontWeight:800, fontSize:'0.85rem', marginBottom:10 }}>🎴 סטטוס קלף הכוכבים</div>
                        <label style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, cursor:'pointer', color:'#c0cce6', fontSize:'0.88rem' }}>
                          <input type="checkbox" checked={!!m.card_sent} onChange={e=>saveFulfillment({ card_sent: e.target.checked })} style={{ accentColor:'#7ab0ff', width:16, height:16 }} />
                          📮 הקלף נשלח בדואר
                        </label>
                        <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', color:'#c0cce6', fontSize:'0.88rem' }}>
                          <input type="checkbox" checked={!!m.card_delivered} onChange={e=>saveFulfillment({ card_delivered: e.target.checked })} style={{ accentColor:'#25d366', width:16, height:16 }} />
                          ✅ הקלף הגיע / התקבל
                        </label>
                      </div>
                      {/* כתובת משלוח */}
                      <div style={{ flex:1, minWidth:280 }}>
                        <div style={{ color:'#d4af37', fontWeight:800, fontSize:'0.85rem', marginBottom:10 }}>📦 כתובת למשלוח הקלף</div>
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(110px,1fr))', gap:8, marginBottom:10 }}>
                          {[
                            { k:'ship_city', ph:'עיר' },
                            { k:'ship_street', ph:'רחוב' },
                            { k:'ship_building', ph:'מס׳ בניין (ריק=בית קרקע)' },
                            { k:'ship_apartment', ph:'מס׳ דירה' },
                            { k:'ship_zip', ph:'מיקוד' },
                          ].map(f => (
                            <input key={f.k} value={addrDraft[f.k]||''} placeholder={f.ph}
                              onChange={e=>setAddrDraft(d=>({ ...d, [f.k]:e.target.value }))}
                              style={{ background:'#0a1a3e', border:'1px solid #2a3a6a', borderRadius:8, padding:'7px 10px', color:'#fff', fontSize:'0.83rem' }} />
                          ))}
                        </div>
                        <button onClick={async()=>{ await saveFulfillment(addrDraft); flash('✅ הכתובת נשמרה'); }}
                          style={{ background:'#d4af37', color:'#000', border:'none', borderRadius:8, padding:'8px 18px', cursor:'pointer', fontWeight:800, fontSize:'0.82rem' }}>💾 שמור כתובת</button>
                        <span style={{ color:'#4a5a7a', fontSize:'0.75rem', marginRight:10 }}>בבית קרקע — השאירו את מספר הבניין ריק</span>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
            );
          };

          return (
            <div>
              {/* ── מטרת הטאב: המרת חברי מועדון ללקוחות ── */}
              <div style={{ marginBottom:16 }}>
                <h2 style={{ color:'#d4af37', margin:0, fontSize:'1.4rem' }}>⭐ חברי ליגת הכוכבים</h2>
                <p style={{ color:'#8a9aba', margin:'5px 0 0', fontSize:'0.88rem' }}>
                  הורים שנרשמו למועדון — <strong style={{ color:'#25d366' }}>המטרה: להמיר אותם ללקוחות יוסטאר</strong>
                </p>
              </div>

              {/* ── משפך ההמרה ── */}
              {clubMembers.length > 0 && (
                <div style={{ background:'linear-gradient(135deg,rgba(37,211,102,0.08),rgba(212,175,55,0.04))', border:'2px solid rgba(37,211,102,0.35)', borderRadius:16, padding:'18px 20px', marginBottom:16 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12, marginBottom:12 }}>
                    <div style={{ color:'#25d366', fontWeight:900, fontSize:'1rem' }}>🎯 משפך ההמרה</div>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <span style={{ color:'#8a9aba', fontSize:'0.82rem' }}>שיעור המרה</span>
                      <span style={{ color: conversionRate >= 30 ? '#25d366' : conversionRate >= 15 ? '#f0cc60' : '#ff9966', fontWeight:900, fontSize:'1.7rem' }}>{conversionRate}%</span>
                    </div>
                  </div>
                  <div style={{ background:'rgba(255,255,255,0.06)', borderRadius:8, height:12, overflow:'hidden', marginBottom:12 }}>
                    <div style={{ width:`${conversionRate}%`, height:'100%', background:'linear-gradient(90deg,#25d366,#7af0a0)', borderRadius:8, transition:'width 0.4s' }} />
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:10 }}>
                    {[
                      { label:'סה״כ חברי מועדון', val:clubMembers.length, color:'#d4af37', icon:'👥',
                        onClick:()=>setMemberStatusFilter('all') },
                      { label:'הומרו ללקוחות', val:convertedMembers.length, color:'#25d366', icon:'✅',
                        onClick:()=>setMemberStatusFilter('customer') },
                      { label:'יעד להמרה', val:toConvertMembers.length, color:'#ff9966', icon:'🎯',
                        onClick:()=>setMemberStatusFilter('prospect') },
                      { label:'בר מצווה קרוב', val:barMitzvahSoon.length, color:'#aa66ff', icon:'🎊',
                        onClick:()=>setTab('birthdays') },
                    ].map(s => (
                      <div key={s.label} onClick={s.onClick}
                        style={{ background:'rgba(0,0,0,0.25)', border:`1px solid ${s.color}33`, borderRadius:12, padding:'11px 13px', cursor:'pointer' }}>
                        <div style={{ color:'#8a9aba', fontSize:'0.72rem', marginBottom:4 }}>{s.icon} {s.label}</div>
                        <div style={{ color:s.color, fontWeight:900, fontSize:'1.5rem', lineHeight:1 }}>{s.val}</div>
                      </div>
                    ))}
                  </div>
                  {toConvertMembers.length > 0 && (
                    <div style={{ marginTop:12, color:'#c0cce6', fontSize:'0.83rem' }}>
                      💡 <strong style={{ color:'#ff9966' }}>{toConvertMembers.length} חברים</strong> עדיין לא הזמינו מוצר — הדאטה שלהם אצלך, זה הקהל להמרה.
                      <button onClick={()=>setMemberStatusFilter('prospect')}
                        style={{ marginRight:8, background:'rgba(255,153,102,0.15)', color:'#ff9966', border:'1px solid rgba(255,153,102,0.4)', borderRadius:8, padding:'3px 12px', cursor:'pointer', fontSize:'0.78rem', fontWeight:700 }}>
                        הצג רק אותם →
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* ── View mode toggle ── */}
              <div style={{ display:'flex', gap:10, marginBottom:16 }}>
                <button onClick={()=>setMemberViewMode('table')}
                  style={{ padding:'7px 18px', borderRadius:20, border:`1px solid ${memberViewMode==='table'?'#d4af37':'#2a3a6a'}`, background:memberViewMode==='table'?'#d4af37':'transparent', color:memberViewMode==='table'?'#0a1120':'#aaa', cursor:'pointer', fontWeight:600, fontSize:'0.88rem' }}>
                  👥 טבלת חברים
                </button>
                <button onClick={()=>setMemberViewMode('cohorts')}
                  style={{ padding:'7px 18px', borderRadius:20, border:`1px solid ${memberViewMode==='cohorts'?'#d4af37':'#2a3a6a'}`, background:memberViewMode==='cohorts'?'#d4af37':'transparent', color:memberViewMode==='cohorts'?'#0a1120':'#aaa', cursor:'pointer', fontWeight:600, fontSize:'0.88rem' }}>
                  📊 שנתונים + קבוצות
                </button>
              </div>

              {/* ── Cohorts view ── */}
              {memberViewMode === 'cohorts' && (() => {
                const cohortMap = {};
                members.forEach(m => {
                  const by = m.birth_year || (m.child_dob ? new Date(m.child_dob).getFullYear() : null);
                  if (!by) { cohortMap['__unknown'] = cohortMap['__unknown'] || { year: null, members: [] }; cohortMap['__unknown'].members.push(m); return; }
                  if (!cohortMap[by]) cohortMap[by] = { year: by, members: [] };
                  cohortMap[by].members.push(m);
                });
                const cohorts = Object.values(cohortMap).sort((a,b) => (b.year||0) - (a.year||0));

                const teamMap = {};
                members.forEach(m => {
                  const t = m.team || '—';
                  if (!teamMap[t]) teamMap[t] = [];
                  teamMap[t].push(m);
                });
                const teams = Object.entries(teamMap).sort((a,b) => b[1].length - a[1].length);

                return (
                  <div>
                    {/* By cohort */}
                    <div style={{ color:'#d4af37', fontWeight:700, fontSize:'1rem', marginBottom:12 }}>📅 שנתונים (לפי שנת לידה)</div>
                    <div style={{ display:'grid', gap:12, gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', marginBottom:28 }}>
                      {cohorts.map(c => {
                        const isBarYear = c.year && (currentYear - c.year === 12 || currentYear - c.year === 13);
                        const teamsInCohort = [...new Set(c.members.map(m=>m.team).filter(Boolean))];
                        return (
                          <div key={c.year||'x'} style={{ background:'#0d1a35', border:`2px solid ${isBarYear?'#d4af37':'#2a3a6a'}`, borderRadius:12, padding:16, position:'relative' }}>
                            {isBarYear && <div style={{ position:'absolute', top:10, left:10, background:'#d4af37', color:'#0a1120', borderRadius:20, padding:'2px 10px', fontSize:'0.72rem', fontWeight:700 }}>✡️ בר מצווה</div>}
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                              <div style={{ color:'#fff', fontWeight:800, fontSize:'1.3rem' }}>{c.year || '?'}</div>
                              <div style={{ background:'#1a3a8e', color:'#7a9aff', borderRadius:20, padding:'2px 12px', fontWeight:700 }}>{c.members.length}</div>
                            </div>
                            <div style={{ color:'#aaa', fontSize:'0.82rem', marginBottom:8 }}>גיל ממוצע: {c.year ? currentYear - c.year : '?'}</div>
                            {teamsInCohort.length > 0 && (
                              <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                                {teamsInCohort.map(t => (
                                  <span key={t} style={{ background:'#1a2a4a', color:'#c0cce6', borderRadius:10, padding:'2px 8px', fontSize:'0.75rem' }}>{t}</span>
                                ))}
                              </div>
                            )}
                            <button onClick={()=>{ setMemberBirthYearFilter(String(c.year)); setMemberViewMode('table'); }}
                              style={{ marginTop:10, width:'100%', background:'transparent', border:'1px solid #2a3a6a', color:'#d4af37', borderRadius:8, padding:'5px 0', cursor:'pointer', fontSize:'0.82rem' }}>
                              הצג חברים →
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    {/* By team */}
                    <div style={{ color:'#d4af37', fontWeight:700, fontSize:'1rem', marginBottom:12 }}>⚽ קבוצות</div>
                    <div style={{ display:'grid', gap:12, gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))' }}>
                      {teams.map(([team, tMembers]) => {
                        const years = [...new Set(tMembers.map(m => m.birth_year || (m.child_dob ? new Date(m.child_dob).getFullYear() : null)).filter(Boolean))].sort();
                        return (
                          <div key={team} style={{ background:'#0d1a35', border:'1px solid #2a3a6a', borderRadius:12, padding:14 }}>
                            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                              <div style={{ color:'#fff', fontWeight:700 }}>{team}</div>
                              <div style={{ background:'#25d366', color:'#0a1120', borderRadius:20, padding:'2px 10px', fontWeight:700, fontSize:'0.85rem' }}>{tMembers.length}</div>
                            </div>
                            {years.length > 0 && <div style={{ color:'#aaa', fontSize:'0.78rem' }}>שנתונים: {years.join(', ')}</div>}
                            <button onClick={()=>{ setMemberTeamFilter(team); setMemberViewMode('table'); }}
                              style={{ marginTop:8, width:'100%', background:'transparent', border:'1px solid #2a3a6a', color:'#d4af37', borderRadius:8, padding:'4px 0', cursor:'pointer', fontSize:'0.8rem' }}>
                              הצג →
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {memberViewMode === 'table' && (<>

              {/* ── לשוניות משנה ── */}
              <div style={{ display:'flex', gap:0, marginBottom:0, borderBottom:'2px solid #1a2a4a', flexWrap:'wrap' }}>
                {[
                  { id:'all',       label:'כל החברים',          count: clubMembers.length,       color:'#d4af37' },
                  { id:'customer',  label:'✅ הומרו ללקוחות',   count: customerCount,         color:'#25d366' },
                  { id:'prospect',  label:'🎯 יעד להמרה',       count: prospectCount,         color:'#ff9966' },
                  { id:'preferred', label:'👑 חברים מועדפים',   count: preferredMembers.length, color:'#ff9966' },
                  { id:'intro',     label:'🎬 פתיח במתנה',       count: introMembers.length,   color:'#7ab0ff' },
                ].map(s => {
                  const active = memberStatusFilter === s.id;
                  return (
                    <button key={s.id} onClick={()=>setMemberStatusFilter(s.id)} style={{
                      padding:'12px 20px', border:'none', borderBottom: active?`3px solid ${s.color}`:'3px solid transparent',
                      background:'transparent', cursor:'pointer', transition:'all 0.15s',
                      color: active ? s.color : '#4a5a7a', fontWeight: active ? 800 : 500, fontSize:'0.88rem',
                      display:'flex', alignItems:'center', gap:7, marginBottom:-2
                    }}>
                      {s.label}
                      <span style={{ background: active?s.color:'#1a2a4a', color: active?'#000':'#556', borderRadius:20, padding:'1px 8px', fontSize:'0.76rem', fontWeight:800 }}>{s.count}</span>
                    </button>
                  );
                })}
              </div>

              {/* ── תצוגת חברים מועדפים ── */}
              {memberStatusFilter === 'preferred' && (
                <div style={{ marginTop:20, marginBottom:8 }}>
                  {/* VIP */}
                  {vipMembers.length > 0 && (
                    <div style={{ marginBottom:20 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
                        <span style={{ fontSize:'1.3rem' }}>👑</span>
                        <div>
                          <div style={{ color:'#d4af37', fontWeight:800, fontSize:'1rem' }}>חברי מועדון VIP — הזמינו מעל ₪1,000</div>
                          <div style={{ color:'#556', fontSize:'0.78rem' }}>לקוחות עם הכי הרבה רכישות — ראויים לטיפול אישי</div>
                        </div>
                      </div>
                      <div style={{ display:'grid', gap:10, gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))' }}>
                        {vipMembers.map(m => {
                          const total = orders.filter(o=>o.member_id===m.id&&o.status==='completed').reduce((s,o)=>s+(o.total||0),0);
                          const waVip = `שלום ${m.first_name}! 👋\n\nמליגת הכוכבים של יוסטאר ⭐\n\nאתה אחד מהלקוחות המועדפים שלנו!\nתודה על האמון — יש לנו הצעה מיוחדת עבורך.\n\nנשמח לדבר: 054-6318704\n\nמועדון יוסטאר ⚽`;
                          return (
                            <div key={m.id} style={{ background:'linear-gradient(135deg,rgba(212,175,55,0.08),rgba(212,175,55,0.03))', border:'1px solid rgba(212,175,55,0.3)', borderRadius:12, padding:14, display:'flex', justifyContent:'space-between', alignItems:'center', gap:10 }}>
                              <div>
                                <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
                                  <span style={{ color:'#d4af37', fontFamily:'monospace', fontSize:'0.82rem' }}>#{pad4(m.member_number)}</span>
                                  <span style={{ color:'#d4af37', fontWeight:700 }}>{m.first_name}</span>
                                </div>
                                <div style={{ color:'#aaa', fontSize:'0.8rem' }}>⚽ {m.child_name} | {m.team||'—'}</div>
                                <div style={{ color:'#25d366', fontWeight:800, fontSize:'1.1rem', marginTop:4 }}>{fmtPrice(total)}</div>
                              </div>
                              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                                <a href={waLink(m.phone, waVip)} target="_blank" rel="noopener noreferrer"
                                  style={{ background:'#25d366', color:'#fff', padding:'5px 12px', borderRadius:8, textDecoration:'none', fontWeight:700, fontSize:'0.78rem', textAlign:'center' }}>📱 WA</a>
                                <button onClick={()=>openMemberOrders(m)}
                                  style={{ background:'#1a3a8e', color:'#fff', border:'none', borderRadius:8, padding:'5px 12px', cursor:'pointer', fontSize:'0.78rem', fontWeight:700 }}>כרטיס חבר</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* בר מצווה */}
                  {barMitzvahSoon.length > 0 && (
                    <div>
                      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
                        <span style={{ fontSize:'1.3rem' }}>✡️</span>
                        <div>
                          <div style={{ color:'#ff9966', fontWeight:800, fontSize:'1rem' }}>בר מצווה — עד 6 חודשים</div>
                          <div style={{ color:'#556', fontSize:'0.78rem' }}>הזדמנות להציע קליפ בר מצווה של יוסטאר</div>
                        </div>
                      </div>
                      <div style={{ display:'grid', gap:10, gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))' }}>
                        {barMitzvahSoon.map(m => {
                          const months = monthsTo13(m.child_dob);
                          const barPrice = products.find(p=>p.name?.includes('בר מצווה'));
                          const waBar = `שלום ${m.first_name}! 👋\n\nמליגת הכוכבים של יוסטאר ⭐\n\nמזל טוב מראש על הבר מצווה של ${m.child_name}! 🎊\n\nרצינו להציע לכם את *קליפ הבר מצווה של יוסטאר* — קליפ כדורגל מקצועי שיהפוך את האירוע לבלתי נשכח ⚽🎬\n\nלפרטים ומחירים: 054-6318704\n\nמועדון יוסטאר ⭐`;
                          return (
                            <div key={m.id} style={{ background:'linear-gradient(135deg,rgba(255,107,0,0.08),rgba(255,107,0,0.03))', border:'1px solid rgba(255,153,0,0.3)', borderRadius:12, padding:14, display:'flex', justifyContent:'space-between', alignItems:'center', gap:10 }}>
                              <div>
                                <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
                                  <span style={{ color:'#d4af37', fontFamily:'monospace', fontSize:'0.82rem' }}>#{pad4(m.member_number)}</span>
                                  <span style={{ color:'#fff', fontWeight:700 }}>{m.first_name}</span>
                                </div>
                                <div style={{ color:'#aaa', fontSize:'0.8rem' }}>⚽ {m.child_name} | {m.team||'—'}</div>
                                <div style={{ color:'#ff9966', fontWeight:700, fontSize:'0.88rem', marginTop:4 }}>
                                  🎊 {Math.round(months || 0)} חודשים לבר מצווה
                                </div>
                                {barPrice && <div style={{ color:'#d4af37', fontSize:'0.75rem' }}>מחיר: {fmtPrice(barPrice.price*(1-((barPrice.member_discount_enabled?barPrice.member_discount_pct:0)||0)/100))}</div>}
                              </div>
                              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                                <a href={waLink(m.phone, waBar)} target="_blank" rel="noopener noreferrer"
                                  style={{ background:'#25d366', color:'#fff', padding:'5px 12px', borderRadius:8, textDecoration:'none', fontWeight:700, fontSize:'0.78rem', textAlign:'center' }}>📱 WA</a>
                                <button onClick={()=>openMemberOrders(m)}
                                  style={{ background:'#1a3a8e', color:'#fff', border:'none', borderRadius:8, padding:'5px 12px', cursor:'pointer', fontSize:'0.78rem', fontWeight:700 }}>כרטיס חבר</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {preferredMembers.length === 0 && (
                    <div style={{ textAlign:'center', padding:40, color:'#445' }}>
                      <div style={{ fontSize:'2.5rem', marginBottom:12 }}>👑</div>
                      <div>אין עדיין חברים מועדפים</div>
                      <div style={{ fontSize:'0.82rem', marginTop:6, color:'#334' }}>חברים יופיעו כאן כשהזמנות יעברו ₪1,000 או כשבר מצווה מתקרב</div>
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginBottom:16 }} />

              {/* ── חיפוש מהיר ── */}
              <div style={{ position:'relative', marginBottom:14 }}>
                <span style={{ position:'absolute', right:14, top:'50%', transform:'translateY(-50%)', fontSize:'1.1rem', pointerEvents:'none' }}>🔍</span>
                <input
                  value={memberSearch}
                  onChange={e=>setMemberSearch(e.target.value)}
                  placeholder="חיפוש לפי שם הורה, שם שחקן, מספר טלפון או מספר חבר..."
                  style={{ width:'100%', padding:'11px 44px 11px 44px', background:'#0d1a35', border:`1px solid ${memberSearch?'#d4af37':'#2a3a6a'}`, borderRadius:10, color:'#fff', fontSize:'0.92rem', boxSizing:'border-box', outline:'none', transition:'border-color 0.2s' }}
                />
                {memberSearch && (
                  <button onClick={()=>setMemberSearch('')}
                    style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', color:'#aaa', cursor:'pointer', fontSize:'1.1rem', lineHeight:1 }}>×</button>
                )}
                {memberSearch && (
                  <div style={{ position:'absolute', left:36, top:'50%', transform:'translateY(-50%)', background:'#d4af37', color:'#000', borderRadius:12, padding:'1px 8px', fontSize:'0.72rem', fontWeight:800 }}>
                    {filteredMembers.length} תוצאות
                  </div>
                )}
              </div>

              {/* ── Filters ── */}
              <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center', marginBottom:16 }}>
                <div style={{ width:1, background:'#2a3a6a', height:24, margin:'0 4px' }} />

                <select value={memberTeamFilter} onChange={e=>setMemberTeamFilter(e.target.value)}
                  style={{ padding:'7px 14px', background:'#0d1a35', color:'#fff', border:'1px solid #2a3a6a', borderRadius:8, cursor:'pointer', fontSize:'0.85rem' }}>
                  <option value="all">כל הקבוצות</option>
                  {allTeams.map(t => (
                    <option key={t} value={t}>{t} ({clubMembers.filter(m=>m.team===t).length})</option>
                  ))}
                </select>

                <select value={memberBirthYearFilter} onChange={e=>setMemberBirthYearFilter(e.target.value)}
                  style={{ padding:'7px 14px', background:'#0d1a35', color:'#fff', border:'1px solid #2a3a6a', borderRadius:8, cursor:'pointer', fontSize:'0.85rem' }}>
                  <option value="all">כל שנות הלידה</option>
                  {allBirthYears.map(y => (
                    <option key={y} value={String(y)}>{y} ({clubMembers.filter(m=>(m.birth_year||(m.child_dob?new Date(m.child_dob).getFullYear():null))===y).length})</option>
                  ))}
                </select>

                {(memberTeamFilter !== 'all' || memberBirthYearFilter !== 'all' || memberStatusFilter !== 'all' || memberSearch) && (
                  <button onClick={()=>{ setMemberTeamFilter('all'); setMemberBirthYearFilter('all'); setMemberStatusFilter('all'); setMemberSearch(''); }}
                    style={{ background:'none', color:'#ff9966', border:'1px solid #ff9966', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:'0.82rem' }}>
                    ✕ נקה
                  </button>
                )}
                <div style={{ color:'#556', fontSize:'0.82rem', marginRight:'auto' }}>
                  {filteredMembers.length} מתוך {clubMembers.length} חברים
                </div>
              </div>

              {/* ── Members table ── */}
              <div className="table-scroll">
                <table className="members-table">
                  <thead>
                    <tr>
                      <Th k="member_number">#</Th>
                      <Th k="name">הורה</Th>
                      <th>טלפון</th>
                      <Th k="child">שחקן/ית</Th>
                      <Th k="team">קבוצה</Th>
                      <Th k="birth_year">שנת לידה</Th>
                      <Th k="age">גיל</Th>
                      <Th k="birthday">יום הולדת</Th>
                      <th>בר מצווה</th>
                      <Th k="joined">הצטרפות</Th>
                      <th>📱 WA</th>
                      <th>📧</th>
                      <Th k="orders">הזמנות</Th>
                      <th>🎴 קלף</th>
                      <th>כרטיס</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedMembers.map(m => <MemberRow key={m.id} m={m} />)}
                    {sortedMembers.length===0 && <tr><td colSpan={16} style={{ textAlign:'center', color:'#555', padding:28 }}>אין חברים תואמים</td></tr>}
                  </tbody>
                </table>
              </div>
              </>)}
            </div>
          );
        })()}

        {/* ══════ MEMBER ORDERS ══════ */}
        {tab === 'member-orders' && selectedMember && (() => {
          const m = selectedMember;
          const isCustomer = memberOrders.length > 0;
          const totalSpend = memberOrders.filter(o=>o.status==='completed').reduce((s,o)=>s+(o.total||0),0);

          // group orders by season
          const seasonGroups = memberOrders.reduce((acc, o) => {
            const s = o.season || 'ללא עונה';
            if (!acc[s]) acc[s] = { orders:[], total:0 };
            acc[s].orders.push(o);
            if (o.status==='completed') acc[s].total += (o.total||0);
            return acc;
          }, {});
          const seasons = Object.entries(seasonGroups).sort((a,b)=>b[0].localeCompare(a[0]));
          const isVip = seasons.some(([,v]) => v.total >= 1000);

          // positions array (stored as comma-separated)
          const positions = m.positions ? m.positions.split(',').map(p=>p.trim()).filter(Boolean).slice(0,3) : [];

          const crmWA = isCustomer
            ? `שלום ${m.first_name}! 👋\n\nמליגת הכוכבים של יוסטאר ⭐\n\nתודה שאתה לקוח שלנו! 🙏\n\nיש לנו הצעות חדשות שיכולות לעניין אותך —\nצור קשר ונספר לך על הנחות לחברי מועדון! 054-6318704\n\nמועדון יוסטאר ⚽`
            : `שלום ${m.first_name}! 👋\n\nמליגת הכוכבים של יוסטאר ⭐\n\nרצינו להזכיר שכחבר המועדון מגיעה לך הנחה של *10%* על כל מוצרי יוסטאר:\n🎬 קליפ ביצועים אישי\n⭐ קליפ הכוכבים של יוסטאר\n🎊 קליפ בר מצווה\n\nלפרטים: 054-6318704\n\nמועדון יוסטאר ⚽`;

          const toggleStarCard = async (field, val) => {
            const updated = { star_card_digital: m.star_card_digital||0, star_card_printed: m.star_card_printed||0, [field]: val ? 1 : 0 };
            const { data } = await axios.put(API+'/members/'+m.id+'/star-card', updated);
            setSelectedMember(data);
            setMembers(members.map(x => x.id===m.id ? data : x));
          };

          return (
          <div style={{ maxWidth:900, margin:'0 auto' }}>
            {/* Back + title */}
            <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:20 }}>
              <button onClick={()=>setTab('members')} style={{ background:'none', color:'#d4af37', border:'1px solid #d4af37', borderRadius:8, padding:'6px 18px', cursor:'pointer' }}>← רשימת חברים</button>
              <div>
                <h2 style={{ color:'#fff', margin:0, fontSize:'1.2rem', fontWeight:800 }}>כרטיס חבר — {m.first_name}</h2>
                <div style={{ color:'#3a4a6a', fontSize:'0.78rem' }}>#{pad4(m.member_number)} · הצטרף {fmtDate(m.joined_at)}</div>
              </div>
              <span style={{ marginRight:'auto', background:isCustomer?'#0a3a1a':'#1a2a3a', color:isCustomer?'#25d366':'#7a9aff', borderRadius:10, padding:'4px 14px', fontSize:'0.8rem', fontWeight:700 }}>
                {isVip ? '👑 מועדף' : isCustomer ? '⚡ פעיל' : '○ פוטנציאל'}
              </span>
            </div>

            {/* ═══ 3-column grid ═══ */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14, marginBottom:16 }}>

              {/* פרטי הורה */}
              <div style={{ background:'#0d1a35', border:'1px solid #1a2a4a', borderRadius:14, padding:18 }}>
                <div style={{ color:'#556', fontSize:'0.7rem', fontWeight:700, letterSpacing:1, marginBottom:10 }}>👤 פרטי הורה</div>
                <div style={{ color:'#fff', fontSize:'1.05rem', fontWeight:800, marginBottom:8 }}>{m.first_name}</div>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                  <span style={{ color:'#aaa', fontSize:'0.85rem' }}>📞</span>
                  <span style={{ color:'#c0cce6', fontSize:'0.9rem' }}>{m.phone}</span>
                </div>
                {m.email
                  ? <div style={{ color:'#7a9aff', fontSize:'0.82rem', marginBottom:8, wordBreak:'break-all' }}>📧 {m.email}</div>
                  : <div style={{ color:'#3a4a5a', fontSize:'0.78rem', marginBottom:8 }}>אין אימייל</div>
                }
                <a href={waLink(m.phone, crmWA)} target="_blank" rel="noopener noreferrer"
                  style={{ display:'inline-flex', alignItems:'center', gap:6, background:'#25d366', color:'#fff', padding:'6px 14px', borderRadius:20, textDecoration:'none', fontWeight:700, fontSize:'0.8rem' }}>
                  📱 וואטסאפ
                </a>
              </div>

              {/* פרטי שחקן */}
              <div style={{ background:'#0d1a35', border:'1px solid #1a2a4a', borderRadius:14, padding:18 }}>
                <div style={{ color:'#556', fontSize:'0.7rem', fontWeight:700, letterSpacing:1, marginBottom:10 }}>⚽ פרטי השחקן</div>
                <div style={{ color:'#fff', fontSize:'1.05rem', fontWeight:800, marginBottom:6 }}>{m.child_name} {m.child_last_name||''}</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:8 }}>
                  {(m.birth_year||(m.child_dob?new Date(m.child_dob).getFullYear():null)) &&
                    <span style={{ background:'#1a2a4a', color:'#7a9aff', borderRadius:8, padding:'2px 9px', fontSize:'0.78rem' }}>
                      שנתון {m.birth_year||(m.child_dob?new Date(m.child_dob).getFullYear():null)}
                    </span>}
                  {calcAge(m.child_dob) && <span style={{ background:'#1a2a4a', color:'#aaa', borderRadius:8, padding:'2px 9px', fontSize:'0.78rem' }}>גיל {calcAge(m.child_dob)}</span>}
                </div>
                {m.team && <div style={{ color:'#c0cce6', fontSize:'0.85rem', marginBottom:6 }}>⚽ <strong>{m.team}</strong></div>}
                {positions.length > 0 && (
                  <div style={{ display:'flex', gap:5, flexWrap:'wrap', marginBottom:6 }}>
                    {positions.map(p => <span key={p} style={{ background:'#2a1a5a', color:'#aa88ff', borderRadius:8, padding:'2px 9px', fontSize:'0.76rem' }}>{p}</span>)}
                  </div>
                )}
                {barBadge(m.child_dob) && (
                  <div style={{ background:'rgba(255,107,0,0.1)', border:'1px solid #ff6b00', borderRadius:8, padding:'3px 9px', display:'inline-block', color:'#ff9966', fontSize:'0.78rem', fontWeight:700 }}>
                    ✡️ {barBadge(m.child_dob)?.label}
                  </div>
                )}
              </div>

              {/* כתובת משלוח */}
              <div style={{ background:'#0d1a35', border:`1px solid ${m.delivery_address?'rgba(212,175,55,0.4)':'#1a2a4a'}`, borderRadius:14, padding:18 }}>
                <div style={{ color:'#556', fontSize:'0.7rem', fontWeight:700, letterSpacing:1, marginBottom:10 }}>📦 כתובת למשלוח קלף</div>
                {editingAddress ? (
                  <div>
                    <textarea value={addressDraft} onChange={e=>setAddressDraft(e.target.value)}
                      placeholder="רחוב, מספר, עיר, מיקוד"
                      rows={3}
                      style={{ width:'100%', padding:'8px 10px', background:'#0a1525', border:'1px solid #d4af37', borderRadius:8, color:'#fff', fontSize:'0.85rem', resize:'vertical', boxSizing:'border-box', fontFamily:'inherit' }} />
                    <div style={{ display:'flex', gap:6, marginTop:8 }}>
                      <button onClick={async()=>{
                        const { data } = await axios.put(API+'/members/'+m.id, { delivery_address: addressDraft });
                        setSelectedMember(data);
                        setMembers(prev=>prev.map(x=>x.id===m.id?data:x));
                        setEditingAddress(false);
                        flash('✅ כתובת נשמרה!');
                      }} style={{ background:'#d4af37', color:'#000', border:'none', borderRadius:7, padding:'6px 16px', cursor:'pointer', fontWeight:800, fontSize:'0.82rem' }}>שמור</button>
                      <button onClick={()=>setEditingAddress(false)}
                        style={{ background:'transparent', color:'#aaa', border:'1px solid #2a3a6a', borderRadius:7, padding:'6px 12px', cursor:'pointer', fontSize:'0.82rem' }}>ביטול</button>
                    </div>
                  </div>
                ) : m.delivery_address ? (
                  <div>
                    <div style={{ color:'#c0cce6', fontSize:'0.88rem', lineHeight:1.6, marginBottom:10, whiteSpace:'pre-line' }}>{m.delivery_address}</div>
                    <button onClick={()=>{ setAddressDraft(m.delivery_address||''); setEditingAddress(true); }}
                      style={{ background:'#1a2a3a', color:'#d4af37', border:'1px solid rgba(212,175,55,0.3)', borderRadius:7, padding:'5px 12px', cursor:'pointer', fontSize:'0.78rem' }}>✏️ עדכן כתובת</button>
                  </div>
                ) : (
                  <div>
                    <div style={{ color:'#3a4a5a', fontSize:'0.82rem', marginBottom:10 }}>לא הוזנה כתובת עדיין</div>
                    <button onClick={()=>{ setAddressDraft(''); setEditingAddress(true); }}
                      style={{ background:'rgba(212,175,55,0.1)', color:'#d4af37', border:'1px solid rgba(212,175,55,0.3)', borderRadius:7, padding:'6px 14px', cursor:'pointer', fontSize:'0.82rem', fontWeight:700 }}>+ הזן כתובת</button>
                  </div>
                )}
              </div>
            </div>

            {/* Status bar */}
            <div style={{ background:'#0d1a35', border:'1px solid #1a2a4a', borderRadius:12, padding:'12px 18px', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap', marginBottom:16 }}>
              {!isCustomer ? (
                <>
                  <div style={{ background:'#1a2a3a', color:'#7a9aff', borderRadius:10, padding:'4px 14px', fontSize:'0.82rem', fontWeight:700 }}>🎯 חבר לא פעיל — פוטנציאל לקוח</div>
                  <button onClick={() => { setShowOrderForm(true); setTimeout(()=>document.querySelector('.order-form-anchor')?.scrollIntoView({behavior:'smooth'}),100); }}
                    style={{ background:'linear-gradient(135deg,#25d366,#1aaa50)', color:'#fff', border:'none', borderRadius:10, padding:'8px 18px', cursor:'pointer', fontWeight:800, fontSize:'0.85rem' }}>
                    ⚡ הוסף הזמנה — הפוך לפעיל
                  </button>
                </>
              ) : (
                <>
                  <div style={{ background:'#0a3a1a', color:'#25d366', borderRadius:10, padding:'4px 14px', fontSize:'0.82rem', fontWeight:700 }}>⚡ חבר פעיל</div>
                  <div style={{ color:'#25d366', fontWeight:800, fontSize:'1.1rem' }}>{fmtPrice(totalSpend)}</div>
                  <div style={{ color:'#556', fontSize:'0.78rem' }}>סך הכנסות</div>
                  {seasons.map(([s, v]) => (
                    <div key={s} style={{ background:'#0a1525', borderRadius:8, padding:'3px 12px' }}>
                      <span style={{ color:'#556', fontSize:'0.72rem' }}>עונת {s}: </span>
                      <span style={{ color:v.total>=1000?'#d4af37':'#c0cce6', fontWeight:700, fontSize:'0.85rem' }}>{fmtPrice(v.total)}</span>
                      {v.total>=1000 && <span style={{ color:'#d4af37', fontSize:'0.7rem', marginRight:4 }}> 👑</span>}
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* ═══ הטבות מועדון ═══ */}
            <div style={{ background:'#0d1a35', border:'2px solid #25d366', borderRadius:14, padding:20, marginBottom:20 }}>
              <div style={{ color:'#25d366', fontWeight:700, fontSize:'1rem', marginBottom:16 }}>🎁 הטבות מועדון — מה החבר קיבל</div>

              {/* קלף הכוכבים — תמיד ראשון */}
              <div style={{ background:'linear-gradient(135deg,rgba(212,175,55,0.08),rgba(212,175,55,0.03))', border:'1px solid rgba(212,175,55,0.3)', borderRadius:12, padding:14, marginBottom:12 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
                  <div>
                    <div style={{ color:'#d4af37', fontWeight:800, fontSize:'0.95rem' }}>🎴 קלף הכוכבים של יוסטאר</div>
                    <div style={{ color:'#556', fontSize:'0.75rem', marginTop:2 }}>הטבת הצטרפות — כל חבר מועדון מקבל</div>
                  </div>
                  <div style={{ display:'flex', gap:10 }}>
                    <button onClick={()=>toggleStarCard('star_card_digital', !m.star_card_digital)}
                      style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', borderRadius:10, border:`2px solid ${m.star_card_digital?'#25d366':'#2a3a6a'}`, background:m.star_card_digital?'#0a3a1a':'transparent', cursor:'pointer' }}>
                      <span style={{ fontSize:'1.1rem' }}>{m.star_card_digital ? '✅' : '⬜'}</span>
                      <div style={{ textAlign:'right' }}>
                        <div style={{ color:m.star_card_digital?'#25d366':'#aaa', fontWeight:700, fontSize:'0.82rem' }}>דיגיטלי</div>
                        <div style={{ color:'#445', fontSize:'0.68rem' }}>WA</div>
                      </div>
                    </button>
                    <button onClick={()=>toggleStarCard('star_card_printed', !m.star_card_printed)}
                      style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', borderRadius:10, border:`2px solid ${m.star_card_printed?'#d4af37':'#2a3a6a'}`, background:m.star_card_printed?'rgba(212,175,55,0.08)':'transparent', cursor:'pointer' }}>
                      <span style={{ fontSize:'1.1rem' }}>{m.star_card_printed ? '✅' : '⬜'}</span>
                      <div style={{ textAlign:'right' }}>
                        <div style={{ color:m.star_card_printed?'#d4af37':'#aaa', fontWeight:700, fontSize:'0.82rem' }}>מודפס</div>
                        <div style={{ color:'#445', fontSize:'0.68rem' }}>משלוח</div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {/* שאר מוצרי ההטבה מהקטלוג */}
              {products.filter(p=>p.product_type==='benefit').map(bp => {
                const bpVars = (() => { try { return JSON.parse(bp.variants||'[]'); } catch(e){ return []; } })();
                const toggleVariant = async (variantName) => {
                  const existing = variantName
                    ? memberBenefits.find(b=>b.product_id===bp.id && b.variant_name===variantName)
                    : memberBenefits.find(b=>b.product_id===bp.id && !b.variant_name);
                  if (existing) {
                    await axios.delete(API+'/member-benefits/'+existing.id);
                    setMemberBenefits(memberBenefits.filter(x=>x.id!==existing.id));
                  } else {
                    const { data } = await axios.post(API+'/member-benefits', { member_id:m.id, title:bp.name, description:bp.description||'', notes:'', product_id:bp.id, variant_name:variantName||null });
                    setMemberBenefits([data, ...memberBenefits]);
                    flash(`✅ ${bp.name}${variantName?' — '+variantName:''} סומן!`);
                  }
                };

                if (bpVars.length > 0) {
                  // הטבה עם וריאציות — כרטיס עם checkbox לכל וריאציה
                  const allReceived = bpVars.every(v => memberBenefits.some(b=>b.product_id===bp.id && b.variant_name===v));
                  return (
                    <div key={bp.id} style={{ background:'#0a1525', border:`1px solid ${allReceived?'#25d366':'#1a2a4a'}`, borderRadius:10, padding:'12px 16px', marginBottom:8 }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                        <div>
                          <div style={{ color:allReceived?'#25d366':'#c0cce6', fontWeight:700, fontSize:'0.9rem' }}>{bp.name}</div>
                          {bp.description && <div style={{ color:'#556', fontSize:'0.75rem', marginTop:2 }}>{bp.description}</div>}
                        </div>
                        {allReceived && <span style={{ color:'#25d366', fontSize:'0.78rem', fontWeight:700 }}>✅ הכל התקבל</span>}
                      </div>
                      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                        {bpVars.map(v => {
                          const varReceived = memberBenefits.some(b=>b.product_id===bp.id && b.variant_name===v);
                          return (
                            <button key={v} onClick={()=>toggleVariant(v)}
                              style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:20, border:`1px solid ${varReceived?'#25d366':'#2a3a6a'}`, background:varReceived?'rgba(37,211,102,0.1)':'transparent', cursor:'pointer', fontWeight:600, fontSize:'0.82rem', color:varReceived?'#25d366':'#aaa' }}>
                              {varReceived?'✅':'⬜'} {v}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                // הטבה ללא וריאציות — שורה רגילה
                const received = memberBenefits.some(b => b.product_id===bp.id && !b.variant_name)
                  || memberBenefits.some(b => b.title===bp.name && !b.product_id);
                return (
                  <div key={bp.id} style={{ background:received?'#0a2a1a':'#0a1525', border:`1px solid ${received?'#25d366':'#1a2a4a'}`, borderRadius:10, padding:'12px 16px', marginBottom:8, display:'flex', alignItems:'center', justifyContent:'space-between', gap:10 }}>
                    <div>
                      <div style={{ color:received?'#25d366':'#c0cce6', fontWeight:700, fontSize:'0.9rem' }}>{bp.name}</div>
                      {bp.description && <div style={{ color:'#556', fontSize:'0.75rem', marginTop:2 }}>{bp.description}</div>}
                    </div>
                    <button onClick={()=>toggleVariant(null)}
                      style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 16px', borderRadius:10, border:`2px solid ${received?'#25d366':'#2a3a6a'}`, background:received?'#0a3a1a':'transparent', cursor:'pointer', fontWeight:700, fontSize:'0.85rem', color:received?'#25d366':'#aaa', whiteSpace:'nowrap' }}>
                      <span>{received?'✅':'⬜'}</span> {received?'התקבל':'לא התקבל'}
                    </button>
                  </div>
                );
              })}

              {products.filter(p=>p.product_type==='benefit').length === 0 && (
                <div style={{ color:'#3a4a6a', fontSize:'0.8rem', textAlign:'center', padding:'8px 0' }}>
                  ניתן להוסיף הטבות בלשונית <strong style={{color:'#d4af37'}}>הטבות יוסטאר</strong>
                </div>
              )}
            </div>

            {/* ═══ PRODUCT CATALOG — quick add ═══ */}
            {products.filter(p=>!p.product_type||p.product_type==='sale').length > 0 && (
              <div style={{ background:'#0d1a35', border:'1px solid #2a3a6a', borderRadius:14, padding:20, marginBottom:20 }}>
                <div style={{ color:'#d4af37', fontWeight:700, fontSize:'1rem', marginBottom:14 }}>🛒 מוצרי יוסטאר — הוספה מהירה לכרטיסייה</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:10 }}>
                  {products.filter(p=>!p.product_type||p.product_type==='sale').map(p => {
                    const disc = (p.member_discount_enabled ? p.member_discount_pct : 0) || 0;
                    const memberPrice = p.price * (1 - disc/100);
                    const alreadyBought = memberOrders.filter(o=>o.product_name===p.name).length;
                    return (
                      <div key={p.id} style={{ background:'#0a1525', border:'1px solid #1a2a4a', borderRadius:10, padding:'12px 14px', display:'flex', flexDirection:'column', gap:6 }}>
                        <div style={{ color:'#fff', fontWeight:700, fontSize:'0.9rem', lineHeight:1.3 }}>{p.name}</div>
                        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                          <span style={{ color:'#aaa', fontSize:'0.78rem', textDecoration:'line-through' }}>₪{p.price}</span>
                          <span style={{ color:'#25d366', fontWeight:700, fontSize:'0.9rem' }}>₪{memberPrice.toFixed(0)}</span>
                          <span style={{ color:'#556', fontSize:'0.72rem' }}>({disc}% הנחה)</span>
                        </div>
                        {alreadyBought > 0 && <div style={{ color:'#d4af37', fontSize:'0.75rem' }}>✓ הוזמן {alreadyBought > 1 ? `×${alreadyBought}` : 'בעבר'}</div>}
                        <button
                          onClick={() => {
                            setOrderForm({ product_id: p.id, product_name: p.name, quantity:1, unit_price: memberPrice.toFixed(2), discount_pct: disc, notes:'', status:'completed', season: currentSeason() });
                            setShowOrderForm(true);
                            setTimeout(() => document.querySelector('.order-form-anchor')?.scrollIntoView({ behavior:'smooth' }), 100);
                          }}
                          style={{ marginTop:4, background:'#1a3a8e', color:'#fff', border:'none', borderRadius:8, padding:'6px 12px', cursor:'pointer', fontWeight:700, fontSize:'0.8rem' }}>
                          + הוסף להזמנות
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Partner deals available */}
                {partners.some(p=>(p.deals||[]).length>0) && (
                  <div style={{ marginTop:14, paddingTop:14, borderTop:'1px solid #1a2a4a' }}>
                    <div style={{ color:'#aaa', fontSize:'0.8rem', fontWeight:700, marginBottom:10 }}>🤝 הטבות שותפים זמינות לחבר</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                      {partners.flatMap(p=>(p.deals||[]).map(d => {
                        const alreadyGiven = memberBenefits.some(b=>b.title===`${p.name} — ${d.title}`);
                        return (
                          <div key={d.id} style={{ background:alreadyGiven?'#0a3a1a':'#0a1525', border:`1px solid ${alreadyGiven?'#25d366':'#2a3a6a'}`, borderRadius:8, padding:'8px 12px', display:'flex', alignItems:'center', gap:8 }}>
                            <div>
                              <div style={{ color:alreadyGiven?'#25d366':'#c0cce6', fontSize:'0.82rem', fontWeight:600 }}>{p.name}</div>
                              <div style={{ color:'#aaa', fontSize:'0.75rem' }}>{d.title}</div>
                              {d.coupon_code && <div style={{ color:'#d4af37', fontSize:'0.72rem', fontFamily:'monospace' }}>קוד: {d.coupon_code}</div>}
                            </div>
                            {alreadyGiven
                              ? <span style={{ color:'#25d366', fontSize:'1rem' }}>✓</span>
                              : <button
                                  onClick={async () => {
                                    const { data } = await axios.post(API+'/member-benefits', { member_id: m.id, title:`${p.name} — ${d.title}`, description:d.coupon_code?`קוד: ${d.coupon_code}`:'', notes:'' });
                                    setMemberBenefits([data, ...memberBenefits]);
                                    flash('✅ הטבה תועדה!');
                                  }}
                                  style={{ background:'#1a3a8e', color:'#fff', border:'none', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:'0.75rem', fontWeight:700, whiteSpace:'nowrap' }}>
                                  + תעד
                                </button>
                            }
                          </div>
                        );
                      }))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ═══ ORDERS BY SEASON ═══ */}
            <div style={{ marginBottom:20 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                <h3 style={{ color:'#d4af37', margin:0 }}>📦 הזמנות לפי עונת משחקים</h3>
                <button onClick={()=>setShowOrderForm(!showOrderForm)} className="btn btn-gold">+ הוסף הזמנה</button>
              </div>

              {showOrderForm && (
                <div style={{ background:'linear-gradient(135deg,rgba(212,175,55,0.08),rgba(212,175,55,0.03))', border:'2px solid rgba(212,175,55,0.4)', borderRadius:14, padding:20, marginBottom:16 }}>
                  <h4 style={{ color:'#d4af37', margin:'0 0 16px', fontSize:'1rem' }}>⚡ הוספת הזמנה — {m.first_name}</h4>
                  <form onSubmit={addOrder}>
                    {/* שלב 1 — בחירת מוצר */}
                    <div style={{ marginBottom:14 }}>
                      <div style={{ color:'#aaa', fontSize:'0.78rem', fontWeight:700, marginBottom:8 }}>① בחר מוצר מהקטלוג</div>
                      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:8 }}>
                        {products.filter(p=>p.product_type==='sale').map(p => {
                          const memberP = p.price*(1-((p.member_discount_enabled?p.member_discount_pct:0)||0)/100);
                          const sel = String(orderForm.product_id)===String(p.id);
                          return (
                            <button key={p.id} type="button" onClick={()=>handleProductSelect({target:{value:String(p.id)}})}
                              style={{ padding:'8px 14px', borderRadius:10, border:`2px solid ${sel?'#d4af37':'#2a3a6a'}`, background:sel?'rgba(212,175,55,0.12)':'#0a1525', cursor:'pointer', textAlign:'right' }}>
                              <div style={{ color:sel?'#d4af37':'#fff', fontWeight:700, fontSize:'0.85rem' }}>{p.name}</div>
                              <div style={{ display:'flex', gap:8, marginTop:3 }}>
                                <span style={{ color:'#556', fontSize:'0.72rem', textDecoration:'line-through' }}>{fmtPrice(p.price)}</span>
                                <span style={{ color:'#25d366', fontSize:'0.78rem', fontWeight:700 }}>לחבר: {fmtPrice(memberP)}</span>
                              </div>
                            </button>
                          );
                        })}
                        <button type="button" onClick={()=>handleProductSelect({target:{value:'custom'}})}
                          style={{ padding:'8px 14px', borderRadius:10, border:`2px solid ${orderForm.product_id==='custom'?'#7a9aff':'#2a3a6a'}`, background:orderForm.product_id==='custom'?'rgba(122,154,255,0.1)':'#0a1525', cursor:'pointer', color:'#7a9aff', fontWeight:700, fontSize:'0.85rem' }}>
                          ✏️ מותאם אישית
                        </button>
                      </div>
                      {(!orderForm.product_id||orderForm.product_id==='custom') && (
                        <input value={orderForm.product_name} onChange={e=>setOrderForm({...orderForm,product_name:e.target.value})}
                          placeholder="שם המוצר / השירות" required
                          style={{ width:'100%', padding:'9px 14px', background:'#0a1525', border:'1px solid #2a3a6a', borderRadius:8, color:'#fff', fontSize:'0.9rem', boxSizing:'border-box' }} />
                      )}
                    </div>

                    {/* שלב 2 — מחיר וכמות */}
                    <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:10 }}>
                      <div style={{ flex:1, minWidth:100 }}>
                        <div style={{ color:'#aaa', fontSize:'0.75rem', marginBottom:5 }}>② מחיר ₪ לפריט</div>
                        <input type="number" step="0.01" value={orderForm.unit_price} onChange={e=>setOrderForm({...orderForm,unit_price:e.target.value})} required
                          style={{ width:'100%', padding:'9px 12px', background:'#0a1525', border:'1px solid #2a3a6a', borderRadius:8, color:'#fff', fontSize:'1rem', fontWeight:700, boxSizing:'border-box' }} />
                      </div>
                      <div style={{ flex:'0 0 80px' }}>
                        <div style={{ color:'#aaa', fontSize:'0.75rem', marginBottom:5 }}>כמות</div>
                        <input type="number" min="1" value={orderForm.quantity} onChange={e=>setOrderForm({...orderForm,quantity:e.target.value})}
                          style={{ width:'100%', padding:'9px 12px', background:'#0a1525', border:'1px solid #2a3a6a', borderRadius:8, color:'#fff', fontSize:'1rem', boxSizing:'border-box' }} />
                      </div>
                      <div style={{ flex:'0 0 90px' }}>
                        <div style={{ color:'#aaa', fontSize:'0.75rem', marginBottom:5 }}>הנחה %</div>
                        <input type="number" value={orderForm.discount_pct} onChange={e=>setOrderForm({...orderForm,discount_pct:e.target.value})}
                          style={{ width:'100%', padding:'9px 12px', background:'#0a1525', border:'1px solid #2a3a6a', borderRadius:8, color:'#fff', fontSize:'1rem', boxSizing:'border-box' }} />
                      </div>
                    </div>

                    {/* סיכום מחיר */}
                    {orderForm.unit_price && (
                      <div style={{ background:'rgba(37,211,102,0.1)', border:'1px solid rgba(37,211,102,0.3)', borderRadius:10, padding:'10px 16px', marginBottom:12, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <span style={{ color:'#aaa', fontSize:'0.85rem' }}>💰 סה"כ לתשלום</span>
                        <span style={{ color:'#25d366', fontWeight:900, fontSize:'1.3rem' }}>
                          {fmtPrice(parseFloat(orderForm.unit_price)*(parseInt(orderForm.quantity)||1)*(1-(parseFloat(orderForm.discount_pct)||0)/100))}
                        </span>
                      </div>
                    )}

                    {/* עונה + סטטוס */}
                    <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:14 }}>
                      <div style={{ flex:1, minWidth:130 }}>
                        <div style={{ color:'#aaa', fontSize:'0.75rem', marginBottom:5 }}>③ עונת משחקים</div>
                        <select value={orderForm.season} onChange={e=>setOrderForm({...orderForm,season:e.target.value})}
                          style={{ width:'100%', padding:'9px 12px', background:'#0a1525', color:'#fff', border:'1px solid #2a3a6a', borderRadius:8 }}>
                          {SEASONS.map(s=><option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div style={{ flex:1, minWidth:130 }}>
                        <div style={{ color:'#aaa', fontSize:'0.75rem', marginBottom:5 }}>סטטוס</div>
                        <select value={orderForm.status} onChange={e=>setOrderForm({...orderForm,status:e.target.value})}
                          style={{ width:'100%', padding:'9px 12px', background:'#0a1525', color:'#fff', border:'1px solid #2a3a6a', borderRadius:8 }}>
                          <option value="pending">ממתין לאישור</option>
                          <option value="completed">✅ הושלם — שולם</option>
                          <option value="cancelled">בוטל</option>
                        </select>
                      </div>
                      <div style={{ flex:2, minWidth:160 }}>
                        <div style={{ color:'#aaa', fontSize:'0.75rem', marginBottom:5 }}>הערות</div>
                        <input value={orderForm.notes} onChange={e=>setOrderForm({...orderForm,notes:e.target.value})} placeholder="פרטים נוספים..."
                          style={{ width:'100%', padding:'9px 12px', background:'#0a1525', border:'1px solid #2a3a6a', borderRadius:8, color:'#fff', boxSizing:'border-box' }} />
                      </div>
                    </div>

                    <div style={{ display:'flex', gap:8 }}>
                      <button type="submit" style={{ flex:1, background:'linear-gradient(135deg,#d4af37,#b8941f)', color:'#000', border:'none', borderRadius:10, padding:'11px 0', cursor:'pointer', fontWeight:900, fontSize:'0.95rem' }}>
                        ⚡ שמור הזמנה — הפוך לחבר פעיל
                      </button>
                      <button type="button" onClick={()=>setShowOrderForm(false)}
                        style={{ background:'transparent', color:'#aaa', border:'1px solid #2a3a6a', borderRadius:10, padding:'11px 18px', cursor:'pointer' }}>ביטול</button>
                    </div>
                  </form>
                </div>
              )}

              {seasons.length === 0
                ? <div style={{ background:'#0d1a35', border:'1px dashed #2a3a6a', borderRadius:12, padding:32, textAlign:'center', color:'#445' }}>
                    <div style={{ fontSize:'2rem', marginBottom:8 }}>📦</div>
                    <div>עדיין לא נרשמו הזמנות — לחץ "+ הוסף הזמנה"</div>
                  </div>
                : seasons.map(([season, group]) => (
                  <div key={season} style={{ background:'#0d1a35', border:`1px solid ${group.total>=1000?'#d4af37':'#1a2a4a'}`, borderRadius:14, marginBottom:12, overflow:'hidden' }}>
                    {/* Season header */}
                    <div style={{ background: group.total>=1000?'linear-gradient(135deg,rgba(212,175,55,0.12),rgba(212,175,55,0.06))':'#0a1525', padding:'12px 18px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <span style={{ color:'#7a9aff', fontWeight:700 }}>🗓 עונת {season}</span>
                        {group.total>=1000 && <span style={{ background:'#d4af37', color:'#0a1120', borderRadius:20, padding:'2px 10px', fontSize:'0.72rem', fontWeight:800 }}>👑 מועדף</span>}
                      </div>
                      <div style={{ display:'flex', gap:16, alignItems:'center' }}>
                        <span style={{ color:'#aaa', fontSize:'0.82rem' }}>{group.orders.length} מוצרים</span>
                        <span style={{ color:group.total>=1000?'#d4af37':'#25d366', fontWeight:800, fontSize:'1.1rem' }}>{fmtPrice(group.total)}</span>
                      </div>
                    </div>
                    {/* Season orders */}
                    <div style={{ padding:'0 4px' }}>
                      <table className="members-table" style={{ marginBottom:0 }}>
                        <thead><tr><th>תאריך</th><th>מוצר</th><th>כמות</th><th>מחיר</th><th>הנחה</th><th>סה״כ</th><th>הערות</th><th>סטטוס</th><th></th></tr></thead>
                        <tbody>
                          {group.orders.map(o=>(
                            <tr key={o.id}>
                              <td style={{ fontSize:'0.8rem', color:'#aaa' }}>{fmtDate(o.created_at)}</td>
                              <td style={{ fontWeight:700, color:'#fff' }}>{o.product_name}</td>
                              <td>{o.quantity}</td>
                              <td style={{ color:'#aaa' }}>{fmtPrice(o.unit_price)}</td>
                              <td style={{ color:'#25d366' }}>{o.discount_pct}%</td>
                              <td style={{ color:'#d4af37', fontWeight:700 }}>{fmtPrice(o.total)}</td>
                              <td style={{ color:'#aaa', fontSize:'0.78rem' }}>{o.notes||'—'}</td>
                              <td>
                                <select value={o.status} onChange={e=>updateOrderStatus(o.id,e.target.value)} style={{ background:'#1a2a4a', color:STATUS_C[o.status]||'#fff', border:'1px solid #333', borderRadius:6, padding:'3px 8px', cursor:'pointer', fontSize:'0.8rem' }}>
                                  <option value="pending">ממתין</option><option value="completed">הושלם ✓</option><option value="cancelled">בוטל</option>
                                </select>
                              </td>
                              <td><button onClick={()=>deleteOrder(o.id)} className="btn btn-danger btn-sm">✕</button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))
              }
            </div>

            {/* ═══ BENEFITS HISTORY ═══ */}
            <div style={{ marginBottom:20 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                <h3 style={{ color:'#d4af37', margin:0 }}>🎁 הטבות שקיבל החבר</h3>
                <button onClick={()=>setShowBenefitHistForm(!showBenefitHistForm)} className="btn btn-gold">+ תעד הטבה</button>
              </div>

              {showBenefitHistForm && (
                <div className="dark-card" style={{ marginBottom:14 }}>
                  <form onSubmit={async e=>{
                    e.preventDefault();
                    const { data } = await axios.post(API+'/member-benefits', { member_id: m.id, ...newBenefitHist });
                    setMemberBenefits([data, ...memberBenefits]);
                    setNewBenefitHist({ title:'', description:'', notes:'' });
                    setShowBenefitHistForm(false);
                    flash('✅ הטבה תועדה!');
                  }}>
                    <div className="form-row">
                      <div className="form-group" style={{ flex:2 }}>
                        <label>הטבה *</label>
                        <select value={newBenefitHist.title} onChange={e=>setNewBenefitHist({...newBenefitHist,title:e.target.value})} style={{ width:'100%', padding:'8px 12px', background:'#1a2a4a', color:'#fff', border:'1px solid #2a3a6a', borderRadius:8 }} required>
                          <option value="">-- בחר הטבה --</option>
                          <option value="🎴 קלף הכוכבים של יוסטאר — מתנת הצטרפות">🎴 קלף הכוכבים — מתנת הצטרפות</option>
                          {partners.flatMap(p=>(p.deals||[]).map(d=>(
                            <option key={d.id} value={`${p.name} — ${d.title}`}>{p.name} — {d.title}</option>
                          )))}
                          <option value="custom">אחר (הקלד ידנית)</option>
                        </select>
                      </div>
                    </div>
                    {(newBenefitHist.title==='custom'||newBenefitHist.title==='') && (
                      <div className="form-group"><label>שם ההטבה *</label><input value={newBenefitHist.title==='custom'?'':newBenefitHist.title} onChange={e=>setNewBenefitHist({...newBenefitHist,title:e.target.value})} required placeholder="שם ההטבה" /></div>
                    )}
                    <div className="form-row">
                      <div className="form-group"><label>פרטים</label><input value={newBenefitHist.description} onChange={e=>setNewBenefitHist({...newBenefitHist,description:e.target.value})} placeholder="תיאור קצר" /></div>
                      <div className="form-group"><label>הערות</label><input value={newBenefitHist.notes} onChange={e=>setNewBenefitHist({...newBenefitHist,notes:e.target.value})} placeholder="הערות פנימיות" /></div>
                    </div>
                    <div style={{ display:'flex', gap:8 }}>
                      <button className="btn btn-gold" type="submit">שמור</button>
                      <button type="button" onClick={()=>setShowBenefitHistForm(false)} className="btn btn-outline">ביטול</button>
                    </div>
                  </form>
                </div>
              )}

              {memberBenefits.length===0 ? (
                <div style={{ color:'#555', textAlign:'center', padding:24, border:'1px dashed #2a3a6a', borderRadius:10 }}>
                  <div style={{ fontSize:'2rem', marginBottom:8 }}>🎁</div>
                  <div>עדיין לא תועדו הטבות לחבר זה</div>
                  <div style={{ fontSize:'0.82rem', marginTop:4, color:'#444' }}>ההטבה הראשונה: קלף הכוכבים של יוסטאר (מתנת הצטרפות)</div>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {memberBenefits.map(b=>(
                    <div key={b.id} style={{ background:'#0d1a35', border:'1px solid #2a3a6a', borderRadius:10, padding:'12px 16px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <div>
                        <div style={{ color:'#d4af37', fontWeight:600 }}>{b.title}</div>
                        {b.description && <div style={{ color:'#aaa', fontSize:'0.83rem' }}>{b.description}</div>}
                        {b.notes && <div style={{ color:'#556', fontSize:'0.78rem' }}>📝 {b.notes}</div>}
                      </div>
                      <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                        <span style={{ color:'#556', fontSize:'0.78rem' }}>{fmtDate(b.given_at)}</span>
                        <button onClick={async()=>{
                          await axios.delete(API+'/member-benefits/'+b.id);
                          setMemberBenefits(memberBenefits.filter(x=>x.id!==b.id));
                        }} style={{ background:'none', border:'none', color:'#ff6b6b', cursor:'pointer', fontSize:'1rem' }}>✕</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          );
        })()}

        {/* ══════ ORDERS TAB ══════ */}
        {tab === 'orders' && (() => {
          const allOrderSeasons = [...new Set(orders.map(o=>o.season).filter(Boolean))].sort().reverse();
          // הזמנות לקוחות (לא חברי מועדון)
          const customerOrders   = orders.filter(o => o.customer_id);
          const pendingCustOrders = customerOrders.filter(o => o.status !== 'completed');
          const completedCustOrders = customerOrders.filter(o => o.status === 'completed');
          const pendingOrders   = orders.filter(o => o.status === 'pending');
          const completedOrders = orders.filter(o => o.status === 'completed');
          const totalRevenue    = completedOrders.reduce((s,o)=>s+(o.total||0),0);

          // ── חלוקה לחודשים ──
          const monthKeyOf = o => (o.created_at||'').slice(0,7);
          const nowD = new Date();
          const curMonth = `${nowD.getFullYear()}-${String(nowD.getMonth()+1).padStart(2,'0')}`;
          const HEB_M = ['ינואר','פברואר','מרץ','אפריל','מאי','יוני','יולי','אוגוסט','ספטמבר','אוקטובר','נובמבר','דצמבר'];
          const monthLabel = k => { const [y,m] = (k||'').split('-'); return m ? `${HEB_M[parseInt(m)-1]} ${y}` : '—'; };
          const orderMonths = [...new Set(orders.map(monthKeyOf).filter(Boolean))].sort().reverse();
          if (!orderMonths.includes(curMonth)) orderMonths.unshift(curMonth);
          const inMonth = o => monthKeyOf(o) === orderMonth;

          const activeCount  = orders.filter(o => inMonth(o) && o.status !== 'completed').length;
          const archiveCount = orders.filter(o => inMonth(o) && o.status === 'completed').length;
          const visibleOrders = orders.filter(o => {
            if (!inMonth(o)) return false;
            // תצוגה ראשית = עבודות פעילות (טרם שולמו) · ארכיון = הושלמו ושולמו
            if (orderView === 'active'  && o.status === 'completed') return false;
            if (orderView === 'archive' && o.status !== 'completed') return false;
            if (orderSeasonFilter !== 'all' && o.season !== orderSeasonFilter) return false;
            if (orderSearch.trim()) {
              const q = orderSearch.trim().toLowerCase();
              const phone = normPhone(o.phone);
              const qp = normPhone(q);
              if (
                !(o.first_name||'').toLowerCase().includes(q) &&
                !(o.product_name||'').toLowerCase().includes(q) &&
                !(String(o.member_number||'')).includes(q) &&
                !(qp && phone.includes(qp))
              ) return false;
            }
            return true;
          }).sort((a,b) => {
            if (orderSort === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
            if (orderSort === 'amount') return (b.total||b.unit_price||0) - (a.total||a.unit_price||0);
            if (orderSort === 'status') {
              const rank = o => o.status==='pending' ? 0 : o.status==='edited' ? 1 : o.status==='completed' ? 2 : 3;
              return rank(a) - rank(b) || (new Date(b.created_at) - new Date(a.created_at));
            }
            return new Date(b.created_at) - new Date(a.created_at); // newest
          });

          const newOrderTotal = newOrderForm.unit_price
            ? parseFloat(newOrderForm.unit_price) * (parseInt(newOrderForm.quantity)||1) * (1 - (parseFloat(newOrderForm.discount_pct)||0)/100)
            : 0;

          const memberSearchResults = newOrderMemberSearch.trim()
            ? members.filter(m => {
                const q = newOrderMemberSearch.trim().toLowerCase();
                const phone = normPhone(m.phone);
                const qp = normPhone(q);
                return `${m.first_name||''} ${m.child_name||''}`.toLowerCase().includes(q)
                  || (qp && phone.includes(qp))
                  || String(m.member_number||'').includes(q);
              }).slice(0, 6)
            : [];

          const submitNewOrder = async () => {
            if (!newOrderForm.product_name) return flash('בחר מוצר','error');
            if (!newOrderForm.unit_price) return flash('הכנס מחיר','error');
            const season = newOrderForm.season || currentSeason();
            const realProductId = newOrderForm.product_id && newOrderForm.product_id !== 'custom' ? newOrderForm.product_id : null;
            const payload = {
              member_id: newOrderMember ? newOrderMember.id : null,
              product_id: realProductId,
              product_name: newOrderForm.product_name,
              quantity: parseInt(newOrderForm.quantity)||1,
              unit_price: parseFloat(newOrderForm.unit_price),
              discount_pct: parseFloat(newOrderForm.discount_pct)||0,
              total: newOrderTotal,
              notes: newOrderForm.notes,
              status: newOrderForm.status || 'pending',
              season,
            };
            const { data } = await axios.post(API+'/orders', payload);
            setOrders(prev => [data, ...prev]);
            setNewOrderForm({ product_id:'', product_name:'', unit_price:'', discount_pct:'10', quantity:'1', notes:'', season:'', status:'pending' });
            setNewOrderMember(null); setNewOrderMemberSearch('');
            setShowNewOrderForm(false);
            flash(`✅ הזמנה נוצרה${newOrderMember?' ושויכה ל-'+newOrderMember.first_name:''}!`);
          };

          return (
            <div>
              {/* Header */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
                <div>
                  <h2 style={{ color:'#d4af37', margin:0 }}>📦 הזמנות עבודה</h2>
                  <p style={{ color:'#aaa', margin:'4px 0 0', fontSize:'0.88rem' }}>ניהול הזמנות — שיוך ללקוח, מעקב תשלום</p>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={async () => {
                    if (!window.confirm('לאפס את כל ההזמנות שבוצעו חזרה לממתין? גם ההכנסות הקשורות יימחקו מקופת יוסטאר.')) return;
                    const { data } = await axios.post(API+'/orders/reset-completed');
                    setOrders(prev => prev.map(o => o.status === 'completed' ? { ...o, status:'pending' } : o));
                    axios.get(API+'/income').then(r => setIncomeLog(r.data));
                    flash(`✅ אופסו ${data.reset} הזמנות בוצעו → ממתין`);
                  }} style={{ background:'rgba(255,80,80,0.1)', color:'#ff8888', border:'1px solid rgba(255,80,80,0.3)', borderRadius:10, padding:'10px 18px', cursor:'pointer', fontWeight:700, fontSize:'0.82rem' }}>
                    🔄 אפס הזמנות שבוצעו
                  </button>
                  <button onClick={()=>setShowNewOrderForm(v=>!v)}
                    style={{ background:'linear-gradient(135deg,#d4af37,#b8941f)', color:'#000', border:'none', borderRadius:10, padding:'10px 22px', cursor:'pointer', fontWeight:800, fontSize:'0.9rem' }}>
                    {showNewOrderForm ? '▲ סגור' : '+ הזמנה חדשה'}
                  </button>
                </div>
              </div>

              {/* Stats */}
              <div className="stats-row" style={{ marginBottom:20 }}>
                <div className="stat-card">
                  <div className="stat-number" style={{ color:'#7a9aff' }}>{pendingOrders.length}</div>
                  <div className="stat-label">🔵 ממתינות לתשלום</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number" style={{ color:'#25d366' }}>{completedOrders.length}</div>
                  <div className="stat-label">🟢 שולמו</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number" style={{ color:'#25d366' }}>{fmtPrice(totalRevenue)}</div>
                  <div className="stat-label">💰 סה"כ הכנסות</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{orders.length}</div>
                  <div className="stat-label">סה"כ הזמנות</div>
                </div>
              </div>

              {/* ── טופס הזמנה חדשה ── */}
              {showNewOrderForm && (
                <div style={{ background:'linear-gradient(135deg,rgba(212,175,55,0.08),rgba(212,175,55,0.03))', border:'2px solid rgba(212,175,55,0.5)', borderRadius:14, padding:22, marginBottom:24 }}>
                  <h3 style={{ color:'#d4af37', margin:'0 0 18px', fontSize:'1rem' }}>⚡ יצירת הזמנת עבודה חדשה</h3>

                  {/* שלב 1 — מוצר */}
                  <div style={{ marginBottom:16 }}>
                    <div style={{ color:'#aaa', fontSize:'0.78rem', fontWeight:700, marginBottom:8 }}>① בחר מוצר יוסטאר</div>
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:8 }}>
                      {products.filter(p=>p.product_type==='sale').map(p => {
                        const sel = String(newOrderForm.product_id)===String(p.id);
                        return (
                          <button key={p.id} type="button" onClick={()=>setNewOrderForm(f=>({...f, product_id:String(p.id), product_name:p.name, unit_price:String(p.price), discount_pct:String((p.member_discount_enabled?p.member_discount_pct:0)||0)}))}
                            style={{ padding:'8px 14px', borderRadius:10, border:`2px solid ${sel?'#d4af37':'#2a3a6a'}`, background:sel?'rgba(212,175,55,0.12)':'#0a1525', cursor:'pointer', textAlign:'right' }}>
                            <div style={{ color:sel?'#d4af37':'#fff', fontWeight:700, fontSize:'0.85rem' }}>{p.name}</div>
                            <div style={{ color:'#25d366', fontSize:'0.78rem', marginTop:2 }}>{fmtPrice(p.price)}</div>
                          </button>
                        );
                      })}
                      <button type="button" onClick={()=>setNewOrderForm(f=>({...f, product_id:'custom', product_name:'', unit_price:'', discount_pct:'0'}))}
                        style={{ padding:'8px 14px', borderRadius:10, border:`2px solid ${newOrderForm.product_id==='custom'?'#7a9aff':'#2a3a6a'}`, background:newOrderForm.product_id==='custom'?'rgba(122,154,255,0.1)':'#0a1525', cursor:'pointer', color:'#7a9aff', fontWeight:700, fontSize:'0.85rem' }}>
                        ✏️ מוצר מותאם
                      </button>
                    </div>
                    {newOrderForm.product_id === 'custom' && (
                      <input value={newOrderForm.product_name} onChange={e=>setNewOrderForm(f=>({...f,product_name:e.target.value}))}
                        placeholder="שם המוצר / השירות"
                        style={{ width:'100%', padding:'9px 14px', background:'#0a1525', border:'1px solid #2a3a6a', borderRadius:8, color:'#fff', fontSize:'0.9rem', boxSizing:'border-box' }} />
                    )}
                  </div>

                  {/* שלב 2 — מחיר */}
                  <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:12 }}>
                    <div style={{ flex:1, minWidth:110 }}>
                      <div style={{ color:'#aaa', fontSize:'0.75rem', marginBottom:5 }}>② מחיר ₪</div>
                      <input type="number" step="0.01" value={newOrderForm.unit_price} onChange={e=>setNewOrderForm(f=>({...f,unit_price:e.target.value}))}
                        style={{ width:'100%', padding:'10px 12px', background:'#0a1525', border:'1px solid #2a3a6a', borderRadius:8, color:'#fff', fontSize:'1.05rem', fontWeight:700, boxSizing:'border-box' }} />
                    </div>
                    <div style={{ flex:'0 0 80px' }}>
                      <div style={{ color:'#aaa', fontSize:'0.75rem', marginBottom:5 }}>כמות</div>
                      <input type="number" min="1" value={newOrderForm.quantity} onChange={e=>setNewOrderForm(f=>({...f,quantity:e.target.value}))}
                        style={{ width:'100%', padding:'10px 12px', background:'#0a1525', border:'1px solid #2a3a6a', borderRadius:8, color:'#fff', fontSize:'1rem', boxSizing:'border-box' }} />
                    </div>
                    <div style={{ flex:'0 0 90px' }}>
                      <div style={{ color:'#aaa', fontSize:'0.75rem', marginBottom:5 }}>הנחה %</div>
                      <input type="number" value={newOrderForm.discount_pct} onChange={e=>setNewOrderForm(f=>({...f,discount_pct:e.target.value}))}
                        style={{ width:'100%', padding:'10px 12px', background:'#0a1525', border:'1px solid #2a3a6a', borderRadius:8, color:'#fff', fontSize:'1rem', boxSizing:'border-box' }} />
                    </div>
                    <div style={{ flex:2, minWidth:140 }}>
                      <div style={{ color:'#aaa', fontSize:'0.75rem', marginBottom:5 }}>הערות</div>
                      <input value={newOrderForm.notes} onChange={e=>setNewOrderForm(f=>({...f,notes:e.target.value}))} placeholder="פרטים נוספים..."
                        style={{ width:'100%', padding:'10px 12px', background:'#0a1525', border:'1px solid #2a3a6a', borderRadius:8, color:'#fff', boxSizing:'border-box' }} />
                    </div>
                  </div>

                  {/* סיכום מחיר */}
                  {newOrderTotal > 0 && (
                    <div style={{ background:'rgba(37,211,102,0.1)', border:'1px solid rgba(37,211,102,0.3)', borderRadius:10, padding:'10px 16px', marginBottom:14, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ color:'#aaa', fontSize:'0.85rem' }}>💰 סה"כ לתשלום</span>
                      <span style={{ color:'#25d366', fontWeight:900, fontSize:'1.4rem' }}>{fmtPrice(newOrderTotal)}</span>
                    </div>
                  )}

                  {/* שלב 3 — שיוך חבר מועדון */}
                  <div style={{ marginBottom:16 }}>
                    <div style={{ color:'#aaa', fontSize:'0.78rem', fontWeight:700, marginBottom:8 }}>③ שיוך לחבר מועדון (אופציונלי)</div>
                    {newOrderMember ? (
                      <div style={{ display:'flex', alignItems:'center', gap:12, background:'rgba(37,211,102,0.08)', border:'1px solid rgba(37,211,102,0.3)', borderRadius:10, padding:'10px 14px' }}>
                        <div style={{ flex:1 }}>
                          <span style={{ color:'#25d366', fontWeight:700 }}>✅ {newOrderMember.first_name}</span>
                          <span style={{ color:'#556', fontSize:'0.82rem', marginRight:8 }}>#{pad4(newOrderMember.member_number)} | {newOrderMember.child_name} | {newOrderMember.phone}</span>
                        </div>
                        <button onClick={()=>{ setNewOrderMember(null); setNewOrderMemberSearch(''); }}
                          style={{ background:'none', border:'none', color:'#ff6b6b', cursor:'pointer', fontSize:'1.1rem' }}>×</button>
                      </div>
                    ) : (
                      <div style={{ position:'relative' }}>
                        <span style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}>🔍</span>
                        <input value={newOrderMemberSearch} onChange={e=>setNewOrderMemberSearch(e.target.value)}
                          placeholder="חפש לפי שם / טלפון / מספר חבר..."
                          style={{ width:'100%', padding:'10px 38px 10px 14px', background:'#0a1525', border:'1px solid #2a3a6a', borderRadius:8, color:'#fff', fontSize:'0.9rem', boxSizing:'border-box' }} />
                        {memberSearchResults.length > 0 && (
                          <div style={{ position:'absolute', top:'100%', right:0, left:0, background:'#0d1a35', border:'1px solid #2a3a6a', borderRadius:8, zIndex:50, marginTop:4, overflow:'hidden' }}>
                            {memberSearchResults.map(m => (
                              <button key={m.id} onClick={()=>{ setNewOrderMember(m); setNewOrderMemberSearch(''); }}
                                style={{ display:'block', width:'100%', textAlign:'right', padding:'10px 14px', background:'transparent', border:'none', borderBottom:'1px solid #1a2a4a', cursor:'pointer', color:'#fff' }}
                                onMouseEnter={e=>e.currentTarget.style.background='#1a2a4a'}
                                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                                <span style={{ color:'#d4af37', fontFamily:'monospace', fontSize:'0.82rem', marginLeft:8 }}>#{pad4(m.member_number)}</span>
                                <span style={{ fontWeight:700 }}>{m.first_name}</span>
                                <span style={{ color:'#556', fontSize:'0.82rem', marginRight:6 }}> | {m.child_name} | {m.phone}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div style={{ display:'flex', gap:8 }}>
                    <button onClick={submitNewOrder}
                      style={{ flex:1, background:'linear-gradient(135deg,#d4af37,#b8941f)', color:'#000', border:'none', borderRadius:10, padding:'12px 0', cursor:'pointer', fontWeight:900, fontSize:'0.95rem' }}>
                      💾 צור הזמנה
                    </button>
                    <button onClick={()=>setShowNewOrderForm(false)}
                      style={{ background:'transparent', color:'#aaa', border:'1px solid #2a3a6a', borderRadius:10, padding:'12px 18px', cursor:'pointer' }}>ביטול</button>
                  </div>
                </div>
              )}

              {/* ══ הגדרת מחיר קליפ ══ */}
              <div style={{ background:'rgba(212,175,55,0.07)', border:'1px solid rgba(212,175,55,0.25)', borderRadius:12, padding:'14px 18px', marginBottom:20, display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
                <span style={{ color:'#d4af37', fontWeight:800, fontSize:'0.9rem' }}>💰 מחיר קליפ ביצועים:</span>
                <input type="number" value={clipPrice} onChange={e=>setClipPrice(e.target.value)} placeholder="₪ מחיר"
                  style={{ background:'#0a1a3e', border:'1px solid #2a3a6a', borderRadius:8, padding:'7px 12px', color:'#fff', fontSize:'0.95rem', width:110 }} />
                <button disabled={clipPriceSaving} onClick={async()=>{
                  setClipPriceSaving(true);
                  await axios.put(API+'/settings/clip_price', { value: clipPrice });
                  setClipPriceSaving(false);
                  flash('✅ המחיר עודכן!');
                }} style={{ background:'#d4af37', color:'#000', border:'none', borderRadius:8, padding:'8px 18px', cursor:'pointer', fontWeight:800, fontSize:'0.85rem' }}>
                  {clipPriceSaving ? '...' : '💾 שמור מחיר'}
                </button>
                <span style={{ color:'#4a5a7a', fontSize:'0.8rem' }}>המחיר מוצג בטופס ההזמנה הציבורי</span>
              </div>

              {/* ── בורר חודש ── */}
              <div style={{ marginBottom:14 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:8 }}>
                  <span style={{ color:'#c0cce6', fontWeight:800, fontSize:'0.9rem' }}>📅 חודש עבודה:</span>
                  <span style={{ color:'#d4af37', fontWeight:900, fontSize:'1.05rem' }}>{monthLabel(orderMonth)}</span>
                  {orderMonth === curMonth
                    ? <span style={{ background:'rgba(37,211,102,0.15)', color:'#25d366', borderRadius:20, padding:'2px 10px', fontSize:'0.75rem', fontWeight:800 }}>החודש הנוכחי</span>
                    : <button onClick={()=>setOrderMonth(curMonth)} style={{ background:'rgba(212,175,55,0.12)', color:'#d4af37', border:'1px solid rgba(212,175,55,0.4)', borderRadius:20, padding:'2px 12px', fontSize:'0.75rem', fontWeight:800, cursor:'pointer' }}>← חזור לחודש הנוכחי</button>}
                </div>
                <div style={{ display:'flex', gap:7, flexWrap:'wrap' }}>
                  {orderMonths.map(mk => {
                    const sel = orderMonth === mk;
                    const cnt = orders.filter(o => (o.created_at||'').slice(0,7) === mk).length;
                    const isCur = mk === curMonth;
                    return (
                      <button key={mk} onClick={()=>setOrderMonth(mk)}
                        style={{ background: sel ? 'rgba(212,175,55,0.18)' : 'transparent',
                          border:`1px solid ${sel ? '#d4af37' : isCur ? 'rgba(37,211,102,0.4)' : '#2a3a6a'}`,
                          color: sel ? '#d4af37' : isCur ? '#25d366' : '#8a9aba',
                          borderRadius:20, padding:'5px 13px', cursor:'pointer', fontSize:'0.8rem', fontWeight: sel?800:500 }}>
                        {monthLabel(mk)} <span style={{ opacity:0.7 }}>({cnt})</span>
                        {isCur && !sel && <span style={{ marginRight:4 }}>•</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── בורר תצוגה: לעבודה / ארכיון ── */}
              <div style={{ display:'flex', gap:10, marginBottom:18 }}>
                {[
                  { id:'active',  label:'🔨 לעבודה',  sub:'טרם שולמו', count:activeCount,  color:'#d4af37' },
                  { id:'archive', label:'🗄 ארכיון',  sub:'הושלמו ושולמו', count:archiveCount, color:'#25d366' },
                ].map(v => {
                  const active = orderView === v.id;
                  return (
                    <button key={v.id} onClick={()=>setOrderView(v.id)} style={{
                      flex:1, background: active ? `${v.color}1a` : 'transparent',
                      border:`2px solid ${active ? v.color : '#1e3060'}`, borderRadius:12,
                      padding:'12px 18px', cursor:'pointer', textAlign:'right',
                      display:'flex', justifyContent:'space-between', alignItems:'center', gap:8
                    }}>
                      <div>
                        <div style={{ color: active ? v.color : '#8a9aba', fontWeight:900, fontSize:'1rem' }}>{v.label}</div>
                        <div style={{ color:'#4a5a7a', fontSize:'0.75rem', marginTop:2 }}>{v.sub}</div>
                      </div>
                      <span style={{ background: active ? v.color : '#1a2a4a', color: active ? '#000' : '#8a9aba', borderRadius:20, padding:'2px 12px', fontSize:'0.9rem', fontWeight:900 }}>{v.count}</span>
                    </button>
                  );
                })}
              </div>

              {/* ── חיפוש + פילטר עונה ── */}
              <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center', marginBottom:16 }}>
                <div style={{ position:'relative', flex:2, minWidth:220 }}>
                  <span style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}>🔍</span>
                  <input value={orderSearch} onChange={e=>setOrderSearch(e.target.value)}
                    placeholder="חפש לפי שם, מוצר, טלפון..."
                    style={{ width:'100%', padding:'9px 36px 9px 36px', background:'#0d1a35', border:`1px solid ${orderSearch?'#d4af37':'#2a3a6a'}`, borderRadius:8, color:'#fff', fontSize:'0.88rem', boxSizing:'border-box' }} />
                  {orderSearch && <button onClick={()=>setOrderSearch('')} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', color:'#aaa', cursor:'pointer', fontSize:'1rem' }}>×</button>}
                </div>
                <select value={orderSeasonFilter} onChange={e=>setOrderSeasonFilter(e.target.value)}
                  style={{ padding:'9px 14px', background:'#0d1a35', color:'#fff', border:'1px solid #2a3a6a', borderRadius:8, cursor:'pointer', fontSize:'0.85rem' }}>
                  <option value="all">כל העונות</option>
                  {allOrderSeasons.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
                <select value={orderSort} onChange={e=>setOrderSort(e.target.value)} title="מיון"
                  style={{ padding:'9px 14px', background:'#0d1a35', color:'#d4af37', border:'1px solid #2a3a6a', borderRadius:8, cursor:'pointer', fontSize:'0.85rem' }}>
                  <option value="newest">↕ מהחדש לישן</option>
                  <option value="oldest">↕ מהישן לחדש</option>
                  <option value="amount">💰 סכום גבוה תחילה</option>
                  <option value="status">🔵 לפי סטטוס</option>
                </select>
                <div style={{ color:'#556', fontSize:'0.82rem', marginRight:'auto' }}>{visibleOrders.length} הזמנות</div>
              </div>

              {/* ── רשימת הזמנות ── */}
              {visibleOrders.length === 0 ? (
                <div style={{ textAlign:'center', padding:48, color:'#445' }}>
                  <div style={{ fontSize:'2.5rem', marginBottom:12 }}>{orderView === 'active' ? '🎉' : '🗄'}</div>
                  <div>{orderView === 'active' ? `אין עבודות פתוחות ב${monthLabel(orderMonth)} — הכל טופל!` : `אין עבודות שהושלמו ב${monthLabel(orderMonth)}`}</div>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {visibleOrders.map(o => {
                    const isPending   = o.status === 'pending';
                    const isEdited    = o.status === 'edited';
                    const isCompleted = o.status === 'completed';
                    const isCustOrder = !!o.customer_id;
                    const borderColor = isCompleted ? '#25d366' : isEdited ? '#5a9bf5' : isPending ? (isCustOrder ? 'rgba(212,175,55,0.5)' : '#3a5aaa') : '#3a3a3a';
                    const bgColor     = isCompleted ? 'rgba(37,211,102,0.06)' : isEdited ? 'rgba(90,155,245,0.07)' : isPending ? (isCustOrder ? 'rgba(212,175,55,0.06)' : 'rgba(58,90,170,0.07)') : 'rgba(0,0,0,0.2)';
                    const parentName  = isCustOrder ? [o.c_first_name, o.c_last_name].filter(Boolean).join(' ') : (o.first_name||'לא משויך');
                    const childName   = isCustOrder ? [o.c_child_name, o.c_child_last_name].filter(Boolean).join(' ') : o.child_name;
                    const waPhone     = isCustOrder ? waNum(o.c_phone) : '';
                    return (
                      <div key={o.id} style={{ background:bgColor, border:`1px solid ${borderColor}`, borderRadius:12, padding:'14px 18px', display:'flex', gap:14, alignItems:'flex-start', flexWrap:'wrap' }}>
                        {/* Status stripe */}
                        <div style={{ width:4, alignSelf:'stretch', borderRadius:4, background:borderColor, flexShrink:0 }} />

                        {/* Info */}
                        <div style={{ flex:1, minWidth:200 }}>
                          {/* שם ההורה / מזמין */}
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
                            {!isCustOrder && <span style={{ color:'#d4af37', fontFamily:'monospace', fontSize:'0.82rem' }}>#{pad4(o.member_number)}</span>}
                            <span style={{ color:'#fff', fontWeight:900, fontSize: isCustOrder ? '1rem' : '0.95rem' }}>{parentName}</span>
                            {isCustOrder && waPhone && (
                              <a href={`https://wa.me/${waPhone}`} target="_blank" rel="noreferrer"
                                style={{ background:'rgba(37,211,102,0.15)', color:'#25d366', border:'1px solid rgba(37,211,102,0.3)', borderRadius:20, padding:'2px 10px', fontSize:'0.75rem', fontWeight:700, textDecoration:'none' }}>
                                💬 וואטסאפ
                              </a>
                            )}
                            <span style={{ background:isCompleted?'#0a2a1a':isEdited?'#0a1a3a':isPending?'#1a2a5a':'#2a1a1a', color:isCompleted?'#25d366':isEdited?'#5a9bf5':isPending?'#7a9aff':'#ff6b6b', borderRadius:20, padding:'2px 10px', fontSize:'0.72rem', fontWeight:700 }}>
                              {isCompleted?'🟢 שולם':isEdited?'✂️ נערך':isPending?'🔵 ממתין':'בוטל'}
                            </span>
                          </div>

                          {/* פרטי השחקן — בולטים, מהם מורכב הקליפ */}
                          {isCustOrder && (() => {
                            const year = o.c_birth_year || (o.c_child_dob ? new Date(o.c_child_dob).getFullYear() : null);
                            return (
                              <div style={{ background:'rgba(212,175,55,0.08)', border:'1px solid rgba(212,175,55,0.3)', borderRadius:10, padding:'10px 14px', marginBottom:8 }}>
                                <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:6 }}>
                                  <span style={{ color:'#fff', fontWeight:900, fontSize:'1.05rem' }}>⚽ {childName || '—'}</span>
                                  {o.jersey_number && <span style={{ background:'#d4af37', color:'#000', borderRadius:8, padding:'2px 10px', fontWeight:900, fontSize:'0.85rem' }}>חולצה #{o.jersey_number}</span>}
                                </div>
                                <div style={{ display:'flex', flexWrap:'wrap', gap:'4px 16px', fontSize:'0.83rem' }}>
                                  {(() => { const lbl = fullTeamLabel({ team:o.c_team, age_group:o.c_age_group, birth_year:o.c_birth_year, child_dob:o.c_child_dob });
                                    return lbl ? <span style={{ color:'#d4af37', fontWeight:800 }}>🏟 {lbl}</span> : null; })()}
                                  {o.c_positions && <span style={{ color:'#c0cce6' }}>📍 {o.c_positions}</span>}
                                  {o.opponent_team && <span style={{ color:'#c0cce6' }}>🆚 {o.opponent_team}</span>}
                                </div>
                              </div>
                            );
                          })()}
                          {!isCustOrder && childName && <div style={{ color:'#8a9aba', fontSize:'0.8rem', marginBottom:4 }}>⚽ {childName}</div>}

                          {/* פרטי ההזמנה */}
                          <div style={{ fontSize:'0.85rem' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                              <span style={{ fontWeight:700, color: isCustOrder ? '#e0ccff' : '#c0cce6', fontSize: isCustOrder ? '0.95rem' : '0.88rem' }}>{o.product_name}{o.quantity>1 && ` ×${o.quantity}`}</span>
                              {editingOrderPrice?.id === o.id ? (
                                <div style={{ display:'flex', gap:5, alignItems:'center' }}>
                                  <input type="number" autoFocus value={editingOrderPrice.value}
                                    onChange={e => setEditingOrderPrice(p => ({ ...p, value: e.target.value }))}
                                    style={{ width:80, background:'#0a1a3e', border:'1px solid #d4af37', borderRadius:8, padding:'4px 8px', color:'#d4af37', fontSize:'1rem', fontWeight:900, textAlign:'center' }} />
                                  <button onClick={async () => {
                                    const price = parseFloat(editingOrderPrice.value) || 0;
                                    await axios.put(API+'/orders/'+o.id, { ...o, unit_price: price, total: price, discount_pct: 0, quantity: 1 });
                                    setOrders(prev => prev.map(x => x.id === o.id ? { ...x, total: price, unit_price: price } : x));
                                    setEditingOrderPrice(null);
                                    flash('✅ מחיר עודכן');
                                  }} style={{ background:'#d4af37', color:'#000', border:'none', borderRadius:6, padding:'4px 9px', cursor:'pointer', fontWeight:800, fontSize:'0.8rem' }}>✓</button>
                                  <button onClick={() => setEditingOrderPrice(null)} style={{ background:'none', border:'none', color:'#888', cursor:'pointer', fontSize:'1rem' }}>✕</button>
                                </div>
                              ) : (
                                <span onClick={() => setEditingOrderPrice({ id: o.id, value: String(o.total || o.unit_price || '') })}
                                  style={{ color: (o.total || o.unit_price) ? '#d4af37' : '#ff8888', fontWeight:900, fontSize: isCustOrder ? '1.3rem' : '1.05rem', lineHeight:1, cursor:'pointer' }}
                                  title="לחץ לעריכת מחיר">
                                  {(o.total || o.unit_price) ? fmtPrice(o.total || o.unit_price) : '₪ — הגדר מחיר'} <span style={{ fontSize:'0.7rem', color:'#4a5a7a' }}>✏️</span>
                                </span>
                              )}
                            </div>
                            <span style={{ color:'#3a4a6a', fontSize:'0.78rem' }}>{o.season||''} {o.season?'|':''} {fmtDate(o.created_at)}</span>
                          </div>

                          {/* ── דדליין ── */}
                          {(() => {
                            if (!o.deadline) return isCompleted ? null : (
                              <div style={{ color:'#4a5a7a', fontSize:'0.78rem', marginTop:5 }}>⏳ ללא דדליין — קבע דרך ✏️ עריכה</div>
                            );
                            const dl = new Date(o.deadline + 'T23:59:59');
                            const daysLeft = Math.ceil((dl - new Date()) / (1000*60*60*24));
                            const overdue = daysLeft < 0, urgent = daysLeft >= 0 && daysLeft <= 2;
                            const col = isCompleted ? '#4a5a7a' : overdue ? '#ff6b6b' : urgent ? '#ffaa44' : '#7ab0ff';
                            return (
                              <div style={{ display:'inline-flex', alignItems:'center', gap:6, marginTop:5,
                                background:`${col}1a`, border:`1px solid ${col}66`, borderRadius:8, padding:'3px 10px' }}>
                                <span style={{ fontSize:'0.78rem' }}>⏳</span>
                                <span style={{ color:col, fontWeight:800, fontSize:'0.8rem' }}>
                                  דדליין: {new Date(o.deadline).toLocaleDateString('he-IL')}
                                  {!isCompleted && (overdue ? ` · באיחור ${Math.abs(daysLeft)} ימים` : daysLeft === 0 ? ' · היום!' : ` · בעוד ${daysLeft} ימים`)}
                                </span>
                              </div>
                            );
                          })()}

                          {o.notes && <div style={{ color:'#3a4a6a', fontSize:'0.75rem', marginTop:4 }}>💬 {o.notes}</div>}

                          {/* ── פאנל עריכת ההזמנה ── */}
                          {editingWorkOrder === o.id && (
                            <div style={{ marginTop:10, background:'rgba(255,150,50,0.06)', border:'1px solid rgba(255,150,50,0.35)', borderRadius:10, padding:'12px 14px' }}>
                              <div style={{ color:'#ffaa44', fontWeight:900, fontSize:'0.85rem', marginBottom:10 }}>✏️ עריכת ההזמנה</div>
                              {products.filter(p => p.product_type === 'sale').length > 0 && (
                                <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:9 }}>
                                  {products.filter(p => p.product_type === 'sale').map(p => {
                                    const sel = editWorkData.product_name === p.name;
                                    return (
                                      <button key={p.id} type="button"
                                        onClick={()=>setEditWorkData(d=>({ ...d, product_name:p.name, unit_price:String(p.price) }))}
                                        style={{ padding:'5px 11px', borderRadius:8, border:`1px solid ${sel?'#d4af37':'#2a3a6a'}`, background:sel?'rgba(212,175,55,0.18)':'#0a1525', cursor:'pointer', fontSize:'0.78rem', color:sel?'#d4af37':'#c0cce6' }}>
                                        {p.name} · ₪{p.price}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:8, marginBottom:9 }}>
                                <div>
                                  <div style={{ color:'#8a9aba', fontSize:'0.72rem', marginBottom:3 }}>מוצר</div>
                                  <input value={editWorkData.product_name||''} placeholder="שם המוצר"
                                    onChange={e=>setEditWorkData(d=>({ ...d, product_name:e.target.value }))}
                                    style={{ width:'100%', background:'#0a1a3e', border:'1px solid #2a3a6a', borderRadius:8, padding:'7px 10px', color:'#fff', fontSize:'0.85rem', boxSizing:'border-box' }} />
                                  {(() => {
                                    const n = (editWorkData.product_name||'').trim();
                                    const inCat = products.some(p => (p.name||'').trim() === n);
                                    if (!n || inCat) return null;
                                    return <div style={{ color:'#ff9966', fontSize:'0.7rem', marginTop:3 }}>⚠️ לא בקטלוג מוצרי יוסטאר</div>;
                                  })()}
                                </div>
                                <div>
                                  <div style={{ color:'#8a9aba', fontSize:'0.72rem', marginBottom:3 }}>מחיר ₪</div>
                                  <input type="number" value={editWorkData.unit_price||''} placeholder="0"
                                    onChange={e=>setEditWorkData(d=>({ ...d, unit_price:e.target.value }))}
                                    style={{ width:'100%', background:'#0a1a3e', border:'1px solid #2a3a6a', borderRadius:8, padding:'7px 10px', color:'#d4af37', fontWeight:800, fontSize:'0.85rem', boxSizing:'border-box' }} />
                                </div>
                                <div>
                                  <div style={{ color:'#8a9aba', fontSize:'0.72rem', marginBottom:3 }}>⏳ דדליין</div>
                                  <input type="date" value={editWorkData.deadline||''}
                                    onChange={e=>setEditWorkData(d=>({ ...d, deadline:e.target.value }))}
                                    style={{ width:'100%', background:'#0a1a3e', border:'1px solid #2a3a6a', borderRadius:8, padding:'7px 10px', color:'#fff', fontSize:'0.85rem', boxSizing:'border-box' }} />
                                </div>
                              </div>
                              <div style={{ marginBottom:10 }}>
                                <div style={{ color:'#8a9aba', fontSize:'0.72rem', marginBottom:3 }}>הערות</div>
                                <input value={editWorkData.notes||''} placeholder="פרטים נוספים לעבודה..."
                                  onChange={e=>setEditWorkData(d=>({ ...d, notes:e.target.value }))}
                                  style={{ width:'100%', background:'#0a1a3e', border:'1px solid #2a3a6a', borderRadius:8, padding:'7px 10px', color:'#c0cce6', fontSize:'0.85rem', boxSizing:'border-box' }} />
                              </div>
                              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                                <button onClick={async()=>{
                                  const { data } = await axios.put(API+'/orders/'+o.id+'/workboard', {
                                    product_name: editWorkData.product_name, unit_price: editWorkData.unit_price,
                                    deadline: editWorkData.deadline ?? '', notes: editWorkData.notes,
                                  });
                                  setOrders(prev => prev.map(x => x.id===o.id ? { ...x, ...data } : x));
                                  setEditingWorkOrder(null);
                                  flash('✅ ההזמנה עודכנה');
                                }} style={{ background:'linear-gradient(135deg,#d4af37,#b8941f)', color:'#000', border:'none', borderRadius:8, padding:'8px 20px', cursor:'pointer', fontWeight:900, fontSize:'0.82rem' }}>💾 שמור</button>
                                {editWorkData.deadline && (
                                  <button onClick={()=>setEditWorkData(d=>({ ...d, deadline:'' }))}
                                    style={{ background:'transparent', color:'#8a9aba', border:'1px solid #2a3a6a', borderRadius:8, padding:'8px 14px', cursor:'pointer', fontSize:'0.8rem' }}>נקה דדליין</button>
                                )}
                                <button onClick={()=>setEditingWorkOrder(null)}
                                  style={{ background:'transparent', color:'#8a9aba', border:'1px solid #2a3a6a', borderRadius:8, padding:'8px 14px', cursor:'pointer', fontSize:'0.8rem' }}>ביטול</button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div style={{ display:'flex', gap:6, flexShrink:0, flexDirection:'column', alignItems:'flex-end' }}>
                          <button onClick={()=>{
                            if (editingWorkOrder === o.id) { setEditingWorkOrder(null); return; }
                            setEditingWorkOrder(o.id);
                            setEditWorkData({ product_name:o.product_name||'', unit_price:String(o.total||o.unit_price||''), deadline:(o.deadline||'').slice(0,10), notes:o.notes||'' });
                          }} style={{ background:'rgba(255,150,50,0.12)', color:'#ffaa44', border:'1px solid rgba(255,150,50,0.4)', borderRadius:8, padding:'7px 12px', cursor:'pointer', fontWeight:800, fontSize:'0.82rem', whiteSpace:'nowrap' }}>
                            ✏️ ערוך {editingWorkOrder === o.id ? '▲' : ''}
                          </button>
                          {isCustOrder && !isCompleted && (
                            <>
                              <button onClick={async()=>{
                                await axios.put(API+'/orders/'+o.id+'/status', { status:'edited' });
                                setOrders(prev=>prev.map(x=>x.id===o.id?{...x,status:'edited'}:x));
                                flash('✂️ עריכה הושלמה!');
                              }} style={{ background: o.status==='edited' ? 'rgba(90,155,245,0.2)' : 'rgba(90,155,245,0.08)', color:'#5a9bf5', border:`1px solid ${o.status==='edited'?'#5a9bf5':'rgba(90,155,245,0.35)'}`, borderRadius:8, padding:'7px 12px', cursor:'pointer', fontWeight:800, fontSize:'0.82rem', whiteSpace:'nowrap' }}>
                                {o.status==='edited' ? '✂️ נערך ✓' : '✂️ סיימתי עריכה'}
                              </button>
                              <button onClick={async()=>{
                                const price = parseFloat(o.total || o.unit_price) || 0;
                                if (price === 0) { flash('⚠️ יש להגדיר מחיר לפני רישום תשלום — לחץ על ₪ לעריכה', 'error'); return; }
                                await axios.put(API+'/orders/'+o.id+'/status', { status:'completed' });
                                setOrders(prev=>prev.map(x=>x.id===o.id?{...x,status:'completed'}:x));
                                axios.get(API+'/income').then(r => setIncomeLog(r.data));
                                flash('💰 תשלום התקבל! ההכנסה נרשמה בקופת יוסטאר');
                              }} style={{ background:'linear-gradient(135deg,#1a4a2a,#0d3a1a)', color:'#25d366', border:'1px solid rgba(37,211,102,0.5)', borderRadius:8, padding:'7px 12px', cursor:'pointer', fontWeight:800, fontSize:'0.82rem', whiteSpace:'nowrap' }}>
                                💰 קיבלתי תשלום
                              </button>
                            </>
                          )}
                          {isCustOrder && isCompleted && (
                            <button onClick={async()=>{
                              if (!window.confirm('לפתוח מחדש הזמנה זו? ההכנסה תימחק מקופת יוסטאר.')) return;
                              await axios.put(API+'/orders/'+o.id+'/status', { status:'pending' });
                              setOrders(prev=>prev.map(x=>x.id===o.id?{...x,status:'pending'}:x));
                              axios.get(API+'/income').then(r => setIncomeLog(r.data));
                              flash('🔄 הזמנה נפתחה מחדש');
                            }} style={{ background:'rgba(255,150,50,0.1)', color:'#ffaa44', border:'1px solid rgba(255,150,50,0.35)', borderRadius:8, padding:'6px 10px', cursor:'pointer', fontWeight:700, fontSize:'0.78rem', whiteSpace:'nowrap' }}>
                              🔄 פתח מחדש
                            </button>
                          )}
                          {!isCustOrder && isPending && (
                            <button onClick={async()=>{
                              await axios.put(API+'/orders/'+o.id, {...o, status:'completed'});
                              setOrders(prev=>prev.map(x=>x.id===o.id?{...x,status:'completed'}:x));
                              flash('✅ תשלום אושר!');
                            }} style={{ background:'#0a3a1a', color:'#25d366', border:'1px solid #25d366', borderRadius:8, padding:'7px 12px', cursor:'pointer', fontWeight:800, fontSize:'0.82rem', whiteSpace:'nowrap' }}>
                              ✅ תשלום עבר
                            </button>
                          )}
                          {o.member_id && (
                            <button onClick={()=>{ const mem=members.find(m=>m.id===o.member_id); if(mem) openMemberOrders(mem); }}
                              style={{ background:'#1a2a5a', color:'#7a9aff', border:'1px solid #3a5aaa', borderRadius:8, padding:'6px 10px', cursor:'pointer', fontSize:'0.78rem', fontWeight:600 }}>
                              👤 כרטיס
                            </button>
                          )}
                          <button onClick={async()=>{
                            if (!window.confirm('למחוק הזמנה זו?')) return;
                            await axios.delete(API+'/orders/'+o.id);
                            setOrders(prev=>prev.filter(x=>x.id!==o.id));
                            if (isCompleted) axios.get(API+'/income').then(r => setIncomeLog(r.data));
                            flash('🗑 הזמנה נמחקה');
                          }} style={{ background:'rgba(255,80,80,0.07)', color:'#ff8888', border:'1px solid rgba(255,80,80,0.25)', borderRadius:8, padding:'5px 10px', cursor:'pointer', fontSize:'0.75rem' }}>
                            🗑
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* ══════ USTAR BENEFITS TAB ══════ */}
        {tab === 'club-benefits' && (() => {
          const benefitProducts = products.filter(p => p.product_type === 'benefit');
          const benefitAudienceMap = {
            all:      members,
            customer: members.filter(m => orders.some(o=>o.member_id===m.id)),
            prospect: members.filter(m => !orders.some(o=>o.member_id===m.id)),
          };
          const benefitAudienceLabels = {
            all:      `כל החברים (${members.length})`,
            customer: `חברים פעילים (${benefitAudienceMap.customer.length})`,
            prospect: `לא פעילים (${benefitAudienceMap.prospect.length})`,
          };

          return (
            <div>
              {/* Header */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
                <div>
                  <h2 style={{ color:'#d4af37', margin:0 }}>🎁 הטבות יוסטאר</h2>
                  <p style={{ color:'#aaa', margin:'4px 0 0', fontSize:'0.88rem' }}>הטבות לחברי המועדון — ניהול, שיתוף ומעקב מימוש</p>
                </div>
                <button onClick={()=>setBenefitShowForm(v=>!v)}
                  style={{ background:'linear-gradient(135deg,#d4af37,#b8941f)', color:'#000', border:'none', borderRadius:10, padding:'10px 22px', cursor:'pointer', fontWeight:800, fontSize:'0.9rem' }}>
                  + הטבה חדשה
                </button>
              </div>

              {benefitShowForm && (
                <div style={{ background:'#0d1a35', border:'2px solid #d4af37', borderRadius:14, padding:20, marginBottom:24 }}>
                  <h3 style={{ color:'#d4af37', margin:'0 0 16px' }}>✨ יצירת הטבה חדשה</h3>
                  <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:12 }}>
                    <input value={benefitNewName} onChange={e=>setBenefitNewName(e.target.value)}
                      placeholder="שם ההטבה (לדוגמה: ג'רזי אישי של יוסטאר)"
                      style={{ flex:2, minWidth:200, padding:'10px 14px', background:'#0a1525', border:'1px solid #2a3a6a', borderRadius:8, color:'#fff', fontSize:'0.9rem' }} />
                    <input value={benefitNewDesc} onChange={e=>setBenefitNewDesc(e.target.value)}
                      placeholder="תיאור קצר (אופציונלי)"
                      style={{ flex:3, minWidth:200, padding:'10px 14px', background:'#0a1525', border:'1px solid #2a3a6a', borderRadius:8, color:'#fff', fontSize:'0.9rem' }} />
                  </div>
                  {/* וריאציות */}
                  <div style={{ marginBottom:14 }}>
                    <div style={{ color:'#aaa', fontSize:'0.8rem', marginBottom:6 }}>🔀 חלקים / וריאציות (אופציונלי) — למשל: דיגיטלי, מודפס</div>
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:8 }}>
                      {benefitNewVariants.map((v,i)=>(
                        <span key={i} style={{ background:'#1a2a5a', color:'#7a9aff', borderRadius:20, padding:'3px 12px', fontSize:'0.82rem', display:'flex', alignItems:'center', gap:6 }}>
                          {v}
                          <button onClick={()=>setBenefitNewVariants(prev=>prev.filter((_,j)=>j!==i))}
                            style={{ background:'none', border:'none', color:'#ff6b6b', cursor:'pointer', fontSize:'0.9rem', padding:0, lineHeight:1 }}>×</button>
                        </span>
                      ))}
                    </div>
                    <div style={{ display:'flex', gap:8 }}>
                      <input value={benefitNewVariantInput} onChange={e=>setBenefitNewVariantInput(e.target.value)}
                        onKeyDown={e=>{ if((e.key==='Enter'||e.key===',')&&benefitNewVariantInput.trim()){ e.preventDefault(); setBenefitNewVariants(prev=>[...prev,benefitNewVariantInput.trim()]); setBenefitNewVariantInput(''); }}}
                        placeholder="הוסף חלק (Enter לאישור)"
                        style={{ flex:1, padding:'7px 12px', background:'#0a1525', border:'1px solid #2a3a6a', borderRadius:8, color:'#fff', fontSize:'0.85rem' }} />
                      <button onClick={()=>{ if(benefitNewVariantInput.trim()){ setBenefitNewVariants(prev=>[...prev,benefitNewVariantInput.trim()]); setBenefitNewVariantInput(''); }}}
                        style={{ background:'#1a2a5a', color:'#7a9aff', border:'1px solid #3a5aaa', borderRadius:8, padding:'7px 14px', cursor:'pointer', fontWeight:700 }}>+ הוסף</button>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:8 }}>
                    <button onClick={async()=>{
                      if(!benefitNewName.trim()) return;
                      const { data } = await axios.post(API+'/products', { name:benefitNewName.trim(), description:benefitNewDesc.trim(), price:0, member_discount_pct:0, category:'הטבה', product_type:'benefit', variants:benefitNewVariants });
                      setProducts(prev=>[...prev, {...data, variants: benefitNewVariants}]);
                      setBenefitNewName(''); setBenefitNewDesc(''); setBenefitNewVariants([]); setBenefitNewVariantInput('');
                      setBenefitShowForm(false);
                      flash('✅ הטבה נוצרה!');
                    }} style={{ background:'#d4af37', color:'#000', border:'none', borderRadius:8, padding:'9px 22px', cursor:'pointer', fontWeight:800 }}>שמור הטבה</button>
                    <button onClick={()=>{ setBenefitShowForm(false); setBenefitNewVariants([]); setBenefitNewVariantInput(''); }}
                      style={{ background:'transparent', color:'#aaa', border:'1px solid #2a3a6a', borderRadius:8, padding:'9px 18px', cursor:'pointer' }}>ביטול</button>
                  </div>
                </div>
              )}

              {/* קלף הכוכבים — ראשון תמיד */}
              {(() => {
                const starMsg = `🎴 ברוכים הבאים לליגת הכוכבים של יוסטאר!\n\n*קלף הכוכבים שלך מחכה!* 🌟\nקלף כדורגל אישי מרהיב עם תמונת השחקן/ת — מתנת הצטרפות מאיתנו.\n\nלשליחת תמונה ומימוש ההטבה: 054-6318704\n\nמועדון יוסטאר ⭐`;
                return (
                  <div style={{ background:'linear-gradient(135deg,rgba(37,211,102,0.08),rgba(37,211,102,0.03))', border:'2px solid rgba(37,211,102,0.4)', borderRadius:14, padding:20, marginBottom:16 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
                      <div style={{ display:'flex', gap:14, alignItems:'center' }}>
                        <div style={{ fontSize:'2.8rem' }}>🎴</div>
                        <div>
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                            <span style={{ color:'#25d366', fontWeight:800, fontSize:'1.05rem' }}>קלף הכוכבים של יוסטאר</span>
                            <span style={{ background:'#25d366', color:'#0a2a0a', borderRadius:20, padding:'2px 10px', fontSize:'0.72rem', fontWeight:700 }}>📌 קבוע</span>
                          </div>
                          <div style={{ color:'#aaa', fontSize:'0.85rem', marginBottom:6 }}>מתנת הצטרפות לכל חבר מועדון — קלף בעיצוב אישי עם תמונת השחקן</div>
                          <div style={{ display:'flex', gap:16 }}>
                            <span style={{ fontSize:'0.78rem', color:'#25d366' }}>✅ דיגיטלי (WA): {members.filter(m=>m.star_card_digital).length}</span>
                            <span style={{ fontSize:'0.78rem', color:'#25d366' }}>✅ מודפס: {members.filter(m=>m.star_card_printed).length}</span>
                            <span style={{ fontSize:'0.78rem', color:'#556' }}>⬜ טרם: {members.filter(m=>!m.star_card_digital&&!m.star_card_printed).length}</span>
                          </div>
                        </div>
                      </div>
                      <button onClick={()=>setBenefitShareId(benefitShareId==='star'?null:'star')}
                        style={{ background: benefitShareId==='star'?'rgba(37,211,102,0.2)':'#1a3a2a', color:'#25d366', border:'1px solid #25d366', borderRadius:10, padding:'8px 18px', cursor:'pointer', fontWeight:700, fontSize:'0.85rem' }}>
                        📣 שתף {benefitShareId==='star'?'▲':'▼'}
                      </button>
                    </div>
                    {benefitShareId === 'star' && (
                      <div style={{ marginTop:14, borderTop:'1px solid rgba(37,211,102,0.2)', paddingTop:14 }}>
                        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
                          {['all','customer','prospect'].map(a=>(
                            <button key={a} onClick={()=>setBenefitShareAudience(a)} style={{
                              padding:'5px 13px', borderRadius:20, fontSize:'0.78rem', fontWeight:700, cursor:'pointer',
                              border:`1px solid ${benefitShareAudience===a?'#25d366':'#2a3a6a'}`,
                              background: benefitShareAudience===a?'rgba(37,211,102,0.15)':'transparent',
                              color: benefitShareAudience===a?'#25d366':'#556'
                            }}>{benefitAudienceLabels[a]}</button>
                          ))}
                        </div>
                        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                          {benefitAudienceMap[benefitShareAudience].slice(0,6).map(m2=>(
                            <a key={m2.id} href={waLink(m2.phone, starMsg)} target="_blank" rel="noopener noreferrer"
                              style={{ background:'#25d366', color:'#fff', padding:'5px 13px', borderRadius:8, textDecoration:'none', fontWeight:700, fontSize:'0.78rem' }}>
                              📱 {m2.first_name}
                            </a>
                          ))}
                          {benefitAudienceMap[benefitShareAudience].filter(m2=>m2.email).length>0 && (
                            <button onClick={async()=>{
                              setBenefitSending(true);
                              try {
                                const we = benefitAudienceMap[benefitShareAudience].filter(m2=>m2.email);
                                await axios.post(API+'/send-bulk-email', { memberIds:we.map(m2=>m2.id), subject:'קלף הכוכבים שלך מחכה! 🎴', html:`<div dir="rtl"><h2>קלף הכוכבים של יוסטאר</h2><p>מתנת ההצטרפות שלך מחכה!</p><p>שלחו לנו תמונה בוואטסאפ: 054-6318704</p></div>` });
                                flash(`✅ מייל נשלח ל-${we.length} חברים!`);
                              } catch(e){ flash('שגיאה','error'); }
                              setBenefitSending(false);
                            }} disabled={benefitSending}
                              style={{ background:'#1a3a8e', color:'#fff', border:'none', borderRadius:8, padding:'5px 13px', cursor:'pointer', fontWeight:700, fontSize:'0.78rem' }}>
                              {benefitSending?'⏳...':'📧 מייל'}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* שאר הטבות */}
              {benefitProducts.length === 0 ? (
                <div style={{ textAlign:'center', padding:40, color:'#445', border:'1px dashed #1a2a4a', borderRadius:14 }}>
                  <div style={{ fontSize:'2.5rem', marginBottom:10 }}>🎁</div>
                  <div style={{ color:'#556' }}>אין הטבות נוספות עדיין</div>
                  <div style={{ color:'#334', fontSize:'0.82rem', marginTop:6 }}>לחץ "+ הטבה חדשה" כדי ליצור</div>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                  {benefitProducts.map(bp => {
                    const isOpen = benefitShareId === bp.id;
                    const shareTarget = benefitAudienceMap[isOpen ? benefitShareAudience : 'all'];
                    const shareMsg = `🎁 הטבה בלעדית לחברי מועדון יוסטאר!\n\n*${bp.name}*\n${bp.description||''}\n\nלמימוש ופרטים: 054-6318704\n\nליגת הכוכבים של יוסטאר ⭐`;
                    const bpVariants = (() => { try { return JSON.parse(bp.variants||'[]'); } catch(e){ return []; } })();
                    const isEditingVariants = benefitEditVariantsId === bp.id;
                    return (
                      <div key={bp.id} style={{ background:'#0d1a35', border:'1px solid #2a3a6a', borderRadius:14, overflow:'hidden' }}>
                        <div style={{ padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>
                          <div style={{ flex:1, minWidth:200 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                              <span style={{ fontSize:'1.3rem' }}>🎁</span>
                              <span style={{ color:'#fff', fontWeight:700, fontSize:'1rem' }}>{bp.name}</span>
                            </div>
                            {bp.description && <div style={{ color:'#aaa', fontSize:'0.83rem', marginBottom:6 }}>{bp.description}</div>}
                            {/* וריאציות */}
                            {bpVariants.length > 0 && (
                              <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:4 }}>
                                {bpVariants.map((v,i)=>(
                                  <span key={i} style={{ background:'rgba(122,154,255,0.12)', color:'#7a9aff', border:'1px solid #3a4a8a', borderRadius:20, padding:'2px 10px', fontSize:'0.76rem', fontWeight:600 }}>
                                    {v}
                                  </span>
                                ))}
                                <button onClick={()=>{ setBenefitEditVariantsId(isEditingVariants?null:bp.id); setBenefitEditVariantInput(''); }}
                                  style={{ background:'none', border:'none', color:'#445', cursor:'pointer', fontSize:'0.75rem', padding:'2px 6px' }}>✏️ ערוך</button>
                              </div>
                            )}
                            {bpVariants.length === 0 && (
                              <button onClick={()=>{ setBenefitEditVariantsId(isEditingVariants?null:bp.id); setBenefitEditVariantInput(''); }}
                                style={{ background:'#0a1525', color:'#445', border:'1px dashed #2a3a5a', borderRadius:8, padding:'3px 12px', cursor:'pointer', fontSize:'0.76rem', marginTop:4 }}>
                                + הוסף חלקים / וריאציות
                              </button>
                            )}
                            {/* עריכת וריאציות inline */}
                            {isEditingVariants && (
                              <div style={{ marginTop:10, background:'#050c1a', borderRadius:8, padding:10 }}>
                                <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
                                  {bpVariants.map((v,i)=>(
                                    <span key={i} style={{ background:'#1a2a5a', color:'#7a9aff', borderRadius:20, padding:'3px 10px', fontSize:'0.8rem', display:'flex', alignItems:'center', gap:5 }}>
                                      {v}
                                      <button onClick={async()=>{
                                        const updated = bpVariants.filter((_,j)=>j!==i);
                                        await axios.put(API+'/products/'+bp.id, {...bp, variants:updated});
                                        setProducts(prev=>prev.map(p=>p.id===bp.id?{...p,variants:JSON.stringify(updated)}:p));
                                      }} style={{ background:'none', border:'none', color:'#ff6b6b', cursor:'pointer', fontSize:'0.85rem', padding:0 }}>×</button>
                                    </span>
                                  ))}
                                </div>
                                <div style={{ display:'flex', gap:6 }}>
                                  <input value={benefitEditVariantInput} onChange={e=>setBenefitEditVariantInput(e.target.value)}
                                    onKeyDown={async e=>{ if(e.key==='Enter'&&benefitEditVariantInput.trim()){ e.preventDefault(); const updated=[...bpVariants,benefitEditVariantInput.trim()]; await axios.put(API+'/products/'+bp.id,{...bp,variants:updated}); setProducts(prev=>prev.map(p=>p.id===bp.id?{...p,variants:JSON.stringify(updated)}:p)); setBenefitEditVariantInput(''); }}}
                                    placeholder="שם החלק (Enter לאישור)"
                                    style={{ flex:1, padding:'6px 10px', background:'#0a1525', border:'1px solid #2a3a6a', borderRadius:6, color:'#fff', fontSize:'0.82rem' }} />
                                  <button onClick={async()=>{
                                    if(!benefitEditVariantInput.trim()) return;
                                    const updated=[...bpVariants,benefitEditVariantInput.trim()];
                                    await axios.put(API+'/products/'+bp.id,{...bp,variants:updated});
                                    setProducts(prev=>prev.map(p=>p.id===bp.id?{...p,variants:JSON.stringify(updated)}:p));
                                    setBenefitEditVariantInput('');
                                  }} style={{ background:'#1a2a5a', color:'#7a9aff', border:'1px solid #3a5aaa', borderRadius:6, padding:'6px 12px', cursor:'pointer', fontWeight:700 }}>+</button>
                                  <button onClick={()=>setBenefitEditVariantsId(null)}
                                    style={{ background:'#1a0a2a', color:'#aaa', border:'1px solid #3a2a5a', borderRadius:6, padding:'6px 10px', cursor:'pointer', fontSize:'0.8rem' }}>סגור</button>
                                </div>
                              </div>
                            )}
                          </div>
                          <div style={{ display:'flex', gap:8 }}>
                            <button onClick={()=>{ setBenefitShareId(isOpen?null:bp.id); setBenefitShareAudience('all'); }}
                              style={{ background: isOpen?'rgba(122,154,255,0.15)':'#1a2a5a', color:'#7a9aff', border:'1px solid #3a5aaa', borderRadius:8, padding:'7px 16px', cursor:'pointer', fontWeight:700, fontSize:'0.82rem' }}>
                              📣 שתף {isOpen?'▲':'▼'}
                            </button>
                            <button onClick={async()=>{
                              if(!window.confirm(`למחוק "${bp.name}"?`)) return;
                              await axios.delete(API+'/products/'+bp.id);
                              setProducts(prev=>prev.filter(x=>x.id!==bp.id));
                              if(benefitShareId===bp.id) setBenefitShareId(null);
                              flash('הטבה נמחקה');
                            }} style={{ background:'#3a0a0a', color:'#ff6b6b', border:'1px solid #ff6b6b', borderRadius:8, padding:'7px 12px', cursor:'pointer' }}>✕</button>
                          </div>
                        </div>
                        {isOpen && (
                          <div style={{ background:'#060e1c', borderTop:'1px solid #1a2a4a', padding:'14px 20px' }}>
                            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
                              {['all','customer','prospect'].map(a=>(
                                <button key={a} onClick={()=>setBenefitShareAudience(a)} style={{
                                  padding:'5px 13px', borderRadius:20, fontSize:'0.78rem', fontWeight:700, cursor:'pointer',
                                  border:`1px solid ${benefitShareAudience===a?'#d4af37':'#2a3a6a'}`,
                                  background: benefitShareAudience===a?'rgba(212,175,55,0.12)':'transparent',
                                  color: benefitShareAudience===a?'#d4af37':'#556'
                                }}>{benefitAudienceLabels[a]}</button>
                              ))}
                            </div>
                            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                              {benefitAudienceMap[benefitShareAudience].slice(0,6).map(m2=>(
                                <a key={m2.id} href={waLink(m2.phone, shareMsg)} target="_blank" rel="noopener noreferrer"
                                  style={{ background:'#25d366', color:'#fff', padding:'5px 13px', borderRadius:8, textDecoration:'none', fontWeight:700, fontSize:'0.78rem' }}>
                                  📱 {m2.first_name}
                                </a>
                              ))}
                              {shareTarget.filter(m2=>m2.email).length>0 && (
                                <button onClick={async()=>{
                                  setBenefitSending(true);
                                  try {
                                    const we = shareTarget.filter(m2=>m2.email);
                                    await axios.post(API+'/send-bulk-email', { memberIds:we.map(m2=>m2.id), subject:`הטבה בלעדית: ${bp.name}`, html:`<div dir="rtl"><h2>${bp.name}</h2><p>${bp.description||''}</p><p>לפרטים ומימוש: 054-6318704</p></div>` });
                                    flash(`✅ מייל נשלח ל-${we.length} חברים!`);
                                  } catch(e){ flash('שגיאה','error'); }
                                  setBenefitSending(false);
                                }} disabled={benefitSending}
                                  style={{ background:'#1a3a8e', color:'#fff', border:'none', borderRadius:8, padding:'5px 13px', cursor:'pointer', fontWeight:700, fontSize:'0.78rem' }}>
                                  {benefitSending?'⏳...':'📧 מייל'}
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );

          // eslint-disable-next-line no-unreachable
          void partners; void benefits; void benefitFilter; void setBenefitFilter; void showBenefitForm; void setShowBenefitForm; void newBenefit; void setNewBenefit; void dealLabel; void openDealCampaign;
          const partnerDeals = partners.flatMap(p =>
            (p.deals||[]).map(d => ({
              id: `pd-${d.id}`, type:'partner', partnerName:p.name, partnerEmoji:p.logo_emoji,
              title: d.title, description: d.description, category: p.category,
              discount: dealLabel(d), coupon_code: d.coupon_code, website: p.website,
              valid_until: d.valid_until, rawDeal: d, rawPartner: p
            }))
          );
          const manualBenefits = benefits.map(b => ({ ...b, id:`b-${b.id}`, type:'manual', rawId: b.id }));
          const allItems = [...partnerDeals, ...manualBenefits];
          const categories = ['all', ...new Set(allItems.map(x=>x.category).filter(Boolean))];
          const filtered = benefitFilter==='all' ? allItems : allItems.filter(x=>x.category===benefitFilter);

          return (
            <div>
              {/* Header */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <div>
                  <h2 style={{ color:'#d4af37', margin:0 }}>🎁 הטבות לחברי המועדון</h2>
                  <p style={{ color:'#aaa', margin:'4px 0 0', fontSize:'0.9rem' }}>כל ההטבות הפעילות לחברי מועדון ההטבות של יוסטאר</p>
                </div>
                <button onClick={()=>setShowBenefitForm(!showBenefitForm)} className="btn btn-gold" style={{ padding:'10px 22px' }}>
                  + הוסף הטבה
                </button>
              </div>

              {/* Stats */}
              <div className="stats-row" style={{ marginBottom:20 }}>
                <div className="stat-card"><div className="stat-number">{allItems.length}</div><div className="stat-label">הטבות פעילות</div></div>
                <div className="stat-card"><div className="stat-number">{partners.length}</div><div className="stat-label">שותפים</div></div>
                <div className="stat-card"><div className="stat-number">{allItems.filter(x=>x.coupon_code).length}</div><div className="stat-label">קופונים</div></div>
              </div>

              {/* Add benefit form */}
              {showBenefitForm && (
                <div className="dark-card" style={{ marginBottom:20, border:'2px solid #d4af37' }}>
                  <h3 style={{ color:'#d4af37', marginTop:0 }}>הוסף הטבה חדשה</h3>
                  <form onSubmit={async e => {
                    e.preventDefault();
                    const { data } = await axios.post(API+'/benefits', newBenefit);
                    setBenefits([...benefits, data]);
                    setNewBenefit({ title:'', description:'', category:'', discount:'', partner_name:'', coupon_code:'', website:'' });
                    setShowBenefitForm(false);
                    flash('✅ הטבה נוספה!');
                  }}>
                    <div className="form-row">
                      <div className="form-group"><label>כותרת ההטבה *</label><input value={newBenefit.title} onChange={e=>setNewBenefit({...newBenefit,title:e.target.value})} required placeholder="5% הנחה בספורט ישיר" /></div>
                      <div className="form-group"><label>קטגוריה</label><input value={newBenefit.category} onChange={e=>setNewBenefit({...newBenefit,category:e.target.value})} placeholder="הנעלה / ציוד / תזונה..." /></div>
                    </div>
                    <div className="form-row">
                      <div className="form-group"><label>תיאור</label><input value={newBenefit.description} onChange={e=>setNewBenefit({...newBenefit,description:e.target.value})} placeholder="תיאור קצר של ההטבה" /></div>
                      <div className="form-group"><label>ערך ההנחה</label><input value={newBenefit.discount} onChange={e=>setNewBenefit({...newBenefit,discount:e.target.value})} placeholder="5% / 50₪ / משלוח חינם" /></div>
                    </div>
                    <div style={{ display:'flex', gap:8 }}>
                      <button className="btn btn-gold" type="submit">שמור הטבה</button>
                      <button type="button" onClick={()=>setShowBenefitForm(false)} className="btn btn-outline">ביטול</button>
                    </div>
                  </form>
                </div>
              )}

              {/* Category filter */}
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:16 }}>
                {categories.map(cat=>(
                  <button key={cat} onClick={()=>setBenefitFilter(cat)}
                    style={{ padding:'5px 14px', borderRadius:20, border:`1px solid ${benefitFilter===cat?'#d4af37':'#2a3a6a'}`, background:benefitFilter===cat?'#d4af37':'transparent', color:benefitFilter===cat?'#0a1120':'#aaa', cursor:'pointer', fontSize:'0.85rem' }}>
                    {cat==='all'?`הכל (${allItems.length})`:cat}
                  </button>
                ))}
              </div>

              {/* ── PINNED: Welcome gift ─────────────────── */}
              <div style={{ background:'linear-gradient(135deg,#1a3a0a,#0a2a0a)', border:'2px solid #25d366', borderRadius:14, padding:20, marginBottom:16, display:'flex', gap:20, alignItems:'center', flexWrap:'wrap' }}>
                <div style={{ fontSize:'3rem' }}>🎴</div>
                <div style={{ flex:1, minWidth:200 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                    <span style={{ color:'#25d366', fontWeight:700, fontSize:'1.1rem' }}>קלף הכוכבים של יוסטאר — מתנת הצטרפות</span>
                    <span style={{ background:'#25d366', color:'#0a2a0a', borderRadius:20, padding:'2px 10px', fontSize:'0.75rem', fontWeight:700 }}>לכל חבר חדש</span>
                  </div>
                  <div style={{ color:'#aaa', fontSize:'0.88rem', marginBottom:6 }}>
                    קלף כדורגל מרהיב בעיצוב אישי עם תמונת השחקן — שווי <strong style={{ color:'#d4af37' }}>65₪</strong>, לחבר המועדון: דמי משלוח בלבד
                  </div>
                  <div style={{ color:'#556', fontSize:'0.78rem' }}>📌 הטבת הצטרפות קבועה — לא ניתנת להסרה</div>
                </div>
              </div>

              {/* Benefits grid */}
              {filtered.length===0 ? (
                <div style={{ textAlign:'center', padding:48, color:'#445' }}>
                  <div style={{ fontSize:'3rem', marginBottom:12 }}>🎁</div>
                  <div>אין הטבות נוספות עדיין — הוסף הטבה או הוסף שותף עם הטבות</div>
                </div>
              ) : (
                <div style={{ display:'grid', gap:16, gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))' }}>
                  {filtered.map(item => (
                    <div key={item.id} style={{ background:'#0d1a35', border:'1px solid #2a3a6a', borderRadius:14, padding:20, display:'flex', flexDirection:'column', position:'relative' }}>
                      {/* Discount badge */}
                      {item.discount && (
                        <div style={{ position:'absolute', top:14, left:14, background:'#d4af37', color:'#0a1120', borderRadius:20, padding:'3px 14px', fontWeight:700, fontSize:'0.9rem' }}>
                          {item.discount}
                        </div>
                      )}
                      <div style={{ marginTop: item.discount?28:0 }}>
                        {/* Partner tag */}
                        {item.type==='partner' && (
                          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
                            <span style={{ fontSize:'1.2rem' }}>{item.partnerEmoji}</span>
                            <span style={{ color:'#7a9aff', fontSize:'0.82rem', background:'#1a2a4a', borderRadius:12, padding:'2px 10px' }}>{item.partnerName}</span>
                          </div>
                        )}
                        {item.type==='manual' && (
                          <div style={{ marginBottom:8 }}>
                            <span style={{ color:'#d4af37', fontSize:'0.82rem', background:'#2a1a0a', borderRadius:12, padding:'2px 10px' }}>⭐ הטבת מועדון</span>
                          </div>
                        )}

                        <div style={{ color:'#fff', fontWeight:700, fontSize:'1rem', marginBottom:6 }}>{item.title}</div>
                        {item.description && <div style={{ color:'#aaa', fontSize:'0.85rem', marginBottom:8, flex:1 }}>{item.description}</div>}
                        {item.category && <div style={{ color:'#556', fontSize:'0.78rem', marginBottom:8 }}>📂 {item.category}</div>}

                        {/* Coupon */}
                        {item.coupon_code && (
                          <div style={{ background:'#0a2a1a', border:'1px dashed #25d366', borderRadius:8, padding:'8px 12px', marginBottom:10, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                            <span style={{ color:'#aaa', fontSize:'0.78rem' }}>קוד קופון:</span>
                            <span style={{ color:'#25d366', fontFamily:'monospace', fontWeight:700, letterSpacing:2 }}>{item.coupon_code}</span>
                            <button onClick={()=>navigator.clipboard.writeText(item.coupon_code)} style={{ background:'none', border:'none', color:'#25d366', cursor:'pointer' }}>📋</button>
                          </div>
                        )}

                        {item.valid_until && <div style={{ color:'#ff9966', fontSize:'0.78rem', marginBottom:8 }}>⏰ בתוקף עד {item.valid_until}</div>}
                        {item.website && <a href={item.website} target="_blank" rel="noopener noreferrer" style={{ color:'#d4af37', fontSize:'0.82rem', display:'block', marginBottom:8 }}>🔗 לאתר השותף</a>}
                      </div>

                      {/* Actions */}
                      <div style={{ display:'flex', gap:8, marginTop:'auto', paddingTop:12 }}>
                        <button onClick={async()=>{
                          if(!window.confirm('להסיר הטבה זו?')) return;
                          if(item.type==='manual'){
                            await axios.delete(API+'/benefits/'+item.rawId);
                            setBenefits(benefits.filter(b=>b.id!==item.rawId));
                          } else {
                            await axios.delete(API+'/partner-deals/'+item.rawDeal.id);
                            setPartners(partners.map(p=>p.id===item.rawPartner.id?{...p,deals:(p.deals||[]).filter(d=>d.id!==item.rawDeal.id)}:p));
                          }
                          flash('הטבה הוסרה');
                        }} style={{ background:'#3a0a0a', color:'#ff6b6b', border:'1px solid #ff6b6b', borderRadius:8, padding:'7px 12px', cursor:'pointer' }}>✕</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* ══════ ORDER-FORMS TAB ══════ */}
        {tab === 'order-forms' && (() => {
          return (
            <div style={{ padding:'24px 20px', maxWidth:1000, margin:'0 auto' }}>
              <div style={{ marginBottom:24 }}>
                <h2 style={{ color:'#d4af37', margin:0, fontSize:'1.3rem' }}>📋 טופס הזמנת מוצרי יוסטאר</h2>
                <p style={{ color:'#8a9aba', margin:'4px 0 0', fontSize:'0.85rem' }}>יצירת טפסי הזמנה עם קישורים ייחודיים לכל לקוח</p>
              </div>

              {/* ── ניהול תבניות טפסים ── */}
              {(() => {
                const activeProducts = products.filter(p => p.is_active);
                const saleCatalog = activeProducts.filter(p => p.product_type === 'sale');
                const tplProduct = saleCatalog.find(p => String(p.id) === String(templateProductId));
                const inputS = { background:'#0a1a3e', border:'1px solid #2a3a6a', borderRadius:8, padding:'8px 12px', color:'#fff', fontSize:'0.88rem' };

                const createTemplate = async () => {
                  if (!newTemplate.name) { flash('יש להזין שם לתבנית', 'error'); return; }
                  if (!tplProduct) { flash('יש לבחור מוצר מקטלוג מוצרי יוסטאר', 'error'); return; }
                  const { data } = await axios.post(API + '/form-templates', {
                    name: newTemplate.name,
                    product_name: tplProduct.name,
                    price: tplProduct.price,
                  });
                  setFormTemplates(prev => [data, ...prev]);
                  setNewTemplate({ name:'', product_name:'', price:'' });
                  setTemplateProductId('');
                  flash('✅ תבנית נוצרה!');
                };

                return (
                  <div style={{ background:'rgba(212,175,55,0.06)', border:'1px solid rgba(212,175,55,0.25)', borderRadius:14, padding:'18px 20px', marginBottom:24 }}>
                    <div style={{ color:'#d4af37', fontWeight:800, fontSize:'0.95rem', marginBottom:16 }}>📋 טפסי הזמנה</div>

                    {/* יצירת תבנית */}
                    <div style={{ background:'rgba(10,26,62,0.6)', borderRadius:10, padding:'14px 16px', marginBottom:16 }}>
                      <div style={{ color:'#c0cce6', fontWeight:700, fontSize:'0.85rem', marginBottom:12 }}>+ יצירת תבנית חדשה</div>
                      <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:12 }}>
                        <input value={newTemplate.name} onChange={e=>setNewTemplate(t=>({...t,name:e.target.value}))}
                          placeholder="שם התבנית (לשימוש פנימי)" style={{ ...inputS, flex:'1 1 180px' }} />
                      </div>
                      {/* בחירת מוצר — רק מקטלוג מוצרי יוסטאר */}
                      <div style={{ color:'#8a9aba', fontSize:'0.8rem', fontWeight:700, marginBottom:7 }}>
                        📦 מוצר יוסטאר <span style={{ color:'#4a5a7a', fontWeight:400 }}>— המחיר נלקח מהקטלוג</span>
                      </div>
                      {saleCatalog.length === 0 ? (
                        <div style={{ background:'rgba(255,153,102,0.1)', border:'1px solid rgba(255,153,102,0.4)', borderRadius:9, padding:'10px 13px', color:'#ff9966', fontSize:'0.83rem' }}>
                          ⚠️ אין מוצרים בקטלוג — צרו מוצר בטאב "מוצרי יוסטאר" לפני יצירת טופס
                          <button onClick={()=>setTab('ustar-products')} style={{ marginRight:8, background:'rgba(212,175,55,0.15)', color:'#d4af37', border:'1px solid rgba(212,175,55,0.4)', borderRadius:7, padding:'3px 12px', cursor:'pointer', fontSize:'0.78rem', fontWeight:700 }}>לטאב מוצרים →</button>
                        </div>
                      ) : (
                        <>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:7, marginBottom:8 }}>
                            {saleCatalog.map(p => {
                              const sel = String(templateProductId) === String(p.id);
                              return (
                                <button key={p.id} type="button" onClick={()=>setTemplateProductId(String(p.id))}
                                  style={{ padding:'7px 13px', borderRadius:9, cursor:'pointer', fontSize:'0.82rem', fontWeight:700, textAlign:'right',
                                    border:`1px solid ${sel?'#d4af37':'#2a3a6a'}`, background:sel?'rgba(212,175,55,0.18)':'#0a1525',
                                    color:sel?'#d4af37':'#c0cce6' }}>
                                  {p.media_type === 'photos' ? '📷' : '🎥'} {p.name} · ₪{p.price}
                                </button>
                              );
                            })}
                          </div>
                          {tplProduct && (
                            <div style={{ color:'#25d366', fontSize:'0.82rem' }}>
                              ✅ הטופס יישלח עם <strong>{tplProduct.name}</strong> במחיר <strong>₪{tplProduct.price}</strong>
                            </div>
                          )}
                        </>
                      )}
                      <button onClick={createTemplate} style={{ marginTop:12, background:'#d4af37', color:'#000', border:'none', borderRadius:8, padding:'9px 22px', cursor:'pointer', fontWeight:800, fontSize:'0.85rem' }}>
                        ✅ צור תבנית
                      </button>
                    </div>

                    {/* רשימת תבניות */}
                    {formTemplates.length === 0 ? (
                      <div style={{ color:'#4a5a7a', textAlign:'center', padding:'20px 0', fontSize:'0.85rem' }}>עדיין אין תבניות — צור את הראשונה למעלה</div>
                    ) : (
                      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                        {formTemplates.map(t => {
                          const url = `${window.location.origin}/order/${t.id}`;
                          return (
                            <div key={t.id} style={{ background:'#0d1b3e', border:'1px solid #1e3060', borderRadius:10, padding:'12px 16px', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                              <div style={{ flex:1, minWidth:160 }}>
                                <div style={{ color:'#fff', fontWeight:800, fontSize:'0.9rem' }}>{t.name}</div>
                                <div style={{ color:'#8a9aba', fontSize:'0.8rem', marginTop:2 }}>{t.product_name} · <span style={{ color:'#d4af37', fontWeight:700 }}>₪{t.price}</span></div>
                              </div>
                              <div style={{ color:'#4a5a7a', fontSize:'0.72rem', fontFamily:'monospace', wordBreak:'break-all', flex:'2 1 200px' }}>{url}</div>
                              <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                                <button onClick={()=>navigator.clipboard.writeText(url).then(()=>flash('🔗 קישור הועתק!'))}
                                  style={{ background:'rgba(212,175,55,0.15)', border:'1px solid rgba(212,175,55,0.4)', color:'#d4af37', borderRadius:8, padding:'6px 12px', cursor:'pointer', fontSize:'0.8rem', fontWeight:800 }}>
                                  🔗 העתק קישור
                                </button>
                                <button onClick={async()=>{
                                  if(!window.confirm('למחוק תבנית זו?')) return;
                                  await axios.delete(API+'/form-templates/'+t.id);
                                  setFormTemplates(prev=>prev.filter(x=>x.id!==t.id));
                                  flash('תבנית נמחקה');
                                }} style={{ background:'rgba(255,80,80,0.1)', border:'1px solid rgba(255,80,80,0.3)', color:'#ff8888', borderRadius:8, padding:'6px 10px', cursor:'pointer', fontSize:'0.8rem' }}>
                                  🗑
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })()}

        {/* ══════ CUSTOMERS TAB ══════ */}
        {tab === 'customers' && (() => {
          const statusColors = { new:'#d4af37', contacted:'#25d366', closed:'#5a9bf5', cancelled:'#888' };
          const statusLabels = { new:'חדש', contacted:'נוצר קשר', closed:'נסגר', cancelled:'בוטל' };
          const q = customerSearch.trim().toLowerCase();
          const matchedCustomers = q
            ? customers.filter(c =>
                (c.first_name||'').toLowerCase().includes(q) ||
                (c.last_name||'').toLowerCase().includes(q) ||
                (normPhone(c.phone).includes(normPhone(q)) && /\d/.test(q))
              )
            : customers;
          const custHasOrder = c => orders.some(o => o.customer_id === c.id);
          const statusFilteredCustomers = matchedCustomers.filter(c => {
            if (customerStatusFilter === 'all') return true;
            if (customerStatusFilter === 'active') return custHasOrder(c);
            return c.status === customerStatusFilter;
          });
          const custTotal    = customers.length;
          const custActive   = customers.filter(custHasOrder).length;
          const custNew      = customers.filter(c => c.status === 'new').length;
          const custCardSent = customers.filter(c => c.star_card_sent).length;
          const nowMonthKey  = new Date().toISOString().slice(0,7);
          const custMonth    = customers.filter(c => (c.created_at||'').slice(0,7) === nowMonthKey).length;
          const statusRank = { new:0, contacted:1, closed:2, cancelled:3 };
          const filteredCustomers = [...statusFilteredCustomers].sort((a,b) => {
            if (customerSort === 'name') return `${a.first_name||''} ${a.last_name||''}`.localeCompare(`${b.first_name||''} ${b.last_name||''}`, 'he');
            if (customerSort === 'status') return (statusRank[a.status]??9) - (statusRank[b.status]??9) || (new Date(b.created_at) - new Date(a.created_at));
            return new Date(b.created_at) - new Date(a.created_at); // newest
          });
          return (
            <div style={{ padding:'24px 20px', maxWidth:1000, margin:'0 auto' }}>
              <div style={{ marginBottom:16, display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10 }}>
                <div>
                  <h2 style={{ color:'#d4af37', margin:0, fontSize:'1.3rem' }}>👨‍👩‍👦 לקוחות יוסטאר</h2>
                  <p style={{ color:'#8a9aba', margin:'4px 0 0', fontSize:'0.85rem' }}>הורים שמילאו טופס הזמנה או שנוצרו ידנית</p>
                </div>
                <button onClick={()=>setShowNewCustomer(v=>!v)}
                  style={{ background:'linear-gradient(135deg,#d4af37,#b8941f)', color:'#000', border:'none', borderRadius:10, padding:'10px 20px', cursor:'pointer', fontWeight:800, fontSize:'0.88rem' }}>
                  {showNewCustomer ? '▲ סגור' : '+ לקוח חדש'}
                </button>
              </div>

              {/* ── יצירת לקוח יוסטאר ידנית ── */}
              {showNewCustomer && (() => {
                const saleProducts = products.filter(p => p.product_type === 'sale');
                const isPlayer = newCustomer.customer_type === 'player';
                const AGE_GROUPS_C = ['טרום א','ילדים ג','ילדים ב','ילדים א','נערים ג','נערים ב','נערים א','נוער'];
                const nowYC = new Date().getFullYear();
                const YEARS_C = Array.from({length:16}, (_,i)=>nowYC-4-i);
                const teamOptionsC = [...new Set([...FOOTBALL_CLUBS, ...members.map(m=>m.team).filter(Boolean)])].sort((a,b)=>a.localeCompare(b,'he'));
                const createCustomer = async () => {
                  if (!newCustomer.full_name.trim()) return flash('הזן שם מלא', 'error');
                  if (!isPlayer && !newCustomer.child_name.trim()) return flash('הזן את שם השחקן/ית', 'error');
                  await axios.post(API+'/customers/manual', {
                    full_name: newCustomer.full_name.trim(), phone: newCustomer.phone || null,
                    product_name: newCustomer.product_name || null, price: newCustomer.price || 0,
                    customer_type: newCustomer.customer_type,
                    team: newCustomer.team || null, age_group: newCustomer.age_group || null,
                    birth_year: newCustomer.birth_year || null,
                    child_name: newCustomer.child_name || null, child_last_name: newCustomer.child_last_name || null,
                    child_dob: newCustomer.child_dob || null,
                    positions: newCustomer.positions || null, jersey_number: newCustomer.jersey_number || null,
                    email: newCustomer.email || null,
                  });
                  axios.get(API+'/customers').then(r => setCustomers(r.data)).catch(()=>{});
                  axios.get(API+'/orders').then(r => setOrders(r.data)).catch(()=>{});
                  axios.get(API+'/members').then(r => setMembers(r.data)).catch(()=>{});
                  setNewCustomer({ full_name:'', phone:'', product_name:'', price:'', productId:'',
                    customer_type:newCustomer.customer_type, team:'', age_group:'', birth_year:'',
                    child_name:'', child_last_name:'', child_dob:'', positions:'', jersey_number:'', email:'' });
                  setShowNewCustomer(false);
                  flash('✅ לקוח נוצר — גם כחבר מועדון + הזמנת עבודה');
                };
                const inS = { width:'100%', background:'#0a1a3e', border:'1px solid #2a3a6a', borderRadius:8, padding:'9px 12px', color:'#fff', fontSize:'0.9rem', boxSizing:'border-box' };
                return (
                  <div style={{ background:'linear-gradient(135deg,rgba(212,175,55,0.08),rgba(212,175,55,0.03))', border:'2px solid rgba(212,175,55,0.4)', borderRadius:14, padding:'18px 20px', marginBottom:18 }}>
                    <div style={{ color:'#d4af37', fontWeight:900, fontSize:'0.95rem', marginBottom:14 }}>➕ לקוח יוסטאר חדש</div>

                    {/* סוג לקוח — שחקן / הורה */}
                    <div style={{ color:'#8a9aba', fontSize:'0.78rem', fontWeight:700, marginBottom:6 }}>סוג לקוח:</div>
                    <div style={{ display:'flex', gap:8, marginBottom:12 }}>
                      {[{v:'player',l:'⚽ שחקן/ית',h:'הלקוח הוא השחקן עצמו'},{v:'parent',l:'👨‍👩‍👦 הורה',h:'נמלא גם את פרטי הילד/ה'}].map(t=>{
                        const sel = newCustomer.customer_type === t.v;
                        return (
                          <button key={t.v} type="button" onClick={()=>setNewCustomer(c=>({...c, customer_type:t.v}))}
                            style={{ flex:1, textAlign:'right', padding:'9px 13px', borderRadius:10, cursor:'pointer',
                              border:`2px solid ${sel?'#d4af37':'#2a3a6a'}`, background:sel?'rgba(212,175,55,0.14)':'#0a1525' }}>
                            <div style={{ color:sel?'#d4af37':'#8a9aba', fontWeight:800, fontSize:'0.86rem' }}>{t.l}</div>
                            <div style={{ color:'#4a5a7a', fontSize:'0.72rem', marginTop:2 }}>{t.h}</div>
                          </button>
                        );
                      })}
                    </div>

                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:10, marginBottom:12 }}>
                      <input value={newCustomer.full_name} placeholder={isPlayer ? 'שם השחקן/ית המלא *' : 'שם ההורה המלא *'}
                        onChange={e=>setNewCustomer(c=>({ ...c, full_name:e.target.value }))} style={inS} />
                      <div>
                        <input value={newCustomer.phone} placeholder="טלפון (כל פורמט)"
                          onChange={e=>setNewCustomer(c=>({ ...c, phone:e.target.value }))} style={inS} />
                        {newCustomer.phone && normPhone(newCustomer.phone) !== newCustomer.phone && (
                          <div style={{ color:'#25d366', fontSize:'0.74rem', marginTop:3 }}>יישמר כ- {normPhone(newCustomer.phone)}</div>
                        )}
                      </div>
                    </div>

                    {/* פרטי הילד — רק להורה */}
                    {!isPlayer && (
                      <div style={{ background:'rgba(122,176,255,0.06)', border:'1px solid rgba(122,176,255,0.25)', borderRadius:10, padding:'12px 14px', marginBottom:12 }}>
                        <div style={{ color:'#7ab0ff', fontWeight:800, fontSize:'0.82rem', marginBottom:8 }}>⚽ פרטי השחקן/ית</div>
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:8, marginBottom:8 }}>
                          <input value={newCustomer.child_name} placeholder="שם פרטי *"
                            onChange={e=>setNewCustomer(c=>({ ...c, child_name:e.target.value }))} style={inS} />
                          <input value={newCustomer.child_last_name} placeholder="שם משפחה"
                            onChange={e=>setNewCustomer(c=>({ ...c, child_last_name:e.target.value }))} style={inS} />
                          <input type="date" value={newCustomer.child_dob} title="תאריך לידה"
                            onChange={e=>setNewCustomer(c=>({ ...c, child_dob:e.target.value }))} style={inS} />
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:8 }}>
                          <input value={newCustomer.positions} placeholder="עמדות במגרש"
                            onChange={e=>setNewCustomer(c=>({ ...c, positions:e.target.value }))} style={inS} />
                          <input value={newCustomer.jersey_number} placeholder="מספר חולצה"
                            onChange={e=>setNewCustomer(c=>({ ...c, jersey_number:e.target.value }))} style={inS} />
                        </div>
                      </div>
                    )}

                    {/* שיוך קבוצה — לשניהם */}
                    <div style={{ color:'#8a9aba', fontSize:'0.78rem', fontWeight:700, marginBottom:6 }}>
                      שיוך {isPlayer ? 'השחקן/ית' : 'הילד/ה'} — מועדון · שכבת גיל · שנתון:
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:10, marginBottom:12 }}>
                      <TeamSearchInput value={newCustomer.team} onChange={v=>setNewCustomer(c=>({...c, team:v}))}
                        options={teamOptionsC} placeholder="הקלד שם מועדון" />
                      <select value={newCustomer.age_group} onChange={e=>setNewCustomer(c=>({...c, age_group:e.target.value}))} style={inS}>
                        <option value="">— שכבת גיל —</option>
                        {AGE_GROUPS_C.map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                      <select value={newCustomer.birth_year} onChange={e=>setNewCustomer(c=>({...c, birth_year:e.target.value}))} style={inS}>
                        <option value="">— שנתון —</option>
                        {YEARS_C.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                    <div style={{ color:'#8a9aba', fontSize:'0.78rem', fontWeight:700, marginBottom:6 }}>מוצר יוסטאר שהזמין + מחיר:</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:10 }}>
                      {saleProducts.map(p => {
                        const sel = String(newCustomer.productId) === String(p.id);
                        return (
                          <button key={p.id} type="button"
                            onClick={()=>setNewCustomer(c=>({ ...c, productId:String(p.id), product_name:p.name, price:String(p.price) }))}
                            style={{ padding:'6px 12px', borderRadius:8, border:`1px solid ${sel?'#d4af37':'#2a3a6a'}`, background:sel?'rgba(212,175,55,0.18)':'#0a1525', cursor:'pointer', fontSize:'0.8rem', color:sel?'#d4af37':'#c0cce6' }}>
                            {p.name} · ₪{p.price}
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:14 }}>
                      <input value={newCustomer.product_name} placeholder="שם מוצר (ידני)"
                        onChange={e=>setNewCustomer(c=>({ ...c, product_name:e.target.value, productId:'' }))} style={{ ...inS, flex:'1 1 160px' }} />
                      <input type="number" value={newCustomer.price} placeholder="₪ מחיר"
                        onChange={e=>setNewCustomer(c=>({ ...c, price:e.target.value }))} style={{ ...inS, width:110 }} />
                    </div>
                    <button onClick={createCustomer}
                      style={{ background:'linear-gradient(135deg,#d4af37,#b8941f)', color:'#000', border:'none', borderRadius:10, padding:'10px 24px', cursor:'pointer', fontWeight:900, fontSize:'0.88rem' }}>
                      ✅ צור לקוח + הזמנה
                    </button>
                  </div>
                );
              })()}

              {/* ── KPI ── */}
              {customers.length > 0 && (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:10, marginBottom:16 }}>
                  {[
                    { label:'סה״כ לקוחות', val:custTotal,    color:'#d4af37', icon:'👥' },
                    { label:'פעילים (הזמינו)', val:custActive, color:'#25d366', icon:'⚡' },
                    { label:'חדשים', val:custNew,           color:'#7a9aff', icon:'🆕' },
                    { label:'נרשמו החודש', val:custMonth,    color:'#b478ff', icon:'📅' },
                    { label:'קיבלו קלף', val:custCardSent,   color:'#ffcc44', icon:'⭐' },
                  ].map(s => (
                    <div key={s.label} style={{ background:'#0d1b3e', border:`1px solid ${s.color}33`, borderRadius:12, padding:'12px 14px' }}>
                      <div style={{ color:'#8a9aba', fontSize:'0.72rem', marginBottom:4 }}>{s.icon} {s.label}</div>
                      <div style={{ color:s.color, fontWeight:900, fontSize:'1.5rem', lineHeight:1 }}>{s.val}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── צ'יפים לסינון סטטוס ── */}
              {customers.length > 0 && (
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
                  {[
                    { id:'all',       label:'הכל',        count:custTotal },
                    { id:'active',    label:'⚡ פעילים',   count:custActive },
                    { id:'new',       label:'🆕 חדשים',    count:custNew },
                    { id:'contacted', label:'📞 נוצר קשר', count:customers.filter(c=>c.status==='contacted').length },
                    { id:'closed',    label:'✓ נסגר',      count:customers.filter(c=>c.status==='closed').length },
                  ].map(chip => {
                    const active = customerStatusFilter === chip.id;
                    return (
                      <button key={chip.id} onClick={()=>setCustomerStatusFilter(chip.id)}
                        style={{ background: active?'rgba(212,175,55,0.18)':'transparent', color:active?'#d4af37':'#8a9aba', border:`1px solid ${active?'#d4af37':'#2a3a6a'}`, borderRadius:20, padding:'5px 14px', cursor:'pointer', fontSize:'0.82rem', fontWeight:active?800:500 }}>
                        {chip.label} <span style={{ opacity:0.7 }}>({chip.count})</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* ── חיפוש + מיון לקוחות ── */}
              {customers.length > 0 && (
                <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center', marginBottom:16 }}>
                  <div style={{ position:'relative', flex:1, minWidth:220 }}>
                    <input
                      value={customerSearch}
                      onChange={e => setCustomerSearch(e.target.value)}
                      placeholder="🔍 חיפוש לפי שם, שם משפחה או טלפון..."
                      style={{ width:'100%', background:'#0d1b3e', border:'1px solid #2a3a6a', borderRadius:10, padding:'11px 16px', color:'#fff', fontSize:'0.95rem', direction:'rtl', boxSizing:'border-box' }}
                    />
                    {customerSearch && (
                      <button onClick={() => setCustomerSearch('')}
                        style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', color:'#8a9aba', cursor:'pointer', fontSize:'1rem' }}>✕</button>
                    )}
                  </div>
                  <select value={customerSort} onChange={e=>setCustomerSort(e.target.value)} title="מיון"
                    style={{ padding:'11px 14px', background:'#0d1b3e', color:'#d4af37', border:'1px solid #2a3a6a', borderRadius:10, cursor:'pointer', fontSize:'0.88rem' }}>
                    <option value="newest">↕ מהחדש לישן</option>
                    <option value="name">🔤 לפי שם</option>
                    <option value="status">🏷 לפי סטטוס</option>
                  </select>
                  <div style={{ color:'#8a9aba', fontSize:'0.82rem', whiteSpace:'nowrap' }}>
                    {filteredCustomers.length} מתוך {customers.length}
                  </div>
                </div>
              )}

              {customers.length === 0 ? (
                <div style={{ textAlign:'center', color:'#4a5a7a', padding:'60px 20px' }}>
                  <div style={{ fontSize:'3rem', marginBottom:12 }}>🎬</div>
                  <div>עוד אין פניות — שתפו את הטופס עם לקוחות פוטנציאליים</div>
                </div>
              ) : filteredCustomers.length === 0 ? (
                <div style={{ textAlign:'center', color:'#4a5a7a', padding:'40px 20px', fontSize:'0.95rem' }}>
                  {customerSearch ? `לא נמצאו לקוחות עבור "${customerSearch}"` : 'אין לקוחות בסינון זה'}
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                  {filteredCustomers.map(c => (
                    <div key={c.id} style={{ background:'#0d1b3e', border:'1px solid #1e3060', borderRadius:10, padding:'10px 14px' }}>
                      {/* ── שורה קומפקטית ── */}
                      <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>

                        {/* פרטים לאורך השורה */}
                        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', flex:'1 1 380px', minWidth:0 }}>
                          <span style={{ color:'#fff', fontWeight:800, fontSize:'0.92rem', whiteSpace:'nowrap' }}>{c.first_name} {c.last_name}</span>
                          <span style={{ fontSize:'0.7rem', border:`1px solid ${statusColors[c.status]||'#888'}`, color: statusColors[c.status]||'#888', borderRadius:20, padding:'1px 8px', fontWeight:700, whiteSpace:'nowrap' }}>
                            {statusLabels[c.status] || c.status}
                          </span>
                          {c.customer_type === 'player' && <span style={{ fontSize:'0.7rem', color:'#7ab0ff', whiteSpace:'nowrap' }}>⚽ שחקן</span>}
                          <a href={`tel:${c.phone}`} style={{ color:'#d4af37', textDecoration:'none', fontSize:'0.82rem', whiteSpace:'nowrap' }}>📞 {normPhone(c.phone) || c.phone}</a>
                          {c.customer_type !== 'player' && (c.child_name||'').trim() && (
                            <span style={{ color:'#c0cce6', fontSize:'0.82rem', whiteSpace:'nowrap' }}>⚽ {c.child_name} {c.child_last_name||''}</span>
                          )}
                          {c.jersey_number && <span style={{ color:'#d4af37', fontSize:'0.78rem', whiteSpace:'nowrap' }}>#{c.jersey_number}</span>}
                          {fullTeamLabel(c) && (
                            <span style={{ background:'rgba(212,175,55,0.12)', border:'1px solid rgba(212,175,55,0.3)', color:'#d4af37', borderRadius:6, padding:'1px 8px', fontSize:'0.76rem', fontWeight:700, whiteSpace:'nowrap' }}>
                              🏟 {fullTeamLabel(c)}
                            </span>
                          )}
                          {/* התראת בר מצווה */}
                          {c.child_dob && (() => {
                            const ageYears = (new Date() - new Date(c.child_dob)) / (1000*60*60*24*365.25);
                            return ageYears >= 12.5 && ageYears < 13
                              ? <span title="קרוב לבר מצווה — הצע קליפ בר מצווה" style={{ background:'rgba(212,175,55,0.2)', border:'1px solid #d4af37', color:'#d4af37', borderRadius:20, padding:'1px 8px', fontSize:'0.72rem', fontWeight:800, whiteSpace:'nowrap' }}>✡️ בר מצווה</span>
                              : null;
                          })()}
                          {c.star_card_sent && <span title="קיבל קלף כוכבים" style={{ fontSize:'0.8rem' }}>⭐</span>}
                          {(() => {
                            const cnt = orders.filter(o => o.customer_id === c.id).length;
                            return cnt ? <span style={{ color:'#25d366', fontSize:'0.78rem', fontWeight:700, whiteSpace:'nowrap' }}>📦 {cnt}</span> : null;
                          })()}
                        </div>

                        {/* כפתורי פעולה בשורה */}
                        <div style={{ display:'flex', gap:5, flexShrink:0, flexWrap:'wrap', alignItems:'center' }}>
                          {[
                            { t:'⚡', title:'הזמנה מהירה', col:'#d4af37', on:quickOrderSetup?.customerId===c.id,
                              fn:()=>setQuickOrderSetup(q => q?.customerId === c.id ? null : { customerId:c.id, productName:'', price:'', productId:'' }) },
                            { t:'📋', title:'הזמנות', col:'#5a9bf5', on:expandedCustomer===c.id,
                              fn:()=>setExpandedCustomer(expandedCustomer === c.id ? null : c.id) },
                            { t:'⭐', title:'הטבות', col:'#d4af37', on:expandedBenefits===c.id,
                              fn:()=>setExpandedBenefits(expandedBenefits === c.id ? null : c.id) },
                            { t:'✏️', title:'ערוך', col:'#ffaa44', on:editingCustomer===c.id,
                              fn:()=>{
                                if (editingCustomer === c.id) { setEditingCustomer(null); return; }
                                setEditingCustomer(c.id);
                                setEditCustomerData({
                                  first_name:c.first_name||'', phone:c.phone||'', email:c.email||'',
                                  customer_type:c.customer_type||'parent',
                                  child_name:c.child_name||'', child_last_name:c.child_last_name||'',
                                  child_dob:(c.child_dob||'').slice(0,10), team:c.team||'',
                                  age_group:c.age_group||'', birth_year:c.birth_year||'',
                                  positions:c.positions||'', jersey_number:c.jersey_number||'',
                                });
                              } },
                          ].map(b => (
                            <button key={b.title} onClick={b.fn} title={b.title}
                              style={{ background: b.on ? `${b.col}33` : `${b.col}14`, border:`1px solid ${b.col}${b.on?'':'55'}`,
                                color:b.col, borderRadius:7, padding:'5px 9px', fontSize:'0.85rem', cursor:'pointer', lineHeight:1 }}>
                              {b.t}
                            </button>
                          ))}
                          <a href={`https://wa.me/${waNum(c.phone)}`} target="_blank" rel="noopener noreferrer" title="וואטסאפ"
                            style={{ background:'rgba(37,211,102,0.14)', border:'1px solid rgba(37,211,102,0.45)', color:'#25d366', borderRadius:7, padding:'5px 9px', fontSize:'0.85rem', textDecoration:'none', lineHeight:1 }}>💬</a>
                          <select value={c.status} title="סטטוס" onChange={async e => {
                            await axios.put(API + '/customers/' + c.id + '/status', { status: e.target.value });
                            setCustomers(cs => cs.map(x => x.id === c.id ? { ...x, status: e.target.value } : x));
                          }} style={{ background:'#0a1a3e', border:'1px solid #2a3a6a', color:'#8a9aba', borderRadius:7, padding:'4px 6px', fontSize:'0.76rem', cursor:'pointer' }}>
                            <option value="new">חדש</option>
                            <option value="contacted">נוצר קשר</option>
                            <option value="closed">נסגר</option>
                            <option value="cancelled">בוטל</option>
                          </select>
                          <button onClick={async () => {
                            if (!window.confirm('למחוק לקוח זה?')) return;
                            await axios.delete(API + '/customers/' + c.id);
                            setCustomers(cs => cs.filter(x => x.id !== c.id));
                          }} title="מחק"
                            style={{ background:'rgba(255,80,80,0.08)', border:'1px solid rgba(255,80,80,0.3)', color:'#ff8888', borderRadius:7, padding:'5px 9px', fontSize:'0.85rem', cursor:'pointer', lineHeight:1 }}>🗑</button>
                        </div>
                      </div>

                      {/* ══ פאנל הזמנה מהירה ══ */}
                      {quickOrderSetup?.customerId === c.id && (() => {
                        const qo = quickOrderSetup;
                        const selectedProduct = products.find(p => String(p.id) === String(qo.productId));
                        const finalName  = qo.productName || (selectedProduct?.name) || '';
                        const finalPrice = qo.price !== '' ? qo.price : (selectedProduct?.price ?? '');
                        const reorderUrl = finalName && finalPrice !== ''
                          ? `${window.location.origin}/reorder/${c.id}?product=${encodeURIComponent(finalName)}&price=${encodeURIComponent(finalPrice)}`
                          : null;
                        return (
                          <div style={{ marginTop:12, background:'rgba(212,175,55,0.06)', border:'1px solid rgba(212,175,55,0.3)', borderRadius:12, padding:'16px 18px' }}>
                            <div style={{ color:'#d4af37', fontWeight:900, fontSize:'0.88rem', marginBottom:12 }}>⚡ הגדרת הזמנה מהירה</div>

                            {/* בחירת מוצר מהרשימה */}
                            <div style={{ marginBottom:10 }}>
                              <div style={{ color:'#8a9aba', fontSize:'0.78rem', fontWeight:700, marginBottom:6 }}>בחר מוצר מרשימה:</div>
                              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                                {products.filter(p => p.product_type === 'sale').map(p => {
                                  const sel = String(qo.productId) === String(p.id);
                                  return (
                                    <button key={p.id} type="button"
                                      onClick={() => setQuickOrderSetup(q => ({ ...q, productId:String(p.id), productName:p.name, price:String(p.price) }))}
                                      style={{ padding:'6px 12px', borderRadius:8, border:`1px solid ${sel?'#d4af37':'#2a3a6a'}`, background:sel?'rgba(212,175,55,0.18)':'#0a1525', cursor:'pointer', fontSize:'0.8rem', color:sel?'#d4af37':'#c0cce6', fontFamily:'Heebo, sans-serif' }}>
                                      {p.name} · ₪{p.price}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            {/* או מחיר ידני */}
                            <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:12, flexWrap:'wrap' }}>
                              <div style={{ flex:1, minWidth:160 }}>
                                <div style={{ color:'#8a9aba', fontSize:'0.78rem', fontWeight:700, marginBottom:4 }}>שם מוצר (ידני):</div>
                                <input value={qo.productName} placeholder="קליפ ביצועים"
                                  onChange={e => setQuickOrderSetup(q => ({ ...q, productName:e.target.value, productId:'' }))}
                                  style={{ width:'100%', background:'#0a1a3e', border:'1px solid #2a3a6a', borderRadius:8, padding:'7px 10px', color:'#fff', fontSize:'0.85rem', boxSizing:'border-box' }} />
                              </div>
                              <div style={{ width:100 }}>
                                <div style={{ color:'#8a9aba', fontSize:'0.78rem', fontWeight:700, marginBottom:4 }}>מחיר ₪:</div>
                                <input type="number" value={qo.price} placeholder="0"
                                  onChange={e => setQuickOrderSetup(q => ({ ...q, price:e.target.value }))}
                                  style={{ width:'100%', background:'#0a1a3e', border:'1px solid #2a3a6a', borderRadius:8, padding:'7px 10px', color:'#fff', fontSize:'0.85rem', boxSizing:'border-box' }} />
                              </div>
                            </div>

                            {/* כפתור העתקת קישור */}
                            {reorderUrl ? (
                              <button onClick={() => {
                                navigator.clipboard.writeText(reorderUrl).then(() => {
                                  setQuickOrderCopied(true);
                                  setTimeout(() => setQuickOrderCopied(false), 2500);
                                }).catch(()=>{});
                              }} style={{ width:'100%', background:'linear-gradient(135deg,#d4af37,#b8941f)', color:'#000', border:'none', borderRadius:10, padding:'11px', cursor:'pointer', fontWeight:900, fontSize:'0.9rem', fontFamily:'Heebo, sans-serif' }}>
                                {quickOrderCopied ? '✅ הקישור הועתק!' : '🔗 העתק קישור הזמנה מהירה'}
                              </button>
                            ) : (
                              <div style={{ color:'#556', fontSize:'0.82rem', textAlign:'center', padding:'8px 0' }}>בחר מוצר או הכנס שם + מחיר כדי לייצר קישור</div>
                            )}
                          </div>
                        );
                      })()}

                      {/* ══ עריכת לקוח ══ */}
                      {editingCustomer === c.id && (() => {
                        const ed = editCustomerData;
                        const isPlayerEd = ed.customer_type === 'player';
                        const AGE_G = ['טרום א','ילדים ג','ילדים ב','ילדים א','נערים ג','נערים ב','נערים א','נוער'];
                        const nowYE = new Date().getFullYear();
                        const YEARS_E = Array.from({length:16}, (_,i)=>nowYE-4-i);
                        const teamOptsE = [...new Set([...FOOTBALL_CLUBS, ...members.map(m=>m.team).filter(Boolean)])].sort((a,b)=>a.localeCompare(b,'he'));
                        const eS = { width:'100%', background:'#0a1a3e', border:'1px solid #2a3a6a', borderRadius:8, padding:'8px 11px', color:'#fff', fontSize:'0.86rem', boxSizing:'border-box' };
                        const saveCustomer = async () => {
                          if (!ed.first_name.trim()) return flash('שם לא יכול להיות ריק', 'error');
                          const { data } = await axios.put(API+'/customers/'+c.id, ed);
                          setCustomers(cs => cs.map(x => x.id===c.id ? { ...x, ...data } : x));
                          axios.get(API+'/members').then(r => setMembers(r.data)).catch(()=>{});
                          setEditingCustomer(null);
                          flash('✅ פרטי הלקוח עודכנו');
                        };
                        return (
                          <div style={{ marginTop:12, background:'rgba(255,150,50,0.05)', border:'1px solid rgba(255,150,50,0.3)', borderRadius:12, padding:'16px 18px' }}>
                            <div style={{ color:'#ffaa44', fontWeight:900, fontSize:'0.88rem', marginBottom:12 }}>✏️ עריכת פרטי לקוח</div>

                            {/* סוג לקוח */}
                            <div style={{ display:'flex', gap:8, marginBottom:12 }}>
                              {[{v:'player',l:'⚽ שחקן/ית'},{v:'parent',l:'👨‍👩‍👦 הורה'}].map(t=>{
                                const sel = ed.customer_type === t.v;
                                return (
                                  <button key={t.v} type="button" onClick={()=>setEditCustomerData(d=>({...d, customer_type:t.v}))}
                                    style={{ flex:1, padding:'8px', borderRadius:8, cursor:'pointer', fontWeight:800, fontSize:'0.83rem',
                                      border:`2px solid ${sel?'#ffaa44':'#2a3a6a'}`, background:sel?'rgba(255,150,50,0.14)':'#0a1525',
                                      color:sel?'#ffaa44':'#8a9aba' }}>{t.l}</button>
                                );
                              })}
                            </div>

                            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:9, marginBottom:10 }}>
                              <input value={ed.first_name} placeholder={isPlayerEd?'שם השחקן/ית':'שם ההורה'}
                                onChange={e=>setEditCustomerData(d=>({...d, first_name:e.target.value}))} style={eS} />
                              <div>
                                <input value={ed.phone} placeholder="טלפון"
                                  onChange={e=>setEditCustomerData(d=>({...d, phone:e.target.value}))} style={eS} />
                                {ed.phone && normPhone(ed.phone) !== ed.phone && (
                                  <div style={{ color:'#25d366', fontSize:'0.72rem', marginTop:3 }}>יישמר כ- {normPhone(ed.phone)}</div>
                                )}
                              </div>
                              <input value={ed.email} placeholder="אימייל"
                                onChange={e=>setEditCustomerData(d=>({...d, email:e.target.value}))} style={eS} />
                            </div>

                            {/* פרטי הילד — רק להורה */}
                            {!isPlayerEd && (
                              <div style={{ background:'rgba(122,176,255,0.06)', border:'1px solid rgba(122,176,255,0.25)', borderRadius:10, padding:'11px 13px', marginBottom:10 }}>
                                <div style={{ color:'#7ab0ff', fontWeight:800, fontSize:'0.8rem', marginBottom:8 }}>⚽ פרטי השחקן/ית</div>
                                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))', gap:8 }}>
                                  <input value={ed.child_name} placeholder="שם פרטי"
                                    onChange={e=>setEditCustomerData(d=>({...d, child_name:e.target.value}))} style={eS} />
                                  <input value={ed.child_last_name} placeholder="שם משפחה"
                                    onChange={e=>setEditCustomerData(d=>({...d, child_last_name:e.target.value}))} style={eS} />
                                  <input type="date" value={ed.child_dob} title="תאריך לידה"
                                    onChange={e=>setEditCustomerData(d=>({...d, child_dob:e.target.value}))} style={eS} />
                                  <input value={ed.positions} placeholder="עמדות"
                                    onChange={e=>setEditCustomerData(d=>({...d, positions:e.target.value}))} style={eS} />
                                  <input value={ed.jersey_number} placeholder="מס׳ חולצה"
                                    onChange={e=>setEditCustomerData(d=>({...d, jersey_number:e.target.value}))} style={eS} />
                                </div>
                              </div>
                            )}

                            {/* שיוך קבוצה */}
                            <div style={{ color:'#8a9aba', fontSize:'0.76rem', fontWeight:700, marginBottom:6 }}>מועדון · שכבת גיל · שנתון:</div>
                            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:9, marginBottom:14 }}>
                              <TeamSearchInput value={ed.team} onChange={v=>setEditCustomerData(d=>({...d, team:v}))}
                                options={teamOptsE} placeholder="הקלד שם מועדון" />
                              <select value={ed.age_group} onChange={e=>setEditCustomerData(d=>({...d, age_group:e.target.value}))} style={eS}>
                                <option value="">— שכבת גיל —</option>
                                {AGE_G.map(a => <option key={a} value={a}>{a}</option>)}
                              </select>
                              <select value={ed.birth_year} onChange={e=>setEditCustomerData(d=>({...d, birth_year:e.target.value}))} style={eS}>
                                <option value="">— שנתון —</option>
                                {YEARS_E.map(y => <option key={y} value={y}>{y}</option>)}
                              </select>
                            </div>

                            <div style={{ display:'flex', gap:8 }}>
                              <button onClick={saveCustomer}
                                style={{ background:'linear-gradient(135deg,#d4af37,#b8941f)', color:'#000', border:'none', borderRadius:9, padding:'9px 22px', cursor:'pointer', fontWeight:900, fontSize:'0.85rem' }}>💾 שמור שינויים</button>
                              <button onClick={()=>setEditingCustomer(null)}
                                style={{ background:'transparent', color:'#8a9aba', border:'1px solid #2a3a6a', borderRadius:9, padding:'9px 18px', cursor:'pointer', fontSize:'0.85rem' }}>ביטול</button>
                            </div>
                          </div>
                        );
                      })()}

                      {/* ══ לשונית הטבות ══ */}
                      {expandedBenefits === c.id && (
                        <div style={{ marginTop:12, background:'rgba(212,175,55,0.04)', border:'1px solid rgba(212,175,55,0.25)', borderRadius:12, padding:'16px 18px' }}>
                          <div style={{ color:'#d4af37', fontWeight:900, fontSize:'0.88rem', marginBottom:14 }}>⭐ הטבות חבר מועדון יוסטאר</div>
                          {/* קלף הכוכבים */}
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'rgba(255,255,255,0.03)', border:`1px solid ${c.star_card_sent?'rgba(212,175,55,0.5)':'#1e3060'}`, borderRadius:10, padding:'14px 18px', gap:12, flexWrap:'wrap' }}>
                            <div style={{ flex:1 }}>
                              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                                {c.star_card_sent
                                  ? <span style={{ background:'rgba(212,175,55,0.2)', color:'#d4af37', borderRadius:20, padding:'2px 10px', fontSize:'0.75rem', fontWeight:800 }}>✅ קיבל</span>
                                  : <span style={{ background:'rgba(255,255,255,0.06)', color:'#8a9aba', borderRadius:20, padding:'2px 10px', fontSize:'0.75rem', fontWeight:700 }}>⏳ טרם קיבל</span>
                                }
                                <span style={{ color:'#fff', fontWeight:800, fontSize:'0.95rem' }}>⭐ קלף הכוכבים</span>
                              </div>
                              <div style={{ color:'#8a9aba', fontSize:'0.8rem' }}>קלף כוכבים אישי לחבר המועדון</div>
                            </div>
                            <button onClick={async () => {
                              const newVal = !c.star_card_sent;
                              await axios.put(API+'/customers/'+c.id+'/star-card', { star_card_sent: newVal });
                              setCustomers(cs => cs.map(x => x.id === c.id ? { ...x, star_card_sent: newVal } : x));
                              flash(newVal ? '✅ קלף הכוכבים סומן כנשלח!' : 'קלף הכוכבים בוטל');
                            }} style={{
                              background: c.star_card_sent ? 'rgba(212,175,55,0.2)' : 'rgba(212,175,55,0.08)',
                              color: c.star_card_sent ? '#d4af37' : '#8a9aba',
                              border: `1px solid ${c.star_card_sent?'rgba(212,175,55,0.6)':'#2a3a6a'}`,
                              borderRadius:8, padding:'8px 18px', cursor:'pointer', fontWeight:800, fontSize:'0.85rem', whiteSpace:'nowrap'
                            }}>
                              {c.star_card_sent ? '✅ סומן כנתקבל' : '○ סמן כנתקבל'}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* היסטוריית הזמנות */}
                      {expandedCustomer === c.id && (() => {
                        const custOrders = orders.filter(o => o.customer_id === c.id).sort((a,b) => new Date(b.created_at) - new Date(a.created_at));
                        const total = custOrders.reduce((s, o) => s + (o.total || 0), 0);
                        const statusLabel = { pending:'ממתין', completed:'הושלם', cancelled:'בוטל' };
                        const statusColor = { pending:'#d4af37', completed:'#25d366', cancelled:'#ff6b6b' };
                        return (
                          <div style={{ marginTop:14, borderTop:'1px solid #1e3060', paddingTop:14 }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                              <span style={{ color:'#8a9aba', fontSize:'0.82rem', fontWeight:700 }}>היסטוריית הזמנות ({custOrders.length})</span>
                              {total > 0 && <span style={{ color:'#d4af37', fontWeight:900, fontSize:'0.95rem' }}>סה״כ: ₪{total.toLocaleString()}</span>}
                            </div>
                            {custOrders.length === 0 ? (
                              <div style={{ color:'#4a5a7a', fontSize:'0.82rem', textAlign:'center', padding:'10px 0' }}>אין הזמנות עדיין</div>
                            ) : (
                              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                                {custOrders.map(o => (
                                  <div key={o.id} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid #1e3060', borderRadius:8, padding:'10px 14px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, flexWrap:'wrap' }}>
                                    <div style={{ fontSize:'0.83rem' }}>
                                      <span style={{ color:'#c0cce6', fontWeight:700 }}>{o.product_name}</span>
                                      {o.notes && <div style={{ color:'#4a5a7a', fontSize:'0.75rem', marginTop:2 }}>📝 {o.notes}</div>}
                                      <div style={{ color:'#4a5a7a', fontSize:'0.75rem', marginTop:2 }}>{new Date(o.created_at).toLocaleDateString('he-IL')}</div>
                                    </div>
                                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                      {o.total > 0 && <span style={{ color:'#d4af37', fontWeight:900 }}>₪{o.total}</span>}
                                      <span style={{ background:`rgba(0,0,0,0.3)`, border:`1px solid ${statusColor[o.status]||'#888'}`, color: statusColor[o.status]||'#888', borderRadius:20, padding:'2px 10px', fontSize:'0.75rem', fontWeight:700 }}>
                                        {statusLabel[o.status] || o.status}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              )}

              {/* ══ הזמנות עבודה פתוחות ══ */}
              {(() => {
                const pending = orders.filter(o => o.customer_id && o.status !== 'completed');
                if (pending.length === 0) return null;
                return (
                  <div style={{ background:'rgba(212,175,55,0.06)', border:'1px solid rgba(212,175,55,0.25)', borderRadius:14, padding:'18px 20px', marginTop:28, marginBottom:16 }}>
                    <div style={{ color:'#d4af37', fontWeight:900, fontSize:'1rem', marginBottom:14 }}>🎬 הזמנות עבודה — {pending.length} ממתינות</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                      {pending.map(o => (
                        <CustomerWorkOrder key={o.id} o={o} products={products} API={API}
                          onUpdate={updated => setOrders(prev => prev.map(x => x.id === updated.id ? { ...x, ...updated } : x))}
                          onComplete={async () => {
                            await axios.put(API+'/orders/'+o.id+'/status', { status:'completed' });
                            setOrders(prev => prev.map(x => x.id === o.id ? { ...x, status:'completed' } : x));
                            axios.get(API+'/income').then(r => setIncomeLog(r.data));
                            flash('✅ עבודה הושלמה! ההכנסה נרשמה בקופת יוסטאר');
                          }}
                        />
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })()}

        {/* ══════ SHOOT SCHEDULE TAB — לוז צילומים ══════ */}
        {tab === 'schedule' && (() => {
          const HEB_DAYS   = ['ראשון','שני','שלישי','רביעי','חמישי','שישי','שבת'];
          const HEB_MON    = ['ינואר','פברואר','מרץ','אפריל','מאי','יוני','יולי','אוגוסט','ספטמבר','אוקטובר','נובמבר','דצמבר'];
          const AGE_GROUPS_S = ['טרום א','ילדים ג','ילדים ב','ילדים א','נערים ג','נערים ב','נערים א','נוער'];

          // ── חוקי צילום אסטרטגיים ──
          // הסדר הוא לפי שכבות הגיל: טרום א → ילדים ג → ילדים ב → ילדים א → נערים ג → נערים ב → נערים א → נוער
          const SHOOT_RULES = [
            {
              id: 'kids',
              ages: ['טרום א','ילדים ג','ילדים ב'],
              agesLabel: 'טרום א · ילדים ג · ילדים ב',
              yearsLabel: 'גילאי 11–13',
              type: 'video',
              typeLabel: '🎥 וידאו — צילום יזום',
              tier: 'דגש דרג 1, מדי פעם דרג 2',
              tiers: [1,2],
              product: 'קליפ ביצועים אישי ממשחק מצולם',
              price: 270,
              why: 'גיל צעיר — הביקוש יכול להיות גבוה',
              color: '#25d366',
            },
            {
              id: 'u14',
              ages: ['נערים ג'],
              agesLabel: 'נערים ג',
              yearsLabel: 'גיל 14',
              type: 'video',
              typeLabel: '🎥 וידאו — צילום יזום',
              tier: 'דרג 1–2',
              tiers: [1,2],
              product: 'קליפ ביצועים',
              price: 270,
              why: 'ביקוש גבוה לקליפים אישיים — דדליין לפני גיל 15',
              color: '#7ab0ff',
            },
            {
              id: 'seniors',
              ages: ['נערים ב','נערים א','נוער'],
              agesLabel: 'נערים ב · נערים א · נוער',
              yearsLabel: 'גילאי 16–18',
              type: 'stills',
              typeLabel: '📷 סטילס בלבד',
              tier: 'דרג 1 בלבד',
              tiers: [1],
              product: 'חבילת תמונות לשחקן',
              price: 70,
              why: 'התדמית חשובה מאוד לשחקנים בגיל הזה',
              color: '#f0cc60',
            },
          ];
          const ruleFor = ag => SHOOT_RULES.find(r => r.ages.includes(ag)) || null;

          // צילומי סופ״ש (שישי/שבת) שכבר נקבעו החודש — מגבלה של פעם בחודש במרכז
          const monthKeyOfDate = d => (d||'').slice(0,7);
          const thisMonthKey = (() => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; })();
          const weekendThisMonth = shootSchedule.filter(x => {
            if (monthKeyOfDate(x.shoot_date) !== thisMonthKey) return false;
            const dow = new Date(String(x.shoot_date).slice(0,10)+'T00:00:00').getDay();
            return dow === 5 || dow === 6; // שישי / שבת
          }).length;
          // ── מנוע הפרות: בודק כל צילום מול החוקים ──
          const tierOf = name => tierOfClub(name, tierOverrides);
          const isWeekendIso = d => { const w = new Date(String(d).slice(0,10)+'T00:00:00').getDay(); return w === 5 || w === 6; };

          // צילומי סופ״ש לפי חודש — כל אחד מעבר לראשון נחשב חריגה
          const weekendOrder = {};
          shootSchedule
            .filter(x => isWeekendIso(x.shoot_date))
            .sort((a,b) => String(a.shoot_date).localeCompare(String(b.shoot_date)))
            .forEach(x => {
              const k = monthKeyOfDate(x.shoot_date);
              weekendOrder[k] = (weekendOrder[k] || 0) + 1;
              x.__weekendIdx = weekendOrder[k];
            });

          const violationsOf = sl => {
            const out = [];
            const rl = ruleFor(sl.age_group);
            if (rl && sl.shoot_type && sl.shoot_type !== rl.type) {
              out.push({
                kind: 'type',
                short: sl.shoot_type === 'stills' ? 'תמונות במקום וידאו' : 'וידאו במקום סטילס',
                text: `${sl.age_group}: צולם ב${sl.shoot_type==='stills'?'תמונות':'וידאו'} — החוק הוא ${rl.type==='stills'?'סטילס בלבד':'וידאו'}`,
              });
            }
            if (rl && rl.tiers) {
              const t = tierOf(sl.team_a);
              if (t && !rl.tiers.includes(t)) {
                out.push({ kind:'tier', short:`דרג ${t}`, text:`${sl.team_a} הוא דרג ${t} — לשכבת ${sl.age_group} החוק הוא ${rl.tier}` });
              }
            }
            if (isWeekendIso(sl.shoot_date) && sl.__weekendIdx > 1) {
              out.push({ kind:'weekend', short:'סופ״ש חריג', text:`צילום סופ״ש מספר ${sl.__weekendIdx} בחודש — ההמלצה היא אחד בלבד` });
            }
            return out;
          };

          const allViolations = shootSchedule
            .map(sl => ({ sl, v: violationsOf(sl) }))
            .filter(x => x.v.length > 0)
            .sort((a,b) => String(b.sl.shoot_date).localeCompare(String(a.sl.shoot_date)));

          const MATCH_CATS_S = ['אימון','ליגה','גביע','טורניר'];
          const memberTeamsS = members.map(m => m.team).filter(Boolean);
          const teamOptionsS = [...new Set([...FOOTBALL_CLUBS, ...memberTeamsS])].sort((a,b)=>a.localeCompare(b,'he'));
          const nowYS = new Date().getFullYear();
          const YEARS_S = Array.from({length:16}, (_,i)=>nowYS-4-i);

          const iso = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
          const todayIso = iso(new Date());
          // תחילת השבוע = יום ראשון
          const weekStart = (() => { const d = new Date(); d.setHours(0,0,0,0); d.setDate(d.getDate() - d.getDay() + weekOffset*7); return d; })();
          const days = Array.from({length:7}, (_,i) => { const d = new Date(weekStart); d.setDate(d.getDate()+i); return d; });
          const weekEnd = days[6];
          const rangeLabel = `${weekStart.getDate()} ${HEB_MON[weekStart.getMonth()]} – ${weekEnd.getDate()} ${HEB_MON[weekEnd.getMonth()]} ${weekEnd.getFullYear()}`;

          const slotsOf = dIso => shootSchedule.filter(x => (x.shoot_date||'').slice(0,10) === dIso)
            .sort((a,b) => (a.shoot_time||'99').localeCompare(b.shoot_time||'99'));
          const weekSlots = days.flatMap(d => slotsOf(iso(d)));
          const weekPlanned  = weekSlots.filter(x => x.status !== 'approved').length;
          const weekApproved = weekSlots.filter(x => x.status === 'approved').length;

          const inS = { width:'100%', background:'#0a1a3e', border:'1px solid #2a3a6a', borderRadius:8, padding:'8px 11px', color:'#fff', fontSize:'0.86rem', boxSizing:'border-box' };

          const createSlot = async (dateIso) => {
            if (!newSlot.team_a) return flash('בחר קבוצה', 'error');
            const { data } = await axios.post(API+'/shoot-schedule', { ...newSlot, shoot_date: dateIso });
            setShootSchedule(prev => [...prev, data]);
            setNewSlot({ shoot_time:'', team_a:'', team_b:'', age_group:'', birth_year:'', shoot_type:'video', match_category:'', location:'', notes:'' });
            setShowNewSlot(null);
            flash('🗓 נוסף ללוז');
          };

          const approveSlot = async (slot) => {
            if (!window.confirm(`לאשר שהמשחק ${slot.team_a} צולם? הוא ייכנס ליוסטאר מצלמים למעקב מוצרים.`)) return;
            const { data } = await axios.post(API+`/shoot-schedule/${slot.id}/approve`);
            setShootSchedule(prev => prev.map(x => x.id===slot.id ? data.schedule : x));
            setGameShoots(prev => [data.shoot, ...prev]);
            flash('✅ אושר — הצילום נכנס ליוסטאר מצלמים');
          };

          return (
            <div style={{ padding:'24px 20px', maxWidth:1100, margin:'0 auto' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12, marginBottom:16 }}>
                <div>
                  <h2 style={{ color:'#d4af37', margin:0, fontSize:'1.4rem' }}>🗓 לוז צילומים</h2>
                  <p style={{ color:'#8a9aba', margin:'5px 0 0', fontSize:'0.88rem' }}>
                    תכנון שבוע העבודה — ראשון עד שבת · אפשר לקבוע כמה משחקים באותו יום.
                    משחק שאושר עובר ל<strong style={{ color:'#25d366' }}>יוסטאר מצלמים</strong> למעקב מוצרים.
                  </p>
                  <div style={{ display:'flex', gap:14, marginTop:7, fontSize:'0.76rem', flexWrap:'wrap' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:5, color:'#8a9aba' }}>
                      <span style={{ width:8, height:8, borderRadius:'50%', background:'#ffffff', display:'inline-block' }} /> קבוצה ביתית
                    </span>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:5, color:'#8a9aba' }}>
                      <span style={{ width:8, height:8, borderRadius:'50%', background:'#5a9bf5', display:'inline-block' }} /> קבוצת חוץ
                    </span>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <button onClick={()=>setWeekOffset(w=>w-1)} style={{ background:'#0a1a3e', color:'#c0cce6', border:'1px solid #2a3a6a', borderRadius:8, padding:'7px 13px', cursor:'pointer', fontWeight:800 }}>→</button>
                  <div style={{ textAlign:'center', minWidth:190 }}>
                    <div style={{ color:'#d4af37', fontWeight:900, fontSize:'0.9rem' }}>{rangeLabel}</div>
                    <div style={{ color:'#4a5a7a', fontSize:'0.73rem' }}>
                      {weekOffset === 0 ? 'השבוע הנוכחי' : weekOffset === 1 ? 'שבוע הבא' : weekOffset === -1 ? 'שבוע שעבר' : `${Math.abs(weekOffset)} שבועות ${weekOffset>0?'קדימה':'אחורה'}`}
                    </div>
                  </div>
                  <button onClick={()=>setWeekOffset(w=>w+1)} style={{ background:'#0a1a3e', color:'#c0cce6', border:'1px solid #2a3a6a', borderRadius:8, padding:'7px 13px', cursor:'pointer', fontWeight:800 }}>←</button>
                  {weekOffset !== 0 && (
                    <button onClick={()=>setWeekOffset(0)} style={{ background:'rgba(212,175,55,0.12)', color:'#d4af37', border:'1px solid rgba(212,175,55,0.4)', borderRadius:8, padding:'7px 13px', cursor:'pointer', fontSize:'0.8rem', fontWeight:700 }}>היום</button>
                  )}
                </div>
              </div>

              {/* סיכום השבוע */}
              <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:16 }}>
                {[
                  { l:'🗓 צילומים בשבוע', v:weekSlots.length, c:'#d4af37' },
                  { l:'⏳ ממתינים לצילום', v:weekPlanned, c:'#ff9966' },
                  { l:'✅ אושרו', v:weekApproved, c:'#25d366' },
                ].map(k => (
                  <div key={k.l} style={{ background:'#0d1b3e', border:`1px solid ${k.c}33`, borderRadius:11, padding:'10px 16px', flex:'1 1 140px' }}>
                    <div style={{ color:'#8a9aba', fontSize:'0.73rem', marginBottom:3 }}>{k.l}</div>
                    <div style={{ color:k.c, fontWeight:900, fontSize:'1.4rem', lineHeight:1 }}>{k.v}</div>
                  </div>
                ))}
              </div>

              {/* ── התראת הפרות ── */}
              {allViolations.length > 0 && (
                <div style={{ background:'rgba(255,80,80,0.09)', border:'2px solid rgba(255,80,80,0.5)', borderRadius:14, padding:'14px 18px', marginBottom:16 }}>
                  <div style={{ color:'#ff6b6b', fontWeight:900, fontSize:'0.95rem', marginBottom:10 }}>
                    🚨 הפרות חוקי צילום ({allViolations.length})
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {allViolations.map(({ sl, v }) => (
                      <div key={sl.id} style={{ background:'rgba(0,0,0,0.25)', border:'1px solid rgba(255,80,80,0.25)', borderRadius:9, padding:'9px 12px' }}>
                        <div style={{ color:'#fff', fontWeight:800, fontSize:'0.83rem' }}>
                          {niceDate(sl.shoot_date)} — {sl.team_a}{sl.team_b ? ` נגד ${sl.team_b}` : ''}
                          {sl.age_group ? ` · ${sl.age_group}` : ''}
                        </div>
                        {v.map((x, i) => (
                          <div key={i} style={{ color:'#ff9999', fontSize:'0.79rem', marginTop:3 }}>⚠️ {x.text}</div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── חוקי צילום — תזכורת לבחירת משחקים ── */}
              <div style={{ background:'linear-gradient(135deg,rgba(212,175,55,0.07),rgba(212,175,55,0.02))', border:'1px solid rgba(212,175,55,0.3)', borderRadius:14, marginBottom:16 }}>
                <button onClick={()=>setShowRules(v=>!v)}
                  style={{ width:'100%', background:'none', border:'none', cursor:'pointer', padding:'12px 18px',
                    display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                  <span style={{ color:'#d4af37', fontWeight:900, fontSize:'0.95rem' }}>📋 חוקי צילום — לפי מה לבחור משחקים</span>
                  <span style={{ color:'#4a5a7a', fontSize:'0.8rem' }}>{showRules ? '▲ סגור' : '▼ פתח'}</span>
                </button>

                {showRules && (
                  <div style={{ borderTop:'1px solid rgba(212,175,55,0.2)', padding:'14px 18px' }}>
                    {/* כללים לפי שכבת גיל */}
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:11, marginBottom:14 }}>
                      {SHOOT_RULES.map(r => (
                        <div key={r.id} style={{ background:'rgba(0,0,0,0.25)', border:`1px solid ${r.color}55`, borderRadius:11, padding:'12px 14px' }}>
                          <div style={{ color:r.color, fontWeight:900, fontSize:'0.88rem' }}>{r.agesLabel}</div>
                          <div style={{ color:'#4a5a7a', fontSize:'0.73rem', marginBottom:8 }}>{r.yearsLabel}</div>
                          <div style={{ display:'flex', flexDirection:'column', gap:4, fontSize:'0.79rem' }}>
                            <div style={{ color:'#fff', fontWeight:800 }}>{r.typeLabel}</div>
                            <div style={{ color:'#c0cce6' }}>🏅 {r.tier}</div>
                            <div style={{ color:'#d4af37', fontWeight:700 }}>💰 {r.product} — ₪{r.price}</div>
                            <div style={{ color:'#8a9aba', fontSize:'0.74rem', marginTop:3, lineHeight:1.5 }}>💡 {r.why}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* כללי תזמון ומיקום */}
                    <div style={{ background:'rgba(122,176,255,0.06)', border:'1px solid rgba(122,176,255,0.28)', borderRadius:11, padding:'12px 14px' }}>
                      <div style={{ color:'#7ab0ff', fontWeight:900, fontSize:'0.86rem', marginBottom:8 }}>🕐 תזמון ומיקום</div>
                      <div style={{ display:'flex', flexDirection:'column', gap:5, fontSize:'0.8rem' }}>
                        <div style={{ color:'#25d366' }}>✅ <strong>אמצע שבוע = הרווחי ביותר</strong> — עדיפות ראשונה</div>
                        <div style={{ color:'#c0cce6' }}>✅ מרכז באמצע השבוע — אין בעיה, יש תחבורה</div>
                        <div style={{ color:'#ff9966' }}>⚠️ מרכז בשישי–שבת — <strong>מקסימום פעם בחודש</strong> (עלויות לינה ונסיעות)</div>
                      </div>
                      <div style={{ marginTop:10, paddingTop:9, borderTop:'1px solid rgba(122,176,255,0.2)',
                        display:'flex', justifyContent:'space-between', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                        <span style={{ color:'#8a9aba', fontSize:'0.79rem' }}>צילומי סופ״ש שנקבעו החודש:</span>
                        <span style={{ color: weekendThisMonth > 1 ? '#ff6b6b' : weekendThisMonth === 1 ? '#ff9966' : '#25d366',
                          fontWeight:900, fontSize:'0.95rem' }}>
                          {weekendThisMonth} / 1 {weekendThisMonth > 1 ? '— חריגה!' : weekendThisMonth === 1 ? '— נוצלה המכסה' : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* לוח השבוע */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(145px,1fr))', gap:9 }}>
                {days.map((d, i) => {
                  const dIso = iso(d);
                  const isToday = dIso === todayIso;
                  const slots = slotsOf(dIso);
                  return (
                    <div key={dIso} style={{ background: isToday?'rgba(212,175,55,0.07)':'#0d1b3e',
                      border:`1px solid ${isToday?'rgba(212,175,55,0.5)':'#1e3060'}`, borderRadius:12, padding:'10px 9px', minHeight:150 }}>
                      <div style={{ textAlign:'center', marginBottom:8, paddingBottom:7, borderBottom:'1px solid #16233f' }}>
                        <div style={{ color: isToday?'#d4af37':'#c0cce6', fontWeight:900, fontSize:'0.83rem' }}>{HEB_DAYS[i]}</div>
                        <div style={{ color:'#4a5a7a', fontSize:'0.72rem' }}>{d.getDate()}/{d.getMonth()+1}{isToday?' • היום':''}</div>
                      </div>

                      <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:7 }}>
                        {slots.length === 0 && <div style={{ color:'#2e3d58', fontSize:'0.73rem', textAlign:'center', padding:'6px 0' }}>—</div>}
                        {slots.map(sl => {
                          const approved = sl.status === 'approved';
                          const isStills = sl.shoot_type === 'stills';
                          const viol = violationsOf(sl);
                          const slTier = tierOf(sl.team_a);
                          return (
                            <div key={sl.id} style={{ background: viol.length?'rgba(255,80,80,0.08)':approved?'rgba(37,211,102,0.09)':'rgba(255,255,255,0.04)',
                              border:`1px solid ${viol.length?'rgba(255,80,80,0.55)':approved?'rgba(37,211,102,0.4)':isStills?'rgba(240,204,96,0.35)':'rgba(122,176,255,0.35)'}`,
                              borderRadius:8, padding:'7px 8px' }}>
                              {viol.length > 0 && (
                                <div title={viol.map(v=>v.text).join('\n')}
                                  style={{ background:'rgba(255,80,80,0.16)', border:'1px solid rgba(255,80,80,0.45)', borderRadius:5,
                                    padding:'2px 6px', marginBottom:5, color:'#ff8888', fontSize:'0.64rem', fontWeight:900 }}>
                                  🚨 הפרת חוק: {viol.map(v=>v.short).join(' · ')}
                                </div>
                              )}
                              <div style={{ display:'flex', alignItems:'center', gap:4, marginBottom:4, flexWrap:'wrap' }}>
                                {sl.shoot_time && <span style={{ color:'#d4af37', fontWeight:800, fontSize:'0.72rem' }}>{sl.shoot_time}</span>}
                                {approved && <span style={{ color:'#25d366', fontSize:'0.68rem', fontWeight:800 }}>✅</span>}
                              </div>
                              {/* סוג הצילום — טקסט בולט */}
                              <div style={{ display:'inline-block', background: isStills?'rgba(240,204,96,0.16)':'rgba(122,176,255,0.16)',
                                color: isStills?'#f0cc60':'#7ab0ff', border:`1px solid ${isStills?'rgba(240,204,96,0.45)':'rgba(122,176,255,0.45)'}`,
                                borderRadius:5, padding:'1px 6px', fontSize:'0.65rem', fontWeight:900, marginBottom:4 }}>
                                {isStills ? '📷 תמונות' : '🎥 וידאו'}
                              </div>
                              {/* ⚪ ביתית · 🔵 חוץ */}
                              <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                                <span style={{ width:7, height:7, borderRadius:'50%', background:'#ffffff', display:'inline-block', flexShrink:0 }} />
                                <span style={{ color:'#ffffff', fontWeight:800, fontSize:'0.75rem', lineHeight:1.3 }}>{sl.team_a}</span>
                                {slTier && <span style={{ background: slTier===1?'rgba(212,175,55,0.18)':'rgba(122,176,255,0.16)',
                                  color: slTier===1?'#d4af37':'#7ab0ff', borderRadius:4, padding:'0 4px', fontSize:'0.6rem', fontWeight:900 }}>ד{slTier}</span>}
                              </div>
                              {sl.team_b && (
                                <div style={{ display:'flex', alignItems:'center', gap:4, marginTop:1 }}>
                                  <span style={{ width:7, height:7, borderRadius:'50%', background:'#5a9bf5', display:'inline-block', flexShrink:0 }} />
                                  <span style={{ color:'#5a9bf5', fontWeight:700, fontSize:'0.72rem' }}>{sl.team_b}</span>
                                </div>
                              )}
                              {(sl.age_group || sl.birth_year) && (
                                <div style={{ color:'#4a5a7a', fontSize:'0.68rem', marginTop:2 }}>{[sl.age_group, sl.birth_year].filter(Boolean).join(' · ')}</div>
                              )}
                              {sl.match_category && <div style={{ color:'#25d366', fontSize:'0.67rem' }}>🏆 {sl.match_category}</div>}
                              {sl.location && <div style={{ color:'#4a5a7a', fontSize:'0.67rem' }}>📍 {sl.location}</div>}
                              <div style={{ display:'flex', gap:4, marginTop:5 }}>
                                {!approved ? (
                                  <button onClick={()=>approveSlot(sl)} title="אשר שצולם"
                                    style={{ flex:1, background:'rgba(37,211,102,0.14)', color:'#25d366', border:'1px solid rgba(37,211,102,0.4)', borderRadius:6, padding:'3px 0', cursor:'pointer', fontSize:'0.68rem', fontWeight:800 }}>✅ צולם</button>
                                ) : (
                                  <button onClick={()=>setTab('filming')} title="למעקב ביוסטאר מצלמים"
                                    style={{ flex:1, background:'rgba(122,176,255,0.12)', color:'#7ab0ff', border:'1px solid rgba(122,176,255,0.35)', borderRadius:6, padding:'3px 0', cursor:'pointer', fontSize:'0.68rem', fontWeight:800 }}>🎥 למעקב</button>
                                )}
                                <button onClick={async()=>{
                                  if(!window.confirm('להסיר מהלוז?')) return;
                                  await axios.delete(API+'/shoot-schedule/'+sl.id);
                                  setShootSchedule(prev => prev.filter(x=>x.id!==sl.id));
                                }} style={{ background:'rgba(255,80,80,0.07)', color:'#ff8888', border:'1px solid rgba(255,80,80,0.25)', borderRadius:6, padding:'3px 6px', cursor:'pointer', fontSize:'0.66rem' }}>🗑</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <button onClick={()=>{ setShowNewSlot(showNewSlot===dIso?null:dIso); setNewSlot(n=>({ ...n, team_a:'', team_b:'' })); }}
                        style={{ width:'100%', background: showNewSlot===dIso?'rgba(212,175,55,0.2)':'transparent',
                          color:'#d4af37', border:'1px dashed rgba(212,175,55,0.45)', borderRadius:7, padding:'5px 0', cursor:'pointer', fontSize:'0.73rem', fontWeight:700 }}>
                        {showNewSlot===dIso ? '▲ סגור' : '+ צילום'}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* טופס הוספה ליום שנבחר */}
              {showNewSlot && (
                <div style={{ marginTop:16, background:'linear-gradient(135deg,rgba(212,175,55,0.08),rgba(212,175,55,0.03))', border:'2px solid rgba(212,175,55,0.4)', borderRadius:14, padding:'18px 20px' }}>
                  <div style={{ color:'#d4af37', fontWeight:900, fontSize:'0.95rem', marginBottom:12 }}>
                    ➕ צילום חדש — {(() => { const d = new Date(showNewSlot+'T00:00:00'); return `יום ${HEB_DAYS[d.getDay()]}, ${d.getDate()} ${HEB_MON[d.getMonth()]}`; })()}
                  </div>

                  {/* סוג צילום */}
                  <div style={{ color:'#8a9aba', fontSize:'0.78rem', fontWeight:700, marginBottom:6 }}>סגנון צילום:</div>
                  <div style={{ display:'flex', gap:8, marginBottom:12 }}>
                    {[{v:'video',l:'🎥 וידאו'},{v:'stills',l:'📷 תמונות'}].map(t => {
                      const sel = newSlot.shoot_type === t.v;
                      return (
                        <button key={t.v} type="button" onClick={()=>setNewSlot(n=>({...n, shoot_type:t.v}))}
                          style={{ flex:1, padding:'9px', borderRadius:9, cursor:'pointer', fontWeight:800, fontSize:'0.86rem',
                            border:`2px solid ${sel?(t.v==='stills'?'#f0cc60':'#7ab0ff'):'#2a3a6a'}`,
                            background: sel?(t.v==='stills'?'rgba(240,204,96,0.12)':'rgba(122,176,255,0.12)'):'#0a1525',
                            color: sel?(t.v==='stills'?'#f0cc60':'#7ab0ff'):'#8a9aba' }}>{t.l}</button>
                      );
                    })}
                  </div>

                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:10, marginBottom:10 }}>
                    <div>
                      <div style={{ color:'#8a9aba', fontSize:'0.74rem', marginBottom:4 }}>שעה</div>
                      <input type="time" value={newSlot.shoot_time} onChange={e=>setNewSlot(n=>({...n, shoot_time:e.target.value}))} style={inS} />
                    </div>
                    <div>
                      <div style={{ color:'#8a9aba', fontSize:'0.74rem', marginBottom:4 }}>קבוצה ביתית *</div>
                      <TeamSearchInput value={newSlot.team_a} onChange={v=>setNewSlot(n=>({...n, team_a:v}))} options={teamOptionsS} placeholder="הקלד שם קבוצה" />
                    </div>
                    <div>
                      <div style={{ color:'#8a9aba', fontSize:'0.74rem', marginBottom:4 }}>קבוצת חוץ</div>
                      <TeamSearchInput value={newSlot.team_b} onChange={v=>setNewSlot(n=>({...n, team_b:v}))} options={teamOptionsS.filter(t=>t!==newSlot.team_a)} placeholder="יריבה" />
                    </div>
                  </div>

                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:10, marginBottom:12 }}>
                    <select value={newSlot.match_category} onChange={e=>setNewSlot(n=>({...n, match_category:e.target.value}))} style={inS}>
                      <option value="">— סוג התמודדות —</option>
                      {MATCH_CATS_S.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select value={newSlot.age_group} onChange={e=>{
                      const ag = e.target.value; const rl = ruleFor(ag);
                      setNewSlot(n=>({ ...n, age_group:ag, shoot_type: rl ? rl.type : n.shoot_type }));
                    }} style={inS}>
                      <option value="">— שכבת גיל —</option>
                      {AGE_GROUPS_S.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <select value={newSlot.birth_year} onChange={e=>setNewSlot(n=>({...n, birth_year:e.target.value}))} style={inS}>
                      <option value="">— שנתון —</option>
                      {YEARS_S.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <input value={newSlot.location} placeholder="📍 מיקום / מגרש"
                      onChange={e=>setNewSlot(n=>({...n, location:e.target.value}))} style={inS} />
                  </div>

                  <input value={newSlot.notes} placeholder="הערות (אופציונלי)"
                    onChange={e=>setNewSlot(n=>({...n, notes:e.target.value}))} style={{ ...inS, marginBottom:12 }} />

                  {/* המלצה חיה לפי החוקים */}
                  {(() => {
                    const rl = ruleFor(newSlot.age_group);
                    const d = new Date(showNewSlot+'T00:00:00');
                    const isWeekend = d.getDay() === 5 || d.getDay() === 6;
                    const typeMismatch = rl && newSlot.shoot_type !== rl.type;
                    if (!rl && !isWeekend) return null;
                    return (
                      <div style={{ marginBottom:12, display:'flex', flexDirection:'column', gap:7 }}>
                        {rl && (
                          <div style={{ background:`${rl.color}14`, border:`1px solid ${rl.color}66`, borderRadius:10, padding:'10px 13px' }}>
                            <div style={{ color:rl.color, fontWeight:900, fontSize:'0.83rem', marginBottom:4 }}>
                              📋 חוק לשכבת {newSlot.age_group} ({rl.yearsLabel})
                            </div>
                            <div style={{ color:'#c0cce6', fontSize:'0.79rem', lineHeight:1.7 }}>
                              {rl.typeLabel} · 🏅 {rl.tier} · 💰 {rl.product} ₪{rl.price}
                              <br/><span style={{ color:'#8a9aba' }}>💡 {rl.why}</span>
                            </div>
                            {(() => {
                              const t = tierOf(newSlot.team_a);
                              if (!t || !rl.tiers || rl.tiers.includes(t)) return null;
                              return (
                                <div style={{ marginTop:7, color:'#ff6b6b', fontSize:'0.79rem', fontWeight:800 }}>
                                  🚨 {newSlot.team_a} הוא דרג {t} — לשכבה הזו החוק הוא {rl.tier}
                                </div>
                              );
                            })()}
                            {typeMismatch && (
                              <div style={{ marginTop:7, color:'#ff6b6b', fontSize:'0.79rem', fontWeight:800 }}>
                                ⚠️ בחרת {newSlot.shoot_type==='stills'?'תמונות':'וידאו'} — לשכבה הזו מומלץ {rl.type==='stills'?'סטילס':'וידאו'}
                              </div>
                            )}
                          </div>
                        )}
                        {isWeekend && (
                          <div style={{ background: weekendThisMonth >= 1 ? 'rgba(255,107,107,0.1)' : 'rgba(255,153,102,0.1)',
                            border:`1px solid ${weekendThisMonth >= 1 ? 'rgba(255,107,107,0.5)' : 'rgba(255,153,102,0.45)'}`, borderRadius:10, padding:'10px 13px' }}>
                            <div style={{ color: weekendThisMonth >= 1 ? '#ff6b6b' : '#ff9966', fontWeight:800, fontSize:'0.81rem' }}>
                              {weekendThisMonth >= 1
                                ? `⚠️ כבר נקבע צילום סופ״ש החודש (${weekendThisMonth}) — במרכז ההמלצה היא פעם בחודש בלבד`
                                : '⚠️ צילום בסופ״ש — במרכז מומלץ מקסימום פעם בחודש (לינה ונסיעות)'}
                            </div>
                            <div style={{ color:'#8a9aba', fontSize:'0.76rem', marginTop:3 }}>אמצע שבוע הוא הרווחי ביותר</div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <button onClick={()=>createSlot(showNewSlot)}
                    style={{ background:'linear-gradient(135deg,#d4af37,#b8941f)', color:'#000', border:'none', borderRadius:10, padding:'10px 24px', cursor:'pointer', fontWeight:900, fontSize:'0.88rem' }}>
                    🗓 הוסף ללוז
                  </button>
                </div>
              )}

              {/* ── קבוצות שכבר צולמו — אינדיקציה למה שווה להמשיך לצלם ── */}
              {gameShoots.length > 0 && (() => {
                const agg = {};
                const touch = (team, side, sh) => {
                  if (!team) return;
                  const key = `${team}||${sh.age_group||'—'}||${sh.birth_year||'—'}`;
                  if (!agg[key]) agg[key] = { team, age_group:sh.age_group||'—', birth_year:sh.birth_year||'—',
                                              shoots:0, video:0, stills:0, orders:0, revenue:0, lastDate:'' };
                  const a = agg[key];
                  a.shoots += 1;
                  if (sh.shoot_type === 'stills') a.stills += 1; else a.video += 1;
                  if ((sh.game_date||'') > a.lastDate) a.lastDate = sh.game_date || '';
                  shootTallies.filter(t => t.shoot_id === sh.id && (t.team_side||'home') === side).forEach(t => {
                    a.orders  += (t.quantity||0);
                    a.revenue += (t.quantity||0) * (t.unit_price||0);
                  });
                  shootOrders.filter(o => o.shoot_id === sh.id && o.team === team).forEach(o => {
                    a.orders  += 1;
                    a.revenue += (o.price||0);
                  });
                };
                gameShoots.forEach(sh => { touch(sh.team_a, 'home', sh); touch(sh.team_b, 'away', sh); });
                const rows = Object.values(agg).sort((a,b) => b.orders - a.orders || b.shoots - a.shoots);
                if (rows.length === 0) return null;
                return (
                  <div style={{ marginTop:22 }}>
                    <div style={{ color:'#c0cce6', fontWeight:900, fontSize:'0.95rem', marginBottom:4 }}>🎥 קבוצות שכבר צולמו</div>
                    <div style={{ color:'#4a5a7a', fontSize:'0.79rem', marginBottom:11 }}>
                      כמה פעמים צילמתי כל קבוצה, באיזה סגנון, וכמה הזמנות יצאו משם — אינדיקציה אם שווה לחזור ולצלם
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
                      {rows.map(r => {
                        const hot = r.orders > 0;
                        return (
                          <div key={`${r.team}-${r.age_group}-${r.birth_year}`}
                            style={{ background:'#0d1b3e', border:`1px solid ${hot?'rgba(37,211,102,0.4)':'#1e3060'}`, borderRadius:10, padding:'10px 14px',
                              display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', flex:'1 1 260px', minWidth:0 }}>
                              <span style={{ color:'#fff', fontWeight:800, fontSize:'0.9rem' }}>{r.team}</span>
                              <span style={{ color:'#8a9aba', fontSize:'0.78rem' }}>{[r.age_group !== '—' ? r.age_group : null, r.birth_year !== '—' ? r.birth_year : null].filter(Boolean).join(' · ')}</span>
                              {r.lastDate && <span style={{ color:'#4a5a7a', fontSize:'0.73rem' }}>אחרון: {String(r.lastDate).slice(0,10).split('-').reverse().join('/')}</span>}
                            </div>
                            <span style={{ background:'rgba(212,175,55,0.14)', color:'#d4af37', borderRadius:20, padding:'2px 10px', fontSize:'0.78rem', fontWeight:900, whiteSpace:'nowrap' }}>
                              🎬 {r.shoots} צילומים
                            </span>
                            <span style={{ fontSize:'0.78rem', whiteSpace:'nowrap' }}>
                              <span style={{ color:'#7ab0ff', fontWeight:700 }}>🎥 {r.video}</span>
                              <span style={{ color:'#4a5a7a' }}> / </span>
                              <span style={{ color:'#f0cc60', fontWeight:700 }}>📷 {r.stills}</span>
                            </span>
                            <span style={{ color: hot?'#25d366':'#4a5a7a', fontWeight:800, fontSize:'0.82rem', whiteSpace:'nowrap' }}>
                              📦 {r.orders} הזמנות
                            </span>
                            <span style={{ color: hot?'#d4af37':'#4a5a7a', fontWeight:900, fontSize:'0.95rem', whiteSpace:'nowrap' }}>
                              ₪{Math.round(r.revenue).toLocaleString('he-IL')}
                            </span>
                            <button onClick={()=>{ setShowNewSlot(iso(new Date())); setNewSlot(n=>({ ...n, team_a:r.team, age_group:r.age_group!=='—'?r.age_group:'', birth_year:r.birth_year!=='—'?String(r.birth_year):'' })); }}
                              title="קבע צילום נוסף לקבוצה"
                              style={{ background:'rgba(212,175,55,0.12)', color:'#d4af37', border:'1px solid rgba(212,175,55,0.4)', borderRadius:7, padding:'5px 11px', cursor:'pointer', fontSize:'0.76rem', fontWeight:700, whiteSpace:'nowrap' }}>
                              🗓 צלם שוב
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

            </div>
          );
        })()}

        {/* ══════ FILMING TAB — יוסטאר מצלמים ══════ */}
        {tab === 'filming' && (() => {
          const memberYear = m => m.birth_year || (m.child_dob ? new Date(m.child_dob).getFullYear() : null);
          // בחירת קבוצות מהמאגר המלא של הטופס + כל קבוצה נוספת שמופיעה אצל חבר קיים
          const memberTeams = members.map(m => m.team).filter(Boolean);
          const allTeams = [...new Set([...FOOTBALL_CLUBS, ...memberTeams])].sort((a,b)=>a.localeCompare(b,'he'));
          const nowY = new Date().getFullYear();
          const rangeYears = Array.from({length:15}, (_,i)=>nowY-4-i); // גילאי ~4 עד ~18
          const allYears = [...new Set([...members.map(memberYear).filter(Boolean), ...rangeYears])].sort((a,b)=>b-a);

          // חלוקה לפי קבוצה × שנתון
          const groups = {};
          members.forEach(m => {
            const t = m.team, y = memberYear(m);
            if (!t || !y) return;
            const key = `${t}||${y}`;
            if (!groups[key]) groups[key] = { team:t, year:y, members:[] };
            groups[key].members.push(m);
          });
          const groupList = Object.values(groups).sort((a,b) => a.team.localeCompare(b.team,'he') || b.year - a.year);

          const matchedForShoot = s => members.filter(m => {
            const y = memberYear(m);
            if (s.birth_year && String(y) !== String(s.birth_year)) return false;
            return m.team === s.team_a || (s.team_b && m.team === s.team_b);
          });

          const shootMsg = (m, s) => `שלום ${m.first_name}! 🎥\n\nצוות יוסטאר צילם את המשחק של *${m.team}* (שנתון ${s.birth_year})${s.team_b ? ` מול ${m.team===s.team_a ? s.team_b : s.team_a}` : ''}${s.game_date ? ` (${s.game_date})` : ''}! ⚽\n\nרוצים *קליפ ביצועים אישי* של ${m.child_name} מהמשחק? 🎬\nהקליפ מתעד את הרגעים הכי טובים של השחקן/ית שלכם על המגרש.\n\nלהזמנה ולפרטים: 054-6318704\n\nליגת הכוכבים של יוסטאר ⭐`;

          const AGE_GROUPS = ['טרום א','ילדים ג','ילדים ב','ילדים א','נערים ג','נערים ב','נערים א','נוער'];
          const MATCH_CATEGORIES = ['אימון','ליגה','גביע','טורניר'];
          const SHOOT_PRODUCTS = ['קליפ ביצועים','תמונות משחק'];

          const createShoot = async () => {
            if (!newShoot.team_a) return flash('בחר לפחות קבוצה אחת', 'error');
            const { data } = await axios.post(API+'/game-shoots', {
              team_a: newShoot.team_a, team_b: newShoot.team_b || null,
              birth_year: newShoot.birth_year || null, game_date: newShoot.game_date || null, notes: newShoot.notes || null,
              shoot_type: newShoot.shoot_type || 'video', age_group: newShoot.age_group || null,
              match_category: newShoot.match_category || null,
              squad_size: newShoot.squad_size || null, active_players: newShoot.active_players || null,
            });
            setGameShoots(prev => [data, ...prev]);
            setNewShoot({ team_a:'', team_b:'', birth_year:'', game_date:'', notes:'', shoot_type:'video', age_group:'', match_category:'', squad_size:'', active_players:'' });
            setExpandedShootTally(data.id);
            flash('🎥 צילום משחק נוצר!');
          };

          // מוצר ברירת מחדל לפי סגנון הצילום — נלקח מקטלוג מוצרי יוסטאר
          const defaultProductFor = shoot => {
            const sale = products.filter(p => p.product_type === 'sale');
            const wantPhotos = shoot.shoot_type === 'stills';
            // קודם לפי סוג המדיה שהוגדר על המוצר, אחר כך לפי שם
            return sale.find(p => (p.media_type || 'video') === (wantPhotos ? 'photos' : 'video'))
                || sale.find(p => wantPhotos ? /תמונ/.test(p.name||'') : /קליפ/.test(p.name||''))
                || null;
          };

          const addTally = async (shootId) => {
            const t = newTally;
            if (!t.product_name) return flash('בחר מוצר', 'error');
            const { data } = await axios.post(API+'/game-shoots/'+shootId+'/tally', {
              product_name: t.product_name, unit_price: t.unit_price || 0, quantity: t.quantity || 0,
              team_side: t.team_side || 'home',
            });
            setShootTallies(prev => [data, ...prev]);
            setNewTally({ product_name:'', unit_price:'', quantity:'', team_side:t.team_side || 'home' });
            flash('✅ נרשם בספירה (סטטיסטיקה בלבד — לא נכנס לקופה)');
          };

          // עדכון ספירה אונליין
          const updateTallyQty = async (tallyId, quantity) => {
            const { data } = await axios.put(API+'/shoot-tally/'+tallyId, { quantity });
            setShootTallies(prev => prev.map(t => t.id === tallyId ? { ...t, ...data } : t));
          };

          const addShootOrder = async (shootId) => {
            const o = newShootOrder;
            if (!o.customer_name.trim()) return flash('הזן שם לקוח', 'error');
            const { data } = await axios.post(API+'/game-shoots/'+shootId+'/orders', {
              customer_name: o.customer_name.trim(), team: o.team || null, birth_year: o.birth_year || null,
              phone: o.phone || null, product_name: o.product_name || null, price: o.price || 0,
            });
            setShootOrders(prev => [data, ...prev]);
            setNewShootOrder({ customer_name:'', team:'', birth_year:'', phone:'', product_name:'', price:'', productId:'' });
            // הלקוח נכנס גם ל"לקוחות יוסטאר" וההכנסה לקופה + שיטת בבל
            axios.get(API+'/customers').then(r => setCustomers(r.data)).catch(()=>{});
            axios.get(API+'/income').then(r => setIncomeLog(r.data)).catch(()=>{});
            flash('✅ לקוח נוסף — גם ללקוחות יוסטאר וגם לקופה');
          };

          const inS = { width:'100%', background:'#0a1a3e', border:'1px solid #2a3a6a', borderRadius:10, padding:'10px 12px', color:'#fff', fontSize:'0.9rem', boxSizing:'border-box' };
          const lblS = { color:'#8a9aba', fontSize:'0.78rem', fontWeight:700, marginBottom:5, display:'block' };

          return (
            <div style={{ padding:'24px 20px', maxWidth:1000, margin:'0 auto' }}>
              <div style={{ marginBottom:20 }}>
                <h2 style={{ color:'#d4af37', margin:0, fontSize:'1.4rem' }}>🎥 יוסטאר מצלמים</h2>
                <p style={{ color:'#8a9aba', margin:'4px 0 0', fontSize:'0.88rem' }}>צלמו משחק לפי קבוצות ושנתון — והמערכת תשייך את ההורים הרלוונטיים לשליחת הצעה לקליפ ביצועים</p>
              </div>

              {/* ── הוספת משחק שצולם ידנית ── */}
              <div style={{ background:'linear-gradient(135deg,rgba(212,175,55,0.08),rgba(212,175,55,0.03))', border:'1px solid rgba(212,175,55,0.35)', borderRadius:14, marginBottom:18 }}>
                <button onClick={()=>setShowManualShoot(v=>!v)}
                  style={{ width:'100%', background:'none', border:'none', cursor:'pointer', padding:'12px 18px',
                    display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                  <span style={{ color:'#d4af37', fontWeight:900, fontSize:'0.95rem' }}>➕ הוספת משחק שצולם ידנית</span>
                  <span style={{ color:'#4a5a7a', fontSize:'0.8rem' }}>{showManualShoot ? '▲ סגור' : '▼ פתח'}</span>
                </button>

                {showManualShoot && (
                  <div style={{ borderTop:'1px solid rgba(212,175,55,0.2)', padding:'16px 18px' }}>
                    <div style={{ color:'#8a9aba', fontSize:'0.78rem', marginBottom:12, lineHeight:1.6 }}>
                      למשחק שצולם בלי שנקבע מראש בלוז הצילומים.
                    </div>

                    {/* סגנון צילום */}
                    <div style={{ ...lblS }}>סגנון צילום</div>
                    <div style={{ display:'flex', gap:8, marginBottom:12 }}>
                      {[{v:'video',l:'🎥 וידאו'},{v:'stills',l:'📷 תמונות'}].map(t => {
                        const sel = newShoot.shoot_type === t.v;
                        return (
                          <button key={t.v} type="button" onClick={()=>setNewShoot(n=>({...n, shoot_type:t.v}))}
                            style={{ flex:1, padding:'9px', borderRadius:9, cursor:'pointer', fontWeight:800, fontSize:'0.86rem',
                              border:`2px solid ${sel?(t.v==='stills'?'#f0cc60':'#7ab0ff'):'#2a3a6a'}`,
                              background: sel?(t.v==='stills'?'rgba(240,204,96,0.12)':'rgba(122,176,255,0.12)'):'#0a1525',
                              color: sel?(t.v==='stills'?'#f0cc60':'#7ab0ff'):'#8a9aba' }}>{t.l}</button>
                        );
                      })}
                    </div>

                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))', gap:11, marginBottom:11 }}>
                      <div>
                        <label style={lblS}>תאריך המשחק</label>
                        <input type="date" value={newShoot.game_date} onChange={e=>setNewShoot(n=>({...n, game_date:e.target.value}))} style={inS} />
                      </div>
                      <div>
                        <label style={lblS}>קבוצה ביתית *</label>
                        <TeamSearchInput value={newShoot.team_a} onChange={v=>setNewShoot(n=>({...n, team_a:v}))} options={allTeams} placeholder="הקלד שם קבוצה" />
                      </div>
                      <div>
                        <label style={lblS}>קבוצת חוץ</label>
                        <TeamSearchInput value={newShoot.team_b} onChange={v=>setNewShoot(n=>({...n, team_b:v}))} options={allTeams.filter(t=>t!==newShoot.team_a)} placeholder="יריבה" />
                      </div>
                    </div>

                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:11, marginBottom:11 }}>
                      <div>
                        <label style={lblS}>שכבת גיל</label>
                        <select value={newShoot.age_group} onChange={e=>setNewShoot(n=>({...n, age_group:e.target.value}))} style={inS}>
                          <option value="">— שכבת גיל —</option>
                          {AGE_GROUPS.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={lblS}>שנתון</label>
                        <select value={newShoot.birth_year} onChange={e=>setNewShoot(n=>({...n, birth_year:e.target.value}))} style={inS}>
                          <option value="">— שנתון —</option>
                          {allYears.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={lblS}>סוג משחק</label>
                        <select value={newShoot.match_category} onChange={e=>setNewShoot(n=>({...n, match_category:e.target.value}))} style={inS}>
                          <option value="">— סוג משחק —</option>
                          {MATCH_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>

                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:11, marginBottom:11 }}>
                      <div>
                        <label style={lblS}>גודל סגל</label>
                        <input type="number" min="0" value={newShoot.squad_size} onChange={e=>setNewShoot(n=>({...n, squad_size:e.target.value}))} style={inS} placeholder="למשל 18" />
                      </div>
                      <div>
                        <label style={lblS}>שחקנים פעילים במשחק</label>
                        <input type="number" min="0" value={newShoot.active_players} onChange={e=>setNewShoot(n=>({...n, active_players:e.target.value}))} style={inS} placeholder="למשל 14" />
                      </div>
                    </div>

                    <div style={{ marginBottom:14 }}>
                      <label style={lblS}>הערות</label>
                      <input value={newShoot.notes} onChange={e=>setNewShoot(n=>({...n, notes:e.target.value}))} style={inS} placeholder="מיקום, נסיבות הצילום וכו׳" />
                    </div>

                    <button onClick={createShoot}
                      style={{ background:'linear-gradient(135deg,#d4af37,#b8941f)', color:'#000', border:'none', borderRadius:10,
                        padding:'11px 26px', cursor:'pointer', fontWeight:900, fontSize:'0.9rem' }}>
                      🎬 הוסף משחק שצולם
                    </button>
                  </div>
                )}
              </div>

              {/* ── צילומים קיימים ── */}
              <div style={{ color:'#c0cce6', fontWeight:800, fontSize:'0.95rem', marginBottom:12 }}>🎬 צילומי משחקים ({gameShoots.length})</div>
              {gameShoots.length === 0 ? (
                <div style={{ textAlign:'center', color:'#4a5a7a', padding:'40px 20px' }}>עדיין לא יצרת צילומי משחק</div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                  {gameShoots.map(s => {
                    const matched = matchedForShoot(s);
                    const isFilmed = s.status === 'filmed';
                    // סיכום הצילום — סך ההזמנות והשווי (ספירה סטטיסטית)
                    const sTallies   = shootTallies.filter(t => t.shoot_id === s.id);
                    const sHomeQty   = sTallies.filter(t => (t.team_side||'home')==='home').reduce((n,t)=>n+(t.quantity||0),0);
                    const sAwayQty   = sTallies.filter(t => t.team_side==='away').reduce((n,t)=>n+(t.quantity||0),0);
                    const sTotalQty  = sHomeQty + sAwayQty;
                    const sHomeRev   = sTallies.filter(t => (t.team_side||'home')==='home').reduce((n,t)=>n+(t.quantity||0)*(t.unit_price||0),0);
                    const sAwayRev   = sTallies.filter(t => t.team_side==='away').reduce((n,t)=>n+(t.quantity||0)*(t.unit_price||0),0);
                    const sTotalRev  = sHomeRev + sAwayRev;
                    const waNums = matched.map(m => (m.phone||'')).join('\n');
                    return (
                      <div key={s.id} style={{ background:'#0d1b3e', border:`2px solid ${isFilmed?'rgba(37,211,102,0.4)':'rgba(212,175,55,0.3)'}`, borderRadius:14, padding:'16px 20px' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>
                          <div>
                            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                              {/* ⚪ ביתית לבן · 🔵 חוץ כחול */}
                              <span style={{ display:'inline-flex', alignItems:'center', gap:5 }}>
                                <span style={{ width:9, height:9, borderRadius:'50%', background:'#ffffff', display:'inline-block' }} />
                                <span style={{ color:'#ffffff', fontWeight:900, fontSize:'1.05rem' }}>{s.team_a}</span>
                              </span>
                              {s.team_b && (
                                <>
                                  <span style={{ color:'#4a5a7a', fontSize:'0.85rem' }}>נגד</span>
                                  <span style={{ display:'inline-flex', alignItems:'center', gap:5 }}>
                                    <span style={{ width:9, height:9, borderRadius:'50%', background:'#5a9bf5', display:'inline-block' }} />
                                    <span style={{ color:'#5a9bf5', fontWeight:900, fontSize:'1.05rem' }}>{s.team_b}</span>
                                  </span>
                                </>
                              )}
                              {/* סוג הצילום — טקסט בולט */}
                              <span style={{ background: s.shoot_type==='stills' ? 'rgba(240,204,96,0.18)' : 'rgba(122,176,255,0.18)',
                                color: s.shoot_type==='stills' ? '#f0cc60' : '#7ab0ff',
                                border:`1px solid ${s.shoot_type==='stills' ? 'rgba(240,204,96,0.5)' : 'rgba(122,176,255,0.5)'}`,
                                borderRadius:7, padding:'2px 11px', fontSize:'0.82rem', fontWeight:900, letterSpacing:0.3 }}>
                                {s.shoot_type === 'stills' ? '📷 צילום תמונות' : '🎥 צילום וידאו'}
                              </span>
                              {s.match_category && <span style={{ background:'rgba(37,211,102,0.15)', color:'#25d366', borderRadius:6, padding:'1px 8px', fontSize:'0.72rem', fontWeight:800 }}>🏆 {s.match_category}</span>}
                            </div>
                            {s.game_date && (
                              <div style={{ color:'#d4af37', fontSize:'0.86rem', fontWeight:700, marginTop:5 }}>📅 {niceDate(s.game_date)}</div>
                            )}
                            <div style={{ color:'#8a9aba', fontSize:'0.83rem', marginTop:3 }}>
                              {s.age_group ? <span style={{ color:'#d4af37', fontWeight:700 }}>{s.age_group} · </span> : ''}
                              שנתון {s.birth_year || '—'} · <span style={{ color:'#25d366', fontWeight:700 }}>{matched.length} הורים</span>
                              {(() => { const cnt = shootOrders.filter(o => o.shoot_id === s.id).length; return cnt ? <span style={{ color:'#f0cc60', fontWeight:700 }}> · 🛒 {cnt} לקוחות</span> : null; })()}
                            </div>
                          </div>
                          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                            <span style={{ background:isFilmed?'rgba(37,211,102,0.15)':'rgba(212,175,55,0.12)', color:isFilmed?'#25d366':'#d4af37', borderRadius:20, padding:'3px 12px', fontSize:'0.78rem', fontWeight:800 }}>
                              {isFilmed ? '✅ צולם' : '📅 מתוכנן'}
                            </span>
                            <button onClick={async()=>{
                              const next = isFilmed ? 'planned' : 'filmed';
                              await axios.put(API+'/game-shoots/'+s.id, { status:next });
                              setGameShoots(prev => prev.map(x => x.id===s.id ? {...x, status:next} : x));
                              if (next==='filmed') setExpandedShoot(s.id);
                            }} style={{ background: isFilmed?'rgba(255,150,50,0.1)':'linear-gradient(135deg,#1a4a2a,#0d3a1a)', color:isFilmed?'#ffaa44':'#25d366', border:`1px solid ${isFilmed?'rgba(255,150,50,0.35)':'rgba(37,211,102,0.5)'}`, borderRadius:8, padding:'7px 14px', cursor:'pointer', fontWeight:800, fontSize:'0.82rem' }}>
                              {isFilmed ? '↩ בטל סיום' : '🎬 סיימתי לצלם'}
                            </button>
                            <button onClick={()=>{ setExpandedShoot(expandedShoot===s.id?null:s.id); setExpandedShootOrders(null); }}
                              style={{ background:'rgba(90,155,245,0.12)', color:'#5a9bf5', border:'1px solid rgba(90,155,245,0.35)', borderRadius:8, padding:'7px 12px', cursor:'pointer', fontWeight:700, fontSize:'0.82rem' }}>
                              👨‍👩‍👦 הורים {expandedShoot===s.id?'▲':'▼'}
                            </button>
                            <button onClick={()=>{ setExpandedShootTally(expandedShootTally===s.id?null:s.id); setExpandedShoot(null); setExpandedShootOrders(null); }}
                              style={{ background:'rgba(37,211,102,0.12)', color:'#25d366', border:'1px solid rgba(37,211,102,0.35)', borderRadius:8, padding:'7px 12px', cursor:'pointer', fontWeight:700, fontSize:'0.82rem' }}>
                              📊 סיכום הזמנות {expandedShootTally===s.id?'▲':'▼'}
                            </button>
                            <button onClick={()=>{ setExpandedShootOrders(expandedShootOrders===s.id?null:s.id); setExpandedShoot(null); setExpandedShootTally(null); }}
                              style={{ background:'rgba(240,204,96,0.12)', color:'#f0cc60', border:'1px solid rgba(240,204,96,0.35)', borderRadius:8, padding:'7px 12px', cursor:'pointer', fontWeight:700, fontSize:'0.82rem' }}>
                              🛒 לקוחות {expandedShootOrders===s.id?'▲':'▼'}
                            </button>
                            <button onClick={()=>{ setExpandedShootLeads(expandedShootLeads===s.id?null:s.id); setExpandedShoot(null); setExpandedShootTally(null); setExpandedShootOrders(null); }}
                              style={{ background:'rgba(180,120,255,0.12)', color:'#b478ff', border:'1px solid rgba(180,120,255,0.4)', borderRadius:8, padding:'7px 12px', cursor:'pointer', fontWeight:700, fontSize:'0.82rem' }}>
                              📇 אנשי קשר {expandedShootLeads===s.id?'▲':'▼'}
                            </button>
                            <button onClick={async()=>{
                              if(!window.confirm('למחוק צילום זה?')) return;
                              await axios.delete(API+'/game-shoots/'+s.id);
                              setGameShoots(prev => prev.filter(x=>x.id!==s.id));
                            }} style={{ background:'rgba(255,80,80,0.07)', color:'#ff8888', border:'1px solid rgba(255,80,80,0.25)', borderRadius:8, padding:'6px 10px', cursor:'pointer', fontSize:'0.78rem' }}>🗑</button>
                          </div>
                        </div>

                        {/* ── סיכום הכנסות מהמשחק + עריכה מהירה ── */}
                        {sTallies.length > 0 && (
                          <div style={{ marginTop:12, background:'rgba(212,175,55,0.06)', border:'1px solid rgba(212,175,55,0.3)', borderRadius:10, padding:'12px 14px' }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10, marginBottom:10 }}>
                              <div style={{ display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
                                <span style={{ color:'#d4af37', fontWeight:900, fontSize:'0.9rem' }}>💰 סך ההכנסות מהמשחק</span>
                                <span style={{ color:'#d4af37', fontWeight:900, fontSize:'1.35rem' }}>₪{sTotalRev.toLocaleString()}</span>
                                <span style={{ color:'#8a9aba', fontSize:'0.8rem' }}>· {sTotalQty} הזמנות</span>
                              </div>
                              <div style={{ display:'flex', gap:10, flexWrap:'wrap', fontSize:'0.8rem' }}>
                                <span style={{ color:'#fff' }}>⚪ {s.team_a}: <strong>{sHomeQty}</strong> · ₪{sHomeRev.toLocaleString()}</span>
                                {s.team_b && <span style={{ color:'#5a9bf5' }}>🔵 {s.team_b}: <strong>{sAwayQty}</strong> · ₪{sAwayRev.toLocaleString()}</span>}
                              </div>
                            </div>
                            {/* עריכה מהירה של הכמויות — מעדכן קבוצה וסה״כ */}
                            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                              {sTallies.map(t => {
                                const isHome = (t.team_side||'home') === 'home';
                                const col = isHome ? '#ffffff' : '#5a9bf5';
                                return (
                                  <div key={t.id} style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', fontSize:'0.82rem' }}>
                                    <span style={{ width:9, height:9, borderRadius:'50%', background:col, display:'inline-block', flexShrink:0 }} />
                                    <span style={{ color:col, fontWeight:700, minWidth:110 }}>{isHome ? s.team_a : s.team_b}</span>
                                    <span style={{ color:'#c0cce6' }}>{t.product_name}</span>
                                    <button onClick={()=>updateTallyQty(t.id, Math.max(0,(t.quantity||0)-1))}
                                      style={{ background:'#1a2a4a', color:'#c0cce6', border:'1px solid #2a3a6a', borderRadius:6, width:24, height:24, cursor:'pointer', fontWeight:900 }}>−</button>
                                    <input type="number" min="0" value={t.quantity||0}
                                      onChange={e=>updateTallyQty(t.id, e.target.value)}
                                      style={{ width:52, background:'#0a1a3e', border:'1px solid #2a3a6a', borderRadius:6, padding:'3px 5px', color:'#25d366', fontSize:'0.85rem', fontWeight:800, textAlign:'center' }} />
                                    <button onClick={()=>updateTallyQty(t.id, (t.quantity||0)+1)}
                                      style={{ background:'#1a2a4a', color:'#c0cce6', border:'1px solid #2a3a6a', borderRadius:6, width:24, height:24, cursor:'pointer', fontWeight:900 }}>+</button>
                                    <span style={{ color:'#8a9aba' }}>× ₪{t.unit_price} =</span>
                                    <span style={{ color:'#d4af37', fontWeight:800 }}>₪{((t.quantity||0)*(t.unit_price||0)).toLocaleString()}</span>
                                  </div>
                                );
                              })}
                            </div>
                            <div style={{ color:'#4a5a7a', fontSize:'0.72rem', marginTop:8 }}>* סטטיסטיקה בלבד — לא נכנס לקופת יוסטאר</div>
                          </div>
                        )}

                        {/* ── סימן: יש חברי מועדון רלוונטים לצילום הזה ── */}
                        {matched.length > 0 && (
                          <div style={{ marginTop:12, background:'linear-gradient(135deg,rgba(37,211,102,0.12),rgba(212,175,55,0.06))', border:'1px solid rgba(37,211,102,0.4)', borderRadius:10, padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                            <div style={{ color:'#25d366', fontWeight:800, fontSize:'0.88rem' }}>
                              ⭐ צילמת קבוצה של {matched.length} חברי מועדון — אפשר לפנות אליהם על מוצר יוסטאר מהצילום!
                            </div>
                            <button onClick={()=>{ setExpandedShoot(s.id); setExpandedShootOrders(null); setExpandedShootTally(null); }}
                              style={{ background:'#25d366', color:'#fff', border:'none', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontWeight:800, fontSize:'0.82rem', whiteSpace:'nowrap' }}>
                              👨‍👩‍👦 פנה אליהם
                            </button>
                          </div>
                        )}

                        {/* רשימת ההורים המשויכים */}
                        {expandedShoot === s.id && (
                          <div style={{ marginTop:16, borderTop:'1px solid #1e3060', paddingTop:14 }}>
                            {matched.length === 0 ? (
                              <div style={{ color:'#4a5a7a', fontSize:'0.85rem', textAlign:'center', padding:'12px 0' }}>אין הורים במועדון שתואמים לצילום הזה</div>
                            ) : (
                              <>
                                {waNums && (
                                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12, flexWrap:'wrap', gap:8 }}>
                                    <span style={{ color:'#8a9aba', fontSize:'0.82rem', fontWeight:700 }}>{matched.length} הורים לשליחה</span>
                                    <button onClick={()=>{ navigator.clipboard.writeText(waNums).then(()=>flash('📋 כל המספרים הועתקו')); }}
                                      style={{ background:'rgba(37,211,102,0.12)', color:'#25d366', border:'1px solid rgba(37,211,102,0.35)', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontWeight:700, fontSize:'0.8rem' }}>
                                      📋 העתק את כל המספרים
                                    </button>
                                  </div>
                                )}
                                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                                  {matched.map(m => {
                                    const wa = `https://wa.me/${waNum(m.phone)}?text=${encodeURIComponent(shootMsg(m, s))}`;
                                    return (
                                      <div key={m.id} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid #1e3060', borderRadius:10, padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                                        <div style={{ fontSize:'0.85rem' }}>
                                          <span style={{ color:'#fff', fontWeight:700 }}>{m.first_name} {m.last_name||''}</span>
                                          <span style={{ color:'#8a9aba' }}> · ⚽ {m.child_name} {m.child_last_name||''}</span>
                                          <span style={{ color:'#4a5a7a', fontSize:'0.78rem' }}> · {m.team}</span>
                                        </div>
                                        <a href={wa} target="_blank" rel="noopener noreferrer"
                                          style={{ background:'#25d366', color:'#fff', borderRadius:8, padding:'6px 14px', textDecoration:'none', fontWeight:700, fontSize:'0.82rem', whiteSpace:'nowrap' }}>
                                          💬 שלח הצעה
                                        </a>
                                      </div>
                                    );
                                  })}
                                </div>
                              </>
                            )}
                          </div>
                        )}

                        {/* ── סיכום הזמנות: כמות × מחיר → הכנסה ── */}
                        {expandedShootTally === s.id && (() => {
                          const myTallies = shootTallies.filter(t => t.shoot_id === s.id);
                          const total = myTallies.reduce((sum, t) => sum + (t.unit_price||0) * (t.quantity||0), 0);
                          const totalOrdered = myTallies.reduce((sum, t) => sum + (t.quantity||0), 0);
                          const preview = (parseFloat(newTally.unit_price)||0) * (parseInt(newTally.quantity)||0);
                          // יחס המרה — קבוע: 22 שחקנים בכל קבוצה
                          const SQUAD = 22;
                          const homeQty = myTallies.filter(t => (t.team_side||'home') === 'home').reduce((sum,t)=>sum+(t.quantity||0), 0);
                          const awayQty = myTallies.filter(t => t.team_side === 'away').reduce((sum,t)=>sum+(t.quantity||0), 0);
                          const teamsCount = s.team_b ? 2 : 1;
                          const convHome = Math.round(homeQty / SQUAD * 100);
                          const convAway = Math.round(awayQty / SQUAD * 100);
                          const convTotal = Math.round(totalOrdered / (SQUAD * teamsCount) * 100);
                          const SIDES = [
                            { id:'home', label:s.team_a, color:'#ffffff', bg:'rgba(255,255,255,0.10)', border:'rgba(255,255,255,0.5)', qty:homeQty, conv:convHome, tag:'ביתית' },
                            ...(s.team_b ? [{ id:'away', label:s.team_b, color:'#5a9bf5', bg:'rgba(90,155,245,0.12)', border:'rgba(90,155,245,0.55)', qty:awayQty, conv:convAway, tag:'חוץ' }] : []),
                          ];
                          return (
                            <div style={{ marginTop:16, borderTop:'1px solid #1e3060', paddingTop:14 }}>
                              <div style={{ color:'#25d366', fontWeight:800, fontSize:'0.9rem', marginBottom:4 }}>📊 ספירת הזמנות מהצילום</div>
                              <div style={{ color:'#4a5a7a', fontSize:'0.78rem', marginBottom:12 }}>
                                סטטיסטיקה בלבד — כמה הזמינו כל מוצר מהמשחק. <strong style={{ color:'#8a9aba' }}>לא נכנס לקופת יוסטאר</strong>; הכסף נרשם רק כשלקוח קונה ומשלם.
                              </div>

                              {/* ── יחס המרה לפי קבוצות (22 שחקנים לקבוצה) ── */}
                              <div style={{ background:'rgba(90,155,245,0.05)', border:'1px solid rgba(90,155,245,0.2)', borderRadius:12, padding:'12px 16px', marginBottom:14 }}>
                                <div style={{ color:'#7ab0ff', fontWeight:800, fontSize:'0.85rem', marginBottom:10 }}>🎯 יחס המרה ללקוחות · {SQUAD} שחקנים לקבוצה</div>
                                <div style={{ display:'grid', gridTemplateColumns:`repeat(auto-fit,minmax(150px,1fr))`, gap:10 }}>
                                  {SIDES.map(side => (
                                    <div key={side.id} style={{ background:side.bg, border:`1px solid ${side.border}`, borderRadius:10, padding:'10px 14px' }}>
                                      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
                                        <span style={{ width:10, height:10, borderRadius:'50%', background:side.color, display:'inline-block', flexShrink:0 }} />
                                        <span style={{ color:side.color, fontWeight:800, fontSize:'0.83rem' }}>{side.label}</span>
                                        <span style={{ color:'#4a5a7a', fontSize:'0.7rem' }}>({side.tag})</span>
                                      </div>
                                      <div style={{ color:'#8a9aba', fontSize:'0.74rem' }}>{side.qty} מתוך {SQUAD} הזמינו</div>
                                      <div style={{ color:side.color, fontWeight:900, fontSize:'1.5rem', lineHeight:1.2 }}>{side.conv}%</div>
                                    </div>
                                  ))}
                                  <div style={{ background:'rgba(37,211,102,0.08)', border:'1px solid rgba(37,211,102,0.35)', borderRadius:10, padding:'10px 14px' }}>
                                    <div style={{ color:'#25d366', fontWeight:800, fontSize:'0.83rem', marginBottom:4 }}>⭐ סה״כ</div>
                                    <div style={{ color:'#8a9aba', fontSize:'0.74rem' }}>{totalOrdered} מתוך {SQUAD*teamsCount} הזמינו</div>
                                    <div style={{ color:'#25d366', fontWeight:900, fontSize:'1.5rem', lineHeight:1.2 }}>{convTotal}%</div>
                                  </div>
                                </div>
                              </div>

                              {/* טופס הוספת סיכום */}
                              <div style={{ background:'rgba(37,211,102,0.05)', border:'1px solid rgba(37,211,102,0.25)', borderRadius:12, padding:'14px 16px', marginBottom:14 }}>
                                {/* בחירת קבוצה */}
                                <div style={{ color:'#8a9aba', fontSize:'0.76rem', fontWeight:700, marginBottom:6 }}>קבוצה:</div>
                                <div style={{ display:'flex', gap:8, marginBottom:10, flexWrap:'wrap' }}>
                                  {SIDES.map(side => {
                                    const sel = (newTally.team_side||'home') === side.id;
                                    return (
                                      <button key={side.id} type="button" onClick={()=>setNewTally(t=>({ ...t, team_side:side.id }))}
                                        style={{ display:'flex', alignItems:'center', gap:7, padding:'7px 14px', borderRadius:8, border:`2px solid ${sel?side.border:'#2a3a6a'}`, background:sel?side.bg:'#0a1525', cursor:'pointer', fontSize:'0.83rem', color:sel?side.color:'#c0cce6', fontWeight:800 }}>
                                        <span style={{ width:10, height:10, borderRadius:'50%', background:side.color, display:'inline-block' }} />
                                        {side.label} <span style={{ color:'#4a5a7a', fontWeight:400, fontSize:'0.72rem' }}>({side.tag})</span>
                                      </button>
                                    );
                                  })}
                                </div>
                                {(() => {
                                  const dp = defaultProductFor(s);
                                  if (!dp) return null;
                                  const isSel = newTally.product_name === dp.name;
                                  return (
                                    <div style={{ background:'rgba(212,175,55,0.08)', border:'1px solid rgba(212,175,55,0.35)', borderRadius:9, padding:'9px 12px', marginBottom:10 }}>
                                      <div style={{ color:'#d4af37', fontSize:'0.76rem', fontWeight:800, marginBottom:5 }}>
                                        ⭐ המוצר של הצילום הזה ({s.shoot_type==='stills' ? 'תמונות' : 'וידאו'})
                                      </div>
                                      <button type="button" onClick={()=>setNewTally(t=>({ ...t, product_name:dp.name, unit_price:String(dp.price) }))}
                                        style={{ padding:'7px 14px', borderRadius:8, border:`1px solid ${isSel?'#25d366':'rgba(212,175,55,0.5)'}`, background:isSel?'rgba(37,211,102,0.18)':'rgba(212,175,55,0.12)', cursor:'pointer', fontSize:'0.85rem', color:isSel?'#25d366':'#d4af37', fontWeight:800 }}>
                                        {isSel ? '✓ ' : ''}{dp.name} · ₪{dp.price}
                                      </button>
                                    </div>
                                  );
                                })()}
                                <div style={{ color:'#8a9aba', fontSize:'0.76rem', fontWeight:700, marginBottom:6 }}>או מוצר אחר מהקטלוג:</div>
                                <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:10 }}>
                                  {products.filter(p => p.product_type === 'sale').map(p => {
                                    const sel = newTally.product_name === p.name;
                                    return (
                                      <button key={p.id} type="button" onClick={()=>setNewTally(t=>({ ...t, product_name:p.name, unit_price:String(p.price) }))}
                                        style={{ padding:'7px 14px', borderRadius:8, border:`1px solid ${sel?'#25d366':'#2a3a6a'}`, background:sel?'rgba(37,211,102,0.18)':'#0a1525', cursor:'pointer', fontSize:'0.83rem', color:sel?'#25d366':'#c0cce6', fontWeight:700 }}>
                                        {p.name} · ₪{p.price}
                                      </button>
                                    );
                                  })}
                                  {products.filter(p => p.product_type === 'sale').length === 0 && (
                                    <span style={{ color:'#ff8888', fontSize:'0.8rem' }}>אין מוצרים — צור מוצר בטאב "מוצרי יוסטאר" תחילה</span>
                                  )}
                                </div>
                                <input value={newTally.product_name} placeholder="או שם מוצר ידני"
                                  onChange={e=>setNewTally(t=>({ ...t, product_name:e.target.value }))} style={{ ...inS, marginBottom:10 }} />
                                <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'flex-end' }}>
                                  <div>
                                    <div style={{ color:'#8a9aba', fontSize:'0.74rem', marginBottom:4 }}>כמה הזמינו</div>
                                    <input type="number" min="0" value={newTally.quantity} placeholder="0"
                                      onChange={e=>setNewTally(t=>({ ...t, quantity:e.target.value }))} style={{ ...inS, width:100 }} />
                                  </div>
                                  <div>
                                    <div style={{ color:'#8a9aba', fontSize:'0.74rem', marginBottom:4 }}>מחיר ליחידה ₪</div>
                                    <input type="number" min="0" value={newTally.unit_price} placeholder="0"
                                      onChange={e=>setNewTally(t=>({ ...t, unit_price:e.target.value }))} style={{ ...inS, width:110 }} />
                                  </div>
                                  {preview > 0 && <div style={{ color:'#d4af37', fontWeight:900, fontSize:'1.1rem', paddingBottom:6 }}>= ₪{preview.toLocaleString()}</div>}
                                </div>
                                <button onClick={()=>addTally(s.id)}
                                  style={{ marginTop:12, background:'linear-gradient(135deg,#1a4a2a,#0d3a1a)', color:'#25d366', border:'1px solid rgba(37,211,102,0.5)', borderRadius:8, padding:'9px 20px', cursor:'pointer', fontWeight:800, fontSize:'0.85rem' }}>
                                  + הוסף לספירה
                                </button>
                              </div>

                              {/* רשימת הספירות */}
                              {myTallies.length === 0 ? (
                                <div style={{ color:'#4a5a7a', fontSize:'0.83rem', textAlign:'center', padding:'8px 0' }}>עדיין לא נרשמה ספירה לצילום זה</div>
                              ) : (
                                <>
                                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                                    <span style={{ color:'#8a9aba', fontSize:'0.8rem', fontWeight:700 }}>ספירת הצילום · {totalOrdered} הזמנות</span>
                                    {total > 0 && <span style={{ color:'#8a9aba', fontWeight:700, fontSize:'0.9rem' }}>שווי משוער ₪{total.toLocaleString()} <span style={{ color:'#4a5a7a', fontWeight:400, fontSize:'0.78rem' }}>(לא בקופה)</span></span>}
                                  </div>
                                  {/* מקובץ לפי קבוצה — לבן=ביתית, כחול=חוץ */}
                                  <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                                    {SIDES.map(side => {
                                      const sideTallies = myTallies.filter(t => (t.team_side||'home') === side.id);
                                      if (sideTallies.length === 0) return null;
                                      return (
                                        <div key={side.id} style={{ borderRight:`3px solid ${side.color}`, paddingRight:12 }}>
                                          <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:8 }}>
                                            <span style={{ width:10, height:10, borderRadius:'50%', background:side.color, display:'inline-block' }} />
                                            <span style={{ color:side.color, fontWeight:800, fontSize:'0.85rem' }}>{side.label}</span>
                                            <span style={{ color:'#4a5a7a', fontSize:'0.74rem' }}>({side.tag})</span>
                                            <span style={{ color:'#8a9aba', fontSize:'0.78rem', marginRight:'auto' }}>{side.qty} הזמנות · {side.conv}%</span>
                                          </div>
                                          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                                            {sideTallies.map(t => (
                                              <div key={t.id} style={{ background:side.bg, border:`1px solid ${side.border}`, borderRadius:10, padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                                                <div style={{ fontSize:'0.85rem', display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                                                  <span style={{ color:side.color, fontWeight:700 }}>{t.product_name}</span>
                                                  <span style={{ color:'#8a9aba' }}>·</span>
                                                  {/* עדכון כמות אונליין */}
                                                  <button onClick={()=>updateTallyQty(t.id, Math.max(0,(t.quantity||0)-1))}
                                                    style={{ background:'#1a2a4a', color:'#c0cce6', border:'1px solid #2a3a6a', borderRadius:6, width:26, height:26, cursor:'pointer', fontWeight:900 }}>−</button>
                                                  <input type="number" min="0" value={t.quantity||0}
                                                    onChange={e=>updateTallyQty(t.id, e.target.value)}
                                                    style={{ width:56, background:'#0a1a3e', border:'1px solid #2a3a6a', borderRadius:6, padding:'4px 6px', color:'#25d366', fontSize:'0.9rem', fontWeight:800, textAlign:'center' }} />
                                                  <button onClick={()=>updateTallyQty(t.id, (t.quantity||0)+1)}
                                                    style={{ background:'#1a2a4a', color:'#c0cce6', border:'1px solid #2a3a6a', borderRadius:6, width:26, height:26, cursor:'pointer', fontWeight:900 }}>+</button>
                                                  <span style={{ color:'#8a9aba', fontSize:'0.82rem' }}>מזמינים × ₪{t.unit_price}</span>
                                                </div>
                                                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                                                  <span style={{ color:'#8a9aba', fontWeight:700 }}>₪{((t.unit_price||0)*(t.quantity||0)).toLocaleString()}</span>
                                                  <button onClick={async()=>{ await axios.delete(API+'/shoot-tally/'+t.id); setShootTallies(prev=>prev.filter(x=>x.id!==t.id)); }}
                                                    style={{ background:'rgba(255,80,80,0.07)', color:'#ff8888', border:'1px solid rgba(255,80,80,0.25)', borderRadius:8, padding:'5px 9px', cursor:'pointer', fontSize:'0.78rem' }}>🗑</button>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })()}

                        {/* ── אנשי קשר רלוונטיים לצילום (לקוחות עבר) ── */}
                        {expandedShootLeads === s.id && (() => {
                          const teams = [s.team_a, s.team_b].filter(Boolean);
                          const yr = s.birth_year ? String(s.birth_year) : null;
                          // לקוחות עבר ששייכים לאחת מהקבוצות במשחק (ולשנתון, אם הוגדר)
                          const leadsAll = customers.filter(c => {
                            if (!teams.includes(c.team)) return false;
                            if (yr) {
                              const cy = c.birth_year || (c.child_dob ? new Date(c.child_dob).getFullYear() : null);
                              if (cy && String(cy) !== yr) return false;
                            }
                            return true;
                          });
                          const dp = defaultProductFor(s);
                          const offerName  = dp ? dp.name  : (s.shoot_type==='stills' ? 'תמונות משחק' : 'קליפ ביצועים');
                          const offerPrice = dp ? dp.price : (s.shoot_type==='stills' ? 70 : 300);
                          const alreadyOrdered = new Set(shootOrders.filter(o => o.shoot_id === s.id).map(o => (o.phone||'').trim()));
                          const msgFor = c => {
                            const child = (c.child_name||'').trim().split(' ')[0] || 'הילד/ה';
                            const vs = s.team_b ? ` מול ${c.team === s.team_a ? s.team_b : s.team_a}` : '';
                            return `שלום ${c.first_name}! 👋\n\nצוות יוסטאר ${s.shoot_type==='stills' ? 'צילם תמונות' : 'צילם'} את המשחק של *${c.team}*${vs}${s.game_date ? ` (${niceDate(s.game_date)})` : ''} ⚽\n\n${s.shoot_type==='stills' ? `יש לנו *תמונות משחק* של ${child} 📷` : `אפשר להפיק ל${child} *קליפ ביצועים אישי* מהמשחק 🎬`}\n💰 ${offerName} — ₪${offerPrice}\n\nמעוניינים? אשמח לשלוח דוגמה 🙂\n\nליגת הכוכבים של יוסטאר ⭐`;
                          };
                          return (
                            <div style={{ marginTop:16, borderTop:'1px solid #1e3060', paddingTop:14 }}>
                              <div style={{ color:'#b478ff', fontWeight:800, fontSize:'0.9rem', marginBottom:4 }}>📇 אנשי קשר לצילום הזה</div>
                              <div style={{ color:'#4a5a7a', fontSize:'0.78rem', marginBottom:12 }}>
                                לקוחות עבר מהקבוצות שצולמו{yr ? ` · שנתון ${yr}` : ''} — הצעה: <strong style={{ color:'#d4af37' }}>{offerName} ₪{offerPrice}</strong>
                              </div>
                              {leadsAll.length === 0 ? (
                                <div style={{ color:'#4a5a7a', fontSize:'0.84rem', textAlign:'center', padding:'14px 0' }}>
                                  אין לקוחות עבר מהקבוצות האלה
                                </div>
                              ) : (
                                <>
                                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:9, flexWrap:'wrap', gap:8 }}>
                                    <span style={{ color:'#8a9aba', fontSize:'0.82rem', fontWeight:700 }}>{leadsAll.length} אנשי קשר</span>
                                    <button onClick={()=>{ navigator.clipboard.writeText(leadsAll.map(c=>normPhone(c.phone)).filter(Boolean).join('\n')).then(()=>flash('📋 המספרים הועתקו')); }}
                                      style={{ background:'rgba(37,211,102,0.12)', color:'#25d366', border:'1px solid rgba(37,211,102,0.35)', borderRadius:8, padding:'5px 13px', cursor:'pointer', fontWeight:700, fontSize:'0.78rem' }}>
                                      📋 העתק מספרים
                                    </button>
                                  </div>
                                  <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
                                    {leadsAll.map(c => {
                                      const done = alreadyOrdered.has(normPhone(c.phone));
                                      const isHome = c.team === s.team_a;
                                      const col = isHome ? '#ffffff' : '#5a9bf5';
                                      return (
                                        <div key={c.id} style={{ background:'rgba(255,255,255,0.03)', border:`1px solid ${done?'rgba(37,211,102,0.35)':'#1e3060'}`, borderRadius:9, padding:'9px 13px',
                                          display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                                          <div style={{ fontSize:'0.85rem', display:'flex', alignItems:'center', gap:7, flexWrap:'wrap' }}>
                                            <span style={{ width:8, height:8, borderRadius:'50%', background:col, display:'inline-block', flexShrink:0 }} />
                                            <span style={{ color:'#fff', fontWeight:700 }}>{c.first_name} {c.last_name||''}</span>
                                            {(c.child_name||'').trim() && <span style={{ color:'#8a9aba' }}>· ⚽ {c.child_name}</span>}
                                            <span style={{ color:col, fontSize:'0.76rem' }}>{c.team}</span>
                                            {done && <span style={{ color:'#25d366', fontSize:'0.72rem', fontWeight:800 }}>✅ כבר הזמין</span>}
                                          </div>
                                          <a href={`https://wa.me/${waNum(c.phone)}?text=${encodeURIComponent(msgFor(c))}`} target="_blank" rel="noopener noreferrer"
                                            style={{ background: done?'rgba(37,211,102,0.12)':'#25d366', color: done?'#25d366':'#fff', border: done?'1px solid rgba(37,211,102,0.4)':'none',
                                              borderRadius:8, padding:'6px 14px', textDecoration:'none', fontWeight:700, fontSize:'0.8rem', whiteSpace:'nowrap' }}>
                                            💬 {done ? 'שלח שוב' : 'הצע מוצר'}
                                          </a>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })()}

                        {/* ── לקוחות שהזמינו מוצר בצילום ── */}
                        {expandedShootOrders === s.id && (() => {
                          const myOrders = shootOrders.filter(o => o.shoot_id === s.id);
                          const total = myOrders.reduce((sum, o) => sum + (o.price || 0), 0);
                          return (
                            <div style={{ marginTop:16, borderTop:'1px solid #1e3060', paddingTop:14 }}>
                              <div style={{ color:'#f0cc60', fontWeight:800, fontSize:'0.9rem', marginBottom:12 }}>🛒 לקוחות שהזמינו מוצר</div>

                              {/* טופס הוספת לקוח */}
                              <div style={{ background:'rgba(240,204,96,0.05)', border:'1px solid rgba(240,204,96,0.25)', borderRadius:12, padding:'14px 16px', marginBottom:14 }}>
                                {/* בחירת מוצר + מחיר */}
                                <div style={{ marginBottom:10 }}>
                                  <div style={{ color:'#8a9aba', fontSize:'0.76rem', fontWeight:700, marginBottom:6 }}>מוצר יוסטאר + מחיר:</div>
                                  <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:8 }}>
                                    {products.filter(p => p.product_type === 'sale').map(p => {
                                      const sel = String(newShootOrder.productId) === String(p.id);
                                      return (
                                        <button key={p.id} type="button"
                                          onClick={()=>setNewShootOrder(o=>({ ...o, productId:String(p.id), product_name:p.name, price:String(p.price) }))}
                                          style={{ padding:'6px 12px', borderRadius:8, border:`1px solid ${sel?'#d4af37':'#2a3a6a'}`, background:sel?'rgba(212,175,55,0.18)':'#0a1525', cursor:'pointer', fontSize:'0.8rem', color:sel?'#d4af37':'#c0cce6' }}>
                                          {p.name} · ₪{p.price}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                                    <input value={newShootOrder.product_name} placeholder="שם מוצר (ידני)"
                                      onChange={e=>setNewShootOrder(o=>({ ...o, product_name:e.target.value, productId:'' }))}
                                      style={{ ...inS, flex:'1 1 150px' }} />
                                    <input type="number" value={newShootOrder.price} placeholder="₪"
                                      onChange={e=>setNewShootOrder(o=>({ ...o, price:e.target.value }))}
                                      style={{ ...inS, width:90 }} />
                                  </div>
                                </div>
                                {/* פרטי הלקוח */}
                                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:8, marginBottom:10 }}>
                                  <input value={newShootOrder.customer_name} placeholder="שם מלא *"
                                    onChange={e=>setNewShootOrder(o=>({ ...o, customer_name:e.target.value }))} style={inS} />
                                  <input value={newShootOrder.team} placeholder="קבוצה"
                                    onChange={e=>setNewShootOrder(o=>({ ...o, team:e.target.value }))} style={inS} />
                                  <input type="number" value={newShootOrder.birth_year} placeholder="שנתון"
                                    onChange={e=>setNewShootOrder(o=>({ ...o, birth_year:e.target.value }))} style={inS} />
                                  <input value={newShootOrder.phone} placeholder="טלפון"
                                    onChange={e=>setNewShootOrder(o=>({ ...o, phone:e.target.value }))} style={inS} />
                                </div>
                                <button onClick={()=>addShootOrder(s.id)}
                                  style={{ background:'linear-gradient(135deg,#d4af37,#b8941f)', color:'#000', border:'none', borderRadius:8, padding:'9px 20px', cursor:'pointer', fontWeight:800, fontSize:'0.85rem' }}>
                                  + הוסף לקוח לצילום
                                </button>
                              </div>

                              {/* רשימת הלקוחות */}
                              {myOrders.length === 0 ? (
                                <div style={{ color:'#4a5a7a', fontSize:'0.83rem', textAlign:'center', padding:'8px 0' }}>עדיין לא נוספו לקוחות לצילום זה</div>
                              ) : (
                                <>
                                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                                    <span style={{ color:'#8a9aba', fontSize:'0.8rem', fontWeight:700 }}>{myOrders.length} לקוחות</span>
                                    {total > 0 && <span style={{ color:'#d4af37', fontWeight:900 }}>סה״כ ₪{total.toLocaleString()}</span>}
                                  </div>
                                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                                    {myOrders.map(o => {
                                      const wa = o.phone ? `https://wa.me/${waNum(o.phone)}` : null;
                                      return (
                                        <div key={o.id} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid #1e3060', borderRadius:10, padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                                          <div style={{ fontSize:'0.85rem' }}>
                                            <span style={{ color:'#fff', fontWeight:700 }}>{o.customer_name}</span>
                                            {o.team && <span style={{ color:'#8a9aba' }}> · {o.team}</span>}
                                            {o.birth_year && <span style={{ color:'#8a9aba' }}> · שנתון {o.birth_year}</span>}
                                            {o.phone && <span style={{ color:'#4a5a7a', fontSize:'0.78rem' }}> · {o.phone}</span>}
                                            <div style={{ color:'#c0cce6', fontSize:'0.8rem', marginTop:2 }}>
                                              📦 {o.product_name || '—'} {o.price > 0 && <strong style={{ color:'#d4af37' }}>· ₪{o.price}</strong>}
                                            </div>
                                          </div>
                                          <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                                            {wa && <a href={wa} target="_blank" rel="noopener noreferrer" style={{ background:'rgba(37,211,102,0.15)', color:'#25d366', border:'1px solid rgba(37,211,102,0.3)', borderRadius:8, padding:'5px 10px', fontSize:'0.78rem', fontWeight:700, textDecoration:'none' }}>💬</a>}
                                            <button onClick={async()=>{ await axios.delete(API+'/shoot-orders/'+o.id); setShootOrders(prev=>prev.filter(x=>x.id!==o.id)); axios.get(API+'/income').then(r=>setIncomeLog(r.data)).catch(()=>{}); }}
                                              style={{ background:'rgba(255,80,80,0.07)', color:'#ff8888', border:'1px solid rgba(255,80,80,0.25)', borderRadius:8, padding:'5px 9px', cursor:'pointer', fontSize:'0.78rem' }}>🗑</button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* ══════ BIRTHDAYS TAB — ימי הולדת ══════ */}
        {tab === 'birthdays' && (() => {
          const hebMonths = ['ינואר','פברואר','מרץ','אפריל','מאי','יוני','יולי','אוגוסט','ספטמבר','אוקטובר','נובמבר','דצמבר'];
          const withDob = members.filter(m => m.child_dob);
          // בר מצווה — חצי שנה לפני גיל 13 (0–6 חודשים)
          const barMitzvahYear = withDob.filter(m => { const mo = monthsTo13(m.child_dob); return mo !== null && mo > 0 && mo <= 6; })
            .sort((a,b) => monthsTo13(a.child_dob) - monthsTo13(b.child_dob));
          // התראות יום הולדת — עד 30 ימים מהיום
          const birthdaySoon = withDob.filter(m => { const d = daysUntilBirthday(m.child_dob); return d !== null && d <= 30; })
            .sort((a,b) => daysUntilBirthday(a.child_dob) - daysUntilBirthday(b.child_dob));
          const barMitzvahIds = new Set(barMitzvahYear.map(m=>m.id));
          const alertsCount = birthdaySoon.length + barMitzvahYear.length;
          // כל השחקנים ממוינים לפי יום ההולדת הקרוב
          const allByUpcoming = [...withDob].sort((a,b) => daysUntilBirthday(a.child_dob) - daysUntilBirthday(b.child_dob));

          const childName = m => [m.child_name, m.child_last_name].map(s=>(s||'').trim()).filter(Boolean).join(' ');
          const parentName = m => `${(m.first_name||'').trim()}${m.last_name && !(m.first_name||'').includes(m.last_name) ? ' '+m.last_name.trim() : ''}`;
          const dobStr = m => m.child_dob ? new Date(m.child_dob).toLocaleDateString('he-IL') : '—';

          const bdayGiftMsg = m => `🎂 יום הולדת שמח ל${m.child_name}!\n\nשלום ${m.first_name}, 👋\n\nליגת הכוכבים של יוסטאר שמח לחגוג עם ${m.child_name} את יום ההולדת! ⚽🎉\n\nלרגל החגיגה — אנחנו רוצים להעניק ל${m.child_name} *קלף הכוכבים של יוסטאר* במתנה 🎁⭐\nקלף כדורגל אישי ומרהיב עם התמונה והפרטים של השחקן/ית.\n\nניצור קשר לתיאום המתנה 💬\n\nליגת הכוכבים של יוסטאר ⭐`;
          const barMsg = m => `🎊 מזל טוב לרגל בר המצווה המתקרב!\n\nשלום ${m.first_name}, 👋\n\nשמנו לב ש${m.child_name} מתקרב/ת לגיל בר מצווה! 🎉\n\nמועדון יוסטאר מציע *קליפ בר מצווה* מרגש ומקצועי —\nמתנת בר המצווה הייחודית ביותר לשחקן/ית צעיר/ה! 🎬⭐\n\nלפרטים ולתיאום: 054-6318704\n\nליגת הכוכבים של יוסטאר ⚽`;

          const PersonRow = ({ m, mode }) => {
            const days = daysUntilBirthday(m.child_dob);
            const turningAge = (calcAge(m.child_dob) ?? 0) + (days === 0 ? 0 : 1);
            const wa = `https://wa.me/${waNum(m.phone)}?text=${encodeURIComponent(mode==='bar'?barMsg(m):bdayGiftMsg(m))}`;
            return (
              <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid #1e3060', borderRadius:10, padding:'11px 15px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                <div style={{ fontSize:'0.88rem' }}>
                  <span style={{ color:'#fff', fontWeight:800 }}>🎈 {childName(m)}</span>
                  <span style={{ color:'#8a9aba' }}> · הורה: {parentName(m)}</span>
                  {m.team && <span style={{ color:'#4a5a7a', fontSize:'0.8rem' }}> · {m.team}</span>}
                  <div style={{ color:'#8a9aba', fontSize:'0.8rem', marginTop:3 }}>
                    📅 {dobStr(m)}
                    {days !== null && <span style={{ color: days<=7?'#ff9966':'#7ab0ff', fontWeight:700, marginRight:8 }}>{days===0?'🎉 היום!':`בעוד ${days} ימים`}</span>}
                    <span style={{ color:'#4a5a7a', marginRight:8 }}>· ימלאו לו/ה {turningAge}</span>
                  </div>
                </div>
                <div style={{ display:'flex', gap:6, alignItems:'center', flexShrink:0 }}>
                  <a href={wa} target="_blank" rel="noopener noreferrer"
                    style={{ background:'#25d366', color:'#fff', borderRadius:8, padding:'7px 15px', textDecoration:'none', fontWeight:700, fontSize:'0.83rem', whiteSpace:'nowrap' }}>
                    💬 {mode==='bar'?'הצע קליפ בר מצווה':'צור קשר למתנה'}
                  </a>
                  <a href={`https://wa.me/${waNum(m.phone)}`} target="_blank" rel="noopener noreferrer" title="וואטסאפ מהיר (ללא הודעה מוכנה)"
                    style={{ background:'rgba(37,211,102,0.12)', border:'1px solid rgba(37,211,102,0.4)', color:'#25d366', borderRadius:8, padding:'6px 11px', textDecoration:'none', fontWeight:800, fontSize:'0.85rem', whiteSpace:'nowrap' }}>
                    ⚡
                  </a>
                </div>
              </div>
            );
          };

          return (
            <div style={{ padding:'24px 20px', maxWidth:1000, margin:'0 auto' }}>
              <div style={{ marginBottom:20 }}>
                <h2 style={{ color:'#d4af37', margin:0, fontSize:'1.4rem' }}>🎂 ימי הולדת</h2>
                <p style={{ color:'#8a9aba', margin:'4px 0 0', fontSize:'0.88rem' }}>עקבו אחרי ימי ההולדת — צרו קשר עם ההורים והעניקו קלף כוכבים במתנה 🎁</p>
              </div>

              {/* ── רשימת התראות ── */}
              {alertsCount > 0 && (
                <div style={{ background:'linear-gradient(135deg,rgba(255,153,102,0.14),rgba(212,175,55,0.06))', border:'2px solid rgba(255,153,102,0.5)', borderRadius:14, padding:'14px 18px', marginBottom:20 }}>
                  <div style={{ color:'#ff9966', fontWeight:900, fontSize:'1rem', marginBottom:4 }}>🔔 התראות — {alertsCount} דורשות טיפול</div>
                  <div style={{ color:'#c0cce6', fontSize:'0.85rem' }}>
                    {birthdaySoon.length > 0 && <span>🎂 <strong>{birthdaySoon.length}</strong> ימי הולדת ב-30 הימים הקרובים</span>}
                    {birthdaySoon.length > 0 && barMitzvahYear.length > 0 && <span style={{ color:'#4a5a7a' }}> · </span>}
                    {barMitzvahYear.length > 0 && <span>🎊 <strong>{barMitzvahYear.length}</strong> בר מצווה בחצי השנה הקרובה</span>}
                  </div>
                </div>
              )}

              {/* ── בר מצווה בשנה הקרובה ── */}
              <div style={{ background:'linear-gradient(135deg,rgba(212,175,55,0.1),rgba(120,80,255,0.05))', border:'2px solid rgba(212,175,55,0.4)', borderRadius:16, padding:'18px 20px', marginBottom:22 }}>
                <div style={{ color:'#d4af37', fontWeight:900, fontSize:'1.05rem', marginBottom:4 }}>🎊 בר מצווה — חצי שנה הקרובה {barMitzvahYear.length > 0 && <span style={{ background:'#d4af37', color:'#0a1120', borderRadius:20, padding:'1px 10px', fontSize:'0.78rem', marginRight:6 }}>{barMitzvahYear.length}</span>}</div>
                <div style={{ color:'#8a9aba', fontSize:'0.82rem', marginBottom:14 }}>שחקנים שיגיעו לגיל 13 בתוך חצי שנה — הזמן להציע את קליפ בר המצווה של יוסטאר</div>
                {barMitzvahYear.length === 0 ? (
                  <div style={{ color:'#4a5a7a', fontSize:'0.85rem' }}>אין שחקנים לקראת בר מצווה בחצי השנה הקרובה</div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {barMitzvahYear.map(m => <PersonRow key={m.id} m={m} mode="bar" />)}
                  </div>
                )}
              </div>

              {/* ── יום הולדת בחודש הקרוב ── */}
              <div style={{ background:'linear-gradient(135deg,rgba(255,153,102,0.1),rgba(255,204,68,0.04))', border:'2px solid rgba(255,153,102,0.4)', borderRadius:16, padding:'18px 20px', marginBottom:22 }}>
                <div style={{ color:'#ff9966', fontWeight:900, fontSize:'1.05rem', marginBottom:4 }}>🎂 יום הולדת — 30 הימים הקרובים {birthdaySoon.length > 0 && <span style={{ background:'#ff9966', color:'#0a1120', borderRadius:20, padding:'1px 10px', fontSize:'0.78rem', marginRight:6 }}>{birthdaySoon.length}</span>}</div>
                <div style={{ color:'#8a9aba', fontSize:'0.82rem', marginBottom:14 }}>צרו קשר עם ההורה והעניקו את קלף הכוכבים במתנה לקראת יום ההולדת</div>
                {birthdaySoon.length === 0 ? (
                  <div style={{ color:'#4a5a7a', fontSize:'0.85rem' }}>אין ימי הולדת ב-30 הימים הקרובים</div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {birthdaySoon.map(m => <PersonRow key={m.id} m={m} mode={barMitzvahIds.has(m.id)?'bar':'birthday'} />)}
                  </div>
                )}
              </div>

              {/* ── כל השחקנים לפי יום הולדת קרוב ── */}
              <div style={{ color:'#c0cce6', fontWeight:800, fontSize:'0.95rem', marginBottom:12 }}>📋 כל השחקנים ({allByUpcoming.length}) — לפי יום ההולדת הקרוב</div>
              {allByUpcoming.length === 0 ? (
                <div style={{ textAlign:'center', color:'#4a5a7a', padding:'40px 20px' }}>אין שחקנים עם תאריך לידה רשום</div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {allByUpcoming.map(m => <PersonRow key={m.id} m={m} mode={barMitzvahIds.has(m.id)?'bar':'birthday'} />)}
                </div>
              )}
            </div>
          );
        })()}

        {/* ══════ CLIP PACKAGES TAB — חבילת קליפ ביצועים ══════ */}
        {tab === 'clip-packages' && (() => {
          const saleProducts = products.filter(p => p.product_type === 'sale');
          const inS = { width:'100%', background:'#0a1a3e', border:'1px solid #2a3a6a', borderRadius:8, padding:'9px 12px', color:'#fff', fontSize:'0.9rem', boxSizing:'border-box' };

          const setStars = async (pkg, n) => {
            const used = Math.max(0, Math.min(pkg.total_clips || 5, n));
            const { data } = await axios.put(API+'/clip-packages/'+pkg.id, { used_clips: used });
            setClipPackages(prev => prev.map(x => x.id === pkg.id ? { ...x, ...data } : x));
            if (used === (pkg.total_clips || 5)) flash('🎉 החבילה נוצלה במלואה!');
          };

          const createPackage = async () => {
            if (!newPackage.customer_name.trim()) return flash('בחר או הזן שם לקוח', 'error');
            const { data } = await axios.post(API+'/clip-packages', {
              customer_id: newPackage.customer_id || null,
              customer_name: newPackage.customer_name.trim(),
              phone: newPackage.phone || null, team: newPackage.team || null,
              birth_year: newPackage.birth_year || null,
              product_name: newPackage.product_name || 'חבילת 5 קליפ ביצועים',
              total_clips: newPackage.total_clips || 5, price: newPackage.price || 0,
              notes: newPackage.notes || null,
            });
            setClipPackages(prev => [data, ...prev]);
            setNewPackage({ customer_id:'', customer_name:'', phone:'', team:'', birth_year:'', product_name:'חבילת 5 קליפ ביצועים', total_clips:'5', price:'', notes:'' });
            setShowNewPackage(false);
            flash('✅ חבילה נוצרה');
          };

          const active = clipPackages.filter(p => (p.used_clips||0) < (p.total_clips||5));
          const done   = clipPackages.filter(p => (p.used_clips||0) >= (p.total_clips||5));
          const totalClipsUsed = clipPackages.reduce((n,p)=>n+(p.used_clips||0), 0);
          const totalClipsSold = clipPackages.reduce((n,p)=>n+(p.total_clips||5), 0);

          const PackageCard = ({ pkg, finished }) => {
            const total = pkg.total_clips || 5;
            const used  = pkg.used_clips || 0;
            const left  = total - used;
            return (
              <div style={{ background: finished ? 'rgba(37,211,102,0.05)' : '#0d1b3e',
                border:`2px solid ${finished ? 'rgba(37,211,102,0.45)' : 'rgba(212,175,55,0.35)'}`,
                borderRadius:14, padding:'16px 20px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, flexWrap:'wrap', marginBottom:12 }}>
                  <div>
                    <div style={{ color:'#fff', fontWeight:900, fontSize:'1.05rem' }}>{pkg.customer_name}</div>
                    <div style={{ color:'#8a9aba', fontSize:'0.82rem', marginTop:3 }}>
                      🎬 {pkg.product_name}
                      {pkg.team && <span> · {pkg.team}</span>}
                      {pkg.birth_year && <span> · שנתון {pkg.birth_year}</span>}
                      {pkg.price > 0 && <span style={{ color:'#d4af37', fontWeight:700 }}> · ₪{pkg.price}</span>}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                    <span style={{ background: finished ? 'rgba(37,211,102,0.15)' : 'rgba(212,175,55,0.14)',
                      color: finished ? '#25d366' : '#d4af37', borderRadius:20, padding:'3px 12px', fontSize:'0.8rem', fontWeight:900 }}>
                      {finished ? '🎉 נוצלה במלואה' : `נותרו ${left} מתוך ${total}`}
                    </span>
                    {pkg.phone && (
                      <a href={`https://wa.me/${waNum(pkg.phone)}`} target="_blank" rel="noopener noreferrer"
                        style={{ background:'rgba(37,211,102,0.14)', border:'1px solid rgba(37,211,102,0.4)', color:'#25d366', borderRadius:8, padding:'5px 12px', textDecoration:'none', fontWeight:800, fontSize:'0.82rem' }}>💬</a>
                    )}
                    <button onClick={async()=>{
                      if(!window.confirm('למחוק חבילה זו?')) return;
                      await axios.delete(API+'/clip-packages/'+pkg.id);
                      setClipPackages(prev => prev.filter(x=>x.id!==pkg.id));
                    }} style={{ background:'rgba(255,80,80,0.07)', color:'#ff8888', border:'1px solid rgba(255,80,80,0.25)', borderRadius:8, padding:'5px 10px', cursor:'pointer', fontSize:'0.78rem' }}>🗑</button>
                  </div>
                </div>

                {/* ⭐ כוכבים — לחיצה מסמנת קליפ שנוצל */}
                <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                  <div style={{ display:'flex', gap:8 }}>
                    {Array.from({length: total}).map((_, i) => {
                      const filled = i < used;
                      return (
                        <button key={i} onClick={()=>setStars(pkg, filled && i === used-1 ? used-1 : i+1)}
                          title={filled ? `בטל סימון קליפ ${i+1}` : `סמן קליפ ${i+1} כנוצל`}
                          style={{ background:'none', border:'none', cursor:'pointer', padding:0,
                            fontSize:'1.9rem', lineHeight:1, filter: filled ? 'none' : 'grayscale(1)',
                            opacity: filled ? 1 : 0.35, transition:'all 0.15s' }}>
                          ⭐
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ marginRight:'auto', display:'flex', alignItems:'center', gap:10 }}>
                    <span style={{ color: finished ? '#25d366' : '#d4af37', fontWeight:900, fontSize:'1.1rem' }}>{used}/{total}</span>
                    <div style={{ background:'rgba(255,255,255,0.07)', borderRadius:6, height:8, width:120, overflow:'hidden' }}>
                      <div style={{ width:`${Math.round(used/total*100)}%`, height:'100%', background: finished ? '#25d366' : 'linear-gradient(90deg,#d4af37,#f0cc60)', borderRadius:6, transition:'width 0.3s' }} />
                    </div>
                  </div>
                </div>
                {pkg.notes && <div style={{ color:'#4a5a7a', fontSize:'0.78rem', marginTop:8 }}>📝 {pkg.notes}</div>}
              </div>
            );
          };

          return (
            <div style={{ padding:'24px 20px', maxWidth:1000, margin:'0 auto' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10, marginBottom:18 }}>
                <div>
                  <h2 style={{ color:'#d4af37', margin:0, fontSize:'1.4rem' }}>🎬 חבילת קליפ ביצועים</h2>
                  <p style={{ color:'#8a9aba', margin:'4px 0 0', fontSize:'0.88rem' }}>מעקב אחרי לקוחות שרכשו חבילה — כל קליפ שנמסר מסומן בכוכב ⭐</p>
                </div>
                <button onClick={()=>setShowNewPackage(v=>!v)}
                  style={{ background:'linear-gradient(135deg,#d4af37,#b8941f)', color:'#000', border:'none', borderRadius:10, padding:'10px 20px', cursor:'pointer', fontWeight:800, fontSize:'0.88rem' }}>
                  {showNewPackage ? '▲ סגור' : '+ חבילה חדשה'}
                </button>
              </div>

              {/* KPI */}
              {clipPackages.length > 0 && (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:10, marginBottom:18 }}>
                  {[
                    { label:'חבילות פעילות', val:active.length, color:'#d4af37', icon:'🎬' },
                    { label:'הושלמו', val:done.length, color:'#25d366', icon:'🎉' },
                    { label:'קליפים שנמסרו', val:`${totalClipsUsed}/${totalClipsSold}`, color:'#7ab0ff', icon:'⭐' },
                  ].map(k => (
                    <div key={k.label} style={{ background:'#0d1b3e', border:`1px solid ${k.color}33`, borderRadius:12, padding:'12px 14px' }}>
                      <div style={{ color:'#8a9aba', fontSize:'0.74rem', marginBottom:4 }}>{k.icon} {k.label}</div>
                      <div style={{ color:k.color, fontWeight:900, fontSize:'1.5rem', lineHeight:1 }}>{k.val}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* טופס חבילה חדשה */}
              {showNewPackage && (
                <div style={{ background:'linear-gradient(135deg,rgba(212,175,55,0.08),rgba(212,175,55,0.03))', border:'2px solid rgba(212,175,55,0.4)', borderRadius:14, padding:'18px 20px', marginBottom:18 }}>
                  <div style={{ color:'#d4af37', fontWeight:900, fontSize:'0.95rem', marginBottom:12 }}>➕ חבילה חדשה</div>

                  {/* בחירת לקוח קיים */}
                  {customers.length > 0 && (
                    <>
                      <div style={{ color:'#8a9aba', fontSize:'0.78rem', fontWeight:700, marginBottom:6 }}>בחר לקוח קיים:</div>
                      <select value={newPackage.customer_id}
                        onChange={e=>{
                          const c = customers.find(x => String(x.id) === e.target.value);
                          setNewPackage(n => c
                            ? { ...n, customer_id:String(c.id), customer_name:`${c.first_name||''} ${c.last_name||''}`.trim(), phone:c.phone||'', team:c.team||'', birth_year:c.birth_year||'' }
                            : { ...n, customer_id:'' });
                        }} style={{ ...inS, marginBottom:10 }}>
                        <option value="">— לקוח חדש / ידני —</option>
                        {customers.map(c => (
                          <option key={c.id} value={c.id}>{`${c.first_name||''} ${c.last_name||''}`.trim()}{fullTeamLabel(c)?` · ${fullTeamLabel(c)}`:''}</option>
                        ))}
                      </select>
                    </>
                  )}

                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:10, marginBottom:10 }}>
                    <input value={newPackage.customer_name} placeholder="שם הלקוח *"
                      onChange={e=>setNewPackage(n=>({ ...n, customer_name:e.target.value }))} style={inS} />
                    <input value={newPackage.phone} placeholder="טלפון"
                      onChange={e=>setNewPackage(n=>({ ...n, phone:e.target.value }))} style={inS} />
                    <input value={newPackage.team} placeholder="קבוצה"
                      onChange={e=>setNewPackage(n=>({ ...n, team:e.target.value }))} style={inS} />
                    <input type="number" value={newPackage.birth_year} placeholder="שנתון"
                      onChange={e=>setNewPackage(n=>({ ...n, birth_year:e.target.value }))} style={inS} />
                  </div>

                  {/* מוצר מהקטלוג */}
                  {saleProducts.length > 0 && (
                    <>
                      <div style={{ color:'#8a9aba', fontSize:'0.78rem', fontWeight:700, marginBottom:6 }}>מוצר מהקטלוג:</div>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:10 }}>
                        {saleProducts.map(p => {
                          const sel = newPackage.product_name === p.name;
                          return (
                            <button key={p.id} type="button"
                              onClick={()=>setNewPackage(n=>({ ...n, product_name:p.name, price:String(p.price) }))}
                              style={{ padding:'6px 12px', borderRadius:8, border:`1px solid ${sel?'#d4af37':'#2a3a6a'}`, background:sel?'rgba(212,175,55,0.18)':'#0a1525', cursor:'pointer', fontSize:'0.8rem', color:sel?'#d4af37':'#c0cce6' }}>
                              {p.name} · ₪{p.price}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}

                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:10, marginBottom:14 }}>
                    <input value={newPackage.product_name} placeholder="שם החבילה"
                      onChange={e=>setNewPackage(n=>({ ...n, product_name:e.target.value }))} style={inS} />
                    <input type="number" min="1" value={newPackage.total_clips} placeholder="מס׳ קליפים"
                      onChange={e=>setNewPackage(n=>({ ...n, total_clips:e.target.value }))} style={inS} />
                    <input type="number" value={newPackage.price} placeholder="₪ מחיר"
                      onChange={e=>setNewPackage(n=>({ ...n, price:e.target.value }))} style={inS} />
                  </div>

                  <button onClick={createPackage}
                    style={{ background:'linear-gradient(135deg,#d4af37,#b8941f)', color:'#000', border:'none', borderRadius:10, padding:'10px 24px', cursor:'pointer', fontWeight:900, fontSize:'0.88rem' }}>
                    ✅ צור חבילה
                  </button>
                </div>
              )}

              {/* חבילות פעילות */}
              <div style={{ color:'#c0cce6', fontWeight:800, fontSize:'0.95rem', marginBottom:12 }}>🎬 חבילות פעילות ({active.length})</div>
              {active.length === 0 ? (
                <div style={{ textAlign:'center', color:'#4a5a7a', padding:'30px 20px' }}>אין חבילות פעילות</div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:12, marginBottom:26 }}>
                  {active.map(pkg => <PackageCard key={pkg.id} pkg={pkg} finished={false} />)}
                </div>
              )}

              {/* חבילות שהושלמו */}
              {done.length > 0 && (
                <>
                  <div style={{ color:'#25d366', fontWeight:800, fontSize:'0.95rem', marginBottom:12 }}>🎉 חבילות שנוצלו במלואן ({done.length})</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                    {done.map(pkg => <PackageCard key={pkg.id} pkg={pkg} finished={true} />)}
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* ══════ INTRO PROFILES TAB — פתיח פרופיל מקצועי ══════ */}
        {tab === 'intro-profiles' && (() => {
          const introList = members
            .filter(m => m.lead_source === 'intro')
            .sort((a,b) => String(b.joined_at||'').localeCompare(String(a.joined_at||'')));

          const q = introSearch.trim().toLowerCase();
          const shown = introList.filter(m => {
            if (introFilter === 'pending' && m.intro_sent) return false;
            if (introFilter === 'sent' && !m.intro_sent) return false;
            if (!q) return true;
            const qPhone = normPhone(q);
            const hay = `${m.child_name||''} ${m.first_name||''} ${m.team||''}`.toLowerCase();
            return hay.includes(q)
              || (qPhone && (normPhone(m.phone).includes(qPhone) || normPhone(m.player_phone).includes(qPhone)))
              || String(m.member_number||'').includes(q);
          });

          const pendingCount = introList.filter(m => !m.intro_sent).length;
          const sentCount    = introList.filter(m => m.intro_sent).length;

          // הפרטים שהשחקן רשם — כפי שנשמרו בשדה החופשי
          const rolesOf = m => {
            const raw = String(m.positions || '');
            const roles = raw.split(' · ')[0];
            return roles && !/^(חולצה|צבע|רגל|קפטן)/.test(roles) ? roles : '';
          };

          const introMsg = (m, toPlayer) => {
            const name = toPlayer ? (String(m.child_name||'').trim().split(' ')[0] || 'שחקן') : (m.first_name || '');
            return toPlayer
              ? `שלום ${name}! 🎬\n\nכאן יוסטאר — הפתיח שלך *פתיח יוסטאר — פרופיל שחקן מקצועי* מוכן! ⭐\n\n${m.team ? `${m.team} · ` : ''}${m.birth_year ? `שנתון ${m.birth_year}` : ''}${m.shirt_number ? ` · חולצה ${m.shirt_number}` : ''}\n\nתהנה, ותשתף איפה שבא לך 🔥\n\nליגת הכוכבים של יוסטאר ⚽`
              : `שלום ${name}! 👋\n\nכאן יוסטאר — הכנו ל${String(m.child_name||'').trim().split(' ')[0] || 'שחקן'} את *פתיח יוסטאר — פרופיל שחקן מקצועי* במתנה 🎬\n\nמצרפים אותו כאן.\n\nיוסטאר מייצרת *קליפי ביצועים אישיים* לשחקני כדורגל צעירים — סרטון ערוך שמציג את היכולות של השחקן על המגרש. כרטיס ביקור מול מאמנים, וגם מזכרת שנשארת למשפחה.\n\nאשמח לספר לך עוד 📞 054-6318704\n\nליגת הכוכבים של יוסטאר ⚽`;
          };

          const markIntro = async (m, val) => {
            const { data } = await axios.put(API+'/members/'+m.id+'/fulfillment', { intro_sent: val });
            setMembers(prev => prev.map(x => x.id===m.id ? { ...x, ...data } : x));
          };

          const chip = (label, val, color) => val ? (
            <span style={{ background:`${color}14`, color, border:`1px solid ${color}44`, borderRadius:7,
              padding:'3px 9px', fontSize:'0.76rem', fontWeight:700, whiteSpace:'nowrap' }}>
              {label} {val}
            </span>
          ) : null;

          return (
            <div style={{ padding:'24px 20px', maxWidth:1080, margin:'0 auto', minWidth:0 }}>
              <div style={{ marginBottom:20 }}>
                <h2 style={{ color:'#d4af37', margin:0, fontSize:'1.4rem' }}>🎞 פתיח פרופיל מקצועי</h2>
                <p style={{ color:'#8a9aba', margin:'4px 0 0', fontSize:'0.88rem' }}>
                  כל מי שנרשם דרך <span style={{ color:'#7ab0ff' }}>ustarclub.co.il/intro</span> — הפרטים שהשחקן מילא, ווואטסאפ של השחקן ושל ההורה
                </p>
              </div>

              {/* מדדים */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:11, marginBottom:18 }}>
                {[
                  { label:'סה״כ נרשמו', val:introList.length, color:'#d4af37' },
                  { label:'ממתינים לפתיח', val:pendingCount, color:'#ff9966' },
                  { label:'הפתיח נשלח', val:sentCount, color:'#25d366' },
                ].map(k => (
                  <div key={k.label} style={{ background:'#0d1b3e', border:`1px solid ${k.color}44`, borderRadius:12, padding:'14px 16px' }}>
                    <div style={{ color:'#8a9aba', fontSize:'0.78rem', marginBottom:5 }}>{k.label}</div>
                    <div style={{ color:k.color, fontWeight:900, fontSize:'1.5rem' }}>{k.val}</div>
                  </div>
                ))}
              </div>

              {/* סינון וחיפוש */}
              <div style={{ display:'flex', gap:9, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
                {[
                  { id:'all',     label:`הכל (${introList.length})`,      color:'#d4af37' },
                  { id:'pending', label:`⏳ ממתינים (${pendingCount})`,   color:'#ff9966' },
                  { id:'sent',    label:`✅ נשלחו (${sentCount})`,        color:'#25d366' },
                ].map(f => {
                  const active = introFilter === f.id;
                  return (
                    <button key={f.id} onClick={()=>setIntroFilter(f.id)}
                      style={{ background: active ? `${f.color}1e` : 'transparent', color: active ? f.color : '#8a9aba',
                        border:`1px solid ${active ? f.color+'88' : '#2a3a6a'}`, borderRadius:9, padding:'7px 15px',
                        cursor:'pointer', fontSize:'0.83rem', fontWeight:active?800:600 }}>{f.label}</button>
                  );
                })}
                <input value={introSearch} onChange={e=>setIntroSearch(e.target.value)}
                  placeholder="🔍 שם שחקן / הורה / קבוצה / טלפון"
                  style={{ flex:'1 1 220px', background:'#0a1a3e', border:'1px solid #2a3a6a', borderRadius:9,
                    padding:'8px 12px', color:'#fff', fontSize:'0.86rem', boxSizing:'border-box' }} />
              </div>

              {shown.length === 0 ? (
                <div style={{ textAlign:'center', color:'#4a5a7a', padding:'48px 20px' }}>
                  {introList.length === 0 ? 'עדיין אין נרשמים לפתיח' : 'אין תוצאות לסינון הזה'}
                </div>
              ) : (
                <div style={{ background:'#0a1225', border:'1px solid #1a2a4a', borderRadius:12, overflow:'hidden' }}>
                 <div style={{ overflowX:'auto' }}>
                  <div style={{ minWidth:860 }}>
                  {/* כותרות עמודות */}
                  <div style={{ display:'grid', gridTemplateColumns:'minmax(150px,1.5fr) minmax(130px,1.3fr) 118px minmax(140px,1.2fr) 190px 96px 38px',
                    gap:10, padding:'9px 14px', background:'rgba(212,175,55,0.06)', borderBottom:'1px solid #1a2a4a',
                    color:'#8a9aba', fontSize:'0.74rem', fontWeight:800 }}>
                    <div>שחקן</div><div>קבוצה · שנתון</div><div>חולצה</div><div>הורה</div><div>וואטסאפ</div><div>פתיח</div><div></div>
                  </div>

                  {shown.map((m, idx) => {
                    const playerFirst = String(m.child_name||'').trim().split(' ')[0] || '';
                    const roles = rolesOf(m);
                    const open = expandedIntro === m.id;
                    const colorHex = (SHIRT_HEX[m.shirt_color] || '#4a5a7a');
                    return (
                      <React.Fragment key={m.id}>
                        <div style={{ display:'grid', gridTemplateColumns:'minmax(150px,1.5fr) minmax(130px,1.3fr) 118px minmax(140px,1.2fr) 190px 96px 38px',
                          gap:10, padding:'10px 14px', alignItems:'center',
                          borderBottom: open ? 'none' : (idx < shown.length-1 ? '1px solid #131f3a' : 'none'),
                          background: open ? 'rgba(212,175,55,0.05)' : idx % 2 ? 'rgba(255,255,255,0.012)' : 'transparent' }}>

                          {/* שחקן */}
                          <div style={{ minWidth:0 }}>
                            <button onClick={()=>setExpandedIntro(open ? null : m.id)}
                              style={{ background:'none', border:'none', padding:0, cursor:'pointer', textAlign:'right',
                                color:'#fff', fontWeight:800, fontSize:'0.88rem', display:'flex', alignItems:'center', gap:5, maxWidth:'100%' }}>
                              <span style={{ color:'#4a5a7a', fontSize:'0.7rem' }}>{open?'▲':'▼'}</span>
                              <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.child_name}</span>
                              {m.is_captain && <span title="קפטן">🎖️</span>}
                            </button>
                            <div style={{ color:'#4a5a7a', fontSize:'0.71rem', fontFamily:'monospace' }}>#{pad4(m.member_number)} · {fmtDate(m.joined_at)}</div>
                          </div>

                          {/* קבוצה · שנתון */}
                          <div style={{ minWidth:0 }}>
                            <div style={{ color:'#d4af37', fontWeight:700, fontSize:'0.82rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.team}</div>
                            <div style={{ color:'#8a9aba', fontSize:'0.74rem' }}>{m.birth_year || ''}{roles ? ` · ${roles.split(',')[0]}` : ''}</div>
                          </div>

                          {/* חולצה */}
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <span style={{ width:13, height:13, borderRadius:'50%', background:colorHex, border:'1px solid rgba(255,255,255,0.3)', flexShrink:0 }} />
                            <span style={{ color:'#fff', fontWeight:800, fontSize:'0.85rem' }}>{m.shirt_number || '—'}</span>
                            <span style={{ color:'#4a5a7a', fontSize:'0.72rem' }}>{m.shirt_color || ''}</span>
                          </div>

                          {/* הורה */}
                          <div style={{ minWidth:0 }}>
                            <div style={{ color:'#c0cce6', fontSize:'0.82rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.first_name}</div>
                            <div style={{ color:'#4a5a7a', fontSize:'0.73rem', fontFamily:'monospace', direction:'ltr', textAlign:'right' }}>{normPhone(m.phone)}</div>
                          </div>

                          {/* וואטסאפ — שחקן + הורה */}
                          <div style={{ display:'flex', gap:5 }}>
                            {m.player_phone ? (
                              <a href={waLink(m.player_phone, introMsg(m, true))} target="_blank" rel="noopener noreferrer"
                                title={`וואטסאפ ל${playerFirst}`}
                                style={{ background:'#25d366', color:'#fff', borderRadius:7, padding:'5px 10px',
                                  textDecoration:'none', fontWeight:800, fontSize:'0.75rem', whiteSpace:'nowrap' }}>📱 שחקן</a>
                            ) : (
                              <span title="לא מסר טלפון" style={{ background:'#151f36', color:'#3a4a6a', borderRadius:7, padding:'5px 10px', fontSize:'0.75rem', fontWeight:700, whiteSpace:'nowrap' }}>📱 שחקן</span>
                            )}
                            <a href={waLink(m.phone, introMsg(m, false))} target="_blank" rel="noopener noreferrer" title="וואטסאפ להורה"
                              style={{ background:'rgba(37,211,102,0.14)', color:'#25d366', border:'1px solid rgba(37,211,102,0.4)',
                                borderRadius:7, padding:'4px 10px', textDecoration:'none', fontWeight:800, fontSize:'0.75rem', whiteSpace:'nowrap' }}>📱 הורה</a>
                          </div>

                          {/* סטטוס פתיח */}
                          <div>
                            <button onClick={()=>markIntro(m, !m.intro_sent)}
                              title={m.intro_sent ? 'הפתיח נשלח — לחץ לביטול' : 'סמן שהפתיח נשלח'}
                              style={{ width:'100%', background: m.intro_sent ? 'rgba(37,211,102,0.14)' : 'rgba(212,175,55,0.1)',
                                color: m.intro_sent ? '#25d366' : '#d4af37',
                                border:`1px solid ${m.intro_sent ? 'rgba(37,211,102,0.45)' : 'rgba(212,175,55,0.4)'}`,
                                borderRadius:7, padding:'5px 0', cursor:'pointer', fontSize:'0.75rem', fontWeight:800, whiteSpace:'nowrap' }}>
                              {m.intro_sent ? '✅ נשלח' : '○ ממתין'}
                            </button>
                          </div>

                          {/* מחיקה — לרישום כפול או פרטים שגויים */}
                          <div>
                            <button onClick={async()=>{
                              if(!window.confirm(`למחוק את ${m.child_name} לצמיתות?\nהרישום יימחק גם מחברי ליגת הכוכבים.`)) return;
                              await axios.delete(API+'/members/'+m.id);
                              setMembers(prev => prev.filter(x => x.id !== m.id));
                              if (expandedIntro === m.id) setExpandedIntro(null);
                              flash('🗑 הרישום נמחק');
                            }} title="מחק רישום (כפילות / פרטים שגויים)"
                              style={{ width:'100%', background:'rgba(255,80,80,0.07)', color:'#ff8888',
                                border:'1px solid rgba(255,80,80,0.28)', borderRadius:7, padding:'5px 0',
                                cursor:'pointer', fontSize:'0.78rem' }}>🗑</button>
                          </div>
                        </div>

                        {/* פירוט מלא */}
                        {open && (
                          <div style={{ padding:'4px 14px 16px', background:'rgba(212,175,55,0.05)',
                            borderBottom: idx < shown.length-1 ? '1px solid #131f3a' : 'none' }}>
                            <div style={{ display:'flex', flexWrap:'wrap', gap:7, marginBottom:12 }}>
                              {chip('👕 חולצה', m.shirt_number, '#7ab0ff')}
                              {chip('🎨 צבע', m.shirt_color, '#f0cc60')}
                              {chip('⚽ תפקיד', roles, '#25d366')}
                              {chip('🦶 רגל חזקה', m.strong_foot, '#c88cf0')}
                              {chip('🎖️ קפטן', m.is_captain ? 'כן' : '', '#d4af37')}
                              {chip('🎂 תאריך לידה', m.child_dob ? fmtDate(m.child_dob) : '', '#8a9aba')}
                            </div>
                            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(250px,1fr))', gap:11 }}>
                              <div style={{ background:'rgba(122,176,255,0.06)', border:'1px solid rgba(122,176,255,0.28)', borderRadius:11, padding:'11px 13px' }}>
                                <div style={{ color:'#7ab0ff', fontWeight:900, fontSize:'0.8rem', marginBottom:6 }}>⚽ השחקן</div>
                                <div style={{ color:'#fff', fontWeight:700, fontSize:'0.88rem' }}>{m.child_name}</div>
                                <div style={{ color: m.player_phone ? '#8a9aba' : '#4a5a7a', fontSize:'0.82rem', marginTop:3, fontFamily: m.player_phone?'monospace':'inherit', direction:'ltr', textAlign:'right' }}>
                                  {m.player_phone ? normPhone(m.player_phone) : 'לא מסר טלפון — שולחים להורה בלבד'}
                                </div>
                              </div>
                              <div style={{ background:'rgba(212,175,55,0.06)', border:'1px solid rgba(212,175,55,0.28)', borderRadius:11, padding:'11px 13px' }}>
                                <div style={{ color:'#d4af37', fontWeight:900, fontSize:'0.8rem', marginBottom:6 }}>👨‍👩‍👦 ההורה</div>
                                <div style={{ color:'#fff', fontWeight:700, fontSize:'0.88rem' }}>{m.first_name}</div>
                                <div style={{ color:'#8a9aba', fontSize:'0.82rem', marginTop:3, fontFamily:'monospace', direction:'ltr', textAlign:'right' }}>{normPhone(m.phone)}</div>
                                {m.email && <div style={{ color:'#4a5a7a', fontSize:'0.77rem', marginTop:2, direction:'ltr', textAlign:'right' }}>{m.email}</div>}
                              </div>
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                  </div>
                 </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* ══════ PLAYER PROFILES TAB — ניהול פרופיל שחקן ══════ */}
        {tab === 'player-profiles' && (() => {
          const POSITIONS_P = ['שוער','בלם','מגן','קשר','כנף','חלוץ'];
          const AGE_GROUPS_P = ['טרום א','ילדים ג','ילדים ב','ילדים א','נערים ג','נערים ב','נערים א','נוער'];
          const MATCH_CATEGORIES_P = ['אימון','ליגה','גביע','טורניר'];
          const nowYP = new Date().getFullYear();
          const YEARS_P = Array.from({length:18}, (_,i)=>nowYP-4-i);

          const readFileAsBase64 = file => new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          const createPlayerProfile = async () => {
            const f = newPlayerForm;
            if (!f.name.trim()) return flash('הזן שם שחקן', 'error');
            if (!f.slug.trim()) return flash('הזן כתובת (slug) באנגלית', 'error');
            try {
              const { data } = await axios.post(API+'/player-profiles', f);
              setPlayerProfiles(prev => [data, ...prev]);
              setNewPlayerForm(emptyPlayerForm);
              setShowNewPlayerProfile(false);
              flash(`⭐ הפרופיל של ${data.name} נוצר! הקישור: ustarclub.co.il/pro/${data.slug}`);
            } catch (e) { flash(e.response?.data?.error || 'שגיאה ביצירת הפרופיל', 'error'); }
          };

          const saveEditPlayerProfile = async id => {
            try {
              const { data } = await axios.put(API+'/player-profiles/'+id, editPlayerForm);
              setPlayerProfiles(prev => prev.map(p => p.id === id ? { ...p, ...data } : p));
              setEditPlayerId(null);
              flash('✅ הפרופיל עודכן');
            } catch (e) { flash(e.response?.data?.error || 'שגיאה בעדכון', 'error'); }
          };

          const deletePlayerProfile = async p => {
            if (!window.confirm(`למחוק לצמיתות את הפרופיל של ${p.name}?\nהאתר שלו יפסיק לפעול והקליפים שלו יימחקו.`)) return;
            await axios.delete(API+'/player-profiles/'+p.id);
            setPlayerProfiles(prev => prev.filter(x => x.id !== p.id));
            flash('🗑 הפרופיל נמחק');
          };

          const loadClips = async playerId => {
            const { data } = await axios.get(API+'/player-profiles/'+playerId+'/clips');
            setPlayerClips(prev => ({ ...prev, [playerId]: data }));
          };

          const loadPhotos = async playerId => {
            const { data } = await axios.get(API+'/player-profiles/'+playerId+'/photos');
            setPlayerPhotos(prev => ({ ...prev, [playerId]: data.map(ph => ({ ...ph, url: API+'/player-photos/'+ph.id+'/image' })) }));
          };

          const toggleExpand = async p => {
            if (expandedPlayerProfile === p.id) { setExpandedPlayerProfile(null); return; }
            setExpandedPlayerProfile(p.id);
            if (!playerClips[p.id]) await loadClips(p.id);
            if (!playerPhotos[p.id]) await loadPhotos(p.id);
          };

          const uploadPhotos = async (playerId, files) => {
            for (const file of files) {
              const b64 = await readFileAsBase64(file);
              try {
                const { data } = await axios.post(API+'/player-profiles/'+playerId+'/photos', { image_base64: b64 });
                setPlayerPhotos(prev => ({ ...prev, [playerId]: [...(prev[playerId]||[]), { ...data, url: API+'/player-photos/'+data.id+'/image' }] }));
              } catch (e) { flash(e.response?.data?.error || 'שגיאה בהעלאת תמונה', 'error'); }
            }
            flash('🖼️ התמונות נוספו לגלריה');
          };

          const deletePhoto = async (playerId, photoId) => {
            if (!window.confirm('להסיר את התמונה מהגלריה?')) return;
            await axios.delete(API+'/player-photos/'+photoId);
            setPlayerPhotos(prev => ({ ...prev, [playerId]: prev[playerId].filter(ph => ph.id !== photoId) }));
            flash('🗑 התמונה הוסרה');
          };

          const addClip = async playerId => {
            const c = newClipForm;
            if (!c.youtube_url.trim()) return flash('הדבק קישור יוטיוב', 'error');
            try {
              const { data } = await axios.post(API+'/player-profiles/'+playerId+'/clips', c);
              setPlayerClips(prev => ({ ...prev, [playerId]: [data, ...(prev[playerId]||[])] }));
              setPlayerProfiles(prev => prev.map(p => p.id === playerId ? { ...p, clip_count: (p.clip_count||0)+1 } : p));
              setNewClipForm(emptyClipForm);
              setShowNewClipFor(null);
              flash('🎬 הקליפ נוסף לאתר השחקן');
            } catch (e) { flash(e.response?.data?.error || 'קישור יוטיוב לא תקין', 'error'); }
          };

          const saveEditClip = async (playerId, clipId) => {
            try {
              const { data } = await axios.put(API+'/player-clips/'+clipId, editClipForm);
              setPlayerClips(prev => ({ ...prev, [playerId]: prev[playerId].map(c => c.id === clipId ? data : c) }));
              setEditClipId(null);
              flash('✅ הקליפ עודכן');
            } catch (e) { flash(e.response?.data?.error || 'שגיאה בעדכון הקליפ', 'error'); }
          };

          // הזזת קליפ למעלה/למטה — קובע איזה קליפ מוצג ראשון בדף השחקן
          const moveClip = async (playerId, index, dir) => {
            const list = [...(playerClips[playerId] || [])];
            const target = index + dir;
            if (target < 0 || target >= list.length) return;
            [list[index], list[target]] = [list[target], list[index]];
            setPlayerClips(prev => ({ ...prev, [playerId]: list }));
            try {
              await axios.put(API+'/player-profiles/'+playerId+'/clips/order', { order: list.map(c => c.id) });
            } catch (e) {
              flash('שגיאה בשמירת הסדר', 'error');
              await loadClips(playerId);
            }
          };

          const deleteClip = async (playerId, clipId) => {
            if (!window.confirm('למחוק את הקליפ הזה מהאתר?')) return;
            await axios.delete(API+'/player-clips/'+clipId);
            setPlayerClips(prev => ({ ...prev, [playerId]: prev[playerId].filter(c => c.id !== clipId) }));
            setPlayerProfiles(prev => prev.map(p => p.id === playerId ? { ...p, clip_count: Math.max(0,(p.clip_count||1)-1) } : p));
            flash('🗑 הקליפ נמחק');
          };

          const inS = { width:'100%', background:'#0a1a3e', border:'1px solid #2a3a6a', borderRadius:10, padding:'9px 12px', color:'#fff', fontSize:'0.87rem', boxSizing:'border-box' };
          const lblS = { color:'#8a9aba', fontSize:'0.76rem', fontWeight:700, marginBottom:5, display:'block' };

          return (
            <div style={{ padding:'24px 20px', maxWidth:1000, margin:'0 auto' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:12 }}>
                <div>
                  <h2 style={{ color:'#d4af37', margin:0, fontSize:'1.4rem' }}>🏅 ניהול פרופיל שחקן</h2>
                  <p style={{ color:'#8a9aba', margin:'4px 0 0', fontSize:'0.88rem' }}>אתר אישי לכל שחקן שהכנתם לו קליפ — עדכנו תוכן, הוסיפו קליפים חדשים ופרסמו פרופילים חדשים</p>
                </div>
                <button onClick={()=>setShowNewPlayerProfile(v=>!v)} className="btn btn-gold" style={{ padding:'10px 22px', whiteSpace:'nowrap' }}>
                  {showNewPlayerProfile ? '▲ סגור' : '+ הוסף פרופיל שחקן חדש'}
                </button>
              </div>

              {/* ── טופס פרופיל חדש ── */}
              {showNewPlayerProfile && (
                <div style={{ background:'linear-gradient(135deg,rgba(212,175,55,0.08),rgba(212,175,55,0.03))', border:'2px solid rgba(212,175,55,0.4)', borderRadius:14, padding:'20px 22px', marginBottom:22 }}>
                  <div style={{ color:'#d4af37', fontWeight:900, fontSize:'0.98rem', marginBottom:16 }}>➕ פרופיל שחקן חדש</div>

                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:11, marginBottom:11 }}>
                    <div>
                      <label style={lblS}>שם מלא *</label>
                      <input value={newPlayerForm.name} onChange={e=>setNewPlayerForm(f=>({...f, name:e.target.value}))} style={inS} placeholder="אריאל לייבוביץ" />
                    </div>
                    <div>
                      <label style={lblS}>שם פרטי (לניסוחים באתר)</label>
                      <input value={newPlayerForm.first_name} onChange={e=>setNewPlayerForm(f=>({...f, first_name:e.target.value}))} style={inS} placeholder="אריאל" />
                    </div>
                    <div>
                      <label style={lblS}>כתובת האתר (אנגלית) *</label>
                      <input value={newPlayerForm.slug} onChange={e=>setNewPlayerForm(f=>({...f, slug:e.target.value}))} style={inS} placeholder="ariel-leibovich" dir="ltr" />
                    </div>
                  </div>

                  <div style={{ marginBottom:11 }}>
                    <label style={lblS}>שפת התצוגה באתר</label>
                    <div style={{ display:'flex', gap:8 }}>
                      {[{v:'he',l:'עברית'},{v:'en',l:'English'}].map(o => {
                        const sel = newPlayerForm.language === o.v;
                        return (
                          <button key={o.v} type="button" onClick={()=>setNewPlayerForm(f=>({...f, language:o.v}))}
                            style={{ padding:'7px 18px', borderRadius:9, cursor:'pointer', fontWeight:800, fontSize:'0.83rem',
                              border:`1px solid ${sel?'#d4af37':'#2a3a6a'}`, background: sel?'rgba(212,175,55,0.14)':'#0a1525', color: sel?'#d4af37':'#8a9aba' }}>{o.l}</button>
                        );
                      })}
                    </div>
                    <div style={{ color:'#5a6a90', fontSize:'0.72rem', marginTop:5 }}>בחרו אנגלית אם גרפיקת הפרופיל שעיצבתם היא באנגלית — כל טקסטי האתר יעברו לאנגלית בהתאם</div>
                  </div>

                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:11, marginBottom:11 }}>
                    <div>
                      <label style={lblS}>תפקיד</label>
                      <select value={newPlayerForm.position} onChange={e=>setNewPlayerForm(f=>({...f, position:e.target.value}))} style={inS}>
                        <option value="">— תפקיד —</option>
                        {POSITIONS_P.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={lblS}>קבוצה</label>
                      <TeamSearchInput value={newPlayerForm.team} onChange={v=>setNewPlayerForm(f=>({...f, team:v}))} options={FOOTBALL_CLUBS} placeholder="הקלד שם קבוצה" />
                    </div>
                    <div>
                      <label style={lblS}>שכבת גיל</label>
                      <select value={newPlayerForm.age_group} onChange={e=>setNewPlayerForm(f=>({...f, age_group:e.target.value}))} style={inS}>
                        <option value="">— שכבה —</option>
                        {AGE_GROUPS_P.map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </div>
                  </div>

                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))', gap:11, marginBottom:16 }}>
                    <div>
                      <label style={lblS}>שנתון</label>
                      <select value={newPlayerForm.birth_year} onChange={e=>setNewPlayerForm(f=>({...f, birth_year:e.target.value}))} style={inS}>
                        <option value="">— שנתון —</option>
                        {YEARS_P.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={lblS}>מספר חולצה</label>
                      <input value={newPlayerForm.shirt} onChange={e=>setNewPlayerForm(f=>({...f, shirt:e.target.value}))} style={inS} placeholder="55" />
                    </div>
                    <div>
                      <label style={lblS}>עונה</label>
                      <input value={newPlayerForm.season} onChange={e=>setNewPlayerForm(f=>({...f, season:e.target.value}))} style={inS} placeholder="26/27" />
                    </div>
                  </div>

                  <div style={{ marginBottom:18 }}>
                    <label style={lblS}>גרפיקת הפרופיל (קובץ תמונה) *</label>
                    <input type="file" accept="image/*" onChange={async e=>{
                      const file = e.target.files[0]; if (!file) return;
                      const b64 = await readFileAsBase64(file);
                      setNewPlayerForm(f=>({...f, graphic_base64:b64}));
                    }} style={{ color:'#c0cce6', fontSize:'0.85rem' }} />
                    {newPlayerForm.graphic_base64 && (
                      <img src={newPlayerForm.graphic_base64} alt="תצוגה מקדימה" style={{ maxWidth:280, borderRadius:10, marginTop:10, border:'1px solid rgba(212,175,55,0.35)' }} />
                    )}
                  </div>

                  <button onClick={createPlayerProfile} className="btn btn-gold" style={{ padding:'11px 26px' }}>⭐ פרסם פרופיל שחקן</button>
                </div>
              )}

              {/* ── רשימת פרופילים קיימים ── */}
              {playerProfiles.length === 0 ? (
                <div style={{ textAlign:'center', color:'#4a5a7a', padding:'48px 20px' }}>עדיין לא הכנתם פרופיל שחקן — לחצו למעלה כדי להוסיף את הראשון</div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:13 }}>
                  {playerProfiles.map(p => {
                    const open = expandedPlayerProfile === p.id;
                    const editing = editPlayerId === p.id;
                    const clips = playerClips[p.id] || [];
                    const liveUrl = `https://www.ustarclub.co.il/pro/${p.slug}`;
                    return (
                      <div key={p.id} style={{ background:'#0d1b3e', border:'1px solid rgba(212,175,55,0.3)', borderRadius:14, overflow:'hidden' }}>
                        <div style={{ display:'flex', gap:14, padding:'14px 16px', alignItems:'center', flexWrap:'wrap' }}>
                          {p.has_graphic ? (
                            <img src={API+'/player-profiles/'+p.id+'/graphic?v='+(p.graphic_updated_at ? new Date(p.graphic_updated_at).getTime() : 0)} alt={p.name} style={{ width:64, height:64, objectFit:'cover', borderRadius:10, border:'1px solid rgba(212,175,55,0.4)', flexShrink:0 }} />
                          ) : (
                            <div style={{ width:64, height:64, borderRadius:10, background:'#152040', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:'1.5rem' }}>🏅</div>
                          )}
                          <div style={{ flex:1, minWidth:180 }}>
                            <div style={{ color:'#fff', fontWeight:900, fontSize:'1.02rem' }}>{p.name}</div>
                            <div style={{ color:'#d4af37', fontSize:'0.83rem', fontWeight:700, marginTop:2 }}>{[p.team, p.age_group, p.birth_year].filter(Boolean).join(' · ')}</div>
                            <a href={liveUrl} target="_blank" rel="noopener noreferrer" style={{ color:'#7ab0ff', fontSize:'0.78rem', textDecoration:'none' }} dir="ltr">ustarclub.co.il/pro/{p.slug} ↗</a>
                          </div>
                          <div style={{ color:'#8a9aba', fontSize:'0.82rem', whiteSpace:'nowrap' }}>🎬 {p.clip_count} קליפים</div>
                          <div style={{ display:'flex', gap:7, flexWrap:'wrap' }}>
                            <button onClick={()=>{ navigator.clipboard.writeText(liveUrl); flash('🔗 הקישור הועתק'); }}
                              style={{ background:'rgba(212,175,55,0.1)', color:'#d4af37', border:'1px solid rgba(212,175,55,0.4)', borderRadius:8, padding:'6px 12px', cursor:'pointer', fontSize:'0.78rem', fontWeight:700 }}>🔗 העתק</button>
                            <a href={`https://wa.me/?text=${encodeURIComponent(`⚽ הפרופיל המקצועי של ${p.name}\n${liveUrl}`)}`} target="_blank" rel="noopener noreferrer"
                              style={{ background:'rgba(37,211,102,0.14)', color:'#25d366', border:'1px solid rgba(37,211,102,0.4)', borderRadius:8, padding:'6px 12px', textDecoration:'none', fontSize:'0.78rem', fontWeight:700 }}>📱 שתף</a>
                            <button onClick={()=>toggleExpand(p)}
                              style={{ background:'rgba(122,176,255,0.12)', color:'#7ab0ff', border:'1px solid rgba(122,176,255,0.35)', borderRadius:8, padding:'6px 12px', cursor:'pointer', fontSize:'0.78rem', fontWeight:700 }}>
                              {open ? '▲ סגור' : '⚙️ נהל'}
                            </button>
                            <button onClick={()=>deletePlayerProfile(p)}
                              style={{ background:'rgba(255,80,80,0.08)', color:'#ff8888', border:'1px solid rgba(255,80,80,0.28)', borderRadius:8, padding:'6px 10px', cursor:'pointer', fontSize:'0.78rem' }}>🗑</button>
                          </div>
                        </div>

                        {open && (
                          <div style={{ borderTop:'1px solid rgba(212,175,55,0.2)', padding:'16px 18px', background:'rgba(212,175,55,0.03)' }}>

                            {/* פרטי הפרופיל — עריכה */}
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                              <div style={{ color:'#d4af37', fontWeight:800, fontSize:'0.88rem' }}>⭐ פרטי הפרופיל</div>
                              {!editing ? (
                                <button onClick={()=>{ setEditPlayerId(p.id); setEditPlayerForm({ ...emptyPlayerForm, ...p, graphic_base64:'' }); }}
                                  style={{ background:'none', border:'none', color:'#7ab0ff', cursor:'pointer', fontSize:'0.8rem', fontWeight:700 }}>✏️ ערוך פרטים</button>
                              ) : (
                                <div style={{ display:'flex', gap:8 }}>
                                  <button onClick={()=>saveEditPlayerProfile(p.id)} style={{ background:'rgba(37,211,102,0.14)', color:'#25d366', border:'1px solid rgba(37,211,102,0.4)', borderRadius:7, padding:'4px 12px', cursor:'pointer', fontSize:'0.78rem', fontWeight:700 }}>💾 שמור</button>
                                  <button onClick={()=>setEditPlayerId(null)} style={{ background:'none', border:'1px solid #2a3a6a', color:'#8a9aba', borderRadius:7, padding:'4px 12px', cursor:'pointer', fontSize:'0.78rem' }}>ביטול</button>
                                </div>
                              )}
                            </div>

                            {editing ? (
                              <div style={{ marginBottom:22 }}>
                                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:10, marginBottom:10 }}>
                                  <div><label style={lblS}>שם מלא</label><input value={editPlayerForm.name} onChange={e=>setEditPlayerForm(f=>({...f, name:e.target.value}))} style={inS} /></div>
                                  <div><label style={lblS}>שם פרטי</label><input value={editPlayerForm.first_name||''} onChange={e=>setEditPlayerForm(f=>({...f, first_name:e.target.value}))} style={inS} /></div>
                                  <div>
                                    <label style={lblS}>תפקיד</label>
                                    <select value={editPlayerForm.position||''} onChange={e=>setEditPlayerForm(f=>({...f, position:e.target.value}))} style={inS}>
                                      <option value="">— תפקיד —</option>
                                      {POSITIONS_P.map(x => <option key={x} value={x}>{x}</option>)}
                                    </select>
                                  </div>
                                </div>
                                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:10, marginBottom:10 }}>
                                  <div><label style={lblS}>קבוצה</label><TeamSearchInput value={editPlayerForm.team||''} onChange={v=>setEditPlayerForm(f=>({...f, team:v}))} options={FOOTBALL_CLUBS} /></div>
                                  <div>
                                    <label style={lblS}>שכבת גיל</label>
                                    <select value={editPlayerForm.age_group||''} onChange={e=>setEditPlayerForm(f=>({...f, age_group:e.target.value}))} style={inS}>
                                      <option value="">— שכבה —</option>
                                      {AGE_GROUPS_P.map(x => <option key={x} value={x}>{x}</option>)}
                                    </select>
                                  </div>
                                  <div>
                                    <label style={lblS}>שנתון</label>
                                    <select value={editPlayerForm.birth_year||''} onChange={e=>setEditPlayerForm(f=>({...f, birth_year:e.target.value}))} style={inS}>
                                      <option value="">— שנתון —</option>
                                      {YEARS_P.map(y => <option key={y} value={y}>{y}</option>)}
                                    </select>
                                  </div>
                                </div>
                                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:10, marginBottom:10 }}>
                                  <div><label style={lblS}>מספר חולצה</label><input value={editPlayerForm.shirt||''} onChange={e=>setEditPlayerForm(f=>({...f, shirt:e.target.value}))} style={inS} /></div>
                                  <div><label style={lblS}>עונה</label><input value={editPlayerForm.season||''} onChange={e=>setEditPlayerForm(f=>({...f, season:e.target.value}))} style={inS} /></div>
                                </div>
                                <div style={{ marginBottom:14 }}>
                                  <label style={lblS}>שפת התצוגה באתר</label>
                                  <div style={{ display:'flex', gap:8 }}>
                                    {[{v:'he',l:'עברית'},{v:'en',l:'English'}].map(o => {
                                      const sel = (editPlayerForm.language||'he') === o.v;
                                      return (
                                        <button key={o.v} type="button" onClick={()=>setEditPlayerForm(f=>({...f, language:o.v}))}
                                          style={{ padding:'6px 16px', borderRadius:9, cursor:'pointer', fontWeight:800, fontSize:'0.8rem',
                                            border:`1px solid ${sel?'#d4af37':'#2a3a6a'}`, background: sel?'rgba(212,175,55,0.14)':'#0a1525', color: sel?'#d4af37':'#8a9aba' }}>{o.l}</button>
                                      );
                                    })}
                                  </div>
                                </div>
                                <div>
                                  <label style={lblS}>החלפת גרפיקת הפרופיל (אופציונלי)</label>
                                  <input type="file" accept="image/*" onChange={async e=>{
                                    const file = e.target.files[0]; if (!file) return;
                                    const b64 = await readFileAsBase64(file);
                                    setEditPlayerForm(f=>({...f, graphic_base64:b64}));
                                  }} style={{ color:'#c0cce6', fontSize:'0.85rem' }} />
                                  {editPlayerForm.graphic_base64 && (
                                    <img src={editPlayerForm.graphic_base64} alt="תצוגה מקדימה" style={{ maxWidth:220, borderRadius:10, marginTop:8, border:'1px solid rgba(212,175,55,0.35)' }} />
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div style={{ display:'flex', flexWrap:'wrap', gap:7, marginBottom:22 }}>
                                {p.position && <span style={{ background:'rgba(212,175,55,0.1)', color:'#d4af37', borderRadius:7, padding:'3px 10px', fontSize:'0.78rem' }}>⚽ {p.position}</span>}
                                {p.shirt && <span style={{ background:'rgba(122,176,255,0.1)', color:'#7ab0ff', borderRadius:7, padding:'3px 10px', fontSize:'0.78rem' }}>👕 {p.shirt}</span>}
                                {p.season && <span style={{ background:'rgba(255,255,255,0.06)', color:'#c0cce6', borderRadius:7, padding:'3px 10px', fontSize:'0.78rem' }}>🗓️ עונת {p.season}</span>}
                              </div>
                            )}

                            {/* קליפים */}
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                              <div style={{ color:'#d4af37', fontWeight:800, fontSize:'0.88rem' }}>🎬 קליפי ביצועים ({clips.length})</div>
                              <button onClick={()=>{ setShowNewClipFor(showNewClipFor===p.id?null:p.id); setNewClipForm(emptyClipForm); }}
                                style={{ background:'rgba(212,175,55,0.1)', color:'#d4af37', border:'1px solid rgba(212,175,55,0.4)', borderRadius:8, padding:'5px 12px', cursor:'pointer', fontSize:'0.78rem', fontWeight:700 }}>
                                {showNewClipFor===p.id ? '▲ סגור' : '+ הוסף קליפ'}
                              </button>
                            </div>

                            {showNewClipFor === p.id && (
                              <div style={{ background:'rgba(212,175,55,0.06)', border:'1px solid rgba(212,175,55,0.3)', borderRadius:10, padding:'14px 16px', marginBottom:14 }}>
                                <div style={{ marginBottom:9 }}>
                                  <label style={lblS}>קישור יוטיוב לקליפ *</label>
                                  <input value={newClipForm.youtube_url} onChange={e=>setNewClipForm(f=>({...f, youtube_url:e.target.value}))} style={inS} dir="ltr" placeholder="https://www.youtube.com/watch?v=..." />
                                </div>
                                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:9, marginBottom:12 }}>
                                  <div><label style={lblS}>נגד יריבה</label><input value={newClipForm.opponent} onChange={e=>setNewClipForm(f=>({...f, opponent:e.target.value}))} style={inS} placeholder="מכבי חיפה" /></div>
                                  <div>
                                    <label style={lblS}>סוג משחק</label>
                                    <select value={newClipForm.match_category} onChange={e=>setNewClipForm(f=>({...f, match_category:e.target.value}))} style={inS}>
                                      <option value="">— סוג משחק —</option>
                                      {MATCH_CATEGORIES_P.map(x => <option key={x} value={x}>{x}</option>)}
                                    </select>
                                  </div>
                                  <div>
                                    <label style={lblS}>שכבת גיל</label>
                                    <select value={newClipForm.age_group} onChange={e=>setNewClipForm(f=>({...f, age_group:e.target.value}))} style={inS}>
                                      <option value="">— שכבה —</option>
                                      {AGE_GROUPS_P.map(x => <option key={x} value={x}>{x}</option>)}
                                    </select>
                                  </div>
                                  <div><label style={lblS}>עונה</label><input value={newClipForm.season} onChange={e=>setNewClipForm(f=>({...f, season:e.target.value}))} style={inS} placeholder="26/27" /></div>
                                  <div><label style={lblS}>תאריך משחק (לא חובה)</label><input type="date" value={newClipForm.match_date} onChange={e=>setNewClipForm(f=>({...f, match_date:e.target.value}))} style={inS} /></div>
                                </div>
                                <button onClick={()=>addClip(p.id)} className="btn btn-gold" style={{ padding:'9px 22px' }}>🎬 הוסף לאתר</button>
                              </div>
                            )}

                            {clips.length === 0 ? (
                              <div style={{ color:'#4a5a7a', fontSize:'0.85rem', padding:'10px 0' }}>עדיין אין קליפים לשחקן הזה</div>
                            ) : (
                              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                                {clips.map((c, ci) => {
                                  const isEditingClip = editClipId === c.id;
                                  return (
                                    <div key={c.id} style={{ background:'#0a1225', border:'1px solid #1a2a4a', borderRadius:9, padding:'10px 12px' }}>
                                      {isEditingClip ? (
                                        <div>
                                          <div style={{ marginBottom:8 }}>
                                            <label style={lblS}>קישור יוטיוב</label>
                                            <input value={editClipForm.youtube_url} onChange={e=>setEditClipForm(f=>({...f, youtube_url:e.target.value}))} style={inS} dir="ltr" placeholder="השאר ריק כדי לא לשנות" />
                                          </div>
                                          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))', gap:8, marginBottom:10 }}>
                                            <input value={editClipForm.opponent} onChange={e=>setEditClipForm(f=>({...f, opponent:e.target.value}))} style={inS} placeholder="יריבה" />
                                            <select value={editClipForm.match_category} onChange={e=>setEditClipForm(f=>({...f, match_category:e.target.value}))} style={inS}>
                                              <option value="">— סוג משחק —</option>
                                              {MATCH_CATEGORIES_P.map(x => <option key={x} value={x}>{x}</option>)}
                                            </select>
                                            <select value={editClipForm.age_group} onChange={e=>setEditClipForm(f=>({...f, age_group:e.target.value}))} style={inS}>
                                              <option value="">— שכבה —</option>
                                              {AGE_GROUPS_P.map(x => <option key={x} value={x}>{x}</option>)}
                                            </select>
                                            <input value={editClipForm.season} onChange={e=>setEditClipForm(f=>({...f, season:e.target.value}))} style={inS} placeholder="עונה" />
                                            <input type="date" value={editClipForm.match_date} onChange={e=>setEditClipForm(f=>({...f, match_date:e.target.value}))} style={inS} />
                                          </div>
                                          <div style={{ display:'flex', gap:8 }}>
                                            <button onClick={()=>saveEditClip(p.id, c.id)} style={{ background:'rgba(37,211,102,0.14)', color:'#25d366', border:'1px solid rgba(37,211,102,0.4)', borderRadius:7, padding:'5px 14px', cursor:'pointer', fontSize:'0.78rem', fontWeight:700 }}>💾 שמור</button>
                                            <button onClick={()=>setEditClipId(null)} style={{ background:'none', border:'1px solid #2a3a6a', color:'#8a9aba', borderRadius:7, padding:'5px 14px', cursor:'pointer', fontSize:'0.78rem' }}>ביטול</button>
                                          </div>
                                        </div>
                                      ) : (
                                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                                          <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                                            {clips.length > 1 && (
                                              <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                                                <button onClick={()=>moveClip(p.id, ci, -1)} disabled={ci === 0} title="העלה — יוצג קודם"
                                                  style={{ background:'rgba(122,176,255,0.1)', color: ci===0?'#2e3d58':'#7ab0ff', border:'1px solid rgba(122,176,255,0.25)', borderRadius:5, padding:'0 6px', cursor: ci===0?'default':'pointer', fontSize:'0.62rem', lineHeight:'14px' }}>▲</button>
                                                <button onClick={()=>moveClip(p.id, ci, 1)} disabled={ci === clips.length-1} title="הורד"
                                                  style={{ background:'rgba(122,176,255,0.1)', color: ci===clips.length-1?'#2e3d58':'#7ab0ff', border:'1px solid rgba(122,176,255,0.25)', borderRadius:5, padding:'0 6px', cursor: ci===clips.length-1?'default':'pointer', fontSize:'0.62rem', lineHeight:'14px' }}>▼</button>
                                              </div>
                                            )}
                                            <div>
                                              <div style={{ color:'#fff', fontWeight:700, fontSize:'0.86rem' }}>
                                                {ci === 0 && clips.length > 1 && <span style={{ background:'rgba(212,175,55,0.16)', color:'#d4af37', borderRadius:5, padding:'1px 7px', fontSize:'0.66rem', fontWeight:900, marginLeft:6 }}>ראשון</span>}
                                                נגד {c.opponent || '—'}
                                              </div>
                                              <div style={{ color:'#8a9aba', fontSize:'0.76rem', marginTop:2 }}>{[c.match_category, c.age_group, c.season && `עונת ${c.season}`, c.match_date && fmtDate(c.match_date)].filter(Boolean).join(' · ')}</div>
                                            </div>
                                          </div>
                                          <div style={{ display:'flex', gap:6 }}>
                                            <a href={`https://www.youtube.com/watch?v=${c.youtube_id}`} target="_blank" rel="noopener noreferrer"
                                              style={{ background:'rgba(255,80,80,0.08)', color:'#ff5555', border:'1px solid rgba(255,80,80,0.3)', borderRadius:7, padding:'4px 10px', textDecoration:'none', fontSize:'0.76rem' }}>▶ צפייה</a>
                                            <button onClick={()=>{ setEditClipId(c.id); setEditClipForm({ youtube_url:'', opponent:c.opponent||'', age_group:c.age_group||'', season:c.season||'', match_date: c.match_date ? String(c.match_date).slice(0,10) : '', match_category:c.match_category||'' }); }}
                                              style={{ background:'rgba(122,176,255,0.1)', color:'#7ab0ff', border:'1px solid rgba(122,176,255,0.3)', borderRadius:7, padding:'4px 10px', cursor:'pointer', fontSize:'0.76rem' }}>✏️</button>
                                            <button onClick={()=>deleteClip(p.id, c.id)}
                                              style={{ background:'rgba(255,80,80,0.08)', color:'#ff8888', border:'1px solid rgba(255,80,80,0.28)', borderRadius:7, padding:'4px 10px', cursor:'pointer', fontSize:'0.76rem' }}>🗑</button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* גלריית תמונות */}
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', margin:'20px 0 10px' }}>
                              <div style={{ color:'#d4af37', fontWeight:800, fontSize:'0.88rem' }}>🖼️ גלריית תמונות ({(playerPhotos[p.id]||[]).length})</div>
                              <label style={{ background:'rgba(212,175,55,0.1)', color:'#d4af37', border:'1px solid rgba(212,175,55,0.4)', borderRadius:8, padding:'5px 12px', cursor:'pointer', fontSize:'0.78rem', fontWeight:700 }}>
                                + הוסף תמונות
                                <input type="file" accept="image/*" multiple style={{ display:'none' }} onChange={e=>{
                                  const files = [...e.target.files]; if (!files.length) return;
                                  uploadPhotos(p.id, files); e.target.value = '';
                                }} />
                              </label>
                            </div>
                            {(playerPhotos[p.id]||[]).length === 0 ? (
                              <div style={{ color:'#4a5a7a', fontSize:'0.85rem', padding:'6px 0' }}>עדיין אין תמונות בגלריה</div>
                            ) : (
                              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(80px,1fr))', gap:8 }}>
                                {playerPhotos[p.id].map(ph => (
                                  <div key={ph.id} style={{ position:'relative' }}>
                                    <img src={ph.url} alt="" style={{ width:'100%', aspectRatio:'3/4', objectFit:'cover', borderRadius:8, border:'1px solid rgba(212,175,55,0.3)' }} />
                                    <button onClick={()=>deletePhoto(p.id, ph.id)}
                                      style={{ position:'absolute', top:4, left:4, background:'rgba(10,10,20,0.75)', color:'#ff8888', border:'1px solid rgba(255,80,80,0.4)', borderRadius:6, width:22, height:22, cursor:'pointer', fontSize:'0.7rem', lineHeight:1 }}>🗑</button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* ══════ USTAR PRODUCTS TAB ══════ */}
        {tab === 'ustar-products' && (
          <div>
            {/* Header */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <div>
                <h2 style={{ color:'#d4af37', margin:0 }}>⭐ מוצרי ושירותי יוסטאר</h2>
                <p style={{ color:'#aaa', margin:'4px 0 0', fontSize:'0.9rem' }}>כל מוצר שנמכר ביוסטאר חייב להיות מוגדר כאן</p>
              </div>
              <button onClick={()=>setShowProductForm(!showProductForm)} className="btn btn-gold" style={{ padding:'10px 22px' }}>
                + הוסף מוצר
              </button>
            </div>

            {/* ── אכיפת הכלל: מוצרים שנמכרו ואינם בקטלוג ── */}
            {(() => {
              const catalog = new Set(products.map(p => (p.name||'').trim()).filter(Boolean));
              const sold = {};
              orders.forEach(o => {
                const n = (o.product_name||'').trim();
                if (n && !catalog.has(n)) sold[n] = sold[n] || { count:0, price:0 };
                if (n && !catalog.has(n)) { sold[n].count += 1; sold[n].price = o.unit_price || o.total || sold[n].price; }
              });
              const missing = Object.entries(sold).sort((a,b)=>b[1].count-a[1].count);
              if (missing.length === 0) {
                return (
                  <div style={{ background:'rgba(37,211,102,0.07)', border:'1px solid rgba(37,211,102,0.3)', borderRadius:12, padding:'11px 16px', marginBottom:20, color:'#25d366', fontSize:'0.86rem', fontWeight:700 }}>
                    ✅ כל המוצרים שנמכרו מוגדרים בקטלוג
                  </div>
                );
              }
              return (
                <div style={{ background:'rgba(255,153,102,0.08)', border:'2px solid rgba(255,153,102,0.45)', borderRadius:14, padding:'16px 18px', marginBottom:20 }}>
                  <div style={{ color:'#ff9966', fontWeight:900, fontSize:'0.95rem', marginBottom:4 }}>
                    ⚠️ {missing.length} מוצרים נמכרו אך אינם בקטלוג
                  </div>
                  <div style={{ color:'#8a9aba', fontSize:'0.8rem', marginBottom:12 }}>
                    הכלל: כל מוצר שנמכר ביוסטאר צריך להיות מוגדר כאן. הוסיפו אותם כדי שיופיעו בטפסים ובדוחות.
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
                    {missing.map(([name, info]) => (
                      <div key={name} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap',
                        background:'rgba(0,0,0,0.25)', borderRadius:9, padding:'8px 12px' }}>
                        <span style={{ fontSize:'0.86rem' }}>
                          <strong style={{ color:'#fff' }}>{name}</strong>
                          <span style={{ color:'#8a9aba' }}> · נמכר {info.count} פעמים</span>
                          {info.price > 0 && <span style={{ color:'#d4af37' }}> · ₪{info.price}</span>}
                        </span>
                        <button onClick={()=>{
                          setNewProduct({ name, description:'', category:'קליפ', price:String(info.price||''),
                            member_discount_pct:'0', product_type:'sale', media_type:'video', member_discount_enabled:false });
                          setShowProductForm(true);
                          window.scrollTo({ top:0, behavior:'smooth' });
                        }} style={{ background:'rgba(212,175,55,0.16)', color:'#d4af37', border:'1px solid rgba(212,175,55,0.45)', borderRadius:8, padding:'5px 14px', cursor:'pointer', fontWeight:800, fontSize:'0.8rem', whiteSpace:'nowrap' }}>
                          ➕ הוסף לקטלוג
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Add product form */}
            {showProductForm && (
              <div className="dark-card" style={{ marginBottom:24, border:`2px solid ${newProduct.product_type==='benefit'?'#25d366':'#d4af37'}` }}>
                <h3 style={{ color: newProduct.product_type==='benefit'?'#25d366':'#d4af37', marginTop:0 }}>
                  {newProduct.product_type==='benefit' ? '🎁 מוצר הטבה חדש' : '⭐ מוצר / שירות חדש של יוסטאר'}
                </h3>

                {/* סוג המוצר */}
                <div style={{ display:'flex', gap:0, marginBottom:18, borderRadius:10, overflow:'hidden', border:'1px solid #2a3a6a', width:'fit-content' }}>
                  {[
                    { v:'sale',    label:'💰 מוצר למכירה',  desc:'לקוחות משלמים' },
                    { v:'benefit', label:'🎁 מוצר הטבה',    desc:'ניתן לחברים חינם' },
                  ].map((t,i) => (
                    <button key={t.v} onClick={()=>setNewProduct({...newProduct, product_type:t.v})} style={{
                      padding:'10px 22px', border:'none', borderRight:i===0?'1px solid #2a3a6a':'none',
                      background: newProduct.product_type===t.v ? (t.v==='benefit'?'#0a3a1a':'#1a2a3e') : 'transparent',
                      cursor:'pointer'
                    }}>
                      <div style={{ color: newProduct.product_type===t.v?(t.v==='benefit'?'#25d366':'#d4af37'):'#4a5a7a', fontWeight:700, fontSize:'0.88rem' }}>{t.label}</div>
                      <div style={{ color:'#3a4a6a', fontSize:'0.7rem' }}>{t.desc}</div>
                    </button>
                  ))}
                </div>

                <form onSubmit={addProduct}>
                  <div className="form-row">
                    <div className="form-group"><label>שם {newProduct.product_type==='benefit'?'ההטבה':'המוצר'} *</label>
                      <input value={newProduct.name} onChange={e=>setNewProduct({...newProduct,name:e.target.value})} required placeholder={newProduct.product_type==='benefit'?'קלף הכוכבים של יוסטאר':'קליפ יוסטאר'} />
                    </div>
                    <div className="form-group">
                      <label>קטגוריה</label>
                      <select value={newProduct.category} onChange={e=>setNewProduct({...newProduct,category:e.target.value})} style={{ width:'100%', padding:'8px 12px', background:'#1a2a4a', color:'#fff', border:'1px solid #2a3a6a', borderRadius:8 }}>
                        {['קליפ','קלף','הטבה','אירוע','שירות','פרימיום','אחר'].map(c=><option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-group"><label>תיאור</label><input value={newProduct.description} onChange={e=>setNewProduct({...newProduct,description:e.target.value})} placeholder={newProduct.product_type==='benefit'?'הטבת הצטרפות למועדון...':'קליפ כדורגל מקצועי...'} /></div>
                  {newProduct.product_type === 'sale' && (
                    <>
                      {/* סוג מוצר — וידאו / תמונות */}
                      <div className="form-group">
                        <label>קטגוריה ראשית *</label>
                        <div style={{ color:'#4a5a7a', fontSize:'0.76rem', marginTop:2 }}>אפשר להוסיף כמה מוצרים שרוצים בכל קטגוריה</div>
                        <div style={{ display:'flex', gap:10, marginTop:6 }}>
                          {[
                            { v:'video',  label:'🎥 וידאו',   hint:'קליפ ביצועים · קליפ בר מצווה · חבילות · צילום' },
                            { v:'photos', label:'📷 תמונות',  hint:'תמונות שחקן · קלף יוסטאר · ועוד' },
                          ].map(t => {
                            const sel = (newProduct.media_type||'video') === t.v;
                            return (
                              <button key={t.v} type="button" onClick={()=>setNewProduct({...newProduct, media_type:t.v})}
                                style={{ flex:1, textAlign:'right', padding:'10px 14px', borderRadius:10, cursor:'pointer',
                                  border:`2px solid ${sel ? (t.v==='photos'?'#f0cc60':'#7ab0ff') : '#2a3a6a'}`,
                                  background: sel ? (t.v==='photos'?'rgba(240,204,96,0.1)':'rgba(122,176,255,0.1)') : 'transparent' }}>
                                <div style={{ color: sel ? (t.v==='photos'?'#f0cc60':'#7ab0ff') : '#8a9aba', fontWeight:800, fontSize:'0.9rem' }}>{t.label}</div>
                                <div style={{ color:'#4a5a7a', fontSize:'0.72rem', marginTop:2 }}>{t.hint}</div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>מחיר המוצר (₪) *</label>
                        <input type="number" step="1" value={newProduct.price} onChange={e=>setNewProduct({...newProduct,price:e.target.value})} required placeholder="490" />
                        <div style={{ color:'#4a5a7a', fontSize:'0.76rem', marginTop:4 }}>זהו המחיר הקבוע של החבילה</div>
                      </div>
                      {/* הנחת חברי מועדון — כבויה כברירת מחדל */}
                      <div className="form-group">
                        <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontWeight:400 }}>
                          <input type="checkbox" checked={!!newProduct.member_discount_enabled}
                            onChange={e=>setNewProduct({...newProduct, member_discount_enabled:e.target.checked, member_discount_pct: e.target.checked ? (newProduct.member_discount_pct||'10') : '0'})}
                            style={{ accentColor:'#25d366', width:16, height:16 }} />
                          <span style={{ color:'#c0cce6', fontSize:'0.88rem' }}>הפעל הנחה לחברי מועדון <span style={{ color:'#4a5a7a', fontSize:'0.8rem' }}>(לא פעיל כרגע — לשימוש עתידי)</span></span>
                        </label>
                        {newProduct.member_discount_enabled && (
                          <input type="number" step="1" min="0" max="100" value={newProduct.member_discount_pct}
                            onChange={e=>setNewProduct({...newProduct,member_discount_pct:e.target.value})}
                            placeholder="אחוז הנחה" style={{ marginTop:8 }} />
                        )}
                      </div>
                    </>
                  )}
                  <div style={{ display:'flex', gap:8 }}>
                    <button className="btn btn-gold" type="submit">שמור {newProduct.product_type==='benefit'?'הטבה':'מוצר'}</button>
                    <button type="button" onClick={()=>setShowProductForm(false)} className="btn btn-outline">ביטול</button>
                  </div>
                </form>
              </div>
            )}

            {/* ── סטטיסטיקת מכירות ── */}
            {(() => {
              // נמכר = הזמנה ששולמה בפועל (עקבי עם לוח הבקרה)
              const soldCount = {};
              orders.filter(o => o.status === 'completed').forEach(o => {
                const n = (o.product_name||'').trim();
                if (n) soldCount[n] = (soldCount[n]||0) + (o.quantity || 1);
              });
              const totalSold = Object.values(soldCount).reduce((a,b)=>a+b, 0);
              const catalogSold = products
                .map(p => ({ name:(p.name||'').trim(), qty: soldCount[(p.name||'').trim()] || 0, price: p.price||0 }))
                .filter(x => x.qty > 0)
                .sort((a,b) => b.qty - a.qty);
              const top = catalogSold[0];
              const premium = products.filter(p => (p.price||0) >= 1000 && p.product_type !== 'benefit');
              if (totalSold === 0 && premium.length === 0) return null;
              return (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:12, marginBottom:20 }}>
                  <div style={{ background:'#0d1b3e', border:'1px solid rgba(212,175,55,0.3)', borderRadius:12, padding:'13px 16px' }}>
                    <div style={{ color:'#8a9aba', fontSize:'0.74rem', marginBottom:5 }}>📦 סה״כ מוצרים שנמכרו</div>
                    <div style={{ color:'#d4af37', fontWeight:900, fontSize:'1.6rem', lineHeight:1 }}>{totalSold}</div>
                  </div>
                  {top && (
                    <div style={{ background:'linear-gradient(135deg,rgba(37,211,102,0.1),rgba(37,211,102,0.03))', border:'2px solid rgba(37,211,102,0.45)', borderRadius:12, padding:'13px 16px' }}>
                      <div style={{ color:'#25d366', fontSize:'0.74rem', marginBottom:5, fontWeight:700 }}>🔥 המוצר הפופולרי</div>
                      <div style={{ color:'#fff', fontWeight:800, fontSize:'0.92rem' }}>{top.name}</div>
                      <div style={{ color:'#25d366', fontWeight:900, fontSize:'1.05rem', marginTop:3 }}>{top.qty} מכירות</div>
                    </div>
                  )}
                  {premium.length > 0 && (
                    <div style={{ background:'linear-gradient(135deg,rgba(180,120,255,0.1),rgba(180,120,255,0.03))', border:'2px solid rgba(180,120,255,0.45)', borderRadius:12, padding:'13px 16px' }}>
                      <div style={{ color:'#b478ff', fontSize:'0.74rem', marginBottom:5, fontWeight:700 }}>💎 מוצרי פרמיום (₪1,000+)</div>
                      <div style={{ color:'#b478ff', fontWeight:900, fontSize:'1.6rem', lineHeight:1 }}>{premium.length}</div>
                      <div style={{ color:'#8a9aba', fontSize:'0.73rem', marginTop:3 }}>{premium.map(p=>p.name).join(' · ')}</div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── מוצרים בשורות, מקובצים לפי קטגוריה ראשית ── */}
            {(() => {
              // נמכר = הזמנה ששולמה בפועל (עקבי עם לוח הבקרה)
              const soldCount = {};
              orders.filter(o => o.status === 'completed').forEach(o => {
                const n = (o.product_name||'').trim();
                if (n) soldCount[n] = (soldCount[n]||0) + (o.quantity || 1);
              });
              const bestSeller = products
                .map(p => ({ id:p.id, qty: soldCount[(p.name||'').trim()] || 0 }))
                .sort((a,b)=>b.qty-a.qty)[0];
              const topId = bestSeller && bestSeller.qty > 0 ? bestSeller.id : null;

              return [
                { id:'video',   label:'🎥 וידאו',   color:'#7ab0ff' },
                { id:'photos',  label:'📷 תמונות',  color:'#f0cc60' },
                { id:'benefit', label:'🎁 הטבות',   color:'#25d366' },
              ].map(cat => {
                const catProducts = products.filter(p =>
                  cat.id === 'benefit'
                    ? p.product_type === 'benefit'
                    : p.product_type !== 'benefit' && (p.media_type || 'video') === cat.id
                );
                if (catProducts.length === 0) return null;
                return (
                  <div key={cat.id} style={{ marginBottom:22 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:9 }}>
                      <span style={{ color:cat.color, fontWeight:900, fontSize:'0.95rem' }}>{cat.label}</span>
                      <span style={{ background:`${cat.color}22`, color:cat.color, borderRadius:20, padding:'1px 9px', fontSize:'0.74rem', fontWeight:800 }}>{catProducts.length}</span>
                    </div>

                    <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
                      {catProducts.map(p => {
                        const isEditing = editingProduct === p.id;
                        const ed = isEditing ? editProductData : p;
                        const discOn = !!ed.member_discount_enabled && (ed.member_discount_pct||0) > 0;
                        const memberPrice = ed.price*(1-(discOn ? (ed.member_discount_pct||0) : 0)/100);
                        const qty = soldCount[(p.name||'').trim()] || 0;
                        const isTop = p.id === topId;
                        const isPremium = (p.price||0) >= 1000 && p.product_type !== 'benefit';
                        return (
                          <div key={p.id} style={{ background:'#0d1b3e',
                            border:`1px solid ${isEditing?'#ff9966':isTop?'rgba(37,211,102,0.5)':isPremium?'rgba(180,120,255,0.45)':'#1e3060'}`,
                            borderRadius:10, padding:'10px 14px' }}>

                            {/* שורה */}
                            <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', flex:'1 1 300px', minWidth:0 }}>
                                <span style={{ color:'#fff', fontWeight:800, fontSize:'0.92rem' }}>{p.name}</span>
                                {isTop && <span style={{ background:'rgba(37,211,102,0.18)', color:'#25d366', borderRadius:6, padding:'1px 8px', fontSize:'0.7rem', fontWeight:800, whiteSpace:'nowrap' }}>🔥 פופולרי</span>}
                                {isPremium && <span style={{ background:'rgba(180,120,255,0.18)', color:'#b478ff', borderRadius:6, padding:'1px 8px', fontSize:'0.7rem', fontWeight:800, whiteSpace:'nowrap' }}>💎 פרמיום</span>}
                                {p.category && <span style={{ color:'#4a5a7a', fontSize:'0.75rem' }}>{p.category}</span>}
                                {p.description && <span style={{ color:'#4a5a7a', fontSize:'0.75rem', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:200 }}>{p.description}</span>}
                              </div>

                              {/* נמכרו */}
                              <span title="כמות שנמכרה" style={{ color: qty>0?'#25d366':'#4a5a7a', fontWeight:800, fontSize:'0.83rem', whiteSpace:'nowrap' }}>
                                📦 {qty}
                              </span>

                              {/* מחיר */}
                              <span style={{ whiteSpace:'nowrap' }}>
                                {discOn ? (
                                  <>
                                    <span style={{ color:'#4a5a7a', textDecoration:'line-through', fontSize:'0.78rem', marginLeft:6 }}>{fmtPrice(p.price)}</span>
                                    <span style={{ color:'#25d366', fontWeight:900, fontSize:'1rem' }}>{fmtPrice(memberPrice)}</span>
                                  </>
                                ) : (
                                  <span style={{ color: isPremium?'#b478ff':'#d4af37', fontWeight:900, fontSize:'1.05rem' }}>{fmtPrice(p.price)}</span>
                                )}
                              </span>

                              {/* פעולות */}
                              <span style={{ display:'flex', gap:5 }}>
                                <button onClick={()=>{ setEditingProduct(isEditing?null:p.id); setEditProductData({ price:p.price, member_discount_pct:p.member_discount_pct, media_type:p.media_type||'video', member_discount_enabled:!!p.member_discount_enabled }); }}
                                  title="ערוך" style={{ background:'rgba(255,150,50,0.12)', color:'#ffaa44', border:'1px solid rgba(255,150,50,0.4)', borderRadius:7, padding:'5px 9px', cursor:'pointer', fontSize:'0.82rem', lineHeight:1 }}>✏️</button>
                                <button onClick={async()=>{
                                  if(!window.confirm(`למחוק את "${p.name}" מהקטלוג?`)) return;
                                  await axios.delete(API+'/products/'+p.id);
                                  setProducts(products.filter(x=>x.id!==p.id));
                                  flash('המוצר הוסר');
                                }} title="מחק" style={{ background:'rgba(255,80,80,0.08)', color:'#ff8888', border:'1px solid rgba(255,80,80,0.3)', borderRadius:7, padding:'5px 9px', cursor:'pointer', fontSize:'0.82rem', lineHeight:1 }}>🗑</button>
                              </span>
                            </div>

                            {/* עריכה */}
                            {isEditing && (
                              <div style={{ marginTop:10, borderTop:'1px solid #1e3060', paddingTop:10 }}>
                                <div style={{ display:'flex', gap:8, marginBottom:9, flexWrap:'wrap' }}>
                                  {[{v:'video',label:'🎥 וידאו'},{v:'photos',label:'📷 תמונות'}].map(t => {
                                    const sel = (editProductData.media_type ?? p.media_type ?? 'video') === t.v;
                                    return (
                                      <button key={t.v} type="button" onClick={()=>setEditProductData({...editProductData, media_type:t.v})}
                                        style={{ padding:'6px 13px', borderRadius:8, cursor:'pointer', fontWeight:800, fontSize:'0.8rem',
                                          border:`1px solid ${sel?(t.v==='photos'?'#f0cc60':'#7ab0ff'):'#2a3a6a'}`,
                                          background: sel?(t.v==='photos'?'rgba(240,204,96,0.12)':'rgba(122,176,255,0.12)'):'transparent',
                                          color: sel?(t.v==='photos'?'#f0cc60':'#7ab0ff'):'#8a9aba' }}>{t.label}</button>
                                    );
                                  })}
                                  <div>
                                    <input type="number" value={editProductData.price} onChange={e=>setEditProductData({...editProductData,price:parseFloat(e.target.value)||0})}
                                      placeholder="מחיר ₪"
                                      style={{ width:110, background:'#0a1a3e', color:'#fff', border:'1px solid #2a3a6a', borderRadius:8, padding:'6px 10px', textAlign:'center', fontWeight:700 }} />
                                  </div>
                                </div>
                                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', marginBottom: editProductData.member_discount_enabled ? 8 : 10 }}>
                                  <input type="checkbox" checked={!!editProductData.member_discount_enabled}
                                    onChange={e=>setEditProductData({...editProductData, member_discount_enabled:e.target.checked, member_discount_pct: e.target.checked ? (editProductData.member_discount_pct||10) : 0})}
                                    style={{ accentColor:'#25d366', width:15, height:15 }} />
                                  <span style={{ color:'#c0cce6', fontSize:'0.82rem' }}>הנחה לחברי מועדון</span>
                                  {editProductData.member_discount_enabled && (
                                    <>
                                      <input type="number" min="0" max="100" value={editProductData.member_discount_pct}
                                        onChange={e=>setEditProductData({...editProductData,member_discount_pct:parseFloat(e.target.value)||0})}
                                        style={{ width:64, background:'#0a1a3e', color:'#25d366', border:'1px solid #25d366', borderRadius:7, padding:'4px 7px', textAlign:'center', fontWeight:700 }} />
                                      <span style={{ color:'#25d366', fontSize:'0.82rem' }}>% → {fmtPrice(memberPrice)}</span>
                                    </>
                                  )}
                                </label>
                                <div style={{ display:'flex', gap:8 }}>
                                  <button onClick={async()=>{
                                    const { data } = await axios.put(API+'/products/'+p.id, { ...p, ...editProductData });
                                    setProducts(products.map(x=>x.id===p.id?data:x));
                                    setEditingProduct(null); setEditProductData(null);
                                    flash('✅ המוצר עודכן');
                                  }} style={{ background:'#d4af37', color:'#000', border:'none', borderRadius:8, padding:'7px 20px', cursor:'pointer', fontWeight:800, fontSize:'0.82rem' }}>שמור</button>
                                  <button onClick={()=>{setEditingProduct(null);setEditProductData(null);}}
                                    style={{ background:'transparent', color:'#8a9aba', border:'1px solid #2a3a6a', borderRadius:8, padding:'7px 16px', cursor:'pointer', fontSize:'0.82rem' }}>ביטול</button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()}

            {products.length===0 && (
              <div style={{ textAlign:'center', padding:48, color:'#445' }}>
                <div style={{ fontSize:'3rem', marginBottom:12 }}>⭐</div>
                <div>אין מוצרים עדיין — לחץ "הוסף מוצר"</div>
              </div>
            )}

            {/* YouTube / links section */}
            <div className="dark-card" style={{ marginTop:24 }}>
              <h3 style={{ color:'#d4af37', marginTop:0 }}>🎬 ערוצי שיווק יוסטאר</h3>
              <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                <a href="https://www.youtube.com/@ustarsoccer2972" target="_blank" rel="noopener noreferrer"
                  style={{ background:'#ff0000', color:'#fff', padding:'10px 20px', borderRadius:10, textDecoration:'none', fontWeight:600, display:'flex', alignItems:'center', gap:8 }}>
                  ▶ ערוץ יוטיוב
                </a>
                <a href="https://www.youtube.com/watch?v=akg97C2Ru8M" target="_blank" rel="noopener noreferrer"
                  style={{ background:'#1a3a8e', color:'#fff', padding:'10px 20px', borderRadius:10, textDecoration:'none', fontWeight:600, display:'flex', alignItems:'center', gap:8 }}>
                  🎬 סרט תדמית
                </a>
                <a href="https://wa.me/9720546318704" target="_blank" rel="noopener noreferrer"
                  style={{ background:'#25d366', color:'#fff', padding:'10px 20px', borderRadius:10, textDecoration:'none', fontWeight:600, display:'flex', alignItems:'center', gap:8 }}>
                  📱 וואטסאפ
                </a>
              </div>
            </div>
          </div>
        )}
        {/* ══════ TREASURY TAB ══════ */}
        {tab === 'treasury' && (() => {
          const byMonth = {};
          incomeLog.forEach(e => {
            const d = new Date(e.created_at);
            const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
            if (!byMonth[key]) byMonth[key] = { total: 0, entries: [] };
            byMonth[key].total += e.amount || 0;
            byMonth[key].entries.push(e);
          });
          const months = Object.keys(byMonth).sort().reverse();
          const hebrewMonths = ['ינואר','פברואר','מרץ','אפריל','מאי','יוני','יולי','אוגוסט','ספטמבר','אוקטובר','נובמבר','דצמבר'];

          // ── מיקוד: חודש נוכחי + שנה נוכחית, כולל חלק המס ──
          const nowT = new Date();
          const curMonthT = `${nowT.getFullYear()}-${String(nowT.getMonth()+1).padStart(2,'0')}`;
          const curYearT  = String(nowT.getFullYear());
          const taxPctT   = (parseFloat(babelVat)||0) + (parseFloat(babelIncomeTax)||0) + (parseFloat(babelNatIns)||0);
          const monthTotal = incomeLog.filter(e => (e.created_at||'').slice(0,7) === curMonthT).reduce((s,e)=>s+(e.amount||0),0);
          const monthCount = incomeLog.filter(e => (e.created_at||'').slice(0,7) === curMonthT).length;
          const yearTotal  = incomeLog.filter(e => (e.created_at||'').slice(0,4) === curYearT).reduce((s,e)=>s+(e.amount||0),0);
          const yearCount  = incomeLog.filter(e => (e.created_at||'').slice(0,4) === curYearT).length;
          const monthTax   = monthTotal * taxPctT / 100;
          const yearTax    = yearTotal  * taxPctT / 100;
          const ils = n => `₪${Math.round(n).toLocaleString('he-IL')}`;
          const mLabelT = k => { const [y,m] = (k||'').split('-'); return m ? `${hebrewMonths[parseInt(m)-1]} ${y}` : '—'; };

          const Panel = ({ title, sub, total, count, tax, accent }) => (
            <div style={{ background:'#0d1b3e', border:`2px solid ${accent}55`, borderRadius:16, padding:'18px 20px', flex:'1 1 260px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10, marginBottom:10 }}>
                <div>
                  <div style={{ color:accent, fontWeight:900, fontSize:'0.92rem' }}>{title}</div>
                  <div style={{ color:'#4a5a7a', fontSize:'0.74rem', marginTop:2 }}>{sub} · {count} עסקאות</div>
                </div>
                <div style={{ color:accent, fontWeight:900, fontSize:'1.9rem', lineHeight:1 }}>{ils(total)}</div>
              </div>
              {/* חלק המס בתוך ההכנסה */}
              <div style={{ background:'rgba(255,120,120,0.06)', border:'1px solid rgba(255,120,120,0.25)', borderRadius:10, padding:'9px 12px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6, flexWrap:'wrap', gap:6 }}>
                  <span style={{ color:'#ff8888', fontSize:'0.79rem', fontWeight:700 }}>🏛 מזה מיסים ({taxPctT.toFixed(2).replace(/\.?0+$/,'')}%)</span>
                  <span style={{ color:'#ff8888', fontWeight:900, fontSize:'1rem' }}>{ils(tax)}</span>
                </div>
                {/* בר יחסי: מס מול נטו */}
                <div style={{ display:'flex', height:8, borderRadius:5, overflow:'hidden', background:'rgba(255,255,255,0.05)' }}>
                  <div style={{ width:`${Math.min(taxPctT,100)}%`, background:'#ff8888' }} />
                  <div style={{ flex:1, background:'#25d366' }} />
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginTop:5, fontSize:'0.73rem' }}>
                  <span style={{ color:'#ff8888' }}>מס {Math.round(taxPctT)}%</span>
                  <span style={{ color:'#25d366' }}>נטו {ils(total - tax)}</span>
                </div>
              </div>
            </div>
          );

          return (
            <div style={{ padding:'28px 24px', maxWidth:820, marginRight:0, marginLeft:'auto' }}>
              <h2 style={{ color:'#d4af37', fontWeight:900, marginBottom:6 }}>💰 קופת יוסטאר</h2>
              <p style={{ color:'#8a9aba', fontSize:'0.9rem', marginBottom:18 }}>סך ההכנסות — חודשי ושנתי, כולל חלק המס</p>

              {/* מיקוד: חודשי + שנתי */}
              <div style={{ display:'flex', gap:14, flexWrap:'wrap', marginBottom:14 }}>
                <Panel title={`📅 ${mLabelT(curMonthT)}`} sub="החודש הנוכחי" total={monthTotal} count={monthCount} tax={monthTax} accent="#25d366" />
                <Panel title={`📆 שנת ${curYearT}`} sub="1.1 – 31.12" total={yearTotal} count={yearCount} tax={yearTax} accent="#d4af37" />
              </div>

              <div style={{ color:'#4a5a7a', fontSize:'0.75rem', marginBottom:22 }}>
                * חלק המס מחושב לפי האחוזים שהוגדרו בשיטת בבל (מע״מ {babelVat}% · מס הכנסה {babelIncomeTax}% · ביטוח לאומי {babelNatIns}%)
              </div>

              {incomeLog.length === 0 ? (
                <div style={{ textAlign:'center', color:'#4a5a7a', padding:'60px 0', fontSize:'1.1rem' }}>
                  עדיין אין הכנסות מתועדות.<br/>
                  <span style={{ fontSize:'0.85rem' }}>כאשר תסמן עבודה כהושלמה — ההכנסה תיכנס לכאן אוטומטית.</span>
                </div>
              ) : months.map(month => {
                const [year, mon] = month.split('-');
                const label = `${hebrewMonths[parseInt(mon)-1]} ${year}`;
                const { total, entries } = byMonth[month];
                return (
                  <div key={month} style={{ marginBottom:24 }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10, flexWrap:'wrap', gap:8 }}>
                      <span style={{ color: month===curMonthT?'#25d366':'#d4af37', fontWeight:800, fontSize:'1rem' }}>
                        📅 {label}{month===curMonthT ? ' • החודש' : ''}
                      </span>
                      <span style={{ display:'flex', gap:12, alignItems:'baseline', flexWrap:'wrap' }}>
                        <span style={{ color:'#ff8888', fontSize:'0.78rem' }}>🏛 מס {ils(total * taxPctT / 100)}</span>
                        <span style={{ color:'#25d366', fontSize:'0.78rem' }}>נטו {ils(total - total * taxPctT / 100)}</span>
                        <span style={{ color:'#d4af37', fontWeight:900 }}>{ils(total)}</span>
                      </span>
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                      {entries.map(e => {
                        const d = new Date(e.created_at);
                        const dayStr = `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
                        return (
                          <div key={e.id} style={{ background:'#0d1b3e', border:'1px solid #1e3060', borderRadius:10, padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
                            <div style={{ color:'#c0cce6', fontSize:'0.88rem' }}>
                              <span style={{ color:'#8a9aba', marginLeft:12, fontSize:'0.8rem' }}>{dayStr}</span>
                              <strong style={{ color:'#fff' }}>{e.customer_name || '—'}</strong>
                              {e.description && <span style={{ color:'#4a6aaa', marginRight:8 }}> · {e.description}</span>}
                            </div>
                            <span style={{ color:'#25d366', fontWeight:900, fontSize:'1.05rem' }}>₪{(e.amount||0).toLocaleString()}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* ══════ BABEL TAB ══════ */}
        {tab === 'babel' && (() => {
          const hebrewMonths = ['ינואר','פברואר','מרץ','אפריל','מאי','יוני','יולי','אוגוסט','ספטמבר','אוקטובר','נובמבר','דצמבר'];
          const nowB = new Date();
          const curMonthB = `${nowB.getFullYear()}-${String(nowB.getMonth()+1).padStart(2,'0')}`;
          const mLabel = k => { const [yy,mm] = (k||'').split('-'); return mm ? `${hebrewMonths[parseInt(mm)-1]} ${yy}` : '—'; };
          const monthLabel = mLabel(babelMonth);
          const selYear = (babelMonth||'').slice(0,4);

          // כל החודשים/שנים שיש בהם הכנסה — ארכיון רב-שנתי
          const babelMonths = [...new Set(incomeLog.map(e => (e.created_at||'').slice(0,7)).filter(Boolean))].sort().reverse();
          if (!babelMonths.includes(curMonthB)) babelMonths.unshift(curMonthB);
          const babelYears = [...new Set(babelMonths.map(k => k.slice(0,4)))].sort().reverse();

          const monthlyEntries = incomeLog.filter(e => (e.created_at||'').slice(0,7) === babelMonth);
          const grossIncome = monthlyEntries.reduce((s, e) => s + (e.amount || 0), 0);

          const vatPct      = parseFloat(babelVat)       || 0;
          const incomePct   = parseFloat(babelIncomeTax) || 0;
          const natInsPct   = parseFloat(babelNatIns)    || 0;

          // חלוקה בבלית לכל סכום — משמש לחודש, לשנה ולארכיון
          const splitOf = gross => {
            const vat = gross * vatPct / 100;
            const inc = gross * incomePct / 100;
            const ni  = gross * natInsPct / 100;
            const tax = vat + inc + ni;
            const net = gross - tax;
            const investment = net * 0.10;
            const loan       = net * 0.20;
            const live       = net * 0.70;
            return { gross, vat, inc, ni, tax, net, investment, loan, live,
              business: tax + loan,      // עסקי: מיסים + החזרי הלוואות
              private:  investment + live }; // פרטי: השקעות + מחיה
          };

          const S = splitOf(grossIncome);
          const vatAmt = S.vat, incomeAmt = S.inc, natInsAmt = S.ni;
          const totalTax = S.tax, afterTax = S.net;
          const invest = S.investment, loans = S.loan, living = S.live;

          // סיכום שנתי + ארכיון חודשי
          const yearGross = incomeLog.filter(e => (e.created_at||'').slice(0,4) === selYear).reduce((s,e)=>s+(e.amount||0), 0);
          const Y = splitOf(yearGross);
          const monthArchive = babelMonths.map(k => ({
            key: k,
            gross: incomeLog.filter(e => (e.created_at||'').slice(0,7) === k).reduce((s,e)=>s+(e.amount||0), 0),
          }));

          const fmt = n => `₪${Math.round(n).toLocaleString('he-IL')}`;
          const pct = (a, b) => b > 0 ? Math.round(a / b * 100) : 0;

          const saveTaxRates = async () => {
            setBabelSaving(true);
            await Promise.all([
              axios.put(API+'/settings/babel_vat',        { value: babelVat }),
              axios.put(API+'/settings/babel_income_tax', { value: babelIncomeTax }),
              axios.put(API+'/settings/babel_nat_ins',    { value: babelNatIns }),
            ]);
            setBabelSaving(false);
            flash('✅ אחוזי המיסים עודכנו');
          };

          const Block = ({ color, icon, title, amount, sub, pctLabel, barPct }) => (
            <div style={{ background:`rgba(${color},0.08)`, border:`2px solid rgba(${color},0.35)`, borderRadius:16, padding:'20px 22px', flex:1, minWidth:200 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                <div>
                  <div style={{ fontSize:'1.5rem', marginBottom:4 }}>{icon}</div>
                  <div style={{ color:`rgb(${color})`, fontWeight:900, fontSize:'0.88rem' }}>{title}</div>
                  {sub && <div style={{ color:'#8a9aba', fontSize:'0.75rem', marginTop:2 }}>{sub}</div>}
                </div>
                <div style={{ textAlign:'left' }}>
                  <div style={{ color:`rgb(${color})`, fontWeight:900, fontSize:'1.6rem', lineHeight:1 }}>{fmt(amount)}</div>
                  <div style={{ color:'#4a5a7a', fontSize:'0.8rem', marginTop:4 }}>{pctLabel}</div>
                </div>
              </div>
              {barPct != null && (
                <div style={{ background:'rgba(255,255,255,0.07)', borderRadius:6, height:6, overflow:'hidden' }}>
                  <div style={{ width:`${Math.min(barPct,100)}%`, height:'100%', background:`rgb(${color})`, borderRadius:6, transition:'width 0.6s ease' }} />
                </div>
              )}
            </div>
          );

          const Money = ({ v, c, size }) => <span style={{ color:c, fontWeight:900, fontSize:size||'1rem' }}>{fmt(v)}</span>;

          return (
            <div style={{ padding:'28px 24px', maxWidth:820, marginRight:0, marginLeft:'auto' }}>

              {/* כותרת + חודש */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12, marginBottom:18 }}>
                <div>
                  <h2 style={{ color:'#d4af37', fontWeight:900, fontSize:'1.5rem', margin:0 }}>🏛 שיטת בבל</h2>
                  <p style={{ color:'#8a9aba', margin:'5px 0 0', fontSize:'0.86rem' }}>חלוקת ההכנסות · {monthLabel}</p>
                </div>
                <select value={babelMonth} onChange={e=>setBabelMonth(e.target.value)}
                  style={{ background:'#0a1a3e', border:'1px solid #2a3a6a', color:'#d4af37', borderRadius:10, padding:'8px 12px', fontSize:'0.86rem', fontWeight:700, cursor:'pointer' }}>
                  {babelMonths.map(k => <option key={k} value={k}>{mLabel(k)}{k===curMonthB?' • החודש':''}</option>)}
                </select>
              </div>

              {/* ── ברוטו החודש ── */}
              <div style={{ background:'#0d1b3e', border:'1px solid rgba(212,175,55,0.35)', borderRadius:14, padding:'16px 20px', marginBottom:12,
                display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
                <span style={{ color:'#8a9aba', fontSize:'0.86rem' }}>הכנסה ברוטו · {monthlyEntries.length} עסקאות</span>
                <Money v={grossIncome} c="#d4af37" size="2rem" />
              </div>

              {/* ── שתי הפעולות: להעביר לפרטי / נשאר בעסקי ── */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:12, marginBottom:12 }}>

                <div style={{ background:'rgba(37,211,102,0.08)', border:'2px solid rgba(37,211,102,0.5)', borderRadius:14, padding:'18px 20px' }}>
                  <div style={{ color:'#25d366', fontWeight:900, fontSize:'0.92rem', marginBottom:2 }}>🏠 להעביר לחשבון הפרטי</div>
                  <div style={{ color:'#4a5a7a', fontSize:'0.74rem', marginBottom:10 }}>הפעולה היחידה לביצוע</div>
                  <Money v={S.private} c="#25d366" size="2.1rem" />
                  <div style={{ borderTop:'1px solid rgba(37,211,102,0.22)', marginTop:12, paddingTop:9, display:'flex', flexDirection:'column', gap:4 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.8rem' }}>
                      <span style={{ color:'#8a9aba' }}>🏠 מחיה 70%</span><Money v={living} c="#25d366" size="0.86rem" />
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.8rem' }}>
                      <span style={{ color:'#8a9aba' }}>📈 השקעות 10%</span><Money v={invest} c="#5a9bf5" size="0.86rem" />
                    </div>
                  </div>
                </div>

                <div style={{ background:'#0d1b3e', border:'1px solid rgba(255,140,120,0.3)', borderRadius:14, padding:'18px 20px' }}>
                  <div style={{ color:'#ff9a88', fontWeight:900, fontSize:'0.92rem', marginBottom:2 }}>🏢 נשאר בחשבון העסקי</div>
                  <div style={{ color:'#4a5a7a', fontSize:'0.74rem', marginBottom:10 }}>לא צריך להעביר — זה החשבון הראשי</div>
                  <Money v={S.business} c="#ff9a88" size="2.1rem" />
                  <div style={{ borderTop:'1px solid rgba(255,140,120,0.2)', marginTop:12, paddingTop:9, display:'flex', flexDirection:'column', gap:4 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.8rem' }}>
                      <span style={{ color:'#8a9aba' }}>🏛 מיסים ({Math.round(vatPct+incomePct+natInsPct)}%)</span><Money v={totalTax} c="#ff9a88" size="0.86rem" />
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.8rem' }}>
                      <span style={{ color:'#8a9aba' }}>💳 החזרי הלוואות 20%</span><Money v={loans} c="#b478ff" size="0.86rem" />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── מיסים: מוצג מכווץ, נפתח לעריכה ── */}
              <div style={{ background:'#0a1525', border:'1px solid #1e3060', borderRadius:12, marginBottom:12 }}>
                <button onClick={()=>setBabelShowTax(v=>!v)}
                  style={{ width:'100%', background:'none', border:'none', cursor:'pointer', padding:'12px 18px',
                    display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                  <span style={{ color:'#8a9aba', fontSize:'0.84rem' }}>
                    🏛 מע״מ {vatPct}% · מס הכנסה {incomePct}% · ביטוח לאומי {natInsPct}%
                  </span>
                  <span style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <Money v={totalTax} c="#ff8888" size="0.95rem" />
                    <span style={{ color:'#4a5a7a', fontSize:'0.78rem' }}>{babelShowTax ? '▲' : '✏️'}</span>
                  </span>
                </button>
                {babelShowTax && (
                  <div style={{ borderTop:'1px solid #1e3060', padding:'14px 18px' }}>
                    <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:10 }}>
                      {[
                        { label:'מע״מ', val:babelVat, set:setBabelVat },
                        { label:'מס הכנסה', val:babelIncomeTax, set:setBabelIncomeTax },
                        { label:'ביטוח לאומי', val:babelNatIns, set:setBabelNatIns },
                      ].map(t => (
                        <div key={t.label}>
                          <div style={{ color:'#8a9aba', fontSize:'0.74rem', marginBottom:4 }}>{t.label} %</div>
                          <input type="number" step="0.01" min="0" max="100" value={t.val} onChange={e=>t.set(e.target.value)}
                            style={{ width:80, background:'#0a1a3e', border:'1px solid #2a3a6a', borderRadius:8, padding:'6px 9px', color:'#fff', fontSize:'0.88rem', textAlign:'center' }} />
                        </div>
                      ))}
                      <button onClick={saveTaxRates} disabled={babelSaving}
                        style={{ alignSelf:'flex-end', background:'#d4af37', color:'#000', border:'none', borderRadius:8, padding:'7px 18px', cursor:'pointer', fontWeight:800, fontSize:'0.82rem' }}>
                        {babelSaving ? '...' : '💾 שמור'}
                      </button>
                    </div>
                    <div style={{ color:'#4a5a7a', fontSize:'0.73rem', lineHeight:1.6 }}>
                      מע״מ 18% (מ-1.1.2025). מס הכנסה וביטוח לאומי מדרגתיים ותלויים בהכנסה השנתית — עדכנו מול רואה החשבון.
                    </div>
                  </div>
                )}
              </div>

              {/* ── סיכום שנתי ── */}
              <div style={{ background:'#0d1b3e', border:'1px solid rgba(212,175,55,0.3)', borderRadius:12, padding:'14px 18px', marginBottom:12 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8, marginBottom:10 }}>
                  <span style={{ color:'#d4af37', fontWeight:900, fontSize:'0.9rem' }}>📆 סיכום {selYear}</span>
                  <Money v={Y.gross} c="#d4af37" size="1.3rem" />
                </div>
                <div style={{ display:'flex', gap:18, flexWrap:'wrap', fontSize:'0.82rem' }}>
                  <span style={{ color:'#8a9aba' }}>🏠 לפרטי <Money v={Y.private} c="#25d366" size="0.86rem" /></span>
                  <span style={{ color:'#8a9aba' }}>🏢 בעסקי <Money v={Y.business} c="#ff9a88" size="0.86rem" /></span>
                  <span style={{ color:'#8a9aba' }}>🏛 מיסים <Money v={Y.tax} c="#ff8888" size="0.86rem" /></span>
                  <span style={{ color:'#8a9aba' }}>📈 השקעות <Money v={Y.investment} c="#5a9bf5" size="0.86rem" /></span>
                </div>
              </div>

              {/* ── ארכיון ── */}
              {monthArchive.length > 1 && (
                <div style={{ background:'#0a1525', border:'1px solid #1e3060', borderRadius:12, padding:'14px 18px' }}>
                  <div style={{ color:'#8a9aba', fontWeight:800, fontSize:'0.84rem', marginBottom:10 }}>🗄 ארכיון</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:2, maxHeight:220, overflowY:'auto' }}>
                    {monthArchive.map(mo => {
                      const sp = splitOf(mo.gross);
                      const sel = mo.key === babelMonth;
                      return (
                        <button key={mo.key} onClick={()=>setBabelMonth(mo.key)}
                          style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap',
                            background: sel?'rgba(212,175,55,0.09)':'transparent', border:'none', borderRadius:6,
                            padding:'7px 8px', cursor:'pointer', textAlign:'right', width:'100%' }}>
                          <span style={{ color: sel?'#d4af37':'#8a9aba', fontWeight: sel?800:400, fontSize:'0.83rem' }}>
                            {mLabel(mo.key)}{mo.key===curMonthB ? ' •' : ''}
                          </span>
                          <span style={{ display:'flex', gap:12, fontSize:'0.8rem' }}>
                            <span style={{ color:'#c0cce6' }}>{fmt(mo.gross)}</span>
                            <span style={{ color:'#25d366' }}>🏠 {fmt(sp.private)}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

      </div>

      <div className="footer" style={{ marginTop:40 }}>
        <span>מועדון יוסטאר</span> — ממשק ניהול פנימי
      </div>

      {/* ══════ AUTO-CAMPAIGN MODAL ══════ */}

      {/* ══════ QUICK-SEND MODAL ══════ */}
      {quickSend && (() => {
        const { partner, deal, msg } = quickSend;
        const phones = members.map(m => m.phone.replace(/\D/g,'').replace(/^0/,'972'));
        const allPhonesTxt = members.map(m => m.phone).join('\n');
        const current = members[qsWaIndex];
        return (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,10,0.85)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
            onClick={e=>{ if(e.target===e.currentTarget) setQuickSend(null); }}>
            <div style={{ background:'#0d1a35', border:'2px solid #d4af37', borderRadius:20, width:'100%', maxWidth:680, maxHeight:'90vh', overflow:'auto', padding:28, direction:'rtl' }}>

              {/* Header */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <div>
                  <div style={{ fontSize:'1.5rem' }}>{partner.logo_emoji} <span style={{ color:'#d4af37', fontWeight:700 }}>{partner.name}</span></div>
                  <div style={{ color:'#aaa', fontSize:'0.88rem', marginTop:2 }}>{deal.title}</div>
                </div>
                <button onClick={()=>setQuickSend(null)} style={{ background:'none', border:'none', color:'#aaa', fontSize:'1.8rem', cursor:'pointer', lineHeight:1 }}>×</button>
              </div>

              {/* Stats */}
              <div style={{ display:'flex', gap:12, marginBottom:20 }}>
                <div style={{ background:'#0a1525', borderRadius:10, padding:'10px 18px', textAlign:'center', flex:1 }}>
                  <div style={{ color:'#d4af37', fontSize:'1.6rem', fontWeight:700 }}>{members.length}</div>
                  <div style={{ color:'#aaa', fontSize:'0.8rem' }}>חברים ירקבלו</div>
                </div>
                {deal.coupon_code && (
                  <div style={{ background:'#0a2a1a', border:'1px dashed #25d366', borderRadius:10, padding:'10px 18px', textAlign:'center', flex:1 }}>
                    <div style={{ color:'#25d366', fontFamily:'monospace', fontWeight:700, fontSize:'1.2rem', letterSpacing:3 }}>{deal.coupon_code}</div>
                    <div style={{ color:'#aaa', fontSize:'0.8rem' }}>קוד קופון</div>
                  </div>
                )}
                <div style={{ background:'#0a1525', borderRadius:10, padding:'10px 18px', textAlign:'center', flex:1 }}>
                  <div style={{ color:'#7a9aff', fontSize:'1.1rem', fontWeight:700 }}>{dealLabel(deal)||'—'}</div>
                  <div style={{ color:'#aaa', fontSize:'0.8rem' }}>הנחה</div>
                </div>
              </div>

              {/* Message preview */}
              <div style={{ background:'#0a1525', borderRadius:12, padding:16, marginBottom:20 }}>
                <div style={{ color:'#d4af37', fontSize:'0.82rem', marginBottom:8, fontWeight:600 }}>✉️ תצוגה מקדימה של ההודעה</div>
                <div style={{ color:'#e0e0e0', fontSize:'0.88rem', whiteSpace:'pre-line', lineHeight:1.7, background:'#1a2a4a', borderRadius:8, padding:14, direction:'rtl' }}>{msg}</div>
              </div>

              {/* ─── WhatsApp section ─── */}
              <div style={{ marginBottom:20 }}>
                <div style={{ color:'#25d366', fontWeight:700, fontSize:'1rem', marginBottom:12, display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:'1.3rem' }}>📱</span> שליחה בווטסאפ
                </div>

                {/* Option A: Broadcast list */}
                <div style={{ background:'#0a2a1a', border:'1px solid #25d366', borderRadius:12, padding:16, marginBottom:10 }}>
                  <div style={{ color:'#fff', fontWeight:600, marginBottom:6 }}>אפשרות 1 — רשימת שידור (הכי מהיר)</div>
                  <div style={{ color:'#aaa', fontSize:'0.82rem', marginBottom:12 }}>
                    העתק את כל המספרים, צור רשימת שידור בווטסאפ ושלח פעם אחת לכולם
                  </div>
                  <div style={{ display:'flex', gap:8 }}>
                    <button onClick={()=>{ navigator.clipboard.writeText(allPhonesTxt); flash('✅ כל המספרים הועתקו!'); }}
                      style={{ flex:1, background:'#25d366', color:'#fff', border:'none', borderRadius:8, padding:'10px 0', cursor:'pointer', fontWeight:700, fontSize:'0.9rem' }}>
                      📋 העתק {members.length} מספרים
                    </button>
                    <button onClick={()=>{ navigator.clipboard.writeText(msg); flash('✅ ההודעה הועתקה!'); }}
                      style={{ background:'#1a3a2a', color:'#25d366', border:'1px solid #25d366', borderRadius:8, padding:'10px 16px', cursor:'pointer', fontWeight:600 }}>
                      📋 העתק הודעה
                    </button>
                  </div>
                </div>

                {/* Option B: One by one */}
                <div style={{ background:'#0a1a2a', border:'1px solid #2a3a6a', borderRadius:12, padding:16 }}>
                  <div style={{ color:'#fff', fontWeight:600, marginBottom:6 }}>אפשרות 2 — שליחה אחד אחד ({qsWaIndex+1}/{members.length})</div>
                  {members.length===0 ? (
                    <div style={{ color:'#556' }}>אין חברים</div>
                  ) : (
                    <>
                      <div style={{ color:'#aaa', fontSize:'0.82rem', marginBottom:10 }}>
                        שולח ל: <strong style={{ color:'#fff' }}>{current.first_name}</strong> — {current.phone}
                      </div>
                      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                        <a href={`https://wa.me/${phones[qsWaIndex]}?text=${encodeURIComponent(msg)}`} target="_blank" rel="noopener noreferrer"
                          onClick={()=>{ if(qsWaIndex<members.length-1) setTimeout(()=>setQsWaIndex(qsWaIndex+1),1500); }}
                          style={{ flex:1, background:'#25d366', color:'#fff', border:'none', borderRadius:8, padding:'10px 0', cursor:'pointer', fontWeight:700, textDecoration:'none', textAlign:'center', display:'block' }}>
                          📱 שלח ל{current.first_name} {qsWaIndex===members.length-1?'✅ אחרון!':'→'}
                        </a>
                        {qsWaIndex > 0 && (
                          <button onClick={()=>setQsWaIndex(qsWaIndex-1)} style={{ background:'#1a2a4a', color:'#aaa', border:'none', borderRadius:8, padding:'10px 14px', cursor:'pointer' }}>← קודם</button>
                        )}
                      </div>
                      <div style={{ marginTop:10, background:'#0a1525', borderRadius:8, overflow:'hidden', height:6 }}>
                        <div style={{ background:'#25d366', height:6, width:`${((qsWaIndex+1)/members.length)*100}%`, transition:'width 0.3s' }} />
                      </div>
                      <div style={{ color:'#556', fontSize:'0.75rem', marginTop:4 }}>נשלח: {qsWaIndex}/{members.length}</div>
                    </>
                  )}
                </div>
              </div>

              {/* ─── Email section ─── */}
              <div>
                <div style={{ color:'#7a9aff', fontWeight:700, fontSize:'1rem', marginBottom:12, display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:'1.3rem' }}>📧</span> שליחה באימייל
                </div>
                <div style={{ background:'#0a1a3a', border:'1px solid #2a3a7a', borderRadius:12, padding:16 }}>
                  <div style={{ color:'#aaa', fontSize:'0.85rem', marginBottom:12 }}>
                    שלח מייל לכל {members.filter(m=>m.email).length} חברים שיש להם כתובת מייל
                  </div>
                  {qsEmailResult ? (
                    <div style={{ color:'#25d366', fontWeight:600 }}>✅ {qsEmailResult}</div>
                  ) : (
                    <button disabled={qsSendingEmail} onClick={async()=>{
                      setQsSendingEmail(true);
                      try {
                        const withEmail = members.filter(m=>m.email);
                        if(withEmail.length===0){ setQsEmailResult('אין חברים עם כתובת מייל'); setQsSendingEmail(false); return; }
                        const html = `<div dir="rtl" style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;background:#0a1120;color:#fff;border-radius:16px;padding:28px;">
                          <div style="text-align:center;margin-bottom:20px;">
                            <div style="font-size:3rem">${partner.logo_emoji}</div>
                            <h2 style="color:#d4af37;margin:8px 0">${deal.title}</h2>
                            ${deal.discount_value?`<div style="background:#d4af37;color:#0a1120;display:inline-block;border-radius:20px;padding:6px 20px;font-weight:700;font-size:1.2rem">${dealLabel(deal)}</div>`:''}
                          </div>
                          <p style="font-size:1rem;line-height:1.7;color:#ccc">${(deal.description||'').replace(/\n/g,'<br/>')}</p>
                          ${deal.coupon_code?`<div style="background:#0a2a1a;border:2px dashed #25d366;border-radius:12px;padding:16px;text-align:center;margin:20px 0;">
                            <div style="color:#aaa;font-size:0.85rem">קוד קופון</div>
                            <div style="color:#25d366;font-family:monospace;font-size:2rem;font-weight:700;letter-spacing:4px">${deal.coupon_code}</div>
                          </div>`:''}
                          ${partner.website?`<div style="text-align:center;margin-top:20px;"><a href="${partner.website}" style="background:#1a3a8e;color:#fff;padding:12px 28px;border-radius:50px;text-decoration:none;font-weight:700">לאתר ${partner.name} →</a></div>`:''}
                          ${deal.valid_until?`<p style="color:#ff9966;font-size:0.85rem;text-align:center;margin-top:16px">⏰ בתוקף עד ${deal.valid_until}</p>`:''}
                          <hr style="border-color:#1a2a4a;margin:24px 0"/>
                          <p style="color:#556;font-size:0.82rem;text-align:center">מועדון ההטבות של יוסטאר ⭐ | 054-6318704</p>
                        </div>`;
                        const r = await axios.post(API+'/email/bulk', { member_ids: withEmail.map(m=>m.id), subject:`הטבה בלעדית מ${partner.name} לחברי מועדון יוסטאר`, html });
                        setQsEmailResult(`נשלח ל-${r.data.sent} חברים ✅`);
                        await axios.post(API+'/campaigns', { title:`הטבת ${partner.name}`, channel:'email', message:deal.title, target:'all' }).then(({data:c})=>axios.put(API+'/campaigns/'+c.id+'/sent',{count:r.data.sent}));
                      } catch(err) {
                        setQsEmailResult('שגיאה בשליחה — בדוק הגדרות GMAIL בסביבת Railway');
                      }
                      setQsSendingEmail(false);
                    }} style={{ width:'100%', background: qsSendingEmail?'#1a2a4a':'#1a3a8e', color:'#fff', border:'none', borderRadius:8, padding:'12px 0', cursor:qsSendingEmail?'not-allowed':'pointer', fontWeight:700, fontSize:'0.95rem' }}>
                      {qsSendingEmail ? '⏳ שולח...' : `📧 שלח מייל ל-${members.filter(m=>m.email).length} חברים`}
                    </button>
                  )}
                </div>
              </div>

            </div>
          </div>
        );
      })()}


    </div>
  );
}
